import type { JobHoldReasonKey } from "./jobHold";
// Thin API client. All calls are same-origin (Vite proxies /api in dev; the web
// service serves both SPA and API in prod) and send the session cookie.
import { BACKGROUND_REQUEST_HEADER, noteSessionActivity, onLocalSignOut, reportIfSessionEnded } from "./sessionEvents";
import { PHONE_VIEW_BLOCKED_BODY, PHONE_VIEW_BLOCKED_MESSAGE, announcePhoneViewWriteBlocked, phoneViewBlocksWrite } from "./phonePreview";
import type { Customer as ResourceCustomer, Asset as ResourceAsset } from "./resources";
import type { NavLayoutSection } from "./nav";
import { SCHEDULE_OF_FEES_HREF, SUBCONTRACTOR_RATE_CARD_HREF } from "./documentHrefs";
import type {
  SafedAttentionItemView,
  SafedDeliveryDoneView,
  SafedDeliveryPreviewView,
  SafedInvoicePlanView,
  SafedLiftLifecycleView,
  SafedQuotePlanView,
  SafedQuoteView,
  SafedRecommendationSourceView,
  SafedRecommendationView,
  SafedSignOffView,
  SafedLifecycleStateView,
  SafedWorklistItemView,
  SafedWorklistStateView,
} from "./safedFlowTypes";
import type {
  CheckJobBody,
  CheckJobResult,
  InsuranceItemDto,
  InsuranceItemListResult,
  InsuranceItemState,
  RaiseDefectPlan,
  RecordInsuranceItemsBody,
  RecordInsuranceItemsResult,
  UpdateInsuranceItemBody,
} from "./insuranceItems";
import type {
  SubcontractorXeroSettings,
  SubcontractorXeroSettingsInput,
  PaymentDetailsView,
  RevealedPaymentDetails,
  OfficePaymentDetailsEntry,
  OfficePaymentDetailsEntryResult,
  PaymentDetailsRequestResult,
  PaymentDetailsRequestPageView,
  PaymentDetailsForm,
  XeroContactPreview as SubcontractorXeroContactPreview,
  XeroContactPushResult,
} from "./subcontractorPayments";
import type {
  EngineerTrainingAttendeeInput,
  EngineerTrainingAttendeesResult,
  EngineerTrainingSessionDto,
  PortalTrainingSessionDto,
  PortalTrainingFeedSessionDto,
  TrainingAttendeeDto,
  TrainingAttendeeInput,
  TrainingCompleteFromPwaResult,
  TrainingAssignableEngineer,
  TrainingCompleteResult,
  TrainingListQuery,
  TrainingSessionCreateInput,
  TrainingSessionDetailDto,
  TrainingSessionListResult,
  TrainingSessionUpdateInput,
  TrainingType,
  TrainingReferenceDocumentDto,
  TrainingCoverage,
  PortalTrainingDocGroup,
} from "./trainingTypes";
import type {
  KnowledgeDocument,
  KnowledgeDocumentDetail,
  KnowledgeDocumentListQuery,
  KnowledgeDocumentListResult,
  KnowledgeDrivePullInput,
  KnowledgeGmailPullInput,
  KnowledgePullResult,
  KnowledgeQueryDetail,
  KnowledgeQueryInput,
  KnowledgeQueryListResult,
  KnowledgeQueryResponse,
  KnowledgeEngineerQueryResponse,
  KnowledgeLiftContext,
  KnowledgeBulkImportInput,
  KnowledgeBulkImportPreview,
  KnowledgeBulkImportResult,
  KnowledgeQueueStatus,
  KnowledgeTagInput,
  KnowledgeTagsPatchInput,
  KnowledgeUploadResult,
  KnowledgeDriveStatusResponse,
  KnowledgeRebuildMode,
  KnowledgeRebuildPreview,
  KnowledgeRebuildStartResult,
  KnowledgeRebuildStatus,
  KnowledgeDriveFileListQuery,
  KnowledgeDriveFileListResult,
  KnowledgeDriveSettingsInput,
  KnowledgeLibraryName,
  KnowledgeLibraryNameInput,
  KnowledgeMakerIdentitySummary,
  KnowledgeVocabularyStatus,
  KnowledgeFaultRowsBackfill,
  KnowledgeFaultRowRecord,
  KnowledgeSuggestion,
  KnowledgeMakerReviewItem,
  KnowledgeDriveSettings,
  KnowledgeDriveActionResult,
  KnowledgePageView,
  KnowledgePageSearchResponse,
} from "./knowledgeTypes";

export type Role = "admin" | "office" | "engineer" | "auditor";

export interface User {
  id: string;
  tenantId: string;
  email: string | null; // office (Google) users; also the auditor login email
  username: string | null; // engineers
  role: Role;
  displayName: string;
  techId: string | null; // engineers (TECH-NNN)
  // External auditor (read-only compliance portal). null for every other role.
  auditorOrg?: string | null;
  auditorExpiresAt?: string | null; // ISO
  // Does this account have an Engineer row of its own (Session AGW)? Set by GET /api/auth/me, which
  // is the only thing that can know: an Engineer row is keyed on userId, not on role, so an admin
  // may hold one. Used ONLY to decide whether the PWA's personal panels are worth rendering (see
  // src/lib/pwaIdentity.ts); it grants nothing and the server re-resolves the row on every read.
  // Optional so a client built against an older server, or any code that constructs a User for a
  // test, simply falls back to the role test.
  hasEngineerRecord?: boolean;
}

// ---- Van stock (per-engineer inventory) ----
// Session BDG (M2): the Clear auto-PPM answer. A preview carries the counts and a plain sentence; a commit carries
// `deleted` as well.
export interface ClearAutoPpmResult {
  preview?: boolean;
  deleted?: number;
  unassigned: number;
  assignedNotAccepted: number;
  keptAcceptedOrCrewed: number;
  includeAssigned: boolean;
  wouldDelete: number;
  message?: string;
}

export interface VanStockItem {
  id: string;
  partCode: string;
  description: string;
  quantity: number;
  reorderThreshold: number;
  unitCost: number | null;
  needsReorder: boolean;
  updatedAt: string | null;
}

export interface VanStockReorderItem extends VanStockItem {
  engineerName: string;
  techId: string | null;
}

// A row on the consolidated office Van stock page (Session AK Item 3): each item with its owning
// engineer's name, techId and User id (the handle for edit/delete).
export interface VanStockAdminItem extends VanStockItem {
  engineerName: string;
  techId: string | null;
  engineerUserId: string | null;
}

export interface VanStockCreateInput {
  partCode: string;
  description: string;
  quantity?: number;
  reorderThreshold?: number;
  unitCost?: number;
}

export interface VanStockUpdateInput {
  delta?: number;
  quantity?: number;
  reorderThreshold?: number;
  unitCost?: number | null;
  description?: string;
}

// ---- RFID (NFC) tag URL management on the lift record ----
export interface NfcTokenResult {
  nfcToken: string;
  rfidSentAt: string | null;
}

// ---- The Guv POP Tag, Session AMZ as "Tapping For Tracking": the lift's SECOND, INTERNAL tag ----
// The same two-fact shape as NfcTokenResult above, in its own columns. Separate types rather than
// one shared one on purpose: the office control writes whichever pair it reads back, so a single
// type would let a tap generate quietly repaint the RFID card's status and vice versa.
export interface TapTokenResult {
  attendanceTapToken: string;
  attendanceTapSentAt: string | null;
}

// One recorded attendance tap, as the office history table shows it. `byUser` is null for an
// ORDINARY tap and is not a fault: a tap records WHEN somebody attended, and the wet-signed log
// card records WHO. A voided tap is returned FLAGGED rather than withheld, because the office is
// cross-checking this list against that log card and a silently missing row makes the check useless.
export interface AttendanceTapEvent {
  id: string;
  tappedAt: string;
  byUser: string | null;
  voided: boolean;
  voidedAt: string | null;
  voidedBy: string | null;
  voidReason: string | null;
  // Session AOE. True when this row is the tap that PROVED THE STICKER WORKS: the first tap after
  // the tag was programmed or rotated. It is real and correct, it is left out of every count, and it
  // is NOT a void, so the office history shows it in its own right rather than struck through.
  installConfirmation: boolean;
  // Session APF: the tag became an on/off pair. "arrival" | "departure" | null, straight off the
  // row: null is the ordinary case for every tap recorded before this session, and for one made by
  // opening the sticker's own page rather than from the job sheet, which has no way to state one.
  direction: "arrival" | "departure" | null;
  // Session APF. A soft pointer with no relation (see the schema comment on AttendanceTap.jobId): the
  // job the tap was made from, when a staff identity supplied one that resolved to a real job on this
  // same asset. null is ordinary. A dangling id (the job has since been deleted) reads as "no longer
  // on file" rather than as an error.
  jobId: string | null;
}

export interface AttendanceTapEventsResponse {
  total: number;
  // Session AOG. The asset's CURRENT confirming instant, stated once rather than as a per-row field:
  // recomputeAttendanceTapConfirmation (void/unvoid) can move it onto an ordinary tap whose own
  // installConfirmation flag stays false for ever, so a row can quietly become the sole surviving
  // evidence a sticker was ever confirmed with nothing else in the table saying so. Compare each
  // row's own tappedAt against this value to find it. Null is ordinary: the tag has never been
  // confirmed, or every confirming tap has since been voided.
  assetAttendanceTapConfirmedAt: string | null;
  taps: AttendanceTapEvent[];
}

// Session BCK: the "Who are you?" step of an engineer sign-in on a shared address.
export interface EngineerSelectionStep {
  token: string;
  engineers: Array<{ id: string; firstName: string }>;
}

// ---- Engineer accounts (admin only) ----
export interface EngineerListItem {
  id: string; // the User id -- the handle activate/deactivate targets
  techId: string | null;
  displayName: string;
  username: string | null;
  isActive: boolean;
  createdAt: string;
  // Contact (for sending the RFID tag URL to programme on site).
  email: string | null;
  mobile: string | null;
  // Internal labour cost rate (pounds/hour, ex VAT) for job-cost margin (Session AK Item 2). Admin only: ABSENT for the
  // office role (Session BDG, M1).
  costRatePerHour?: number | null;
  // The office's fallback contact if a lone-worker escalation call to this engineer goes unanswered
  // (Session AOU). All null is the normal state -- most engineers will never fill this in. The
  // platform never contacts this number itself.
  nextOfKinName: string | null;
  nextOfKinRelationship: string | null;
  nextOfKinPhone: string | null;
  // Session BCW: the engineer's home postcode (never a full address), so a call-out's travel is measured from the nearest
  // of the office and the engineers' homes. Office and admin only; never shown to an engineer, a customer or a chat.
  homePostcode?: string | null;
  // The engineer's own row on the SUBCONTRACTOR register, when they have one (Session ALV). ELE's
  // engineers are engaged as subcontractors rather than PAYE employees, so an engineer may also
  // hold insurance, a signed agreement, competency certificates and a DBS check over there. Both
  // are null when there is no link, which is the normal state and must render as nothing at all --
  // never as an empty or dead link, which would imply a record that does not exist.
  // subcontractorCompany is the DESTINATION'S NAME, read live in the same request, so a link can
  // say where it goes. No other subcontractor fact crosses: insurance levels, agreement status and
  // certificate expiry all stay on the subcontractor's own page.
  subcontractorId: string | null;
  subcontractorCompany: string | null;
  // Session BCK: first names of the OTHER active engineers of the same subcontractor who sign in with this same email
  // address (an address may be shared only inside one subcontractor). Absent or empty is the normal state.
  sharesEmailWith?: string[];
  // Session PN1: whether a new job reaches this engineer by push. on = a phone is registered; off = one was, none now; never = none
  // ever. textFallback is true when their own text setting is on and a UK mobile is on record. Absent on a create/patch reply.
  notifications?: { push: "on" | "off" | "never"; phones: number; textFallback: boolean } | null;
  // Session RC1: a technician (only a technician is booked on a technician visit) and how the engineer is paid. For everyone
  // who can open the page; absent on an older reply.
  isTechnician?: boolean;
  payType?: EngineerPayType | null;
  // Session RC1: ADMIN ONLY, absent (never null) for the office, like costRatePerHour.
  payeSalaryPence?: number | null;
  payeContractedWeeklyMins?: number | null;
  /** YYYY-MM-DD. */
  payeStartDate?: string | null;
}
export type EngineerPayType = "PAYE" | "SUBCONTRACTOR";

// Session PN1: the quiet line that says whether the engineer on a job was told (push, text, or not told and why). Derived live
// from the audit trail; absent when the platform holds no record of a telling.
export interface EngineerToldLine {
  state: "push" | "text" | "not_told";
  text: string;
  warn: boolean;
  at: string;
}

// The engineer's own fallback contact (Session AOU), as returned by GET/PATCH
// /engineers/me/next-of-kin. All null is a normal, complete state.
export interface EngineerNextOfKin {
  nextOfKinName: string | null;
  nextOfKinRelationship: string | null;
  nextOfKinPhone: string | null;
}

// ---- Calendar sync and the engineer calendar feed (Session ATY) ----
// Whether the weekly database backup is working, as GET /backup/status reports it (server/src/modules/backup/backupStatus.ts).
// Derived live from the audit trail on every visit: never cached by the office GET cache.
export interface BackupStatus {
  state: "fresh" | "stale" | "interrupted" | "never_recorded" | "check_failed";
  headline: string;
  staleAfterDays: number;
  lastSuccess: { at: string; ageDays: number; filename: string | null; sizeBytes: number | null } | null;
  // Session BDS: set only when state is interrupted (a run started and recorded no end).
  interruptedRun: { startedAt: string; filename: string | null } | null;
  problemsSinceLastSuccess: { at: string; kind: "failed" | "not_connected" | "too_small" | "killed"; filename: string | null; what: string }[];
  problemCount: number;
  checkError: string | null;
  // Session BEF: unconfigured it is just { configured: false, note }; configured it adds the state and the last copy and failure.
  offPlatformCopy: {
    configured: boolean;
    note: string;
    state?: "ok" | "failing" | "overdue" | "no_copy_yet" | "unknown";
    lastCopy?: { at: string; ageDays: number; filename: string | null; sizeBytes: number | null } | null;
    lastFailure?: { at: string; filename: string | null; what: string } | null;
  };
  checkedAt: string;
}

// The four Google calendars Guvnor keeps on the info@ account, as GET /calendar-sync/status reports them
// (server/src/modules/calendarSync/status.ts). Read live on every visit: never cached by the office GET cache.
export type CalendarSyncKey = "service" | "standby" | "compliance" | "email";
export interface CalendarSyncStatus {
  access: "connected" | "needs_reconnect" | "not_connected";
  // One plain sentence when the sync cannot run; null when it can.
  accessMessage: string | null;
  calendars: Array<{ key: CalendarSyncKey; name: string; carries: string; on: boolean; ready: boolean; events: number }>;
  lastSyncAt: string | null;
  lastReconcileAt: string | null;
  lastReconcileCounts: { created: number; updated: number; repaired: number; removed: number; unchanged: number; failed: number } | null;
  // Session ATY (review): the nightly full check has not completed in the last day (a check that did not run is never shown as an all-clear).
  reconcileOverdue: boolean;
  reconcileOverdueMessage: string | null;
  lastError: string | null;
  lastErrorAt: string | null;
  // Engineers with a live private calendar feed.
  engineerFeedsOn: number;
  // How a person adds one of these shared calendars to their own Google Calendar.
  howToAdd: string;
}
export interface CalendarSyncSettingsChange {
  key: CalendarSyncKey;
  name: string;
  from: boolean;
  to: boolean;
  effect: string;
}

// An engineer's private calendar feed, never the link itself: the server stores only a hash of the token, so the
// link exists on the client ONLY in the reply to the create (CalendarFeedLinks), held in component state and nowhere
// else (never storage, never logged).
export interface CalendarFeedSummary {
  id: string;
  engineerId: string;
  on: boolean;
  createdAt: string;
  revokedAt: string | null;
  revokedByName: string | null;
  lastFetchedAt: string | null;
}
// No webcal:// link for Apple (the ATY review): some Apple Calendar versions fetch webcal:// over plain http, which would
// send the token in clear, so the screen tells Apple users to paste `url` instead.
export interface CalendarFeedLinks {
  url: string;
  google: string;
  outlook: string;
  outlookWork: string;
}
// One engineer's feeds on the office Engineers page. userId maps to that page's rows (EngineerListItem.id).
export interface EngineerCalendarFeedRow {
  engineerId: string;
  userId: string | null;
  engineerName: string;
  live: CalendarFeedSummary | null;
  history: CalendarFeedSummary[];
}

// Session BCE: what Send app details would do, from the route's own preview (it writes nothing).
export interface EngineerAppDetailsPreview {
  preview: true;
  engineer: string;
  to: string;
  message: string;
}

// Session BCE: the Gmail draft of the app details the server leaves when an engineer is created or the office presses
// Send app details. Never carries a code or a password (engineers have neither to send).
export interface EngineerAppDetailsResult {
  drafted: boolean;
  to: string;
  message: string;
}

// Session BCE: an engineer has no password and no username to type. Email and mobile are both required, because the
// emailed sign-in code goes to the email and the office reaches them on the mobile.
export interface EngineerCreateInput {
  displayName: string;
  email: string;
  mobile: string;
  /** Session BAV: create the engineer already linked to this subcontractor (refused 409 if it is archived). */
  subcontractorId?: string;
  /** Session ANK: the deliberate override for two real people of the same name. */
  acknowledgeDuplicateName?: boolean;
}

export type EngineerCreateResult = EngineerListItem & { appDetails?: EngineerAppDetailsResult };

// ---- Engineer certifications (office/admin) ----
export type CertStatus = "valid" | "expiring" | "expired" | "no-expiry";

export interface CertificationItem {
  id: string;
  engineerUserId: string;
  certType: string;
  issuingBody: string;
  certNumber: string | null;
  issuedDate: string;
  expiryDate: string | null;
  status: CertStatus; // derived server-side from expiryDate
  daysUntilExpiry: number | null;
  hasDocument: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ExpiringCertificationItem extends CertificationItem {
  engineerName: string;
  techId: string | null;
}

export interface CertificationCreateInput {
  certType: string;
  issuingBody: string;
  certNumber?: string;
  issuedDate: string; // yyyy-mm-dd
  expiryDate?: string | null;
  notes?: string;
}

export type CertificationUpdateInput = Partial<CertificationCreateInput>;

// ---- Engineer competence (SAFed matrix) ----
export type ReviewStatus = "none" | "ok" | "due_soon" | "overdue";

export interface CompetenceRecordItem {
  id: string;
  engineerUserId: string;
  competenceArea: string;
  level: string;
  assessedDate: string;
  reviewDate: string | null;
  reviewStatus: ReviewStatus; // derived server-side from reviewDate
  evidenceUrl: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CompetenceCell {
  recordId: string;
  level: string;
  reviewStatus: ReviewStatus;
  assessedDate: string;
  reviewDate: string | null;
}

export interface CompetenceMatrixRow {
  engineerUserId: string;
  engineerName: string;
  techId: string | null;
  isActive: boolean;
  cells: Record<string, CompetenceCell | null>; // keyed by competence area
}

export interface CompetenceMatrix {
  areas: string[];
  rows: CompetenceMatrixRow[];
}

// The consolidated reviews-due list (overdue + due soon across all engineers).
export interface CompetenceReviewDueItem {
  recordId: string;
  engineerUserId: string;
  engineerName: string;
  techId: string | null;
  competenceArea: string;
  level: string;
  reviewStatus: "overdue" | "due_soon";
  reviewDate: string;
  assessedDate: string;
}

export interface CompetenceCreateInput {
  competenceArea: string;
  level: string;
  assessedDate: string; // yyyy-mm-dd
  reviewDate?: string | null;
  evidenceUrl?: string;
  notes?: string;
}

export type CompetenceUpdateInput = Partial<CompetenceCreateInput>;

// ---- Management reporting / BI dashboard ----
export interface ReportingWindow {
  from: string;
  to: string;
  label: string;
}
export interface ReportingPortfolioHealth {
  totalLifts: number;
  green: number;
  amber: number;
  red: number;
  ppmOverdue: number;
  lolerOverdue: number;
  renewalsDue90: number;
}
export interface ReportingQuoteConversion {
  sent: number;
  signed: number;
  rate: number | null;
}
export interface ReportingPipelineStage {
  stage: string;
  count: number;
  value: number;
}
export interface ReportingPipeline {
  openCount: number;
  openValue: number;
  byStage: ReportingPipelineStage[];
}
export interface ReportingCustomerValue {
  customerName: string;
  value: number;
}
export interface ReportingJobTypeCount {
  type: string;
  count: number;
}
export interface ReportingCalloutStats {
  volume: number;
  // How many had a response time to average: logged to an engineer ASSIGNED (Session ATC, D18).
  assigned: number;
  // Reported in the window but with no honest clock start, so excluded from the average and shown
  // alongside it rather than silently dropped (Session ADQ).
  unmeasured: number;
  avgResponseHours: number | null;
}
export interface ReportingPhaseCount {
  status: string;
  count: number;
  value: number;
}
// Mirrors the server's canonical ACTION_CATEGORIES (server/src/modules/reporting/
// actionCategories.ts). A frontend coverage test (src/lib/actionCategories.test.ts) asserts the
// badge/label maps cover every server key, so this union can never silently drift behind the
// server again (it previously omitted five keys, which rendered as blank badges).
export type ActionCategory =
  | "loler_overdue"
  | "ppm_visit_overdue"
  | "job_unassigned_soon"
  | "callout_pending_approval"
  | "loler_defect"
  | "defect_no_client_notification"
  | "safed_test_stopped"
  | "complaint_overdue"
  | "sub_pl_below_minimum"
  | "cert_expired"
  | "sub_doc_expired"
  | "service_pending_approval"
  | "contract_renewal_overdue"
  | "contract_renewal_due"
  | "nps_below_target"
  | "sub_pending_review"
  | "cert_expiring"
  | "env_objective_at_risk"
  | "sub_doc_expiring"
  | "prospect_cold"
  // Session AVI: a pre-contract LOLER report (a lead's lift) that is due, overdue or carries an open defect.
  | "loler_precontract_lead"
  | "quote_follow_up"
  | "project_quote_follow_up"
  | "portfolio_discount_review"
  | "xero_billing_out_of_step"
  | "van_stock_reorder"
  // Session ARS (D91): an APPROVED repair estimate with no repair job linked, derived live. The
  // automatic raise on approval could not tell which job the work belongs to, or the linked job was
  // cancelled, so the office picks on the estimate's own panel.
  | "repair_estimate_needs_job"
  // Session ATF: an engineer raised a repair estimate on site and it has no price yet, derived live.
  | "repair_estimate_needs_price"
  | "engineer_notifications_off"
  // Session BBF: an insurer's findings waiting for a check, or checked and waiting for the office, derived live.
  | "insurance_items_unbooked"
  | "insurance_items_unscheduled"
  | "insurance_item_needs_estimate"
  | "insurance_item_verified"
  // Session BBL: an approved job with no invoice after a stated number of days, and a lift with three or more call-outs in 90 days. Derived live.
  | "job_uninvoiced"
  | "repeat_callouts"
  // Session SC6: an open chase with no reply to its latest sent step for over 30 days.
  | "chase_no_response"
  // Session SC6B: a completed planned service visit that took at least twice its expected time on site.
  | "long_ppm_visit"
  // Session PJ2: submitted project site surveys the office has not reviewed, one item counted per project.
  | "project_surveys_awaiting_review"
  // Session BCD: an approved call-out, repair or tech visit whose Xero invoice Xero reports as still a draft, read live.
  | "xero_draft_awaiting_authorisation"
  // Session ARZ: a subcontractor's payment (bank) details need a person, derived live from its own columns.
  | "subcontractor_payment_details"
  // Session AUH (email system Phase 7): a chase that is now a phone call, a debt ready to refer for recovery, and a quote
  // that has run its ladder and can be closed.
  | "chase_call"
  | "chase_referral"
  | "quote_close_no_response";

// Session SC2B: GET /api/reporting/morning-actions?limit=N.
export interface MorningActionsPage {
  items: MorningActionItem[];
  total: number;
  counts: Record<string, number>;
}

// Session SC2B: the part of GET /api/reporting/counts a page reads (the route answers more).
export interface PlatformCountsLite {
  invoices: { tracked: number };
}

// Session SPD1: the two release-queue figures of GET /api/reporting/counts, which the Release and send tabs print instead of loading the
// other tab's whole list to count it.
export interface ReleaseCountsLite {
  release: { sheetsAwaitingRelease: number; reportsReadyToSend: number };
}

// Session SPD1: POST /api/jobsheets/sent-outside-batch. One result per report, in the order asked. A refused report carries the route's own
// error code and plain-English message; a recorded (or, in a preview, a would-be recorded) one carries the addresses as the route cleaned them.
export interface SentOutsideBatchResult {
  preview: boolean;
  sentAt: string;
  total: number;
  recorded?: number;
  wouldRecord?: number;
  refused: number;
  sendsNothing: true;
  results: Array<{ jobId: string; ok: boolean; status?: number; error?: string; message?: string; to?: string[]; cc?: string[]; becomesLatestSend?: boolean; becameLatestSend?: boolean }>;
}

export interface MorningActionItem {
  id: string;
  category: ActionCategory;
  title: string;
  detail: string;
  urgency: number;
  dateIso: string | null;
  link: string | null;
  // Session AJR. Only ever "below" or "tight" on a contract_renewal_due / contract_renewal_overdue
  // item; null everywhere else, including the common "ok" or cannot-tell case (the least noisy
  // choice). Advisory only, see src/lib/costFloorTone.ts.
  marginStatus?: "below" | "tight" | null;
}

// The public NPS response page view (allow-list; never echoes ids or the token).
export interface PublicNpsView {
  status: "open" | "responded";
  siteName: string | null;
}

// ---- The privacy notice (Session ASQ, Stream G, Item 5) ----
// What an admin saved in Settings, as the public /privacy page reads it (GET /api/public/privacy-notice). The route
// answers 404 not_published until a notice is saved, and the page and every link to it stay hidden until then.
export interface PublicPrivacyNotice {
  text: string;
  updatedAt: string | null; // ISO; shown as "Last updated DD/MM/YYYY"
}

// The Settings read and write (GET office/admin, PUT admin). text null = not published; updatedAt is when it last
// changed (set or cleared), null when it has never been set.
export interface PrivacyNoticeSettings {
  text: string | null;
  updatedAt: string | null;
}

// ---- Public (no-auth) repair estimate page (Session ARS, D91) ----
// The customer's own page for a repair estimate (/estimate/:token), where they approve or decline the
// price. Field for field the server's PublicEstimateView (server/src/modules/public/publicRepairEstimate.ts),
// an explicit allow-list: never an id, the token, the office's note or the customer record. Money is PENCE.
export type PublicRepairEstimateState = "open" | "approved" | "declined" | "expired";

export interface PublicRepairEstimateView {
  reference: string;
  documentKind: "quotation" | "price"; // what the customer's own document called itself
  issuedOn: string; // the document's own date, "25 September 2026"
  validUntil: string; // the last day the price is valid on, same form
  works: string;
  siteLabel: string | null;
  unit: { label: "Lift" | "Equipment"; eleNumber: string } | null;
  exVatPence: number;
  vatPence: number;
  incVatPence: number;
  vatPercent: number;
  state: PublicRepairEstimateState;
  answeredOn: string | null;
}

// The office NPS reporting panel (rolling window).
export interface NpsSummary {
  windowDays: number;
  average: number | null;
  responses: number;
  sent: number;
  responseRate: number | null;
  distribution: { detractors: number; passives: number; promoters: number };
  npsScore: number | null;
  recent: { score: number; comment: string | null; siteName: string | null; respondedAt: string }[];
}

export interface ReportingOverview {
  window: ReportingWindow;
  generatedAt: string;
  portfolioHealth: ReportingPortfolioHealth;
  commercial: {
    arr: number;
    mrr: number;
    quoteConversion: ReportingQuoteConversion;
    pipeline: ReportingPipeline;
    topCustomers: ReportingCustomerValue[];
  };
  operations: {
    jobsByType: ReportingJobTypeCount[];
    callouts: ReportingCalloutStats;
    lolerDefects: { open: number; resolved: number };
    projects: { activeCount: number; activeValue: number; byPhase: ReportingPhaseCount[] };
  };
  compliance: {
    certs: { expiringSoon: number; expired: number };
    competence: { overdue: number; dueSoon: number };
    lolerExams: { overdue: number; dueSoon: number };
    subcontractorDocs: { expiringSoon: number; expired: number };
    labourOnlySpend: number;
  };
}

// ---- Split panels (Session V): each panel has its own endpoint + "last updated" stamp ----
export interface ReportingLolerBreakdown {
  total: number;
  overdue: number;
  expiring: number;
  valid: number;
  overduePct: number;
  expiringPct: number;
  validPct: number;
}
// THE one PPM on-time figure (Session BDH; Director rulings 16 October 2026; server/src/modules/reporting/ppmOnTime.ts).
// The clock runs from the last completed visit; on time is the month the visit was due or the allowed weeks either
// side; three outcomes (on time, late, open). onTimePercent is a whole number over onTime + late + openOverdue and is
// null when there is not enough history yet: the page then says so, never 0 or 100 percent.
export interface ReportingPpmCompliance {
  onTime: number;
  late: number;
  open: number; // not done yet (includes those still inside their window)
  openOverdue: number; // not done and the window has passed
  judged: number;
  onTimePercent: number | null;
  dueFrom: string;
  dueTo: string;
  liftsAssessed: number;
  liftsNotAssessed: { no_recorded_service: number; no_cadence: number };
}
export interface ReportingPpmComplianceResult extends ReportingPpmCompliance {
  generatedAt: string;
  from: string;
  to: string;
}
export interface ReportingSiteOption {
  id: string;
  name: string;
}
export interface ReportingRenewalItem {
  id: string;
  ref: string | null;
  customerName: string | null;
  siteName: string | null;
  dateIso: string;
  daysUntil: number;
}
export interface PortfolioHealthPanel {
  generatedAt: string;
  totalLifts: number;
  green: number;
  amber: number;
  red: number;
  ppmOverdue: number;
  lolerOverdue: number;
  loler: ReportingLolerBreakdown;
  ppmCompliance: ReportingPpmCompliance;
  renewals: { count: number; list: ReportingRenewalItem[] };
}
export interface CommercialPanel {
  generatedAt: string;
  arr: number;
  mrr: number;
  quoteConversion: ReportingQuoteConversion & { windowDays: number };
  pipeline: ReportingPipeline;
  topCustomers: ReportingCustomerValue[];
}
export interface OperationsPanel {
  generatedAt: string;
  window: ReportingWindow;
  jobsByType: ReportingJobTypeCount[];
  callouts: ReportingCalloutStats;
  lolerDefects: { open: number; resolved: number };
  projects: { activeCount: number; activeValue: number; byPhase: ReportingPhaseCount[] };
}
// Job-cost profitability panel (Session AK Item 2). All money in pence; the engineer cost rate
// itself is never returned, only the derived figures.
export interface ProfitabilityExtreme {
  ref: string;
  siteName: string;
  marginPence: number;
  marginPercent: number | null;
}
export interface ProfitabilityPanel {
  generatedAt: string;
  window: ReportingWindow;
  jobCount: number;
  jobsWithMargin: number;
  // Session BDL (F3): the next four are built from engineers' cost rates, which are admin only, so they are ABSENT
  // (not null) for anyone who is not an admin.
  avgMarginPercent?: number | null;
  totalLabourCostPence?: number;
  totalPartsCostPence: number;
  totalOtherCostsPence: number; // approved job-linked expenses summed across these jobs, Session ATE
  highest?: ProfitabilityExtreme | null;
  lowest?: ProfitabilityExtreme | null;
  // Session ATE: APPROVED expenses with no job link, for this same window -- reported separately here
  // rather than folded into any one job's margin (a general claim has no job to attach to).
  generalExpensesTotalPence: number;
}
// Per-lift cost and breakdown analytics (Session ANO): profitability.ts's per-job cost/charge/margin
// summed BY LIFT, a call-out breakdown count, and the repeat-visit signal (slaStats.ts's own
// first-time-fix recurrence definition, never a second one). All money in pence, display-only.
export interface LiftPerformanceRow {
  assetId: string;
  ele: string;
  siteName: string;
  jobCount: number;
  jobsWithMargin: number;
  // Session BDL (F3): cost, margin and margin percent are absent (not null) for anyone who is not an admin.
  totalCostPence?: number;
  totalChargePence: number;
  marginPence?: number | null;
  marginPercent?: number | null;
  breakdownCount: number;
  repeatVisitCount: number;
}
export interface LiftPerformancePanel {
  generatedAt: string;
  window: ReportingWindow;
  recurrenceDays: number;
  rows: LiftPerformanceRow[];
}
// Tap reconciliation (Session AVS): where the POP tag record and the job record disagree. Office information only.
export interface TapReconciliationFinding {
  kind: "visit_without_tap" | "tap_without_visit";
  day: string;
  at: string;
  assetId: string;
  ele: string;
  siteName: string;
  jobId: string | null;
  reference: string | null;
  jobType: string | null;
  tapId: string | null;
}
export interface TapReconciliationPanel {
  window: { from: string; to: string; clamped: boolean };
  counts: {
    visitsConsidered: number;
    visitsWithTap: number;
    visitsWithoutTap: number;
    visitsNotApplicable: number;
    notApplicableByReason: { no_tag: number; tag_not_proven: number; before_tag_proven: number };
    visitsExcludedHistorical: number;
    tapsConsidered: number;
    tapsMatchedToVisit: number;
    tapsWithoutVisit: number;
  };
  total: number;
  page: number;
  pageSize: number;
  pages: number;
  findings: TapReconciliationFinding[];
  note: string;
}
export interface TapReconciliationQuery {
  from?: string;
  to?: string;
  kind?: TapReconciliationFinding["kind"];
  search?: string;
  sortBy?: "day" | "lift" | "site" | "kind";
  dir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}
// Engineer utilisation panel (Session AK Item 5).
export interface UtilisationRow {
  engineerId: string;
  engineerName: string;
  jobsCompleted: number;
  jobsWithTimes: number;
  totalHours: number;
  billableHours: number; // on-site hours for a chargeable job type; sums with nonBillableHours to totalHours
  nonBillableHours: number;
  avgHoursPerJob: number | null;
  ppm: number;
  callout: number;
  other: number;
  npsResponses: number;
  avgNps: number | null;
}
export interface UtilisationPanel {
  generatedAt: string;
  window: ReportingWindow;
  rows: UtilisationRow[];
}
// Weekly engineer timesheet (Session ABU Part C).
export interface TimesheetRow {
  engineerId: string;
  engineerName: string;
  jobsAttended: number;
  jobsWithTime: number;
  totalMinutes: number;
}
export interface TimesheetWeekResult {
  generatedAt: string;
  week: { weekStart: string; label: string };
  rows: TimesheetRow[];
  totalJobs: number;
  totalMinutes: number;
}

// ---- The editable weekly timesheet (Session AEL, D41 and D31) ----
// Distinct from TimesheetWeekResult above, which is the READ-ONLY derived roll-up (Session ABU)
// answering "what do the job records say". The types below are the timesheet the office signs
// off. The two can legitimately differ once the office edits, because a timesheet also carries
// travel, office time, training and office corrections. Label them differently on screen.
export type TimesheetStatus = "DRAFT" | "SUBMITTED" | "APPROVED";
export type TimesheetEntryType = "TRAVEL" | "ON_SITE" | "OFFICE" | "TRAINING" | "OTHER";

export interface TimesheetEntry {
  id: string;
  jobId: string | null;
  date: string; // YYYY-MM-DD
  entryType: TimesheetEntryType;
  typeLabel: string;
  startTime: string | null;
  endTime: string | null;
  durationMins: number | null;
  // The RESOLVED length in whole minutes (a start/end pair wins over durationMins), or null when
  // neither yields a positive span. Always render this rather than recomputing, so a row can
  // never disagree with the totals.
  minutes: number | null;
  notes: string | null;
  // True for time derived from an approved job, false once the office has typed or corrected it.
  autoGenerated: boolean;
  // Display context inherited from the linked job (Session AFF), null for a manual entry with no
  // job: the site the time was spent on, the lift's ELE number and the job's type ("service" |
  // "callout" | "loler" | "repair"). Optional because a sheet read back from the offline cache from
  // before this field existed will not carry it; the server always sends it (null when absent).
  siteName?: string | null;
  eleNumber?: string | null;
  jobType?: string | null;
}

export interface TimesheetDay {
  date: string; // YYYY-MM-DD
  entries: TimesheetEntry[];
}

// A job the engineer completed and submitted but the office has not yet approved (Session AFF).
// DISPLAY-ONLY on the engineer's timesheet: it is not a TimesheetEntry, never counts toward any
// total, and joins the timesheet as an entry only once the office approves it.
export interface TimesheetPendingJob {
  jobId: string;
  date: string; // YYYY-MM-DD, the completion date when set, else the scheduled date
  siteName: string | null;
  eleNumber: string | null;
  jobType: string;
  arrivalTime: string | null;
  departureTime: string | null;
  durationMins: number | null;
  awaitingApproval: true;
}

export interface TimesheetTotals {
  travelMins: number;
  onSiteMins: number;
  otherMins: number;
  totalMins: number;
}

// Where the week's minutes actually went (Session ANF). An ORDERED array rather than a keyed
// object: the server decides the order and the wording, zero buckets are omitted, and the client
// renders whatever it is handed without needing to know the JobType enum. A seventh job type added
// tomorrow therefore appears here with no client change, and cannot be silently mislabelled by
// jobTypeStyle's unknown-key fallback (which quietly returns "Service").
//
// `kind` distinguishes the two populations that share the array: "job" is ON_SITE time attributed
// to a linked job, keyed by that job's type; "entry" is everything else, keyed by its own entry
// type. The buckets sum exactly to totals.totalMins.
export interface TimesheetBreakdownBucket {
  key: string;
  kind: "job" | "entry";
  label: string;
  minutes: number;
}

// A visit the office approved whose hours reached no timesheet at all (Session ANF). NOT a
// TimesheetEntry and never counted in any total: the job wrote no entry, and will not until
// somebody records the times by hand. Distinct from TimesheetPendingJob above, which is queued to
// arrive on its own once the office approves it.
export interface TimesheetUnrecordedJob {
  jobId: string;
  date: string; // YYYY-MM-DD
  siteName: string | null;
  eleNumber: string | null;
  jobType: string;
  noTimeRecorded: true;
}

export interface TimesheetSheet {
  engineerId: string;
  engineerName: string;
  weekStarting: string; // the Monday, YYYY-MM-DD
  weekLabel: string;
  status: TimesheetStatus;
  notes: string | null;
  approvedAt: string | null;
  approvedByName: string | null;
  // False once approved. The server enforces this too, so never rely on it alone.
  canEdit: boolean;
  days: TimesheetDay[]; // always seven, including days with no entries
  totals: TimesheetTotals;
  // Jobs submitted but not yet approved (Session AFF): shown as "awaiting office approval", never
  // counted in totals. Empty when there is nothing pending for the week. Optional because a sheet
  // read back from the offline cache from before this field existed will not carry it; the server
  // always sends it (an empty array when nothing is pending).
  pendingJobs?: TimesheetPendingJob[];
  // Session ANF. Both are optional for the same reason pendingJobs above is: MyTimesheet reads
  // through the offline cache, and a sheet saved before these fields existed will not carry them.
  // The server always sends both (as empty arrays when there is nothing to report).
  //
  // canLogTravel stood here until Session ANT and is GONE with the standalone weekly travel log it
  // gated. Travel is captured on the JOB now, on the job sheet, and reaches the timesheet at office
  // approval like every other recorded figure. A cached sheet from before ANT may still carry the
  // field; nothing reads it.
  byType?: TimesheetBreakdownBucket[];
  unrecordedJobs?: TimesheetUnrecordedJob[];
  // Standby, in three parts since Session ANY (it was one boolean at Session ANH). Optional for the
  // same offline-cache reason as the fields above; the server always sends all three.
  //   standbyWeek    EFFECTIVE: the rota puts this engineer on standby this week, OR the office
  //                  ticked the manual box. This is what the rate card prices.
  //   standbyManual  the manual box on its own (Timesheet.standbyWeek, PATCH /:id/standby). What the
  //                  checkbox binds to; a rota week leaves it false.
  //   standbyRota    what the ROTA says for this week whoever is being viewed, so the page can name
  //                  the engineer who IS on standby rather than showing a bare unticked box.
  standbyWeek?: boolean;
  standbyManual?: boolean;
  standbyRota?: { onStandby: boolean; engineerName: string | null; source: StandbySource | null; reason: string | null };
  // Null when no timesheet row exists for that week yet. Call ensure() before adding an entry.
  timesheetId: string | null;
}

// ---- The standby rota (Session ANY) ----
// Who is on call which week: a recurring pattern the office sets in advance, with a per-week
// override for the exception. Nothing is materialised -- every week below is RESOLVED on read from
// the pattern and the overrides, so an edit to the rota changes every future week at once.
export type StandbySource = "override" | "rota";

export interface StandbyWeek {
  weekStarting: string;
  engineerId: string | null;
  engineerName: string | null;
  /** null = neither an override nor a started rota covers this week: nobody is on standby, and nobody decided that. */
  source: StandbySource | null;
  /** The override's reason, when one answered. */
  reason: string | null;
  /**
   * Session ANZ: whether the engineer on call that week has actually been TOLD, and by what.
   * null means not yet -- which is normal for a future week (the daily sweep tells them at the start
   * of their own week) and worth attention on the current one. pushDevices is how many registered
   * browsers the notification reached; smsOutcome is recordSms's verdict, or null when no valid UK
   * mobile is on file.
   */
  notified: { at: string; pushDevices: number; smsOutcome: string | null } | null;
}

export interface StandbyRota {
  id: string;
  /** The Monday the rotation starts from. Weeks before it resolve to nothing rather than a projection backwards. */
  startWeek: string;
  /** Consecutive weeks each engineer holds: 1 weekly, 2 fortnightly, 4 four-weekly. */
  weeksPerTurn: number;
  notes: string | null;
  /** members.length * weeksPerTurn: how long before it comes back round to the first engineer. */
  cycleWeeks: number;
  members: { engineerId: string; engineerName: string; position: number }[];
}

export interface StandbyOverview {
  /** The Monday of the current week, so the page can mark "this week" without re-deriving it. */
  thisWeek: string;
  fromWeek: string;
  rota: StandbyRota | null;
  weeks: StandbyWeek[];
  /** Active engineers only: a retired engineer is never put on next month's rota. */
  engineers: { id: string; name: string }[];
}

// ---- Sending the standby rota to the engineers (Session ATU) ----
// The office's advance notice: one branded PDF of the coming weeks, one email per engineer naming
// their own weeks. Never sent on a save: the office previews, then presses Send.
export type StandbyNoticeWeeks = 4 | 8 | 12 | 26;
export const STANDBY_NOTICE_WEEK_OPTIONS: StandbyNoticeWeeks[] = [4, 8, 12, 26];

export interface StandbyNoticePreview {
  thisWeek: string;
  fromWeek: string;
  lastWeek: string;
  weeksCovered: number;
  issueDateKey: string;
  filename: string;
  rows: { weekStarting: string; onStandby: string }[];
  /** Every engineer in the rotation, then anyone set on a week by hand in the period. email null = none on file. */
  recipients: { engineerId: string; name: string; email: string | null; inRotation: boolean; ownWeeks: string[] }[];
  /** Why Send is disabled, in words; null when it can be sent. */
  cannotSendReason: string | null;
}

export type StandbyNoticeOutcome = "sent" | "skipped_no_email" | "failed";
export interface StandbyNoticeResult {
  fromWeek: string;
  lastWeek: string;
  weeksCovered: number;
  filename: string;
  results: { engineerId: string; name: string; outcome: StandbyNoticeOutcome; reason: string | null }[];
  sent: number;
  skipped: number;
  failed: number;
}

// ---- The rate card check figure (Session ANH) ----
// What the subcontractor's rate card says the hours on one engineer's timesheet come to, whatever the
// week's status, so the office can hold the subcontractor's own invoice against it once the week is
// approved. ADMIN ONLY on the server. A check figure, never an amount the platform pays. Pence
// throughout.
export type TimesheetPayLineKind =
  | "callout_first_hour"
  | "callout_additional"
  | "repair"
  | "overtime"
  | "tech_visit"
  | "servicing"
  | "site_survey"
  | "travel"
  | "travel_unpaid"
  | "day_rate_cap"
  | "day_rate"
  | "standby"
  // Session RC1: the technician block, its agreed and unagreed time past the block, and the modernisation day.
  | "tech_visit_block"
  | "tech_visit_overtime"
  | "tech_visit_unpaid"
  | "modernisation_day";

export interface TimesheetPayLine {
  kind: TimesheetPayLineKind;
  label: string;
  date: string | null;
  jobLabel: string | null;
  minutes: number | null;
  ratePence: number | null;
  amountPence: number;
  note: string | null;
}

export interface TimesheetPayException {
  code:
    | "no_rate_card"
    | "two_man_not_booked"
    | "two_man_not_booked_other"
    | "two_man_more_than_two"
    | "on_site_no_job"
    | "job_type_not_on_card"
    | "project_work"
    | "entry_type_not_on_card"
    | "ordering_uncertain"
    | "flat_fee_repeat_visit"
    | "travel_without_priced_work"
    // Session ANI: an entry whose two logged times round onto the same 15-minute mark, so the rate
    // card prices no time for it. A SECOND HOME for the server's PayExceptionCode, which is why
    // timesheetPay.test.ts asserts the two lists are identical: nothing else binds them, so a code
    // added on one side and not the other compiles clean and drops out of the office panel's own
    // narrowing without a word.
    | "rounded_to_zero"
    // Session AUU: the card is data with an effective date, so a date before the earliest version, or a line the card
    // leaves not agreed, is named rather than guessed. name_ambiguous went with the name-keyed code card.
    | "no_card_in_force"
    | "rate_not_on_card"
    // Session AUZ: the Labour Only notices (the deduction not set, or the signed agreement predates clause 12.5). Neither
    // needs review: the figure is the Bona Fide figure, and the panel says so.
    | "labour_only_rate_not_set"
    | "labour_only_agreement_version"
    // Session RC1: not a technician on a tech visit, a weekday entry with no times when an evening uplift applies, other
    // work on a modernisation day, and a working day with no times (no semicolon here: the binding test reads to the first).
    | "not_a_technician"
    | "no_times_for_band"
    | "modernisation_day_other_work"
    | "work_day_other_work"
    | "work_day_no_time";
  needsReview: boolean;
  date: string | null;
  jobId: string | null;
  jobLabel: string | null;
  minutes: number | null;
  message: string;
}

export interface TimesheetPayDay {
  date: string;
  lines: TimesheetPayLine[];
  multiJob: boolean;
  countedMinutes: number;
  windowOnSitePence: number;
  cappedAtPence: number | null;
  overtimeMinutes: number;
  overtimePence: number;
  orderingUncertain: boolean;
  totalPence: number;
}

export interface TimesheetPay {
  engineerId: string;
  engineerName: string;
  weekStarting: string;
  rateCardEngineer: string | null;
  /** Session AUU: which rate card version(s) priced the week, in words, or null when there is no card for the engineer. */
  rateCardSource: string | null;
  days: TimesheetPayDay[];
  standby: { applies: boolean; reason: "callout_attended" | "office_flagged" | "both" | null; amountPence: number };
  exceptions: TimesheetPayException[];
  unpricedMinutes: number;
  lines: TimesheetPayLine[];
  totalPence: number;
  /** Session AUZ: whether and why the Labour Only deduction reached the week; null when the server carried no status history. */
  labourOnly?: TimesheetPayLabourOnly | null;
}

export type TimesheetPayLabourOnlyReason = "applied" | "bona_fide" | "agreement_earlier_version" | "deduction_not_set" | "no_status_recorded" | "no_priced_days";
export interface TimesheetPayLabourOnly {
  applied: boolean;
  reason: TimesheetPayLabourOnlyReason;
  statusLabel: string;
  deductionLabel: string | null;
  clauseSigned: boolean;
  message: string;
  days: { date: string; statusState: SubcontractorStatusValue; deductionBps: number | null; applied: boolean }[];
}

export interface TimesheetPayGroupMember {
  engineerId: string;
  engineerName: string;
  status: TimesheetStatus;
  totalPence: number;
  reviewCount: number;
  unpriced: boolean;
}

// Session RC1: a PAYE engineer's week by band, hours only (server/src/modules/timesheets/payeHours.ts). Payroll runs outside.
export interface PayeHours {
  contractedMins: number;
  normalOvertimeMins: number;
  timeAndHalfMins: number;
  doubleTimeMins: number;
  totalMins: number;
  standbyWeek: boolean;
  outOfHoursCalls: number;
  callMinimumAddedMins: number;
  untimedEntries: number;
  contractedWeeklyMins?: number;
  contractedFromRecord?: boolean;
}

export interface TimesheetPayReport {
  weekStarting: string;
  weekLabel: string;
  /** Session RC1: present only for a PAYE engineer; the panel then shows hours by band, never a money figure. */
  payeHours?: PayeHours | null;
  pay: TimesheetPay;
  timesheet: {
    timesheetId: string | null;
    status: TimesheetStatus;
    // EFFECTIVE standby (Session ANY): the rota OR the manual box. What the retainer was priced on.
    standbyWeek: boolean;
    // The two halves behind it, so the panel can say which produced the retainer line.
    standbyManual: boolean;
    standbyFromRota: boolean;
    xeroJournalId: string | null;
    xeroJournalAmountPence: number | null;
    xeroJournalDraftedAt: string | null;
    // What the stamp means now, derived by the server: "none", a live "pending" draft attempt, a
    // "stale_pending" attempt that crashed and can be cleared, or a "drafted" journal.
    xeroJournalState: "none" | "pending" | "stale_pending" | "drafted";
  };
  // The rollup across the engineers who trade as the same subcontractor: keyed on the register link
  // (Session AUU: the link alone; an unlinked engineer stands alone), and `basis` says which.
  group: {
    basis: "subcontractor_link" | "engineer_alone";
    subcontractorName: string | null;
    weekStarting: string;
    members: TimesheetPayGroupMember[];
    combinedTotalPence: number;
  };
}

// ---- A subcontractor's rate card (Session AUU): versions with an effective date, per-engineer lines, the schedule ----
export type RateCardLineKey =
  | "standbyPerWeekPence"
  | "calloutFirstHourPence"
  | "repairHourPence"
  | "travelHourPence"
  | "techVisitHourPence"
  | "servicingPerVisitPence"
  | "siteSurveyPerVisitPence"
  | "dayRatePence"
  | "modernisationPerWeekPence"
  // Session RC1: a flat sum per office-booked technician visit.
  | "techVisitBlockPence";
// techVisitBlockPence (Session RC1) is optional, as on the server: absent means null, not agreed.
export type RateCardFigures = { [K in Exclude<RateCardLineKey, "techVisitBlockPence">]: number | null } & { techVisitBlockPence?: number | null };
export interface RateCardEngineerRate extends RateCardFigures {
  engineerId: string;
  engineerName: string | null;
}
export interface RateCardVersion extends RateCardFigures {
  id: string;
  version: number;
  /** YYYY-MM-DD */
  effectiveFrom: string;
  effectiveFromLabel: string;
  dailyCapThresholdMins: number;
  saturdayMultiplierPct: number;
  sundayMultiplierPct: number;
  labourOnlyDeductionBps: number | null;
  /** Session BAL: the most one paid travel leg is paid for, in minutes; null = no cap. */
  travelCapPerLegMins?: number | null;
  /** Session RC1: the uplift bands and the technician block's hours (the server fills the defaults). */
  weekdayEveningMultiplierPct?: number;
  weekdayEveningStartMins?: number;
  bankHolidayMultiplierPct?: number;
  techVisitBlockMins?: number;
  /** Session RC1: the standard card version this one is a copy of ("Standard vN"), null for the office's own figures. */
  standardVersion?: number | null;
  sourceNote: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  engineerRates: RateCardEngineerRate[];
}
export type SubcontractorKind = "LABOUR" | "SUPPLY_AND_FIT";
export type RateCardSource = "STANDARD" | "CUSTOM";
export interface RateCardChange {
  what: string;
  engineerName: string | null;
  before: string;
  after: string;
}
export interface RateCardApprovedWeek {
  engineerId: string;
  engineerName: string;
  weekStarting: string;
}
// AUU fix-up 2: what PATCH /api/engineers/:id answers to { subcontractorId, preview: true }.
/** Session BAV: the engineers linked to one subcontractor, for the Engineers section of its page. */
export interface SubcontractorEngineersView {
  suppliesEngineers: boolean;
  engineers: { id: string; userId: string | null; name: string; active: boolean; username: string | null }[];
}

export interface EngineerSubcontractorLinkPreview {
  preview: true;
  subcontractorId: string | null;
  currentSubcontractorId: string | null;
  /** A first link from no link, a move between two firms, an unlink, or no change at all. */
  change: "link" | "move" | "unlink" | "unchanged";
  approvedWeeksMoved: RateCardApprovedWeek[];
  message: string;
}
export interface RateCardScheduleLine {
  key: RateCardLineKey;
  label: string;
  unit: string;
  note: string;
  card: string;
  cardPence: number | null;
  perEngineer: { engineerName: string; rate: string; pence: number | null; ownRate: boolean }[];
}
export interface RateCardSchedule {
  subcontractorName: string;
  version: number;
  effectiveFrom: string;
  effectiveFromLabel: string;
  lines: RateCardScheduleLine[];
  rules: { dailyCapThreshold: string; saturday: string; sunday: string; rounding: string; twoMan: string; travel: string };
  ratesDifferByPerson: boolean;
}
export interface SubcontractorRateCardView {
  subcontractorName: string;
  /** Session RC1: the kind of subcontractor and where the rates come from (null = not set). */
  kind?: SubcontractorKind | null;
  rateCardSource?: RateCardSource | null;
  engineers: { id: string; name: string; active: boolean }[];
  versions: RateCardVersion[];
  currentVersionId: string | null;
  schedule: RateCardSchedule | null;
}
export interface RateCardWriteBody extends Partial<RateCardFigures> {
  effectiveFrom: string;
  dailyCapThresholdMins?: number;
  saturdayMultiplierPct?: number;
  sundayMultiplierPct?: number;
  labourOnlyDeductionBps?: number | null;
  travelCapPerLegMins?: number | null;
  weekdayEveningMultiplierPct?: number;
  weekdayEveningStartMins?: number;
  bankHolidayMultiplierPct?: number;
  techVisitBlockMins?: number;
  sourceNote?: string | null;
  engineerRates?: ({ engineerId: string } & Partial<RateCardFigures>)[];
  preview?: boolean;
  affectsApprovedWeeks?: boolean;
}
export interface RateCardPreview {
  preview: true;
  version: number;
  supersedes?: { version: number; effectiveFrom: string } | null;
  changes: RateCardChange[];
  approvedWeeksMoved: RateCardApprovedWeek[];
  /** Session RC1: saving the office's own figures takes a Standard-card subcontractor onto custom rates. */
  movesToCustom?: boolean;
  movesToCustomMessage?: string;
}

// ---- The company standard rate card (Session RC1, admin only) ----
export interface StandardCardSettings {
  dailyCapThresholdMins: number;
  saturdayMultiplierPct: number;
  sundayMultiplierPct: number;
  weekdayEveningMultiplierPct: number;
  weekdayEveningStartMins: number;
  bankHolidayMultiplierPct: number;
  techVisitBlockMins: number;
  labourOnlyDeductionBps: number | null;
  travelCapPerLegMins: number | null;
}
export interface StandardCardVersion extends RateCardFigures, StandardCardSettings {
  id: string;
  version: number;
  /** YYYY-MM-DD */
  effectiveFrom: string;
  effectiveFromLabel: string;
  label: string;
  copies: number;
  sourceNote: string | null;
  createdByName: string | null;
  createdAt: string;
}
export interface StandardCardView {
  versions: StandardCardVersion[];
  currentVersionId: string | null;
  today: string;
  onStandardCard: { id: string; company: string }[];
}
/** The standard card body (also the custom rates a new Labour subcontractor is registered with, minus effectiveFrom). */
export type StandardCardFiguresBody = Partial<RateCardFigures> & Partial<StandardCardSettings> & { sourceNote?: string | null };
export interface StandardCardWriteBody extends StandardCardFiguresBody {
  effectiveFrom: string;
  preview?: boolean;
  affectsApprovedWeeks?: boolean;
}
export interface StandardCardPreview {
  preview: true;
  version: number;
  label: string;
  effectiveFrom: string;
  effectiveFromLabel: string;
  changes: RateCardChange[];
  moves: { subcontractorId: string; company: string; approvedWeeksMoved: RateCardApprovedWeek[] }[];
  blocked: { subcontractorId: string; company: string; reason: string }[];
  summary: string;
}
export interface StandardCardWriteResult {
  version: StandardCardVersion;
  changes: RateCardChange[];
  moved?: string[];
}
export interface RateCardWriteResult {
  version: RateCardVersion;
  changes: RateCardChange[];
  approvedWeeksMoved: RateCardApprovedWeek[];
}

export interface TimesheetEngineerWeek {
  weekStarting: string;
  timesheetId: string | null;
  status: TimesheetStatus;
  totalMins: number;
}

export interface TimesheetEngineerRow {
  engineerId: string;
  engineerName: string;
  current: TimesheetEngineerWeek;
  previous: TimesheetEngineerWeek;
}

export interface TimesheetEngineersResult {
  engineers: TimesheetEngineerRow[];
  currentWeekStarting: string;
  previousWeekStarting: string;
}

export interface TimesheetEntryInput {
  date: string;
  entryType: TimesheetEntryType;
  startTime?: string;
  endTime?: string;
  durationMins?: number;
  jobId?: string | null;
  notes?: string;
  // BDG2-R review finding 6 (Session BEM): only after the office confirms this ON_SITE time is a second attendance on a job the
  // week already holds on-site time on; without it the server refuses 409 duplicate_job_entry.
  confirmSecondAttendance?: boolean;
}

// The edit shape is NOT Partial<TimesheetEntryInput>: the timing fields accept an explicit
// null, which is how an entry is switched between a start/end pair and a plain duration.
// Omitting a key leaves the stored value alone; sending null clears it. Sending a start/end
// pair clears any duration server-side, so there is no need to null it as well. jobId follows
// the same convention (Session ANX): omitted leaves the link alone, an explicit null detaches
// an entry from a job it was previously linked to.
export interface TimesheetEntryPatch {
  date?: string;
  entryType?: TimesheetEntryType;
  startTime?: string | null;
  endTime?: string | null;
  durationMins?: number | null;
  jobId?: string | null;
  notes?: string;
}
export interface ReportingCertExpiryItem {
  certType: string;
  engineerName: string;
  expiryDate: string;
  status: CertStatus;
  daysUntilExpiry: number | null;
}
export interface ReportingLolerOverdueItem {
  id: string;
  ele: string | null;
  siteName: string | null;
  dateIso: string;
  daysOverdue: number;
}
export interface ComplianceSummaryPanel {
  generatedAt: string;
  certs: { expiringSoon: number; expired: number; list: ReportingCertExpiryItem[] };
  competence: { overdue: number; dueSoon: number; list: CompetenceReviewDueItem[] };
  lolerExams: { overdue: number; dueSoon: number; overdueList: ReportingLolerOverdueItem[] };
  subcontractorDocs: { expiringSoon: number; expired: number };
  labourOnlySpend: number;
}

// Office "Sign in with Google": a full-page navigation (the server redirects to
// Google's consent screen), not a fetch. The button sets window.location to this.
export const GOOGLE_SIGN_IN_URL = "/api/auth/google";
// The same sign-in, returning to the engineer app afterwards (Session ASB review, finding F2): the engineer sign-in page
// offers it, for the admin or office user who drives the PWA (the password form there cannot sign them in). The server
// honours only /engineer (and plain paths under it) and /oauth/authorize as a return path; anything else lands on /office.
// "/engineer/", with the slash, is the installed engineer app's own start_url and scope (public/engineer-manifest.json),
// so the sign-in lands back INSIDE the installed app rather than in a browser tab beside it.
export const GOOGLE_SIGN_IN_TO_ENGINEER_URL = "/api/auth/google?return_to=%2Fengineer%2F";

/** Session RCS: a private link to the standard rate card for one named person. The link itself is never listed. */
export interface RateCardShareLink {
  id: string;
  recipientName: string;
  state: "live" | "expired" | "switched_off";
  createdAt: string;
  createdByName: string | null;
  expiresAt: string;
  revokedAt: string | null;
  revokedByName: string | null;
  openCount: number;
  firstOpenedAt: string | null;
  lastOpenedAt: string | null;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    public details?: Record<string, string[]>,
    // The parsed error body, when the server sent JSON (Session AGG). Additive and optional: it
    // exists because some refusals carry a FACT the caller needs, not just wording -- a training
    // session that is already completed answers 409 with the certificateRef that was issued, and a
    // retry after a dropped response has to be able to show that reference rather than scraping it
    // out of a sentence. Prefer `code` / `details` for anything they already cover.
    public body?: Record<string, unknown>,
  ) {
    super(code);
    this.name = "ApiError";
  }
}

export interface ApiRequestInit extends RequestInit {
  // Opt out of the request cache below (Session AQT, D47) for an endpoint whose caller must
  // always see a live value on every call, never a stale one, e.g. LoneWorkerPage's 60-second
  // safety poll ("this is the one office screen somebody might sit and watch while ringing an
  // engineer who is not answering" -- its own comment). A cache with no way to say "not this one"
  // is exactly the kind of derive-live violation docs/reference/platform-rules.md's clock-gating
  // rule and CLAUDE.md's RULE 6 both warn about.
  noCache?: boolean;
  // Session ASB review (finding F4, IDLE THAT IS REALLY IDLE): true for a request made on a TIMER (a poll), never for
  // anything a person did. It is sent with the X-ELE-Background header, and the server then serves it without moving
  // the session's idle clock, so a page left open on a polling screen does not keep an office session alive for ever.
  // It is also not counted as activity for the office shell's idle warning (sessionEvents.ts).
  background?: boolean;
}

// The API paths a staff session answers for: /public/, /portal/ and (Session ATA) /subcontractor-portal/ routes have
// their own credentials, or none.
function isStaffSessionPath(path: string): boolean {
  return !path.startsWith("/public/") && !path.startsWith("/portal/") && !path.startsWith("/subcontractor-portal/");
}

// ---- A session that has ended (Session ASB, Item 3) ----
// When the server ends a session, the next request answers 401 with the guard's own body; rawRequest (and the raw file
// downloads below) report it through sessionEvents.ts, and AuthProvider signs this browser out with the reason. The
// signal lives in its own small module so the auth context depends on it rather than on this whole file; re-exported
// here so callers of the API find it beside the requests that raise it.
export { onSessionEnded, reportIfSessionEnded } from "./sessionEvents";
export type { SessionEndReason, SessionEndedEvent } from "./sessionEvents";

// THE PHONE VIEW IS READ ONLY (Session BDZ). A page inside the Phone view's frame is an admin reviewing layout, not an
// engineer, so any request that is not a read is refused here BEFORE it leaves the browser, on every network path this
// file owns (rawRequest and the download helpers below; no other file calls fetch). The one exception is a local
// development server. See phonePreview.ts.
function refuseWriteInPhoneView(method: string | undefined, path: string): void {
  if (!phoneViewBlocksWrite(method, path)) return;
  announcePhoneViewWriteBlocked();
  // The body carries the message so a screen that shows the server's own words (the accept button on a job) shows these.
  throw new ApiError(403, PHONE_VIEW_BLOCKED_MESSAGE, undefined, { ...PHONE_VIEW_BLOCKED_BODY });
}

async function rawRequest<T>(path: string, init: ApiRequestInit): Promise<T> {
  refuseWriteInPhoneView(init.method, path);
  const startedAt = Date.now();
  // Only declare a JSON content type when we actually send a JSON body. A no-body
  // mutation (e.g. PATCH /callouts/:id/cancel) with Content-Type: application/json
  // and an empty body is rejected by strict JSON parsers with a 400 before the
  // handler. A FormData body (photo upload) must NOT carry a JSON content type
  // either -- the browser sets multipart/form-data with the boundary itself.
  const isFormData = typeof FormData !== "undefined" && init.body instanceof FormData;
  const hasJsonBody = init.body != null && !isFormData;
  const { background, ...fetchInit } = init;
  const res = await fetch(`/api${path}`, {
    credentials: "include",
    ...fetchInit,
    headers: {
      ...(hasJsonBody ? { "Content-Type": "application/json" } : {}),
      ...((fetchInit.headers as Record<string, string> | undefined) ?? {}),
      ...(background ? { [BACKGROUND_REQUEST_HEADER]: "1" } : {}),
    },
  });
  // Something the person did reached the server and was not refused as signed out: their session's idle clock moved
  // (the office shell's idle warning reads this). A timer's request, and a 401, never count.
  if (!background && res.status !== 401 && isStaffSessionPath(path)) noteSessionActivity(startedAt);

  if (!res.ok) {
    let code = res.statusText;
    let details: Record<string, string[]> | undefined;
    let parsed: Record<string, unknown> | undefined;
    try {
      const body = (await res.json()) as { error?: string; message?: string; details?: Record<string, string[]> };
      if (body?.message) code = body.message;
      else if (body?.error) code = body.error;
      details = body?.details;
      if (body && typeof body === "object") parsed = body as Record<string, unknown>;
    } catch {
      // non-JSON error body; keep the status text
    }
    reportIfSessionEnded(path, res.status, parsed, startedAt);
    throw new ApiError(res.status, code, details, parsed);
  }

  const contentType = res.headers.get("content-type") ?? "";
  return (contentType.includes("application/json") ? await res.json() : undefined) as T;
}

// ---- Request cache (Session AQT, D47: "a small keyed SWR cache in api.ts, no new dependency") ----
//
// Office pages re-fetch and re-show a loading state on every single visit, even one made seconds
// after the last (131 bare "Loading..." states counted by the restructure audit). This wraps the
// one shared fetch primitive every api.* method already calls, so every GET benefits with no
// per-call-site change: a hit under CACHE_FRESH_MS returns instantly with no network call at all;
// a hit older than that but under CACHE_STALE_MS ALSO returns instantly, with a silent background
// refetch updating the cache for the next read (classic stale-while-revalidate); anything older,
// or a cold miss, fetches normally and the caller waits for it, exactly as before this session.
//
// Invalidation is deliberately blunt: ANY successful mutation (a non-GET call) clears the WHOLE
// cache, rather than trying to map which GET keys a given write affects. Mapping that correctly
// across 394 mutation call sites is its own large, error-prone project, and getting it wrong is
// precisely the "nobody keeps it correct" cache RULE 6 exists to prevent; a blunt clear is simple,
// safe, and costs at most one extra fetch per page thanks to the short freshness window above.
const requestCache = new Map<string, { data: unknown; at: number }>();
const CACHE_FRESH_MS = 15_000;
const CACHE_STALE_MS = 5 * 60_000;

function isGetRequest(init: ApiRequestInit): boolean {
  return !init.method || init.method.toUpperCase() === "GET";
}

// Exported for a mutation-heavy test to force a clean cache between assertions, and as the one
// thing this file needs to do after a successful mutation.
export function clearRequestCache(): void {
  requestCache.clear();
}

// Session ASB: the cache is keyed by path, not by user, so it goes whenever this browser signs out (a sign-out the
// person chose, or a session the server ended), not only as a side effect of the logout POST succeeding.
onLocalSignOut(clearRequestCache);

async function fetchAndCache<T>(path: string, init: ApiRequestInit): Promise<T> {
  const data = await rawRequest<T>(path, init);
  if (isGetRequest(init) && !init.noCache) requestCache.set(path, { data, at: Date.now() });
  else if (!isGetRequest(init)) clearRequestCache();
  return data;
}

export async function apiRequest<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  if (isGetRequest(init) && !init.noCache) {
    const hit = requestCache.get(path);
    if (hit) {
      const age = Date.now() - hit.at;
      if (age < CACHE_STALE_MS) {
        if (age >= CACHE_FRESH_MS) {
          // Best-effort: a poor network on this silent background refresh must not surface as an
          // error to a caller who already has the stale value on screen and returned below.
          void fetchAndCache<T>(path, init).catch(() => {});
        }
        return hit.data as T;
      }
    }
  }
  return fetchAndCache<T>(path, init);
}

const request = apiRequest;

// ---- Public (no-auth) customer quote accept page ----
export type PublicTierKey = "core" | "care" | "complete";

export interface PublicTier {
  annual: number; // ALL-IN site total (lift maintenance + flat extras)
  // ACP care equipment model: the tier's annual EXCLUDING care equipment (the mixed-quote
  // tier-card figure). Optional so a stale bundle/API overlap during a deploy degrades to
  // the all-in annual cleanly; equals annual when the quote carries no equipment.
  liftAnnual?: number;
  maintenanceAnnual: number; // lift maintenance only (annual minus the flat extras)
  monthly: number;
  call1: number;
  callAdd: number;
  callOOH: number;
  promoMonthly: number | null;
}

// A customer-facing line item (additional item or care-home equipment) on the
// public quote view. qty is always present (defaults to 1).
export interface PublicQuoteLineItem {
  description: string;
  price: number; // unit price per year, ex VAT
  qty: number;
}

// A customer-facing CARE EQUIPMENT line (ACP): the flat item fields plus the serviced
// cadence and the row's computed annual (tapered frequency price x qty; a legacy flat
// row keeps annual = price x qty with null cadence fields). The cadence/annual fields
// are optional so a stale bundle during a deploy degrades to the flat treatment.
export interface PublicEquipmentItem extends PublicQuoteLineItem {
  visitsYr?: number | null;
  frequencyLabel?: string | null;
  annual?: number;
}

// A named postcode-surcharge line item (Session AAE): a statutory/operational cost
// pass-through, identical on every tier, shown in the price breakdown with its
// per-visit rate and its annual at the quote's billing frequency.
export interface PublicSurchargeLine {
  key: "ccz" | "westminsterParking";
  label: string;
  perVisit: number; // per planned visit, ex VAT
  annual: number; // perVisit x visits/year
}

// A per-lift entry on the public quote view (Session AAG): the customer-facing label and its
// current visit frequency, so the accept page can render a per-lift frequency selector, PLUS the
// documentation-only lift specification (Session AAK) shown in the Lift specification section.
// The spec fields are optional: the server always sends them ("" = omit that field), but they are
// declared optional so a stale bundle/API overlap during a deploy degrades to "no spec" cleanly.
export interface PublicLift {
  label: string;
  frequency: string; // engine key: monthly | bi_monthly | quarterly | six_monthly | annual
  type?: string;
  capacity?: string;
  age?: string;
  stops?: string;
  manufacturer?: string;
  serial?: string;
  controller?: string;
  door?: string;
  driveSystem?: string;
  motorRoom?: string;
  // Per-lift annual maintenance for each tier (ex VAT): what this lift costs for the selected tier,
  // shown on the accept page and updated live on a tier or frequency change. null for a legacy quote
  // whose payload predates per-lift pricing (the page omits the per-lift figure). Optional so a stale
  // bundle during a deploy degrades cleanly. Excludes site-level zone surcharge and any flat extras.
  annualByTier?: { core: number; care: number; complete: number } | null;
}

export interface PublicQuote {
  quoteRef: string;
  dateDisplay: string;
  validUntilDisplay: string;
  customerName: string;
  siteName: string | null;
  siteAddress: string | null;
  frequencyLabel: string;
  numLifts: number;
  // Session AIV: the multi-lift discount actually applied on this quote (0 when none, as a whole
  // percentage), the lift count the band was decided on, and whether that count came from this
  // quote's own lifts ("quote") or the customer's wider grouped portfolio ("portfolio").
  // discountBasis is null exactly when multiLiftDiscountPct is 0. Sourced from the same stored
  // fields the quote PDF already reads, so the accept page and the PDF can never disagree.
  multiLiftDiscountPct: number;
  discountLiftCount: number;
  discountBasis: "portfolio" | "quote" | null;
  // Per-lift label + frequency for the customer's frequency selector (Session AAG). Empty for
  // legacy quotes whose stored payload never carried the lifts.
  lifts: PublicLift[];
  recommendedTier: PublicTierKey;
  tiers: Record<PublicTierKey, PublicTier>;
  // Extras (the customer's own line items), shown as separate sections in the price
  // breakdown. Never multi-lift-discounted. Since Session ACS `additionalItems` carries
  // the FLAT (maintenance-only) rows; tier-covered rows arrive in additionalCovered.
  additionalItems: PublicQuoteLineItem[];
  careHomeItems: PublicEquipmentItem[];
  additionalItemsTotal: number;
  careHomeTotal: number;
  // Session ACS: the tier-covered additional items (per-tier annuals + totals), or null
  // when the quote has none (every legacy quote). Optional so a stale bundle during a
  // deploy degrades to the flat-only treatment cleanly.
  additionalCovered?: {
    // Session ACV: frequencyLabel is the item's serviced cadence on a lift-less quote
    // ("Yearly" | "Six-monthly" | "Quarterly"); null on a mixed quote. Optional so a
    // stale bundle/API overlap during a deploy degrades to no cadence line.
    rows: { description: string; qty: number; annual: { core: number; care: number; complete: number }; frequencyLabel?: string | null }[];
    totals: { core: number; care: number; complete: number };
  } | null;
  // Named postcode-surcharge line items (Session AAE): statutory/operational cost
  // pass-throughs, identical on every tier. Empty for legacy quotes.
  surcharges: PublicSurchargeLine[];
  surchargeAnnualTotal: number;
  promo: { pct: number; months: number; reason: string } | null;
  // AI-written, tier-free customer-facing narrative (no prices, no tier names). One per quote,
  // shown for every tier. Null when none was generated.
  quoteNarrative: string | null;
  // Session AAU Loop 4: plain-English "explain this quote" (AI or static fallback). Always present.
  quoteExplanation: string | null;
  status: "open" | "accepted" | "expired";
  acceptedTier: PublicTierKey | null;
  // Session APD: whether the standard Terms and Conditions belong on this page (false for an intermediary,
  // who signs the Intermediary ESA those clauses contradict). The server's decision, read through
  // standardTermsApplyTo (AcceptQuote.tsx). Optional so a response from an older server degrades to the
  // page as it always was: absent reads as standard, exactly as the server's own function reads it.
  standardTermsApply?: boolean;
}

export interface AcceptResult {
  status: "accepted";
  quoteRef: string;
  tier: PublicTierKey;
  message: string;
}

// ---- Public Schedule of Fees rate card (Session AKD the PDF link, Session AKR the JSON behind
// the in-page modal). Shared by the quote accept page above and the renewal accept page. ----
export interface PublicFeeRate {
  firstHour: number; // ex VAT
  addHour: number; // ex VAT
  travel: number; // per hour, ex VAT
}

export interface PublicScheduleOfFeesRates {
  inContract: PublicFeeRate;
  outOfHours: PublicFeeRate;
  nonContract: PublicFeeRate;
  nonContractOutOfHours: PublicFeeRate;
  fullConditionReport: number; // ex VAT
  // Session AOX (reshaped by Session TV2, see below). The published technician visit DAY RATE card, ex VAT: GBP 700 flat,
  // covering up to `capHours` (5) hours on site.
  // This REPLACES Session AOC's four hourly bases (inContract / outOfHours / nonContract /
  // nonContractOutOfHours at GBP 175 / 350 / 350 / 700); the old keys are deliberately not kept
  // alongside, because an un-redeployed bundle reading them would render "charged per hour" beside a
  // day-rate figure; the card is served under a NEW key for the same reason. See fees.ts techVisitRates.
  // OPTIONAL on the client type, deliberately, even though the server always sends it. The SPA and
  // the API are separate Render services, so a browser holding a new bundle can briefly call a
  // server still serving the old JSON. Typing it as always-present would let a component read
  // through it unguarded and throw on the page a customer signs from. ScheduleOfFeesModal guards on
  // the NEW shape (a numeric dayRate), so AOC's hourly payload renders no technician paragraph at
  // all rather than a wrong one.
  // RETIRED by Session TV2 (the AOX card with its second figure): no longer served and never read.
  technicianVisitDayRate?: unknown;
  // Session TV2 (Director, 29 October 2026): the card is served under yet another NEW key because the shape changed again:
  // ONE day rate (GBP 700) covering up to `capHours` hours on site in or out of hours and contract, time beyond them at
  // `overageHourRate` an hour in `overageStepMinutes`-minute steps, or as a further day at `furtherDayRate`. OPTIONAL on the
  // client type for the reason above, and ScheduleOfFeesModal guards on every field: an older server's payload simply has
  // no `technicianVisitCharge`, and the modal then renders no technician paragraph rather than a wrong one.
  technicianVisitCharge?: PublicTechVisitRates;
}

export interface PublicTechVisitRates {
  dayRate: number; // flat, in or out of hours and contract
  capHours: number; // the hours on site the day rate covers
  overageHourRate: number; // the hourly rate beyond the cap
  overageStepMinutes: number; // the step the overage is rounded up to
  furtherDayRate: number; // the alternative to the hourly overage, one more day rate
}

// ---- Public subcontractor onboarding (no auth; one-time invite token) ----
// The strict allow-list the public form renders from (never any internal id or token).
export interface SubcontractorOnboardingView {
  company: string;
  contactName: string;
  contactEmail: string;
  trade: string;
}
export interface SubcontractorOnboardingResult {
  ok: boolean;
  company: string;
  message: string;
}

// ---- Office subcontractor directory (office/admin) ----
export type SubcontractorStatus = "pending_invite" | "pending_review" | "active" | "inactive";
export type SubcontractorAgreementStatus = "none" | "sent" | "signed" | "expired";
export type SubcontractorType = "bona_fide" | "labour_only";
export type DocExpiryStatus = "none" | "expiring" | "expired";

// Session AUZ: the status history and the document review view.
export interface SubcontractorStatusPreview {
  preview: true;
  subcontractor: string;
  currentStatus: string;
  newStatus: string;
  effectiveFrom: string;
  effectiveFromLabel: string;
  approvedWeeksMoved: RateCardApprovedWeek[];
  message: string;
}
export interface SubcontractorStatusPeriodDto {
  id: string;
  effectiveFrom: string;
  effectiveFromLabel: string;
  status: SubcontractorType | null;
  stateLabel: string;
  source: "sign_up" | "office" | "certificates_approved";
  sourceLabel: string;
  recordedBy: string;
  recordedAt: string;
}
export interface SubcontractorStatusHistory {
  today: string;
  inForceToday: SubcontractorStatusValue;
  inForceTodayLabel: string;
  periods: SubcontractorStatusPeriodDto[];
}
export type SubcontractorReviewItemKind = "awaiting_review" | "not_accepted" | "expired" | "expiring" | "missing_insurance" | "missing_nvq";
export interface SubcontractorReviewItem {
  kind: SubcontractorReviewItemKind;
  kindLabel: string;
  label: string;
  docId: string | null;
  certType: SubCertType | null;
  engineerName: string | null;
  expiryDate: string | null;
  since: string | null;
  rejectionReason: string | null;
}
export interface SubcontractorDocumentReview {
  subcontractors: {
    subcontractorId: string;
    subcontractorName: string;
    status: SubcontractorStatusValue;
    statusLabel: string;
    insuranceEvidenced: boolean;
    items: SubcontractorReviewItem[];
  }[];
  counts: { awaitingReview: number; notAccepted: number; expired: number; missing: number; expiring: number };
  checkedAt: string;
}
export interface SubcontractorCertificateApproval {
  certificate: SubcontractorCertificate;
  statusMoved: boolean;
  status: SubcontractorStatusValue;
  statusLabel: string;
  insuranceEvidenced: boolean;
  missingInsurance: string[];
  approvedWeeksMoved: RateCardApprovedWeek[];
  note: string;
}

export interface SubcontractorListItem {
  // Session RC1: what kind of subcontractor (null = not set), where the rates come from, and the "Technician" tick.
  kind?: SubcontractorKind | null;
  rateCardSource?: RateCardSource | null;
  technicianDefault?: boolean;
  id: string;
  company: string;
  contactName: string;
  trade: string;
  subcontractorType: SubcontractorType | null;
  // Session AUR: the office-owned status. "bona_fide_pending" is Bona Fide recorded by the sign-up form that the office
  // has not yet approved; "not_set" is every subcontractor still to be classified.
  subcontractorStatus?: SubcontractorStatusValue;
  subcontractorStatusLabel?: string;
  status: SubcontractorStatus;
  agreementStatus: SubcontractorAgreementStatus;
  docCount: number;
  // Projects this subcontractor is engaged on (drives the client-side Delete guard).
  linkedProjectCount: number;
  // Soft-delete marker (null = active; set = shown in the Archived tab).
  archivedAt: string | null;
  updatedAt: string | null;
}

export type SubcontractorStatusValue = "bona_fide" | "bona_fide_pending" | "labour_only" | "not_set";

export interface SubcontractorDoc {
  id: string;
  docType: string;
  description: string | null;
  expiryDate: string | null;
  uploadedAt: string | null;
  expiryStatus: DocExpiryStatus;
  daysUntilExpiry: number | null;
  verified?: boolean;
}

export interface SubcontractorDetail extends SubcontractorListItem {
  email: string;
  contactPhone: string | null;
  address: string | null;
  tradingAddress: string | null;
  companyNo: string | null;
  utrNumber: string | null;
  vatNo: string | null;
  notes: string | null;
  publicLiabilityHeld: number | null;
  employerLiabilityHeld: number | null;
  expectedAnnualSpend: number | null;
  publicLiabilityMinimum: number;
  employerLiabilityMinimum: number;
  publicLiabilityBelowMin: boolean;
  employerLiabilityBelowMin: boolean;
  onboardingActive: boolean;
  // The live onboarding link (office can copy/re-send it); null once completed or expired.
  onboardingUrl: string | null;
  onboardingTokenExpiresAt: string | null;
  onboardingCompletedAt: string | null;
  agreementSentAt: string | null;
  agreementSignedAt: string | null;
  agreementFileUrl: string | null;
  hasSignedAgreement: boolean;
  // Session ARZ: payment details on record (current or earlier). Mirrors the server's delete guard; never a digit.
  hasPaymentDetailsRecord?: boolean;
  // The Xero ContactID once pushed to Xero as a payable contact; null until pushed.
  xeroContactId: string | null;
  createdAt: string | null;
  // Session AUR: when and by whom (a name, or "the sign-up form") the status was set; null when not set.
  subcontractorStatusSetAt?: string | null;
  subcontractorStatusSetBy?: string | null;
  // "Do you employ anyone?" from the registration; null when not answered.
  employsStaff?: boolean | null;
  // When the subcontractor passed the induction; null when not (it no longer blocks a registration).
  inductionPassedAt?: string | null;
  complianceDocs: SubcontractorDoc[];
}

export interface SubcontractorCreateInput {
  company: string;
  contactName: string;
  email: string;
  trade: string;
  // Session ARZ: optional; the number the office already has, so a payment-details call-back need not use one typed on the
  // registration form.
  contactPhone?: string;
  // Session RC1: required by the server. LABOUR needs rateCardSource; CUSTOM needs customRates and is admin only.
  kind: SubcontractorKind;
  rateCardSource?: RateCardSource;
  /** YYYY-MM-DD, today or later. */
  effectiveFrom?: string;
  customRates?: StandardCardFiguresBody;
  technician?: boolean;
  preview?: boolean;
}
// Session RC2: the invite is a Gmail DRAFT the office sends. What the create response says about it.
export interface SubcontractorInviteDraftOutcome {
  inviteDrafted: boolean;
  // The plain reason when no draft was made, or that one may exist ("Check Gmail Drafts"); null when drafted.
  inviteDraftError: string | null;
  inviteLinkExpiresAt: string;
}

// What the Add modal carries to the detail page about the invite (navigation state).
export interface InviteNavState {
  drafted: boolean;
  error: string | null;
  expiresAt: string;
}

export interface SubcontractorCreatePreview {
  preview: true;
  /** The save button's words, for example "Create subcontractor on Standard card v1 from 23 Oct 2026". */
  plan: string;
  kind: SubcontractorKind;
  rateCardSource: RateCardSource | null;
  effectiveFrom: string | null;
  standardVersion: number | null;
}
export interface SubcontractorTypeInput {
  kind: SubcontractorKind;
  rateCardSource?: RateCardSource;
  effectiveFrom?: string;
  technician?: boolean;
  preview?: boolean;
}
export interface SubcontractorUpdateInput {
  contactName?: string;
  email?: string;
  contactPhone?: string;
  trade?: string;
  address?: string;
  tradingAddress?: string;
  companyNo?: string;
  utrNumber?: string;
  vatNo?: string;
  notes?: string;
  subcontractorType?: SubcontractorType;
  publicLiabilityHeld?: number | null;
  employerLiabilityHeld?: number | null;
  expectedAnnualSpend?: number | null;
  action?: "approve" | "reject" | "deactivate";
}
export interface SubcontractorProjectLink {
  linkId: string;
  role: string | null;
  projectId: string;
  projectRef: string;
  projectTitle: string;
  projectStatus: string;
}

// ---- Subcontractor certificates and insurance (Session AEL, D37) ----
// The structured compliance credential types. Never render these raw: use typeLabel, which
// the server derives (and which falls back to the legacy free-text label on a pre-AEL row).
export type SubCertType =
  | "PUBLIC_LIABILITY"
  | "EMPLOYERS_LIABILITY"
  | "PROFESSIONAL_INDEMNITY"
  | "CSCS"
  | "CPCS"
  | "NIC_EIC"
  | "GAS_SAFE"
  | "ASBESTOS_AWARENESS"
  | "WORKING_AT_HEIGHT"
  | "MANUAL_HANDLING"
  // Session ALV. ELE's engineers are engaged as subcontractors rather than PAYE employees, so a
  // DBS check is a credential every subcontractor holds, inside or outside. Distinct from the
  // office personnel register's own PersonalDocType "DBS_CHECK", which is keyed to a NAMED
  // INDIVIDUAL with no platform account: two separate registers, by Director decision.
  | "DBS_CHECK"
  // Session AUZ: the NVQ Level 3 competence certificate (ordered before OTHER, as in the schema).
  | "NVQ_LEVEL_3"
  | "OTHER";

// status is DERIVED server-side against the current time and is never stored, so it cannot go
// stale. "no-expiry" is a distinct state from "valid": nothing was recorded to expire.
export type SubCertStatus = "valid" | "expiring" | "expired" | "no-expiry";

export interface SubcontractorCertificate {
  id: string;
  certType: SubCertType | null;
  typeLabel: string;
  name: string | null;
  issuer: string | null;
  referenceNumber: string | null;
  issuedAt: string | null;
  expiryDate: string | null;
  status: SubCertStatus;
  daysUntilExpiry: number | null;
  // Session AUZ: the review state (submitted / approved / not accepted), the office's reason when not accepted, the
  // engineer a personal document is about (a name) and whether the subcontractor uploaded it on the portal.
  reviewState?: "submitted" | "approved" | "rejected";
  reviewLabel?: string;
  rejectionReason?: string | null;
  engineerName?: string | null;
  submittedViaPortal?: boolean;
  notes: string | null;
  // False for a certificate the office recorded without uploading a document. Offer the
  // download only when this is true.
  hasFile: boolean;
  uploadedAt: string;
  // Session AUR: false until the office approves the document. An unverified certificate never shows as valid.
  verified?: boolean;
}

export interface SubcontractorExpiringCertificate extends SubcontractorCertificate {
  subcontractorId: string;
  subcontractorName: string;
}

export interface SubcontractorCertificateInput {
  certType: SubCertType;
  name: string;
  issuer?: string;
  referenceNumber?: string;
  issuedAt?: string;
  expiresAt: string;
  fileUrl?: string;
  notes?: string;
}
// A standalone subcontractor linked to a project (the project detail page's view).
export interface ProjectLinkedSubcontractor {
  linkId: string;
  role: string | null;
  subcontractorId: string;
  company: string;
  trade: string;
  status: SubcontractorStatus;
  agreementStatus: SubcontractorAgreementStatus;
}

// ---- Engineer jobs (engineer/admin; cached offline by the PWA) ----
export type ChecklistCategory = "safety" | "mechanical" | "electrical" | "hydraulic" | "documentation";
export type ChecklistStatus = "pass" | "fail" | "advisory" | "na";

export type DriveType = "hydraulic" | "electric_traction" | "both";

export interface ChecklistItemDef {
  id: string;
  description: string;
  category: ChecklistCategory;
  section?: string;
  applies?: "B" | "H" | "E";
}

export interface ChecklistTemplate {
  liftType: string;
  name: string;
  items: ChecklistItemDef[];
}

// Session AQB (Director decision D78): the additional items THIS CONTRACT COVERS -- gates, doors,
// barriers, shutters sold alongside the lift. They ride the lift's visits (the locked Session ACS
// model), so they are no asset and no job of their own, and the engineer was never told they existed
// although clause 6 of the signed agreement says the contract covers them.
//
// OPTIONAL, AND ABSENT READS AS "NOTHING TO SHOW", which is the honest direction: a job cached before
// D78, a contract that covers only lifts, and a contract whose agreement lists no schedule all look
// the same to the phone and all render no block. It is never a claim that the contract covers nothing.
export interface CoveredItem {
  id: string;
  description: string;
  qty: number;
  maintenanceOnly: boolean;
}

export interface EngineerJob {
  /** Session BDM: the working days of a multi-day job that this engineer is on (absent on a one-day job). */
  workDays?: import("./engineerWorkDays").EngineerWorkDay[];
  id: string;
  jobNumber: string | null;
  type: string;
  status: string;
  scheduledFor: string | null;
  description: string | null;
  eleNumber: string | null;
  assetType: string;
  liftType: string | null;
  driveType: DriveType | null;
  siteName: string;
  siteAddress: string;
  sitePostcode: string | null;
  // Session AFW: internal site access instructions (door codes, key location, parking) the engineer
  // reads before attending. Optional so a job cached before AFW still loads (absent -> no panel).
  accessDetails?: string | null;
  hasJobSheet: boolean;
  returnReason: string | null;
  photos: string[];
  calloutOutOfHours: boolean;
  checklist: ChecklistTemplate | null;
  // Frequency-filtered checklist context (Session AFF). Optional: pre-AFF offline caches lack
  // them; the server always sends them (null for non-service jobs / no cadence / cross-year).
  visitsPerYear?: number | null;
  visitNumberThisYear?: number | null;
  // Session AFZ: the RAMS compliance surface. ramsDoc is the applicable Risk Assessment and Method
  // Statement for this job; ramsAcknowledgedAt is when the engineer confirmed they read it (null
  // until they do). Optional so a job cached before AFZ still loads (absent -> the compliance section
  // does not render / treats it as not yet acknowledged, and the server re-sends both on next sync).
  ramsDoc?: RamsDocRef | null;
  ramsAcknowledgedAt?: string | null;
  // Session ALW: the engineer's acceptance stamp (null = assigned, not yet confirmed) and a site
  // inspection's own reason for happening. Optional so a job cached before ALW still loads.
  acceptedAt?: string | null;
  inspectionReason?: string | null;
  // Session AOL: is this job assigned to the account reading it? Only the assigned engineer may
  // accept, so the Accept control is offered on their own work alone.
  //
  // OPTIONAL, AND `undefined` MEANS ALLOWED, NOT REFUSED. A job cached offline before this shipped
  // carries no such field, and reading absence as "not mine" would take the Accept button away from
  // an engineer standing in a basement with a stale cache -- which would wedge the job sheet behind
  // a gate they could no longer open. Same reading the acceptedAt badge already takes for an older
  // response: absent means "cannot say", never a false negative. The server is the real gate.
  assignedToMe?: boolean;
  // Session ASM (Phase 6, Director decision D83): true only on a job this engineer is booked on as
  // ADDITIONAL crew, not as its primary. The phone shows it read-only (the RAMS, who leads the visit) and
  // opens no job sheet: the sheet is one record, kept by the primary engineer. Absent reads as the primary's
  // own job, which is every job cached before this shipped.
  crewMember?: boolean;
  // With crewMember: the primary engineer's name.
  leadEngineerName?: string | null;
  // Session APF: derived live from whether the job's lift currently has a POP tag token fitted. The
  // token itself never crosses to the phone, only this boolean -- see server/src/modules/jobs/jobs.ts.
  // The PWA offers its in-flow "tap the tag" prompts only where this is true, so an engineer is never
  // asked to tap a sticker that does not exist. OPTIONAL, and absent reads as "cannot say" (a job
  // cached before this session): the wiring of the actual tap prompt is a later session's work.
  hasPopTag?: boolean;
  // Session SC9A: why the office put this job on hold (present only while it is), shown on the phone as "On hold: <reason>".
  holdReason?: JobHoldReasonKey;
  holdReasonText?: string | null;
  // Session APG: when this job's arrival and departure POP tag taps were recorded, or null while that
  // end has not been tapped. Derived live from the taps on the server. Mirrors CachedJob, which
  // documents how the job sheet uses them. Optional: a server that predates APG never sends them.
  arrivalTapAt?: string | null;
  departureTapAt?: string | null;
  // Session AQB (D78): the additional items this job's contract covers, on a SERVICE visit to a lift
  // whose agreement lists any. Absent everywhere else, and on any server that predates D78.
  coveredItems?: CoveredItem[];
  // Session BAQ: the examiner's LOLER report items still open at this job's lift. Only on a service
  // visit to a lift and on a 'loler_follow_up' job. Absent reads as nothing to show.
  lolerItems?: LolerItemToCheck[];
}

// One examiner's LOLER report item the engineer is asked to look at (mirrors the offline type).
export interface LolerItemToCheck {
  id: string;
  description: string;
  category: "immediate" | "timed" | "advisory";
  deadline: string | null;
  reportRef: string | null;
  examinationDate: string | null;
  lastOutcome: "checked" | "needs_work" | "no_access" | null;
  lastNote: string | null;
  lastCheckedAt: string | null;
}

// The applicable RAMS document for a job (Session AFZ). Opened in the phone's viewer via
// api.jobs.ramsHref(); never a Drive link.
export interface RamsDocRef {
  code: string; // e.g. "ELE-RAMS-SVC-001"
  label: string; // plain-English title
}

// A site document surfaced on the engineer's job compliance section (Session AFZ). The download href
// proxies the file through the server, so the browser never needs Drive auth.
export interface EngineerSiteDocument {
  id: string;
  label: string;
  categoryLabel: string;
}

// One row of the engineer diary "My Week" view (Session AH Item 5). Lightweight (no checklist).
export interface EngineerDiaryRow {
  id: string;
  type: string;
  status: string;
  scheduledDate: string; // ISO ("" if missing)
  siteName: string;
  siteAddress: string;
  notes: string | null;
}

// ---- Company-wide site and lift history for the engineer PWA (Session AEK, D33) ----
// Read-only, and deliberately narrow: these DTOs mirror the server's engineer-namespaced history
// endpoints, which expose no money field, no contract term and no customer contact detail.
// The page size the PWA asks for. The server's house pagination defaults to 20; the history
// screens want the 50 the Director asked for, so they request it explicitly.
export const ENGINEER_HISTORY_PAGE_SIZE = 50;

export interface EngineerSiteListItem {
  id: string;
  name: string;
  address: string;
  postcode: string | null;
  // True for a site ELE no longer services. Listed under a separate collapsed "Archived sites"
  // section rather than hidden: the history is still useful, the clutter is not.
  archived: boolean;
  eleNumbers: string[];
}

// One completed visit, in either history feed.
export interface EngineerHistoryJob {
  /** Session SC9A: a closed visit whose own faults are still open in To fix (My past visits only). */
  followOnWorks?: boolean;
  id: string;
  jobNumber: string | null;
  type: string;
  completedAt: string | null;
  engineerName: string | null;
  eleNumber: string | null;
  outcome: string;
}

export interface EngineerHistoryLift {
  id: string;
  eleNumber: string | null;
  liftType: string | null;
  assetClass: string;
}

// An open defect on a lift. Safety information the attending engineer needs before starting.
export interface EngineerHistoryDefect {
  id: string;
  severity: string;
  status: string;
  description: string;
  raisedAt: string | null;
}

// ---- One completed job sheet, in full (Session AEM) ----
// The record behind a history row. Same exposure contract as the feeds above, applied to a much
// richer record: no money, no customer identity, no defect triage internals, and the signature
// reported as TEXT only (there is no image and no URL to fetch one with).

export interface EngineerJobSheetChecklistEntry {
  id: string | null;
  item: string;
  status: string; // pass | fail | advisory | na
  note: string | null;
}

export interface EngineerJobSheetPart {
  description: string;
  quantity: number;
  partCode: string | null;
}

export interface EngineerJobSheetSignature {
  signed: boolean;
  signerName: string | null;
  // Plain English, never the stored key.
  note: string | null;
}

export interface EngineerJobSheetOutcome {
  label: string;
  attention: boolean;
}

export interface EngineerJobSheetCallout {
  faultReported: string | null;
  faultFound: string | null;
  workCarriedOut: string | null;
  liftStatusOnLeaving: string | null;
  arrivedAt: string | null;
  leftAt: string | null;
}

export interface EngineerJobSheetDetail {
  jobId: string;
  jobNumber: string | null;
  type: string;
  completedAt: string | null;
  isCallout: boolean;
  siteName: string | null;
  siteAddress: string | null;
  siteId: string | null;
  assetId: string | null;
  eleNumber: string | null;
  liftType: string | null;
  assetClass: string | null;
  engineerName: string | null;
  outcome: EngineerJobSheetOutcome;
  signature: EngineerJobSheetSignature;
  checklist: EngineerJobSheetChecklistEntry[];
  // Stored checks that could not be named. Normally 0; said out loud rather than swallowed, so a
  // short list is never presented as the whole visit.
  checklistNotShown: number;
  engineerNotes: string | null;
  advisoryNotes: string | null;
  parts: EngineerJobSheetPart[];
  photoCount: number;
  // Structured defect rows raised from this visit.
  defects: EngineerHistoryDefect[];
  // The engineer's own free-text defect notes on the sheet. A different thing from the rows
  // above, so the screen labels them apart rather than merging two lists that mean two things.
  defectNotes: string[];
  callout: EngineerJobSheetCallout | null;
}

export interface EngineerSiteHistory {
  // accessDetails (Session AFR): internal operational notes the engineer needs before attending
  // (door codes, key location, parking, motor-room access). Not customer PII; cached offline.
  site: { id: string; name: string; address: string; postcode: string | null; accessDetails: string | null };
  lifts: EngineerHistoryLift[];
  items: EngineerHistoryJob[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

export interface EngineerLiftHistory {
  asset: {
    id: string;
    eleNumber: string | null;
    liftType: string | null;
    assetClass: string;
    driveType: string | null;
    // Session ASB (D-SEC-6): which site this lift is at, so a cached lift page can be dropped once
    // its site leaves the engineer's scope (historyCache.ts pruneHistoryCache). Optional: a page
    // cached before ASB has none, and is dropped on the next sync for that reason.
    siteId?: string | null;
    siteName: string | null;
  };
  defects: EngineerHistoryDefect[];
  items: EngineerHistoryJob[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

export interface ChecklistResultBody {
  id?: string;
  item: string;
  category?: ChecklistCategory;
  status: ChecklistStatus;
  note?: string;
}

export type SignatureSkipReason = "customer_unavailable" | "remote_site" | "emergency";

export interface JobPartUsedBody {
  partCode?: string;
  description: string;
  quantity: number;
  unitCost?: number;
}

export interface JobCompletionBody {
  checklist: ChecklistResultBody[];
  defects: string[];
  advisoryNotes?: string;
  partsUsed: JobPartUsedBody[];
  engineerNotes?: string;
  signatureData?: string; // PNG data URL
  signatureSkipReason?: SignatureSkipReason;
  // Call-out (reactive) job fields. Sent only for a call-out job; PPM visits omit them.
  outOfHours?: boolean;
  arrivalTime?: string; // ISO
  departureTime?: string; // ISO
  faultFound?: string;
  workCarriedOut?: string;
  liftStatusOnLeaving?: string;
  completedAt: string;
  // Session AFZ: the engineer's RAMS acknowledgement, carried on the completion so an offline-only
  // visit still records it server-side (the gate itself is client-side; this never blocks completion).
  ramsAcknowledged?: boolean;
}

// ---- Office dashboard tiles (office/admin, fed from live Postgres) ----
export interface DashboardTiles {
  arr: number;
  servicesDueThisMonth: number;
  invoicesOverdue: { count: number; amount: number };
  // Session SC9B: customers on stop (more than 30 days past an invoice due date). null = not checked. Optional so an older server shows no tile.
  accountsOnStop?: { count: number | null };
  renewalsDue90: number;
  simExpiryAlerts: number;
  contractsNotStarted: number;
  priceRiseDue: number;
  // When the Xero overdue-invoice discovery last ran (ISO), or null if never.
  invoicesSyncedAt: string | null;
  // Count of clients carrying at least one active risk flag.
  atRiskClients: number;
  // Emergency entrapments this month (Session AK Item 10): count + average resolution time (minutes).
  // avgResolutionMins is measured slaStartAt -> workFinishedAt over the measurable rows only
  // (Session ADU, the locked SLA clock rule). measured/unmeasured let the tile count the gap openly
  // rather than average a silent subset.
  // open (SC7B review, optional so an older server still works): how many are still open NOW, of any age; the dashboard's attention rule reads it.
  entrapments: { count: number; avgResolutionMins: number | null; measured: number; unmeasured: number; open?: number };
  // Session AMW: active contracts whose most recent renewal anniversary was missed, is waiting on a
  // proposal, or was settled without the renewal date moving on. Counted from each contract's real
  // first-term end, never the renewal date field, so a review that dropped off the two forward-only
  // renewal tiles still counts here. Optional so older fixtures stay valid.
  renewalCyclesAttention?: number;
  // Session APA: how many of renewalCyclesAttention are a CPI rise applied without the customer being
  // told (state priced_not_confirmed_with_customer). Optional so older fixtures stay valid.
  renewalCyclesNotTold?: number;
  // Session ARZ: payment (bank) details across ACTIVE subcontractors, derived live on the server
  // (dashboard/subcontractorPaymentDetails.ts). Counts only, never a bank detail. unverified includes
  // changedUnverified (a set that replaced an earlier one, a possible fraud). Zero is 0. Optional so an
  // older server (and older fixtures) simply show no tile rather than a fabricated zero.
  subcontractorPaymentDetails?: {
    active: number;
    noDetails: number;
    unverified: number;
    changedUnverified: number;
    verifiedNotInXero: number;
  };
  // Session ARY, Item 4: the Xero-billing check over every active contract. Optional so older
  // fixtures stay valid; checkedAt is the oldest successful read among them (null = never checked).
  xeroBilling?: { outOfStep: number; notLinked: number; checkedAt: string | null; stale: boolean; error: string | null };
  // Session ASO: the email engine (server/src/modules/emailEngine/summary.ts via dashboard.routes.ts). Optional so an
  // older server shows no tile at all rather than a zero nobody measured.
  emailEngine?: EmailEngineTileData;
  // Session ASQ (GDPR, Stream G, Item 4): open retention proposals and when the weekly sweep last ran. Sent to an ADMIN
  // only (only an admin can act on a proposal), so an office user, and an older server, show no tile at all. open is
  // null when the figure could not be read (shown as not checked, never as zero). incomplete: the last sweep could not
  // read every record or plan every rule, so an open count of 0 is not a clean list (review F1). notApplicable: records
  // the last sweep found with no retention date to count from, kept and never proposed (review F4). Both optional so an
  // older server reads as it did.
  dataRetention?: { open: number | null; lastSweepAt: string | null; stale: boolean; incomplete?: boolean; notApplicable?: number | null };
}

// ---- The email engine (Session ASO) -------------------------------------------------------------------------------------
// Client mirrors of the /api/email-engine response shapes (server/src/modules/emailEngine/emailEngine.routes.ts, whose
// handlers are the source of truth). Read by the Email engine page, the dashboard tile and the enquiry mirror panel.

export type InboundEmailOutcome = "pending" | "drafted" | "needs_you" | "no_reply_needed";

export interface EmailEngineTileData {
  started: boolean;
  polledAt: string | null;
  pollError: string | null;
  stale: boolean;
  last24h: { drafted: number; needsYou: number; noReplyNeeded: number; pending: number };
  overdue: number;
  proposedStandardLines: number;
  // Session AUA (email system Phase 3): messages past the 24-hour first-reply target (DE-5: modernisation, new install and
  // maintenance quote enquiries), and template proposals waiting in the queue. Optional so an older server reads as
  // "not reported" rather than a zero nobody measured.
  replyOverdue?: number;
  pendingTemplateProposals?: number;
}

export interface EmailEngineStatus extends EmailEngineTileData {
  startedAt: string | null;
  draftingEnabled: boolean;
  /** Session BCR2 (D-BCR1-1): the sentence gate's mode. Optional so an older server reads as log only. */
  sentenceGateMode?: "log_only" | "enforce";
  /** Session BCR6 Phase 4: who writes live replies, whether the agent also runs in the background (shadow), and its model pins (null = default). */
  engineMode?: "classic" | "agent";
  agentShadow?: boolean;
  agentDraftModel?: string | null;
  agentVerifierModel?: string | null;
  // Session ATX: syncedAt is the last COMPLETED sent-mail walk; categorisedAt the last completed categorise step; syncError
  // names the step that did not complete on the last run. Optional so an older server reads as "not reported".
  library: {
    total: number;
    categorised: number;
    syncedAt: string | null;
    categorisedAt?: string | null;
    syncError?: string | null;
    backfillReachedBack: string | null;
  };
  /** Session ATX: the hourly triage sweep's own error field (why it could not read every conversation). */
  sweepError?: string | null;
  deadlineMinutes: number;
}

// Session ATX, Item 5: the all-time mailbox backfill (server/src/modules/emailEngine/backfill.ts loadBackfillStatus).
export type EmailBackfillState = "running" | "paused" | "stopped" | "complete";

export interface EmailBackfillStatus {
  status: EmailBackfillState | null;
  engineStartedAt: string | null;
  from: string | null;
  until: string | null;
  cursor: string | null;
  windows: { total: number; done: number; remaining: number };
  recorded: number;
  outcomes: { pending: number; drafted: number; needsYou: number; noReplyNeeded: number };
  categorised: number;
  batchSize: number;
  pauseSeconds: number;
  startedAt: string | null;
  startedBy: string | null;
  lastBatchAt: string | null;
  nextBatchAfter: string | null;
  finishedAt: string | null;
  error: string | null;
  draftingEnabled: boolean;
  headline: string;
}

export interface EmailBackfillPlan {
  resume: boolean;
  from: string;
  until: string;
  cursor: string;
  windowsTotal: number;
  windowsRemaining: number;
  batchSize: number;
  pauseSeconds: number;
  draftingEnabled: boolean;
  // Session ATX review: messages an earlier stop left needing a person, which starting again puts back to be decided.
  wouldReopen?: number;
  wouldDo: string;
}

export interface EmailEnginePollResult {
  initialised: boolean;
  historyRestarted: boolean;
  messagesSeen: number;
  threadsRead: number;
  inboundRecorded: number;
  sentHandled: number;
  readFailures: number;
  truncated: boolean;
  processed: { claimed: number; decided: number; failed: number; gaveUp: number } | null;
}

export interface EmailEngineMessageRow {
  id: string;
  gmailMessageId: string;
  gmailThreadId: string;
  enquiryId: string | null;
  from: string | null;
  fromName: string | null;
  subject: string | null;
  receivedAt: string;
  seenBy: string;
  outcome: InboundEmailOutcome;
  outcomeReason: string | null;
  outcomeAt: string | null;
  overdue: boolean;
  category: string | null;
  categoryLabel: string | null;
  inGmail: boolean;
  personDraftPresent: boolean;
  editClass: string | null;
  attempts: number;
  /** Session ATX: why the message could not be read in full (null when it was). */
  readIssue?: string | null;
  categorySource?: string | null;
  /** Session ATZ (Items 2 and 3): "file" (filed with a Gmail label, no reply needed) or "work" (a work order). */
  lane?: "file" | "work" | null;
  fileKind?: string | null;
  fileKindLabel?: string | null;
  workOrderProposalId?: string | null;
  // Session AUA (email system Phase 3): where the draft's wording came from. "template": an approved reply template (key
  // and version); "free": the labelled fallback. Null when nothing was drafted. replyOverdue: past the 24-hour first-reply
  // target that applies to modernisation, new install and maintenance quote enquiries (firstReplyTargetHours 24).
  draftKind?: "template" | "free" | "agent" | null;
  freeDraft?: boolean;
  templateId?: string | null;
  templateKey?: string | null;
  templateVersion?: number | null;
  replyOverdue?: boolean;
  firstReplyTargetHours?: number | null;
  /** Session AUE (D-LATE-1): "Late reply: N working days" when the draft was made late (it carries the fixed opener). */
  lateReply?: string | null;
}

// Session AUA: why no template was used for a draft (templateEngine.ts TemplateMiss). `text` is the plain-English reason.
export interface EmailTemplateMiss {
  reason: string;
  text: string;
  templateKey?: string;
  templateVersion?: number;
  missingSlots?: string[];
  failures?: EmailEngineValidationFailure[];
}

// Session ATZ (Item 5): adding a conversation the engine missed, by its Gmail id.
export interface EmailConversationPlan {
  gmailThreadId: string;
  subject: string | null;
  messages: { gmailMessageId: string; from: string | null; receivedAt: string; alreadyRecorded: boolean }[];
  toRecord: number;
  alreadyRecorded: number;
  queueNote: string | null;
}
export interface EmailConversationAdded {
  gmailThreadId: string;
  enquiryId: string | null;
  recorded: number;
  alreadyRecorded: number;
  queueNote: string | null;
  outcomes: { id: string; gmailMessageId: string; outcome: string; outcomeReason: string | null }[];
}

/**
 * Session BCM: what the sentence gate cut from the model's text (sentence, reason, plain-English detail) and whether it held
 * the draft. Session BCR2: `mode` says whether the cuts were applied ("enforce") or only recorded ("log_only", the text
 * unchanged); absent on a record written before BCR2, when the gate always enforced.
 */
export interface EmailEngineGateLog {
  removed?: Array<{ sentence: string; reason: string; detail: string }>;
  hold?: { reason: string; gaps?: string[] } | null;
  mode?: "log_only" | "enforce";
}

export type EmailEngineCheck =
  | "figures"
  | "references"
  | "dates"
  | "style"
  | "phone"
  | "address"
  | "personal_name"
  | "promise"
  | "attachments"
  | "house_words"
  | "loler"
  | "tier"
  | "privacy"
  | "sentence_gate"
  | "already_given"
  | "emergency_services"
  | "visit_window";

export interface EmailEngineValidationFailure {
  check: EmailEngineCheck;
  line: string;
  detail: string;
}

export interface EmailEngineFacts {
  text: string;
  lines: { key: string; label: string; value: string }[];
  sender: { email: string | null; name: string | null; match: string; customerId: string | null; customerName: string | null };
  newEnquirer: boolean;
}

export interface EmailEngineExample {
  id: string;
  category: string | null;
  sentAt: string;
  theyWrote: string;
  officeReplied: string;
}

// Session BCR6 Phase 4: the investigating agent's run log (InboundEmail.agentRun) and its shadow run (InboundEmail.agentShadow), as the
// record page reads them. The fact TEXTS are kept only for the ids a sentence cites (citedFacts).
export type EmailAgentVerdict = "supported" | "unsupported" | "not_a_fact" | "states_fact_uncited";
export interface EmailAgentSentence {
  n: number;
  text: string;
  kind: "fact" | "courtesy" | "question" | "next_step";
  effectiveKind: "fact" | "courtesy" | "question" | "next_step";
  cites: string[];
  verdict: EmailAgentVerdict | null;
  why: string | null;
  kept: boolean;
}
export interface EmailAgentRun {
  version: number;
  decision: "reply" | "no_reply" | "needs_input";
  body: string | null;
  reason: string | null;
  situation: {
    asked: string[];
    counterparty: string;
    alreadySettled: string[];
    replyOwed: boolean;
    noReplyReason: string | null;
    lookups: string[];
  } | null;
  sentences: EmailAgentSentence[];
  toolCalls: { name: string; input: Record<string, unknown>; factIds: string[]; note?: string; byCode?: boolean }[];
  verifier: { n: number; verdict: EmailAgentVerdict; why: string }[];
  citationFailures: { check: string; n: number; sentence: string; detail: string }[];
  validatorFailures: { check: string; line: string; detail: string }[];
  disagreements: { sentence: string; verifier: EmailAgentVerdict; validator: "passed" | "failed"; detail: string }[];
  decisionNeeded: string | null;
  options: string[];
  rewrites: number;
  models: { draft: string; verifier: string };
  usage: { inputTokens: number; outputTokens: number; costUsd: number | null };
  latencyMs: number;
  failure: string | null;
  citedFacts: { id: string; text: string; source: string; citable: boolean }[];
}
export interface EmailAgentShadow {
  version: number;
  at: string;
  decision: "reply" | "needs_input" | "no_reply" | "refused" | "error";
  body: string | null;
  reason: string | null;
  run: EmailAgentRun | null;
  models: { draft: string; verifier: string } | null;
  costUsd: number | null;
  latencyMs: number;
  asOf: string;
  gapCount: number;
}
export interface EmailAgentComparison {
  days: number;
  since: string;
  listed: number;
  total: number;
  rows: {
    inboundEmailId: string;
    receivedAt: string;
    category: string | null;
    classic: { outcome: string; draftKind: string | null; draftBodyPresent: boolean; editClass: string | null };
    agent: { decision: string; reason: string | null; verifierFailures: number; disagreements: number; costUsd: number | null; latencyMs: number | null };
    officeReplied: boolean;
  }[];
  summary: {
    total: number;
    matrix: { classic: string; agent: string; count: number }[];
    replyOwed: { compared: number; agree: number; classicOnly: number; agentOnly: number; agreeShare: number | null; notCompared: number };
    needsInputShare: number | null;
    meanCostUsd: number | null;
    costedRuns: number;
    p95LatencyMs: number | null;
    classicSentClean: { count: number; agentReplied: number; agentNeededInput: number; agentNoReply: number; agentNoDecision: number };
  };
}

export interface EmailEngineMessageDetail extends EmailEngineMessageRow {
  to: string[];
  cc: string[];
  /** Session ATX, Item 2: what the message said, stored when it was recorded (null before then, or headers only). */
  body?: { text: string; truncated: boolean } | null;
  attachments?: { filename: string; mimeType: string; size: number | null }[];
  // Session ATZ (Item 2): how a filed message was filed, and the invoices a remittance names, matched LIVE to the invoice
  // cache (the status shown is Xero's; a remittance never marks an invoice paid).
  filed?: {
    kind: string | null;
    label: string | null;
    labelledInGmail: boolean;
    invoices: { invoiceNumber: string; invoices: { id: string; invoiceNumber: string | null; status: string; amount: string; customerName: string | null }[] }[];
  } | null;
  lastError: string | null;
  draft: {
    subject: string | null;
    body: string;
    attachments: string[];
    model: string | null;
    inputTokens: number | null;
    outputTokens: number | null;
    generatedAt: string | null;
    instruction: string | null;
    // Stored as the engine wrote it: { passed, attempts: [{ failures }], failures }. Null before the checks existed.
    validation: { passed?: boolean; failures?: EmailEngineValidationFailure[]; gate?: EmailEngineGateLog } | null;
    // Session AUA: the wording's source, what each slot resolved to, why no template was used, and the signature every
    // draft carries (always "Company signature", DE-3). Optional so an older server reads as before.
    kind?: "template" | "free" | null;
    freeDraft?: boolean;
    template?: { id: string | null; key: string; version: number | null } | null;
    templateSlots?: Record<string, string> | null;
    templateMiss?: EmailTemplateMiss | null;
    signature?: string;
  } | null;
  facts: EmailEngineFacts | null;
  /** Session BCR6 Phase 4: how the investigating agent wrote the live draft (null when the classic engine did). */
  agentRun?: EmailAgentRun | null;
  /** Session BCR6 Phase 4: what the agent would have written beside the classic draft; never delivered. */
  agentShadow?: EmailAgentShadow | null;
  examples: EmailEngineExample[];
  sent: { at: string; text: string | null; similarity: number | null; addedSentences: string[] } | null;
}

export interface EmailEngineDraftPreview {
  kind: string;
  category: string;
  categoryLabel: string;
  body: string | null;
  attachments: string[];
  passedChecks: boolean | null;
  failures: EmailEngineValidationFailure[];
  reason: string | null;
  facts: EmailEngineFacts;
  examples: EmailEngineExample[];
  instruction: string | null;
  // Session AUA: the wording's source ("template" with the approved template it filled, or "free" with why no template
  // was used), the one bespoke sentence when the template allows one, and the signature it carries ("Company signature").
  draftKind?: "template" | "free" | "agent" | null;
  freeDraft?: boolean;
  template?: { id: string; key: string; version: number; title: string } | null;
  templateMiss?: EmailTemplateMiss | null;
  bespokeSentence?: string | null;
  signature?: string;
}

export type EmailEngineDraftResponse =
  | { preview: true; candidateId: string; draft: EmailEngineDraftPreview }
  | {
      preview: false;
      inboundEmailId: string;
      outcome: "drafted" | "needs_you" | "no_reply_needed";
      outcomeReason: string;
      failures: EmailEngineValidationFailure[];
      delivery: { gmailDraftId: string | null; personDraftPresent: boolean } | null;
      draft: EmailEngineDraftPreview;
      fullText: string | null;
    };

// ---- Reply templates (Session AUA, email system Phase 3) -----------------------------------------------------------------
// Client mirrors of /api/reply-templates (server/src/modules/emailEngine/templates/templates.routes.ts and its stores, the
// source of truth). Every write previews by default and writes only when `preview` is explicitly false. Approving a
// TEMPLATE is not approving a REPLY: every reply made from one is still a Gmail draft a person sends.

export type ReplyTemplateStatus = "draft" | "approved" | "retired";
export type ReplyTemplateOrigin = "manual" | "proposal" | "import";
export type ReplyTemplateSender = "verified_customer" | "new_enquirer";

export interface ReplyTemplateSlot {
  name: string;
  label: string;
  resolvesWhen: string;
  needsCustomerRecords: boolean;
  example: string;
}

export interface ReplyCategoryOption {
  key: string;
  label: string;
}

export interface ReplyTemplateUsage {
  drafts: number;
  unchanged: number;
  smallEdits: number;
  heavilyRewritten: number;
  discarded: number;
  notYetSent: number;
  judged: number;
  /** Sent unchanged or with small edits over the judged uses, as a fraction (0.75); null before any use is settled. */
  goodRate: number | null;
  flagged: boolean;
  flagReason: string | null;
}

export interface ReplyTemplateListItem {
  key: string;
  category: string;
  categoryLabel: string;
  title: string;
  current: { id: string; version: number; status: ReplyTemplateStatus };
  approvedVersion: number | null;
  approvedId: string | null;
  draftVersion: { id: string; version: number } | null;
  versions: number;
  usage: ReplyTemplateUsage;
  draftsAllVersions: number;
  flagged: boolean;
  flagReason: string | null;
  updatedAt: string;
}

export interface ReplyTemplateListResponse {
  total: number;
  items: ReplyTemplateListItem[];
  counts: { keys: number; approved: number; drafts: number; flagged: number; pendingProposals: number };
  categories: ReplyCategoryOption[];
}

export interface ReplyTemplate {
  id: string;
  key: string;
  version: number;
  category: string;
  categoryLabel: string;
  title: string;
  whenToUse: string;
  whenNotToUse: string | null;
  status: ReplyTemplateStatus;
  body: string;
  requiredSlots: string[];
  attachments: string[];
  allowBespokeSentence: boolean;
  origin: ReplyTemplateOrigin;
  proposalId: string | null;
  sourceExampleIds: string[];
  sourceInboundEmailIds: string[];
  createdByName: string | null;
  approvedAt: string | null;
  approvedByName: string | null;
  retiredAt: string | null;
  retiredByName: string | null;
  retiredReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReplyTemplateCheckFailure {
  field: string;
  check?: string;
  line?: string;
  detail: string;
  /** A shape failure: the wording cannot even be saved as a draft until it is fixed. */
  hard?: true;
}

export interface ReplyTemplateCheck {
  ok: boolean;
  failures: ReplyTemplateCheckFailure[];
  requiredSlots: string[];
  usableFor: ReplyTemplateSender[];
}

export interface ReplyTemplateSample {
  text: string | null;
  sample: ReplyTemplateSender | "examples";
}

export interface ReplyTemplateSourceReply {
  id: string;
  kind: "library" | "sent";
  sentAt: string | null;
  theyWrote: string | null;
  officeReplied: string | null;
  available: boolean;
}

export type ReplyTemplateProposalKind = "new_template" | "revision" | "flagged_revision";
export type ReplyTemplateProposalStatus = "pending" | "accepted" | "rejected";

export interface ReplyTemplateProposalEdit {
  kind: "added" | "removed" | "reworded";
  from?: string;
  to?: string;
  after?: string | null;
}

export interface ReplyTemplateProposal {
  id: string;
  kind: ReplyTemplateProposalKind;
  status: ReplyTemplateProposalStatus;
  category: string;
  categoryLabel: string;
  baseTemplateId: string | null;
  baseKey: string | null;
  baseVersion: number | null;
  key: string;
  title: string;
  whenToUse: string;
  whenNotToUse: string | null;
  body: string;
  requiredSlots: string[];
  attachments: string[];
  allowBespokeSentence: boolean;
  rationale: string | null;
  edits: ReplyTemplateProposalEdit[];
  checkFailures: Array<{ field: string; detail: string; line?: string }>;
  sourceReplies: ReplyTemplateSourceReply[];
  model: string | null;
  createdTemplateId: string | null;
  decidedAt: string | null;
  decidedByName: string | null;
  rejectReason: string | null;
  createdAt: string;
}

export interface ReplyTemplateDetail {
  template: ReplyTemplate;
  versions: Array<ReplyTemplate & { usage: ReplyTemplateUsage }>;
  usage: ReplyTemplateUsage;
  check: ReplyTemplateCheck;
  sample: ReplyTemplateSample;
  sourceReplies: ReplyTemplateSourceReply[];
  proposals: ReplyTemplateProposal[];
  canDelete: boolean;
  deleteRefusal: string | null;
}

/** The fields a template is made of. `key` is set only on a new template (it never changes afterwards). */
export interface ReplyTemplateFields {
  key?: string;
  category?: string;
  title?: string;
  whenToUse?: string;
  whenNotToUse?: string;
  body?: string;
  requiredSlots?: string[];
  attachments?: string[];
  allowBespokeSentence?: boolean;
}

export type ReplyTemplateCreateResponse =
  | { preview: true; wouldCreate: { key: string; version: number; status: "draft" }; keyExists: boolean; check: ReplyTemplateCheck; sample: ReplyTemplateSample }
  | { preview: false; template: ReplyTemplate; check: ReplyTemplateCheck };

export type ReplyTemplateUpdateResponse =
  | { preview: true; check: ReplyTemplateCheck; sample: ReplyTemplateSample }
  | { preview: false; template: ReplyTemplate; check: ReplyTemplateCheck };

export type ReplyTemplateNewVersionResponse =
  | { preview: true; wouldCreate: { key: string; version: number; status: "draft" }; refusal: string | null; check: ReplyTemplateCheck }
  | { preview: false; template: ReplyTemplate; check: ReplyTemplateCheck };

export type ReplyTemplateApproveResponse =
  | { preview: true; template: ReplyTemplate; check: ReplyTemplateCheck; replaces: { id: string; version: number } | null; outcome: string }
  | { preview: false; template: ReplyTemplate; retired: { id: string; version: number } | null };

export type ReplyTemplateRetireResponse =
  | { preview: true; template: ReplyTemplate; refusal: string | null; outcome: string }
  | { preview: false; template: ReplyTemplate };

export interface ReplyTemplateAcceptOverrides {
  approve?: boolean;
  key?: string;
  title?: string;
  whenToUse?: string;
  whenNotToUse?: string;
  body?: string;
  attachments?: string[];
  allowBespokeSentence?: boolean;
}

export type ReplyTemplateAcceptResponse =
  | { preview: true; mode: "new_key" | "new_version"; key: string; check: ReplyTemplateCheck; approve: boolean; outcome: string }
  | { preview: false; template: ReplyTemplate; approved: boolean };

export type ReplyTemplateRejectResponse =
  | { preview: true; proposal: ReplyTemplateProposal; outcome: string }
  | { preview: false; proposal: ReplyTemplateProposal };

export interface ReplyTemplateMiningCategory {
  category: string;
  label: string;
  replies: number;
  recentReplies: number;
  freeDrafts30d: number;
  templateDrafts30d: number;
  approvedTemplates: number;
  pendingProposals: number;
  mine: boolean;
  reason: string;
}

export interface ReplyTemplateMiningStatus {
  ranAt: string | null;
  error: string | null;
  running: boolean;
  startedAt: string | null;
  categories: ReplyTemplateMiningCategory[];
}

export type ReplyTemplateMiningResponse =
  | { preview: true; categories: ReplyTemplateMiningCategory[]; wouldMine: Array<{ category: string; label: string; replies: number }>; outcome: string }
  | { preview: false; started: true; categories: string[] };

export interface ReplyTemplateExport {
  document: string;
  filename: string;
  counts: { approved: number; drafts: number; proposals: number };
}

export type ReplyTemplateImportAction = "create" | "new_version" | "update_draft" | "unchanged" | "refused";

export interface ReplyTemplateImportBlock {
  index: number;
  heading: string;
  key: string | null;
  action: ReplyTemplateImportAction;
  version: number | null;
  failures: Array<{ field: string; detail: string; line?: string }>;
}

export interface ReplyTemplateImportResponse {
  preview: boolean;
  ok: boolean;
  blocks: ReplyTemplateImportBlock[];
  counts: Record<ReplyTemplateImportAction, number>;
  written?: Array<{ key: string; version: number; id: string; action: ReplyTemplateImportAction }>;
}

export interface ReplyStyleGuide {
  text: string;
  isDefault: boolean;
  bannedPhrases: string[];
  updatedAt: string | null;
  updatedByName: string | null;
  limits: { textMaxChars: number; bannedMax: number; bannedMaxChars: number };
}

export type ReplyStyleGuideResponse =
  | { preview: true; text: string | null; isDefault: boolean; bannedPhrases: string[]; refusals: string[] }
  | ({ preview: false } & Omit<ReplyStyleGuide, "limits">);

// Session AUA (DE-3): the one company signature on every reply the platform drafts.
export interface CompanySignatureFailure {
  check: string;
  line: string;
  detail: string;
}

// Session AUC: the signature is three fields (the rest of its branded design is fixed), and comes in two forms: the plain
// text of the text/plain part and the branded HTML of the text/html part. sample / sampleHtml are how a reply ends with it.
export interface CompanySignatureFields {
  headline: string;
  smallPrint: string[];
  animated: boolean;
}

export interface CompanySignatureSettings {
  /** What is saved, even when it now fails its checks and the default is in use (storedRefused); the form edits this. Optional for an older server. */
  storedFields?: CompanySignatureFields;
  fields: CompanySignatureFields;
  text: string;
  html: string;
  isDefault: boolean;
  updatedAt: string | null;
  updatedByName: string | null;
  storedRefused: string | null;
  label: string;
  defaults: CompanySignatureFields;
  limits: { headlineMax: number; smallPrintMaxLines: number; smallPrintLineMax: number };
  sample: string;
  sampleHtml: string;
}

export type CompanySignatureResponse =
  | {
      preview: true;
      fields: CompanySignatureFields;
      text: string;
      html: string;
      isDefault: boolean;
      failures: CompanySignatureFailure[];
      ok: boolean;
      sample: string;
      sampleHtml: string;
    }
  | ({ preview: false } & CompanySignatureSettings);

/** Session AUE (D-LATE-2): the two fixed lines a late reply carries after its greeting. */
export interface LateReplyWordingSettings {
  late: string;
  veryLate: string;
  stored: { late: string | null; veryLate: string | null };
  isDefault: boolean;
  updatedAt: string | null;
  updatedByName: string | null;
  defaults: { late: string; veryLate: string };
  lateAtWorkingDays: number;
  veryLateAtWorkingDays: number;
  maxLength: number;
  lateSample: string;
  veryLateSample: string;
}

export type LateReplyWordingResponse =
  | { preview: true; late: string; veryLate: string; isDefault: boolean; refusals: string[]; ok: boolean; lateSample: string; veryLateSample: string }
  | ({ preview: false } & LateReplyWordingSettings);

/** A change: a line left out keeps its wording, null puts it back to the default, useDefault puts both back. */
export interface LateReplyWordingChange {
  late?: string | null;
  veryLate?: string | null;
  useDefault?: true;
  preview: boolean;
}

// Session AUY: the subcontractor welcome pack (agreement, Your Rates, FAQ, Getting Set Up) and its welcome email DRAFT.
export type WelcomePackDocumentKey = "agreement" | "your-rates" | "faq" | "getting-set-up";
export interface SubcontractorWelcomePackReadiness {
  subcontractor: string;
  archived: boolean;
  email: string | null;
  statusState: "bona_fide" | "bona_fide_pending" | "labour_only" | "not_set";
  statusLabel: string;
  rateCard: { present: boolean; version: number | null; effectiveFromLabel: string | null; labourOnlyDeductionSet: boolean };
  agreement: { status: string; sentAt: string | null; signedAt: string | null; templateVersion: string | null; templateLabel: string | null; wordingConfirmed: boolean };
  registration: { completedAt: string | null };
  portal: { logins: number; signedIn: number };
  bothConfirmed: boolean;
  warnings: string[];
  blockers: string[];
  preparedAt: string | null;
  preparedByName: string | null;
  preparedRateCardVersion: number | null;
  cisNoticeIsDefault: boolean;
  plan: { to: string | null; subject: string; attachments: string[]; agreementSource: WelcomePackAgreementSource; agreementNote: string; warnings: string[]; blockers: string[] };
  documents: { key: WelcomePackDocumentKey; label: string }[];
}
/** Which agreement the pack carries: the signed copy, the copy exactly as sent, a pre-send copy marked DRAFT, or none (said why). */
export type WelcomePackAgreementSource = "signed" | "sent" | "draft" | "none";
export interface SubcontractorWelcomePackPrepared {
  preview: false;
  draftId: string;
  to: string;
  subject: string;
  attachments: string[];
  agreementSource: WelcomePackAgreementSource;
  agreementNote: string;
  filedToDrive: boolean;
  rateCardVersion: number;
  warnings: string[];
}
export interface SubcontractorDocumentsSettings {
  cisNotice: { text: string; stored: string | null; isDefault: boolean; updatedAt: string | null; updatedByName: string | null; default: string; maxLength: number };
  agreementWording: { confirmed: boolean; confirmedAt: string | null; confirmedByName: string | null; templateVersion: string; draftMarking: string };
}
export type CisNoticeResponse = { preview: true; text: string; isDefault: boolean; refusals: string[]; ok: boolean } | ({ preview: false } & SubcontractorDocumentsSettings);
export type AgreementWordingResponse = { preview: true; confirmed: boolean; currentlyConfirmed: boolean; effect: string } | ({ preview: false } & SubcontractorDocumentsSettings);

/** Session AUF (DL-1): who arranges a customer's LOLER examinations. arranged false is the default (the customer's own). */
export interface LolerArrangement {
  customerId: string;
  customerName: string;
  archived: boolean;
  arranged: boolean;
  reason: string | null;
  setAt: string | null;
  setByName: string | null;
  sites: { id: string; name: string; postcode: string | null }[];
  meaning: string;
  rebillOffered: boolean;
}
export type LolerArrangementResponse =
  | { preview: true; changed: boolean; before: LolerArrangement; after: LolerArrangement }
  | ({ preview: false; changed: boolean } & LolerArrangement);

/** Session AUF (DL-3): an examining body's invoice re-billed to a customer we arrange examinations for, as a Xero DRAFT. */
export interface LolerRebillRequest {
  costExVat: number;
  siteId?: string;
  supplierInvoiceRef?: string;
  inboundEmailId?: string;
  again?: boolean;
  preview: boolean;
}
export interface LolerRebillResponse {
  preview: boolean;
  customerId: string;
  customerName: string;
  siteId: string;
  siteName: string;
  invoiceLine: string;
  reference: string;
  amountExVat: number;
  accountCode: string;
  dueDate: string;
  office: { examiningBodyInvoiceExVat: number; multiplier: number; supplierInvoiceRef: string | null; inboundEmailSubject: string | null };
  alreadyRebilled: { at: string } | null;
  invoiceId?: string;
  invoiceNumber?: string | null;
  status?: "draft";
}

/** Session AUF (DL-2): the one examining body a customer with no examiner is referred to by name. */
export interface RecommendedExaminerSettings {
  name: string;
  email: string;
  isDefault: boolean;
  updatedAt: string | null;
  updatedByName: string | null;
  defaults: { name: string; email: string };
  referralText: string;
}
export type RecommendedExaminerResponse =
  | { preview: true; name: string; email: string; isDefault: boolean; referralText: string; refusals: string[]; ok: boolean }
  | ({ preview: false } & RecommendedExaminerSettings);

/** A change to the signature: any field left out keeps its value, null puts it back to its default (Session AUC). */
export interface CompanySignatureChange {
  headline?: string | null;
  smallPrint?: string[] | null;
  animated?: boolean;
  useDefault?: true;
  preview: boolean;
}

export interface ReplyLibraryExample {
  id: string;
  category: string | null;
  categoryLabel: string;
  sentAt: string;
  theyWrote: string;
  officeReplied: string;
  attachmentNames: string[];
}

export interface ReplyLibraryResponse {
  examples: ReplyLibraryExample[];
  total: number;
  categories: { key: string; label: string; count: number }[];
}

export interface ReplyLibrarySyncResult {
  scanned: number;
  added: number;
  skipped: number;
  failed: number;
  categorised: number;
  backfillBefore: string | null;
  done: boolean;
  // Session ATX: which steps completed, and why one did not.
  walk?: "complete" | "failed" | "skipped";
  walkError?: string | null;
  categorise?: "complete" | "failed" | "nothing_to_do" | "skipped";
  categoriseError?: string | null;
}

export interface EmailQualityCategory {
  category: string;
  label: string;
  drafted: number;
  unchanged: number;
  smallEdits: number;
  heavilyRewritten: number;
  discarded: number;
  notYetSent: number;
}

// Session AUG (email system Phase 6, Item 2): the metrics view, mirroring server/src/modules/emailEngine/metrics.ts.
export interface EmailHandlingCounts {
  filed: number;
  work: number;
  templated: number;
  freeDraft: number;
  person: number;
  noReplyNeeded: number;
  pending: number;
}
export interface EmailHandlingShares {
  filedPct: number | null;
  workPct: number | null;
  owedReply: number;
  templatedPct: number | null;
  bespokePct: number | null;
  // Session SC4: filed plus settled as needing no reply, over every message received. Absent from an older server.
  settledWithoutPerson?: number;
  settledWithoutPersonPct?: number | null;
}
export interface EmailMetricsMonth {
  month: string;
  total: number;
  byCategory: Record<string, number>;
  perActiveLift: number | null;
  handling: EmailHandlingCounts;
  shares: EmailHandlingShares;
  portalFaultReports: number;
  noticesSent: number;
  deflectedPct: number | null;
  medianHoursToFirstReply: number | null;
  modernisationMedianHours: number | null;
}
export interface EmailMetricsTemplateRate {
  templateId: string | null;
  key: string;
  version: number | null;
  title: string | null;
  category: string | null;
  drafts: number;
  unchanged: number;
  smallEdits: number;
  heavilyRewritten: number;
  discarded: number;
  notYetSent: number;
  sentUnchangedPct: number | null;
  sentUnchangedOrSmallEditsPct: number | null;
}
export interface EmailMetrics {
  from: string;
  to: string;
  months: EmailMetricsMonth[];
  categories: Array<{
    category: string;
    label: string;
    total: number;
    outcomes: { pending: number; drafted: number; needsYou: number; noReplyNeeded: number };
    firstReply: { replied: number; noReplyRecorded: number; medianHours: number | null };
  }>;
  totals: { messages: number; replied: number; medianHoursToFirstReply: number | null };
  activeLifts: { count: number; basis: string };
  coverage: { engineStartedAt: string | null; backfillStatus: string | null; backfillReachedTo: string | null; replyLibraryReachesBackTo: string | null; note: string };
  handling: EmailHandlingCounts & EmailHandlingShares & { basis: string; settledComposition?: { liveReceived: number; filed: number; alreadyAnswered: number; internalOrGone: number; automatedOrOther: number } };
  deflection: { portalFaultReports: number; noticesSent: number; deflectedPct: number | null; basis: string };
  // Session SC4: what still reached a person, counted by the shape of its sender and subject. Absent from an older server.
  reachedAPerson?: { total: number; byShape: Array<{ shape: string; label: string; count: number }>; otherShape: number; basis: string };
  templates: EmailMetricsTemplateRate[];
  modernisation: {
    categories: string[];
    received: number;
    replied: number;
    medianHours: number | null;
    targetHours: number;
    repliedWithinTarget: number;
    repliedAfterTarget: number;
    unansweredPastTarget: number;
    unansweredWithinTarget: number;
    withinTargetPct: number | null;
  };
}

export interface EmailQualityReport {
  since: string;
  until: string;
  days: number;
  categories: EmailQualityCategory[];
  // kind (5 October 2026): "standard_line" is a sentence the office keeps adding; "banned_phrase" is one it keeps removing.
  // Absent from an older server, which only proposed standard lines.
  proposedStandardLines: { id: string; category: string; kind?: EmailStandardLineKind; label: string; text: string; occurrences: number; lastSeenAt: string }[];
}

export type EmailStandardLineKind = "standard_line" | "banned_phrase";

export interface EmailStandardLineRow {
  id: string;
  category: string;
  kind?: EmailStandardLineKind;
  label: string;
  text: string;
  status: "collecting" | "proposed" | "accepted" | "rejected";
  occurrences: number;
  firstSeenAt: string;
  lastSeenAt: string;
  decidedAt: string | null;
}

// Session AMW: the renewal-cycle check for one contract, derived live on every read (renewalCycle.ts
// on the server). healthy is true only for not_yet_due, actioned and not_active; anything else is a
// finding the office must look at, and message says what to do.
export type RenewalCycleState =
  | "not_active"
  | "no_dates"
  | "not_yet_due"
  | "actioned"
  | "pending_proposal"
  | "missed_rolled_past"
  | "missed_overdue"
  | "anchor_stalled"
  | "priced_not_confirmed_with_customer"
  | "unavailable";
// Session APA: how far the customer got with the cycle whose CPI rise was applied (contact, never "told" on
// its own since Session APB: priceChangeDisclosure answers that). Null when no rise has been applied for the
// current cycle. "unknown" means Gmail could not be checked, never "not contacted".
export type CustomerEngagement = "not_contacted" | "notified" | "invited" | "opened" | "accepted" | "unknown";
// Session BAK: the office's record that a customer was told a CPI rise (the ledger reads it as evidence).
export type DisclosureMethod = "email" | "letter" | "phone" | "in_person" | "other";
export interface RenewalDisclosureRecordDto {
  id: string;
  disclosedOn: string;
  method: DisclosureMethod;
  emailLink: string | null;
  note: string | null;
  recordedByName: string;
  recordedAt: string;
}
export interface RenewalDisclosureLedger {
  priceChangeDisclosure: "disclosed" | "not_disclosed" | "unknown" | "no_change" | null;
  priceChangeDisclosedAt: string | null;
  priceChangeDisclosedVia: string | null;
  healthy: boolean;
  message: string;
}

export interface RenewalCycleAssessment {
  contractId: string;
  state: RenewalCycleState;
  healthy: boolean;
  cycleBase: string | null;
  cycleBaseSource: "initialTermEnd" | "commencedAt" | "startDate" | null;
  lastDueAnniversary: string | null;
  daysOverdue: number | null;
  currentAnchor: string | null;
  anchorRolledPast: boolean;
  action: { kind: "price_applied" | "signed" | "skip_recorded" | "declined" | "link_actioned"; at: string | null; reason: string | null } | null;
  proposalReady: boolean;
  wouldHaveEscalatedTo: number | null;
  noPriceReview: boolean;
  // Session APA. Optional so older fixtures stay valid.
  customerEngagement?: CustomerEngagement | null;
  customerEngagementCycle?: string | null;
  customerEngagementAt?: string | null;
  priceAppliedAt?: string | null;
  // Session APB: when the customer was first sent an email whose text states both figures of the rise,
  // and which email it was. A priced cycle is healthy only when this is set; customerEngagement above is
  // contact, which no longer clears it on its own. Optional so older fixtures stay valid.
  priceChangeDisclosedAt?: string | null;
  priceChangeDisclosedVia?: "letter" | "invitation" | "notice" | "recorded" | null;
  // Session APB: which answer the disclosure check gave; "unknown" means Gmail could not be read in full.
  priceChangeDisclosure?: "disclosed" | "not_disclosed" | "unknown" | "no_change" | null;
  message: string;
}
// The Renewals page's list of findings, each named by customer and site.
export interface RenewalCycleFinding extends RenewalCycleAssessment {
  customerName: string;
  siteName: string;
}

// ---- Dashboard "launchpad" layout (Session ADC) ----
// ONE shared office layout per tenant, persisted server-side on TenantConfig.dashboardLayout
// (never in browser storage). The versioned descriptor mirrors the server's
// dashboardLayoutSchema (shape-validated there); the block registry + drift-graceful apply live
// in src/lib/dashboardLayout.ts.
export type DashboardBlockSpan = "full" | "half";
export interface DashboardLayoutConfig {
  version: 1;
  // Array order = block position. `key` is a registry block key; an unknown key is dropped on
  // apply (drift-graceful), so the wire type is a plain string.
  blocks: { key: string; visible: boolean; span: DashboardBlockSpan }[];
  // Pinned left-menu shortcuts: nav `to` paths, in order.
  pins: string[];
}

// ---- Dashboard action panels (Session AAJ) ----
// Outstanding jobs: scheduled + in_progress maintenance work, most overdue/imminent first.
// `type` is the raw JobType enum (service | callout | loler | repair); the panel renders its
// colour + label via the shared src/lib/jobType.ts helper.
export interface OutstandingJob {
  id: string;
  type: string;
  status: string;
  scheduledFor: string | null;
  isOverdue: boolean;
  site: { id: string; name: string } | null;
  lift: { id: string; liftRef: string | null } | null;
  // True for a walk-in / lapsed-contract call-out job (billed on the published non-contract card).
  // Drives the amber "Non-contract" badge on the panel; always false for a non-call-out job.
  isNonContract: boolean;
}

export interface OutstandingJobs {
  overdueCount: number;
  jobs: OutstandingJob[];
}

// Active Quotes and Projects panel (Session AAJ, Item 2). Enum-ish fields (status,
// worksClassification, quotationStatus) are raw values the panel maps to labels/colours via
// the shared projects UI helpers.
export interface DashboardMaintenanceQuote {
  id: string;
  ref: string;
  status: string;
  annualValue: number | null;
  sentAt: string | null;
  // Resolved display names (Session AAK): the linked record name, else the free-text/payload name
  // the quotes list page uses, else null (genuinely no name). Never the literal "Not linked".
  customerName: string | null;
  siteName: string | null;
}

export interface DashboardActiveProject {
  id: string;
  ref: string;
  worksClassification: string | null;
  projectValue: number | null;
  projectStatus: string;
  quotationStatus: "draft" | "pending" | "signed" | null;
  // Resolved display name (Session AAK): the linked Site name, else the free-text siteName column
  // (mirroring the projects list page), else null.
  siteName: string | null;
}

export interface ActiveQuotesAndProjects {
  maintenanceQuotes: DashboardMaintenanceQuote[];
  activeProjects: DashboardActiveProject[];
}

// ---- Client risk flags (office/admin) ----
// Mirrors the backend RiskFlagKey (server/src/modules/risk/risk.ts) exactly, LOW_NPS included.
export type RiskFlagKey = "NO_CONTACT" | "HIGH_BREAKDOWN" | "OVERDUE_INVOICE" | "LOLER_BREACH" | "PPM_BREACH" | "LOW_NPS";

export interface RiskFlag {
  key: RiskFlagKey;
  label: string;
  summary: string;
}

export interface AtRiskClient {
  customerId: string;
  name: string;
  flags: RiskFlag[];
}

// ---- "Draft welcome email now" (Session APC; office/admin) ----
// Mirrors server/src/modules/pipeline/welcomeEmailDraftNow.ts. The welcome is drafted once, at contract
// signing, so a customer who signed before their welcome setting existed has an active contract with no
// draft. GET /customers/:id/welcome-email/draft says whether the action is on offer; POST does it.
export type WelcomeDraftRefusalCode =
  | "not_found"
  | "not_direct_client"
  | "welcome_pack_off"
  | "no_contract"
  | "renewal_contract"
  | "customer_archived"
  | "no_billing_email"
  | "no_covered_units"
  | "already_drafted"
  | "portal_grant_failed"
  | "gmail_not_connected"
  | "draft_failed";

export interface WelcomeDraftStatus {
  // Whether the customer's welcome setting lets one be drafted at all (an intermediary on the default, or a
  // customer set to Never draft, is not eligible).
  eligible: boolean;
  // The customer's current ACTIVE contract, or null when they have none.
  contract: { id: string; siteName: string; coveredItems: number } | null;
  // When the contract's welcome was drafted (at signing, or by this action); null if it never was. A fact
  // about a past event, never a claim that the draft is still waiting or has gone.
  draftedAt: string | null;
  canDraft: boolean;
  // Why not, when canDraft is false: a plain-English sentence to show as written.
  refusal: { code: WelcomeDraftRefusalCode; message: string } | null;
  // The billing email the draft would be addressed to.
  to: string | null;
}

export interface WelcomeDraftResult {
  ok: true;
  contractId: string;
  siteName: string;
  draftedAt: string;
  to: string;
  // The customer-facing names of the documents that were attached.
  documents: string[];
}

export interface InvoiceSyncResult {
  ingested: number; // invoices upserted into the cache
  overdueCount: number;
  overdueTotal: number; // gross, GBP
  lastSyncedAt: string; // ISO
}

// ---- Dispatch board + PPM scheduling (admin/office) ----
// ---- Portfolio map (office/admin) ----
export type MapMarkerStatus = "red" | "amber" | "green" | "grey";
export interface MapSite {
  id: string;
  name: string;
  address: string;
  postcode: string | null;
  lat: number | null;
  lng: number | null;
  liftCount: number;
  worstStatus: MapMarkerStatus;
}
export interface GeocodeStatus {
  geocoded: number;
  total: number;
}
export interface GeocodeSummary {
  queued: number;
  succeeded: number;
  failed: number;
}

/** Session BDM: one working day of a multi-day job on the board. */
export interface DispatchJobDay {
  day: string; // YYYY-MM-DD, the London day
  workDayId: string;
  number: number;
  total: number;
  label: string; // "Day 2 of 10"
  start?: string; // the instant this day starts (London day at the job's time of day)
  engineerIds: string[]; // the people working THAT day
}

/** Session BDM: a job's days with who is on each and what is recorded (GET /schedule/jobs/:id/days). Never a price or a pay figure. */
export interface JobDaysView {
  jobId: string;
  multiDay: boolean;
  days: Array<{
    day: string;
    workDayId: string | null;
    number: number;
    total: number;
    label: string;
    engineers: Array<{ engineerId: string; name: string | null }>;
    excluded: Array<{ engineerId: string; name: string | null }>;
    recorded: Array<{
      engineerId: string;
      name: string | null;
      arrivedAt: string | null;
      leftAt: string | null;
      note: string | null;
      overtimeBooked: boolean;
      /** Session OTA: the office has said yes, so the overtime counts. Absent from an older server. */
      overtimeApproved?: boolean;
      overtimeApprovedAt?: string | null;
      overtimeApprovedByName?: string | null;
    }>;
  }>;
}
/** Session OTA: the office's yes or no to overtime an engineer asked for on one working day (POST /schedule/jobs/:id/days/:day/overtime). */
export interface OvertimeDecisionResult {
  preview: boolean;
  decision: "approve" | "refuse";
  day: string;
  engineerId: string;
  engineerName: string;
  minutesWorked: number | null;
  minutesPastThreshold: number | null;
  /** The hours the office is shown; a commit sends them back so it is tied to what was looked at. */
  arrivedAt: string | null;
  leftAt: string | null;
  willRewriteTimesheetEntry: boolean;
  /** A completed job whose day entry was never written: the decision changes nothing on the timesheet, and `note` says so. */
  timesheetEntryMissing?: boolean;
  payCheckReadsApproval: boolean;
  payNote: string | null;
  message: string;
  note?: string;
  days?: JobDaysView | null;
}
export interface JobDaysPlan {
  current: string[];
  next: string[];
  added: string[];
  removed: string[];
  kept: string[];
  warnings: { capacityWarning: string | null; leaveWarning: string | null };
  /** Session R4F: for a job that is not a project and runs over two or more days, what its one invoice covers. Absent from an older server. */
  billingNote?: string | null;
}
export interface JobDaysResult {
  preview: boolean;
  plan: JobDaysPlan;
  days?: JobDaysView | null;
  willRaiseJob?: boolean;
  jobId?: string | null;
}

export interface DispatchJob {
  /**
   * Session SC9A (one look for every state): why the job is ON HOLD (status parts_pending; an older pause reads awaiting_parts), else null. The
   * office's own words for Other come in holdReasonText. entrapment, slaHeldLabel and followOnWorks are the other facts the row's ONE state
   * pill is chosen from (statusRegistry.ts primaryStatePill). All optional: an older cached response simply shows the ordinary status.
   */
  holdReason?: JobHoldReasonKey | null;
  holdReasonText?: string | null;
  entrapment?: boolean;
  slaHeldLabel?: string | null;
  followOnWorks?: boolean;
  /** Session PN1: whether the engineer on this job was told (push, text, or not told and why). Absent when nothing is recorded. */
  engineerTold?: EngineerToldLine | null;
  /** Session BDM: the working days of a multi-day job (absent or null for a one-day job). scheduledFor is the first day. */
  days?: DispatchJobDay[] | null;
  id: string;
  eleNumber: string | null;
  // The canonical {TYPE}-ELE-{number}-{DD}-{MM}-{YY} reference (Session AJF), dated from
  // scheduledFor. Null when no lift is resolved -- fall back to `eleNumber` then.
  reference: string | null;
  siteName: string;
  sitePostcode: string | null;
  type: string;
  status: string;
  scheduledFor: string | null;
  // Session ALS: when the job was RAISED. Only meaningful for work with no scheduledFor -- what the
  // needs-scheduling panel counts "waiting N days" from. Optional: an older cached response omits it.
  raisedAt?: string | null;
  // The fault or work text. A repair has no lift reference of its own to describe it.
  description?: string | null;
  assignedTo: string | null;
  durationMins: number | null;
  // True when the PPM auto-generator created this job (Session AK Item 1); drives the "AUTO" badge.
  autoGenerated: boolean;
  // Session ACR: true when the job's asset is care equipment (assetClass != lift). The calendar
  // labels the card "Equipment service" (same locked service colour). Optional: older cached
  // responses and the leaner dispatch-board select omit it.
  equipmentAsset?: boolean;
  // Who a reschedule confirmation email would go to (Session AG Item 3); null when unknown.
  customerName: string | null;
  customerEmail: string | null;
  // Who a dispatch "engineer on the way" text would go to (Session AKA); null when unknown.
  customerPhone: string | null;
  // Session AGC: true when the linked call-out is billed non-contract (walk-in / lapsed). Drives the
  // amber "Non-contract" pill on the calendar card. Optional: older cached responses omit it.
  isNonContract?: boolean;
  // Session ALU: whether this visit has a service record on file, as a FACT rather than an
  // inference from `status`. Optional so a leaner/older response still maps; the calendar treats
  // an ABSENT flag as "fall back to the old status inference", never as "no record", so a caller
  // that does not send it is no worse off than before.
  hasServiceRecord?: boolean;
  // Session ALW: when the ENGINEER confirmed this job on their phone, or null when nobody has.
  // Read together with assignedTo -- assigned + null is what the office's "Not accepted" badge
  // reports. Optional so a leaner/older response still maps; ABSENT is treated as "the server did
  // not say", never as "not accepted", so an older response shows no badge rather than a false one.
  acceptedAt?: string | null;
  // Session ALW: why a site inspection is happening, in the office's own words. Null/absent for
  // every other type.
  inspectionReason?: string | null;
  // Session ANP (D63): present only on the PATCH /schedule/jobs/:id response, and only when a NEW
  // engineer was just assigned who holds no on-file certification mentioning the lift's
  // manufacturer. Informational only -- the assignment always succeeds regardless. Absent on the
  // board's own list response (never computed there; see the route's own comment).
  certificationWarning?: string | null;
  // Session ARA (S-5): present only on the PATCH /schedule/jobs/:id response, beside
  // certificationWarning and independent of it, and only when a real engineer was just assigned who
  // already has other open visits on the same London calendar day the job now sits on.
  // Informational only -- the assignment always succeeds regardless. Absent on the board's own list
  // response (never computed there, for the same reason as its sibling).
  capacityWarning?: string | null;
  // Session ASZ (D-V1) and Session BDG (M5): the leave sibling of capacityWarning, present on the same response, for
  // an engineer named or already on the job who has recorded leave on the day the job now sits on. Informational only.
  leaveWarning?: string | null;
  // MDF1: present only on the PATCH /schedule/jobs/:id response, and only when moving the visit (another day or engineer) left another
  // visit's travel as it was because its week is approved or the office changed it by hand. Plain sentences, shown with the warnings.
  travelNotRechecked?: string[];
  // Session AQW (W-6): the CallOut this job IS the dispatch attendance for (never
  // Job.originCalloutId, the different "diagnosed by" provenance). Null for a service visit,
  // project or lift-less repair. Optional so an older/leaner response still maps.
  calloutId?: string | null;
  // Session ARA (S-9): a live derivation (RULE 6), never stored -- whether this visit currently has
  // an OPEN lone-worker check-in, and if checked in, whether the next check-in the safety sweep
  // expects has already passed. Optional so an older/leaner response still maps; absent reads as
  // false, never as true, the same "the server did not say" default this type uses throughout.
  loneWorkerCheckedIn?: boolean;
  loneWorkerOverdue?: boolean;
  // Session ASM (Phase 6, Director decision D83): the engineers booked on the job BESIDES its primary
  // (assignedTo), oldest first. An entry with a null engineerId is a booked slot nobody has been named for
  // yet (a job raised "two-man" before a second engineer was chosen). Optional so an older response maps.
  // Session RC1: on a technician visit, when and by whom the office recorded time past the block as agreed (null = not agreed).
  techOvertimeAgreedAt?: string | null;
  techOvertimeAgreedByName?: string | null;
  crew?: { engineerId: string | null; name: string | null }[];
  // Derived from crew by the server: true when anybody besides the primary is booked.
  twoManBooked?: boolean;
  // Session ATD (S-8 follow-up): the real customer/site/lift ids, so the office can deep-link to
  // them from the dispatch board. Optional so an older/leaner response still maps; absent reads as
  // "the server did not say", collapsed to null the same as a genuinely missing link -- there is no
  // link to show either way.
  customerId?: string | null;
  siteId?: string | null;
  assetId?: string | null;
}

export interface ScheduleEngineer {
  id: string;
  name: string;
  // The linked login's User id (Session AJF/AJE). Only /scheduling/engineers sends it; the
  // /calendar picker's own copy of this shape does not, so it is optional here too.
  userId?: string | null;
}

export interface ScheduleProposalItem {
  assetId: string;
  eleNumber: string | null;
  siteName: string;
  type: string;
  durationMins: number;
}

export interface ScheduleProposal {
  id: string;
  status: string;
  proposedDate: string;
  areaLabel: string;
  notes: string | null;
  assignedTo: string | null;
  items: ScheduleProposalItem[];
  count: number;
}

// An unscheduled remedial (repair) job awaiting a date and engineer.
export interface BacklogJob {
  id: string;
  eleNumber: string | null;
  siteName: string;
  description: string | null;
  raisedAt: string | null;
}

export interface DispatchBoard {
  jobs: DispatchJob[];
  engineers: ScheduleEngineer[];
  proposals: ScheduleProposal[];
  backlog: BacklogJob[];
}

// What raising a planned repair, a condition report or a site inspection answers (Session AMF's
// office "Raise..." control; the shared shape all three routes already returned to their MCP
// callers). `reused` is the one field a surface must not ignore: a second open repair or condition
// report on the same lift is REFUSED by the database and the route hands back the existing job
// instead of creating one, so reporting "raised" for a reuse would tell the office it had created
// work it had not. A site inspection never reuses (deliberately exempt from the uniqueness index),
// so its `reused` is always false.
export interface RaisedJobResult {
  jobId: string;
  reused: boolean;
  description: string | null;
  createdAt: string;
  // A site inspection's own reason, echoed back. Absent on the other three types.
  reason?: string;
  // Session ANG, a tech visit only: the call-out whose diagnosis prompted it, and that call-out's
  // own reference. On the REUSE branch these describe the visit that ALREADY existed, which may be
  // a different call-out from the one just given -- which is the whole reason they are reported.
  originCalloutId?: string | null;
  originCalloutRef?: string | null;
  assignedTo?: string | null;
  // Session ANH: whether the office booked the job as two-man (the rate card only pays a second
  // engineer on a job booked that way). On the reuse branch this is the EXISTING job's booking.
  twoManBooked?: boolean;
  // Session ANV: the call this REQUEST recorded against the job, never the existing job's own
  // provenance. It is therefore ALWAYS null on the reuse branch, deliberately: a reused job was
  // raised by something else, possibly by an earlier call, and claiming otherwise would overwrite
  // real provenance with a guess. See Job.callLogId in the schema.
  callLogId?: string | null;
  // Session AOH, a planned repair only: the direct customer and free-text site of a LIFT-LESS
  // repair, echoed back so a caller reports what was created rather than what it asked for. Both
  // null for a repair raised against a lift on the register, and on the REUSE branch (which is
  // reachable only through a lift, so the job handed back always has one).
  customerId?: string | null;
  siteLabel?: string | null;
  // Session ARM, a site inspection raised for an UNKNOWN CALLER only: the sales lead it was raised
  // against, and whether this request made that lead (false when an older call's lead was reused).
  prospectId?: string | null;
  prospectCreated?: boolean;
  // Session ASC (A4), a planned repair only: on the REUSE branch, the existing job's fixed price when it is a repair on
  // an approved repair estimate (its one invoice is that agreed price, covering only the estimate's work); null on a
  // new job and on an ordinary one. Absent on the other raise types.
  fixedPrice?: { estimateReference: string | null; agreedPricePence: number } | null;
}

// ---- Scheduling calendar (the /office/scheduling page) ----
export interface CalendarProject {
  id: string;
  ref: string; // ELE-P-NNNN
  title: string;
  siteName: string;
  status: string;
  date: string | null; // ISO: planned start, else target completion (the single-marker fallback)
  // Session ARA (S-13): the raw pair `date` was derived from, so the calendar can draw a span across
  // a project's date columns instead of a single marker when BOTH are known. Either can still be
  // null (Project has no `endDate` column: only startDate and targetCompletion); a caller with only
  // one, or neither, falls back to the existing single-day `date` marker.
  startDate: string | null;
  targetCompletion: string | null;
}

// An open call-out still waiting for its FIRST engineer (Session ALS). Deliberately not a
// DispatchJob: until an engineer is assigned there is no Job row at all, so `id` here is the
// CallOut id -- the id PATCH /api/callouts/:id/assign takes, never a job id. Assigning one is
// ADMIN ONLY (callout.routes.ts adminGuard), unlike assigning a job.
export interface UnscheduledCallout {
  id: string;
  jobRef: string | null;
  eleNumber: string | null;
  siteName: string;
  reportedAt: string | null;
  status: string;
  faultDescription: string | null;
  isNonContract: boolean;
  isEntrapment: boolean;
  isEmergency: boolean;
  customerName: string | null;
}

// A training session placed on the calendar by its own date (Session ARA, S-9), the same "one
// placement date" shape CalendarProject already has. Read-only: no assign route, no drag, no
// reschedule -- open it on the Training pages to act on it.
export interface CalendarTrainingSession {
  id: string;
  date: string; // ISO: sessionDate
  typeLabel: string;
  siteName: string;
  durationMinutes: number | null;
  status: string; // SCHEDULED | COMPLETED
  engineerName: string | null;
}

// One week's resolved standby (Session ARA, S-9): who the rota, or the office's own per-week
// override, puts on call. Covers a WHOLE WEEK, never a day -- rendered as a banner, not a day-cell
// marker. Read-only: no assign route here (Settings -> Standby rota is where that is set).
export interface CalendarStandbyWeek {
  weekStarting: string; // YYYY-MM-DD, Monday
  engineerName: string | null; // null = nobody on standby, or nothing has decided yet
  source: "override" | "rota" | null;
  reason: string | null;
}

export interface SchedulingCalendar {
  jobs: DispatchJob[];
  engineers: ScheduleEngineer[];
  projects: CalendarProject[];
  // Session ARA (S-9): two read-only overlays alongside jobs/projects/unscheduled -- neither is
  // dispatchable work. Optional so an older cached response still parses.
  trainingSessions?: CalendarTrainingSession[];
  standbyWeeks?: CalendarStandbyWeek[];
  // Work with no date at all, so it can never appear in a day cell (Session ALS). NOT bounded by
  // the requested window -- the same list shows whichever day/week/month is in view, mirroring the
  // retired dispatch board's own backlog. Optional so an older cached response still parses.
  unscheduled?: {
    // Undated JOBS: a repair, a condition report or a site inspection (Session ALW widened this
    // from repairs alone, and renamed the key with it -- a key called `repairs` holding two things
    // that are not repairs is exactly the quietly-wrong name a later session reads and trusts).
    // Ordinary DispatchJobs whose scheduledFor is null, so the existing job mapper consumes them
    // unchanged; each carries its own `type`.
    jobs: DispatchJob[];
    callouts: UnscheduledCallout[];
    // Session ARA (S-15): undated SERVICE (PPM) visits, kept in their OWN key -- never merged into
    // `jobs` above. A maintenance visit is always auto-generated WITH a date, so one with none is a
    // signal something went wrong upstream, not ordinary backlog; the client renders this as its own
    // visibly alarmed panel rather than folding it into the ordinary "Needs scheduling" list.
    // Optional so an older cached response still parses.
    servicesNeedingAttention?: DispatchJob[];
    // True when more is waiting than this response carries: the panel says so rather than letting
    // a bounded list read as a total. Optional so an older cached response still parses.
    capped?: boolean;
    shownLimit?: number;
  };
}

// ---- Day route (Session ARA, S-14): one day's dispatched work, grouped by engineer and put into a
// sensible visit order, with a rough total drive time (real minutes when Google Maps is connected;
// null otherwise, or when there is nothing to route between). ----
export interface EngineerDayRoute {
  engineerId: string;
  // In optimised visit order; a stop with no postcode is appended after the routed stops.
  stops: DispatchJob[];
  driveMinutes: number | null;
  // How many of `stops` carry no postcode and so could not be routed (appended, not ordered).
  unroutedCount: number;
}

export interface DayRoute {
  date: string; // echoes the requested date
  // True when at least one route used a real Google Maps drive-time matrix; false when every route
  // fell back to the postcode-proximity ordering (no key configured, or Maps could not be reached).
  usedLiveDriveTimes: boolean;
  routes: EngineerDayRoute[];
  // Dated jobs with no engineer assigned yet -- listed, not routed (there is no one engineer's route
  // to order them into).
  unassigned: DispatchJob[];
  engineers: ScheduleEngineer[];
}

// ---- Public (no-auth) RFID service page (the lift sticker resolves here) ----
export type LolerStateKey = "clear" | "advisory" | "defect";
export type ComplianceKey = "green" | "amber" | "red";

export interface PublicNfcServiceVisit {
  dateDisplay: string;
  engineerFirstName: string | null;
  type: string;
  // Raw job-type key (service | callout | loler | repair) for the canonical colour.
  typeKey: string;
  // Session ADY: the visit verdict, the ONLY fault-adjacent fact the public payload carries.
  // true = no faults recorded (the page may show the green "Service successful" tick);
  // false = something was recorded (rendered neutrally, never the detail); null = unknown
  // (an equipment thorough-examination row), also neutral.
  clean: boolean | null;
}

// ---- Public project quotation page (Session ABN, Item 3) ----
// The server sends EXACTLY the content the customer PDF renders (the same allow-listed
// builder, signature variant off) plus the project works T&Cs, so the page and the PDF can
// never disagree. Shapes mirror server ProjectQuotationData + projectWorksTerms().
export interface PublicProjectQuotationLineItem {
  refLabel: string;
  element: string;
  scopeDescription: string;
  notes: string;
  basis: string;
  priceExVat: number;
}
export interface PublicProjectQuotationInstallRow {
  liftRef: string;
  type: string;
  controller: string;
  emergencyComms: string;
}
export interface PublicProjectQuotationSupplier {
  name: string;
  supplies: string;
  leadTime: string;
  complianceNote: string;
}
export interface PublicProjectQuotationStage {
  label: string;
  amount: number; // ex VAT
}
export interface PublicProjectQuotationData {
  projectRef: string;
  quoteRefDisplay?: string;
  title: string;
  typeLabel: string;
  dateDisplay: string;
  validUntilDisplay: string;
  clientName: string | null;
  siteName: string | null;
  siteAddress: string | null;
  liftDetails: string | null;
  scopeSummary: string | null;
  exclusions: string | null;
  conditionNotes?: string | null;
  applicableStandard: string | null;
  contractValueExVat: number | null;
  vatRate: number;
  stages: PublicProjectQuotationStage[];
  startDate: string | null;
  targetCompletion: string | null;
  bsrRequired: boolean;
  section20Required: boolean;
  lineItems?: PublicProjectQuotationLineItem[];
  existingInstall?: PublicProjectQuotationInstallRow[];
  supplier?: PublicProjectQuotationSupplier | null;
  liftsInScope?: number | null;
  clientRef?: string | null;
  additionalTerms?: string[];
}
export interface ProjectWorksTermsClause {
  heading: string;
  paragraphs: string[];
}
export interface ProjectWorksTermsView {
  version: string;
  keyTerms: Array<{ label: string; text: string }>;
  agreementClauses: ProjectWorksTermsClause[];
  conditions: ProjectWorksTermsClause[];
}
export interface PublicProjectQuotationView {
  quotation: PublicProjectQuotationData;
  // The same quotation-terms bullet list the PDF prints (validity + standard + additional).
  quotationTerms: string[];
  terms: ProjectWorksTermsView;
  // Session AED: whether the quotation has been executed (signed via Signable), so the page
  // shows an executed notice instead of inviting a second signature. A boolean + a display
  // date only; no envelope id or internal state ever crosses the public boundary.
  signed: boolean;
  signedOnDisplay: string | null;
}

// Session AEI (D1): the modernisation ACCEPTANCE page view. The same quotation content the info
// page renders (signature variant off) plus the full T&Cs, the acceptance status, and the
// display-only signatory names. No email / envelope id / tenant id crosses the boundary.
export type ProjectAcceptStatus = "awaiting_signature" | "sent" | "signed" | "expired";
export interface PublicProjectAcceptView {
  quotation: PublicProjectQuotationData;
  quotationTerms: string[];
  terms: ProjectWorksTermsView;
  status: ProjectAcceptStatus;
  expired: boolean;
  signed: boolean;
  signedOnDisplay: string | null;
  validUntilDisplay: string;
  primarySignatoryName: string | null;
  secondSignatory: { name: string; role: string } | null;
}

// Session ACT: care equipment carries a certificate-driven examination clock (not the lift
// ELTI clear/advisory/defect state). "awaiting" = people-lifting kit with no exam on record
// (shown honestly, amber, never a fabricated date); "not_required" = fixed-height kit.
export type EquipmentExamStateKey = "valid" | "overdue" | "awaiting" | "not_required";

// Session AMZ: what the INTERNAL attendance tap page is told. Deliberately tiny -- the lift's own
// reference and the time recorded, and nothing that identifies a building or a client. A sticker in
// a plant room can be photographed by anyone who gets into the plant room.
export interface PublicTapResult {
  assetRef: string;
  tappedAt: string;
  // True when this open joined a tap recorded moments ago rather than creating a new one.
  alreadyRecorded: boolean;
  // The signed-in staff member's name, when the tapper already had a session on this phone AND the
  // row on file is theirs. Null is an ORDINARY tap and not a fault: the wet-signed log card remains
  // the record of who attended. It is never anybody else's name.
  signedInAs: string | null;
  // True when this open collapsed onto a tap already carrying SOMEONE ELSE'S name, so the page can
  // say so without claiming the visit was recorded against the person reading it.
  recordedUnderAnotherName: boolean;
  // Session AOE. True when the row this open resolved to is the tag's INSTALL CONFIRMATION rather
  // than an attendance: the first tap since the token was generated or rotated, which is somebody
  // checking a sticker they have just fitted actually reads. The page MUST NOT say "Attendance
  // recorded" for one: the platform has deliberately left it out of every count, so claiming a visit
  // would promise the office a number the record does not carry.
  //
  // OPTIONAL ON THE WIRE, DELIBERATELY. The SPA and the API are separate Render services, so a new
  // bundle can call a server that predates this field, and the honest degradation is the pre-AOE
  // reading (an ordinary attendance) rather than a page that renders a confirmation it cannot know
  // about. Read it as `=== true`, never as a truthy branch on a possibly-absent key.
  isInstallConfirmation?: boolean;
  // Session APF. Which end of a visit this tap was: "arrival" or "departure" when the caller stated
  // one and the row is a real attendance, otherwise null. OPTIONAL on the wire for the identical
  // reason isInstallConfirmation is: a server that predates this session simply never sends it.
  direction?: "arrival" | "departure" | null;
}

// Session APG: the Home screen's POP tag scan asks the server which of the caller's OWN jobs a
// scanned tag belongs to (POST /api/engineer/pop-tag/match). Authenticated, unlike the tap route
// above, so it may name the caller's own jobs and the lift's site; the tap itself is still recorded by
// the public route, exactly as the job sheet's own tag step records it.
export interface PopTagCandidate {
  jobId: string;
  // The Prisma JobType, as the job list carries it. Labelled on the phone by jobTypeLabel, never here.
  type: string;
  jobNumber: string | null;
  // Which end of THIS job the next tap will be, worked out on the server from the job's own taps.
  // Told to the phone so it can name the end; never a choice the engineer makes.
  nextEnd: "arrival" | "departure";
  // When this job's arrival was recorded (the earliest live arrival), or null while it has none.
  arrivedAt?: string | null;
  // True when this job has an arrival and no departure AND that arrival is inside the platform's
  // three-minute repeat window. A second press then reads, by the job's own taps, as the DEPARTURE, which
  // for a press that seemed to do nothing (or whose answer was lost with the signal) would fabricate a
  // visit of no length; so the phone ASKS before logging the leaving. OPTIONAL on the wire and read as
  // `=== true`: a server that predates it never sends it, and absent reads as "not recent", which is the
  // pre-review behaviour exactly.
  arrivedRecently?: boolean;
  // Session BDN: when the job is booked for (ISO), or null while it has no date, and how many London days
  // that is before today (0 for today or no date). OPTIONAL: an older server never sends them, and absent
  // reads as not overdue.
  scheduledFor?: string | null;
  daysOverdue?: number;
}

// Why a scan found nothing to match, when it found nothing. Says which of the caller's OWN situations it
// was and nothing about a site or a job.
export type PopTagNoMatchReason = "no_engineer" | "none_today" | "all_done" | "other_type";

export interface PopTagMatch {
  // The lift's own reference, as the tap route returns it.
  assetRef: string;
  // The lift's site name, the same for every candidate. "" whenever there is NO candidate: the server
  // gives a site's name only to somebody with an open job at the lift (the engineer-scope rule), so a
  // scan with no job to match is told which lift it was and nothing about whose building it is.
  siteName: string;
  // The caller's open service visits and call-outs at this lift that are booked for today, overdue or
  // undated (Session BDN), oldest booked date first. Empty when there
  // is none, which the phone says plainly and still records the tap.
  candidates: PopTagCandidate[];
  // Set only when `candidates` is empty. "no_engineer" (an account with no Engineer row), "none_today"
  // (nothing the tag matches is open at this lift), "other_type" (an open job here of a type logged on the
  // job sheet, named in otherType) or "all_done" (open jobs here, every one already fully logged), so the phone says the true thing for each. Optional: an older server never sends it.
  reason?: PopTagNoMatchReason | null;
  // Set only with reason "other_type": the JobType of the caller's open job here that the tag does not
  // match (it is logged on the job sheet).
  otherType?: string;
}

export interface PublicNfc {
  // "lift" or "equipment" (Session ACT): drives which strip the page renders.
  assetKind: "lift" | "equipment";
  // Session ACU: true when a (care-equipment) asset has been retired from service. The scan still
  // resolves, but the page shows an honest retired banner and no live due dates. Always false for a lift.
  retired: boolean;
  eleNumber: string;
  assetType: string;
  liftType: string | null;
  siteName: string;
  siteAddress: string;
  compliance: { status: ComplianceKey; label: string; detail: string };
  lastServiceDisplay: string | null;
  nextServiceDisplay: string | null;
  nextServiceOverdue: boolean;
  // The lift ELTI safety-inspection strip (lifts only; null for equipment).
  loler:
    | {
        state: LolerStateKey;
        label: string;
        detail: string;
        expiryDisplay: string | null;
        inspector: string | null;
      }
    | null;
  // The certificate-driven examination strip (equipment only; null for lifts).
  examination:
    | {
        state: EquipmentExamStateKey;
        label: string;
        detail: string;
        validUntilDisplay: string | null;
      }
    | null;
  serviceHistory: PublicNfcServiceVisit[];
  // Session ADY: deliberately NO openDefects (the fault list used to cross here and the page
  // printed it; the Director's privacy decision removed it -- no defect detail on the public tag).
}

// ---- Customer portal (passwordless magic-link; separate session cookie) ----
export type PortalLolerStateKey = "clear" | "advisory" | "defect" | "unknown";

export interface PortalDefect {
  description: string;
  severityLabel: string; // "Advisory" | "Action required"
  raisedDisplay: string | null;
}
export interface PortalServiceVisit {
  dateDisplay: string;
  typeLabel: string;
  outcome: string;
}
export interface PortalLolerStatus {
  state: PortalLolerStateKey;
  label: string;
  lastInspectionDisplay: string | null;
  nextDueDisplay: string | null;
  inspector: string | null;
}
export type PortalComplianceStatus = "green" | "amber" | "red";
export interface PortalLift {
  assetId: string;
  ref: string;
  assetType: string;
  // Session ACR: true for a care-equipment asset (grouped separately on the site page).
  // examState drives the honest examination line: "awaiting" = people-lifting kit with no
  // recorded exam; "not_required" = fixed-height kit. Optional for older cached responses.
  equipment?: boolean;
  // Session ACV: true for an additional item (gate / door / barrier); the site page heads
  // the equipment group honestly when the group holds any. Optional for older responses.
  additionalItem?: boolean;
  examState?: "known" | "awaiting" | "not_required";
  complianceStatus: PortalComplianceStatus; // the coloured dot
  complianceLabel: string; // "Up to date" | "Due soon" | "Overdue"
  hasLolerCert: boolean; // the site page shows a LOLER certificate download when true
  nextServiceDisplay: string | null;
  lolerDueDisplay: string | null;
  loler: PortalLolerStatus;
  openDefects: PortalDefect[];
  recentVisits: PortalServiceVisit[];
}
export interface PortalSitePpmCompliance {
  // Whole percent of planned services done on time over the last 12 months; null = not enough history yet.
  onTimePct: number | null;
}
export interface PortalSite {
  id: string;
  name: string;
  address: string;
  liftCount: number; // LIFTS only (Session ACR)
  equipmentCount?: number; // care-equipment assets at the site (Session ACR)
  worstStatus: PortalComplianceStatus;
  lifts: PortalLift[]; // every asset row; each carries its `equipment` flag
  ppmCompliance?: PortalSitePpmCompliance | null;
  // The site's current signed service agreement, when retrievable (Session ADZ). Only the
  // contract id crosses; the PDF is served by the ownership-scoped proxy (serviceAgreementHref).
  serviceAgreement?: { contractId: string } | null;
  // Site-specific documents (Session AFE): site RAMS / risk assessments, served by the
  // ownership-scoped proxy (siteDocumentHref). Optional for older cached responses.
  siteDocuments?: PortalSiteDocumentRow[];
}
export interface PortalInvoice {
  amountDisplay: string;
  dueDateDisplay: string | null;
  statusLabel: string;
  payUrl: string | null;
}
// Phase 8 (Session ATJ, M-9a): the full portal Invoices view (outstanding + paid, live status).
export interface PortalInvoiceListRow {
  id: string;
  invoiceNumber: string | null;
  amountDisplay: string;
  dueDateDisplay: string | null;
  statusLabel: string;
  paid: boolean;
  payUrl: string | null;
  canPayOnline: boolean;
}
export interface PortalSummary {
  totalLifts: number;
  liftsWithCurrentLoler: number;
  liftsOverdueForService: number;
}
export interface PortalOverview {
  customerName: string;
  summary: PortalSummary;
  sites: PortalSite[];
  outstandingInvoices: PortalInvoice[];
  invoicesUnconfirmed?: boolean;
  // The company document library (Session AFE), grouped by category server-side.
  // Optional so older cached responses render unchanged (absent = no section).
  companyDocuments?: PortalDocumentGroup[];
}

// ---- Portal document library + site history (Session AFE) ----
export interface PortalDocumentRow {
  id: string;
  label: string;
  validUntilDisplay: string | null; // "Valid to" line, pre-formatted server-side
}
export interface PortalDocumentGroup {
  category: string;
  categoryLabel: string;
  documents: PortalDocumentRow[];
}
export interface PortalSiteDocumentRow extends PortalDocumentRow {
  categoryLabel: string;
}

// ---- Portal compliance document library (Session AFH) ----
// ELE's static on-disk Health and Safety (ohs) and RAMS (rams) documents, exposed
// read-only to the signed-in customer. Downloads go through the same-origin proxy
// (complianceDocHref); the server serves the file from disk, gated by the portal cookie.
export interface PortalComplianceDocItem {
  filename: string;
  displayName: string;
  kind: string; // PDF | DOCX
}
export interface PortalComplianceDocGroup {
  category: string; // "ohs" | "rams"
  label: string;
  documents: PortalComplianceDocItem[];
}
// Session AMX: which document opens this row, and whether it is ready. `reportSource` is
// deliberately NOT called `source` -- PortalHistoryWorkRow below already owns that name for the
// Repairs/Projects tab split. Exactly one of jobId/calloutId is set, matching reportSource.
export interface PortalHistoryVisitRow {
  dateDisplay: string;
  liftRef: string;
  outcome: string | null; // "No faults found" for a clean service visit; null otherwise
  reportSource: "job" | "callout";
  jobId: string | null;
  calloutId: string | null;
  reportAvailable: boolean; // false = the report is not finished yet, so the row does not open
}
export interface PortalHistoryWorkRow {
  dateDisplay: string | null;
  label: string; // "Repair works" / "Technician visit" / "Modernisation works" / ...
  reportLabel?: string; // Session TV4: what the report is called when it is not a repair report
  title: string | null; // the project title; null for a repair visit
  liftRef: string | null;
  // Session AFH fix: "job" (a completed repair job) vs "project" (a completed Project). The site
  // page splits Repairs and Projects on this, not on a label substring. NOT the same question as
  // PortalHistoryVisitRow.reportSource above, which names the ROUTE that serves a row's PDF.
  source: "job" | "project";
  // Session AMX. A works row is only ever a job-sourced repair or a project, never CallOut-sourced:
  // a job-less call-out resolves to the generic "callout" and lands in the Call-outs section.
  // A project row keeps jobId null and reportAvailable false (project documents are out of scope).
  jobId: string | null;
  reportAvailable: boolean;
}
export interface PortalSupplementaryTestRow {
  liftRef: string;
  testType: string;
  lastTestDisplay: string | null; // null = not yet carried out
}
// Session BDB: the portal's SAFed section. Test names and plain states only: no source wording, no price, no engineer.
export interface PortalSiteSafed {
  lifts: {
    liftRef: string | null;
    tests: { name: string; state: "recommended" | "authorised" | "booked" | "done"; stateLabel: string; lastTestDisplay: string | null }[];
    certificates: { jobId: string; reference: string; dateDisplay: string }[];
  }[];
}
export interface PortalSiteHistory {
  serviceVisits: PortalHistoryVisitRow[];
  callouts: PortalHistoryVisitRow[];
  works: PortalHistoryWorkRow[];
  supplementaryTests: PortalSupplementaryTestRow[];
  supplementaryAnnexes: PortalDocumentRow[];
}

// ---- Cross-site history feeds (Session AFH) ----
// The customer's SERVICE and CALL-OUT history across ALL their sites, for the two top-level
// portal sections. Each row names its site; outcome carries the clean-service verdict only.
export interface PortalHistoryFeedRow {
  dateDisplay: string;
  siteName: string;
  liftRef: string;
  outcome: string | null; // "No faults found" for a clean service visit; null otherwise (always null for a call-out)
  // Session AMX, as on PortalHistoryVisitRow above.
  reportSource: "job" | "callout";
  jobId: string | null;
  calloutId: string | null;
  reportAvailable: boolean;
  reportLabel?: string; // Session TV4: what the report is called when it is not a repair report
}
export interface PortalHistory {
  serviceVisits: PortalHistoryFeedRow[];
  callouts: PortalHistoryFeedRow[];
  // Session AMX: repairs get their own top-level section. Like a call-out, a repair carries no
  // verdict; only a service visit gets "No faults found".
  repairs: PortalHistoryFeedRow[];
  // Session AUD (email system Phase 5, Item 3): approved condition reports, their own section. Optional
  // only so a page reading an older server's reply treats a missing list as empty.
  conditionReports?: PortalHistoryFeedRow[];
}

// Session AUD (Item 3): the customer's OPEN work (GET /api/portal/open-work), an explicit allow-list:
// what it is, where, the stage in plain words, the booked day and the engineer's FIRST name only.
export type PortalOpenWorkKind = "callout" | "repair" | "service";
export interface PortalOpenWorkRow {
  key: string;
  kind: PortalOpenWorkKind;
  kindLabel: string;
  siteName: string;
  liftRef: string;
  statusLabel: string;
  bookedDateDisplay: string | null;
}
// Session AUD (Item 6): what a fault report returns. `reference` is the call-out's own reference when
// one was set at intake (usually none); the page then says the office will be in touch.
export interface PortalFaultReported {
  reference: string | null;
  liftRef: string | null;
}

// ---- Customer portal admin (office/admin staff cookie) ----
export interface PortalAdminUser {
  id: string;
  email: string;
  name: string | null;
  customerId: string;
  // Session SC2B: the customer's name, from the server (null only for a login whose customer record is gone).
  customerName?: string | null;
  lastLoginAt: string | null;
  disabledAt: string | null;
  createdAt: string;
}

// The create endpoint echoes a subset of the row (no login/disabled state yet).
export type CreatedPortalUser = Pick<PortalAdminUser, "id" | "email" | "customerId" | "name" | "createdAt">;

// Session BCC (D-BCB-4): the removal of an archived customer's portal logins. The plan carries masked emails only.
export interface RemovePortalUsersPlan {
  customer: { id: string; name: string };
  count: number;
  logins: { id: string; maskedEmail: string; lastLoginAt: string | null; disabled: boolean }[];
  callOutsUnlinked: number;
}
export type RemovePortalUsersResponse =
  | { preview: true; allowed: true; plan: RemovePortalUsersPlan; afterRemoval: { canDelete: boolean; message: string | null } }
  | { removed: true; count: number; plan: RemovePortalUsersPlan };

// ---- Subcontractor Portal (Session ATA) ------------------------------------------------------------------------------
// The server's allow-listed shapes (server/src/modules/subcontractorPortal/subcontractorPortal.ts): no customer, no
// access details, no description, no price or pay, no timesheet note; the address only while work is open.
export interface SubPortalJob {
  id: string;
  reference: string | null;
  type: string;
  typeLabel: string;
  status: string;
  statusLabel: string;
  open: boolean;
  scheduledFor: string | null;
  completedAt: string | null;
  engineers: { name: string; role: "lead" | "crew" }[];
  accepted: boolean | null;
  site: { name: string; address: string | null; postcode: string | null } | null;
  eleNumber: string | null;
  arrivedAt: string | null;
  leftAt: string | null;
}
export interface SubPortalJobsView {
  company: string;
  engineers: { name: string; active: boolean }[];
  open: SubPortalJob[];
  recent: SubPortalJob[];
  recentDays: number;
}
// Session AUZ: the portal's documents (its one write) and the subcontractor's own rates.
export interface SubPortalDocument {
  typeLabel: string;
  description: string | null;
  engineerName: string | null;
  certificateNumber: string | null;
  expiryDate: string | null;
  submittedAt: string;
  reviewState: "submitted" | "approved" | "rejected" | "expired";
  reviewLabel: string;
  reason: string | null;
}
export interface SubPortalDocumentType {
  key: string;
  label: string;
  category: "compliance" | "nvq" | "insurance" | "other";
  personal: boolean;
  expiry: "required" | "optional" | "none";
  certificateNumber: boolean;
}
export interface SubPortalDocumentsView {
  documents: SubPortalDocument[];
  engineers: { name: string }[];
  types: SubPortalDocumentType[];
}
export interface SubPortalRatesView {
  present: boolean;
  effectiveFromLabel: string | null;
  /** False when the signed agreement carries no clause 12.5: one column is shown and no Labour Only column is named (Session AUZ fix-up). */
  labourOnlyColumn: boolean;
  appliesNow: "bona_fide" | "labour_only" | null;
  appliesNowLabel: string | null;
  columnsIdentical: boolean;
  columns: { name: string; lines: { label: string; unit: string; bonaFide: string; labourOnly: string | null }[] }[];
  /** Session RC1: how the card is paid, in plain words, from the card in force. Shown under the table. */
  rules?: string[];
  /** Session RC1: a supply and fit company has no rates; the tab shows only this sentence. */
  supplyAndFit?: boolean;
  supplyAndFitMessage?: string | null;
}

// Session AVB: the status of the invoices a subcontractor has sent ELE, read from the bills the office entered in Xero on the
// subcontractor's own payable contact (server/src/modules/subcontractorInvoices/invoiceStatus.ts). Reference and total
// only; "not_available" is never a guess and never read as paid. The office reads the same object (admin only).
export type SubInvoiceState = "received" | "approved" | "paid" | "not_available";
export interface SubInvoiceRow {
  reference: string | null;
  totalLabel: string | null;
  state: SubInvoiceState;
  label: string;
  detail: string;
  paidOn: string | null;
  paidOnLabel: string | null;
}
export interface SubInvoiceStatusView {
  available: boolean;
  message: string;
  invoices: SubInvoiceRow[];
  readAt: string | null;
  truncated: boolean;
}
// Session RC2: a supply and fit company's XERO purchase orders (not the platform's van parts orders), admin only, never on
// the portal. A read that cannot be made carries its reason in one sentence, never an empty list.
export interface XeroPurchaseOrderRow {
  number: string | null;
  date: string | null;
  deliveryDate: string | null;
  reference: string | null;
  total: number | null;
  currency: string | null;
  status: string | null;
}
export type XeroPurchaseOrdersView =
  | { available: true; orders: XeroPurchaseOrderRow[]; truncated: boolean; readAt: string; monthsBack: number }
  | { available: false; reason: string; message: string };

export interface SubInvoiceStatusOffice {
  view: SubInvoiceStatusView;
  unavailableReason: string | null;
  officeNote: string | null;
}

export interface SubPortalTimesheetEntry {
  date: string;
  typeLabel: string;
  startTime: string | null;
  endTime: string | null;
  minutes: number | null;
  siteName: string | null;
  eleNumber: string | null;
  jobTypeLabel: string | null;
}
export interface SubPortalEngineerWeek {
  engineerName: string;
  status: string;
  statusLabel: string;
  approvedAt: string | null;
  totals: { travelMins: number; onSiteMins: number; otherMins: number; totalMins: number };
  days: { date: string; entries: SubPortalTimesheetEntry[] }[];
  visitsAwaitingApproval: number;
  // Session AVA: the week's statement of hours, when the office has released it here; `ref` is the download handle.
  statement: { ref: string; filename: string } | null;
}
export interface SubPortalTimesheetView {
  weekStarting: string;
  weekLabel: string;
  engineers: SubPortalEngineerWeek[];
  beyondLookback?: boolean;
}
// ---- The weekly statement of hours (Session AVA): hours only, never money ----
export interface TimesheetStatementLine {
  date: string;
  dateLabel: string;
  reference: string | null;
  workLabel: string;
  siteName: string | null;
  eleNumber: string | null;
  arrival: string | null;
  departure: string | null;
  onSiteMinutes: number;
  flatFee: boolean;
  travel: { label: string; minutes: number }[];
}
export interface TimesheetStatement {
  heading: string;
  engineerName: string;
  company: string | null;
  weekStart: string;
  weekLabel: string;
  status: string;
  lines: TimesheetStatementLine[];
  other: { date: string; dateLabel: string; label: string; minutes: number }[];
  toBeConfirmed: { date: string | null; dateLabel: string; reference: string | null; workLabel: string; siteName: string | null; eleNumber: string | null; reason: string; reasonText: string }[];
  totals: { onSiteMinutes: number; travelMinutes: number; totalMinutes: number; otherMinutes: number; jobs: number; flatFeeVisits: number };
}
export interface TimesheetStatementView {
  statement: TimesheetStatement;
  approved: boolean;
  filename: string;
  subcontractor: { company: string; email: string; archived: boolean } | null;
  release: { inForce: boolean; releasedAt: string | null; releasedByName: string | null; supersededByApproval: boolean; emailDraftedAt: string | null };
  refusals: { generate: string | null; portal: string | null; emailDraft: string | null };
}
export interface TimesheetStatementReleasePreview {
  preview: true;
  via: "portal" | "email_draft";
  ok: boolean;
  refusal: string | null;
  company: string | null;
  to: string | null;
  subject: string | null;
  attachment: string | null;
  alreadyReleased: boolean | null;
}
export interface SubcontractorPortalLogin {
  id: string;
  email: string;
  name: string | null;
  lastLoginAt: string | null;
  disabledAt: string | null;
  createdAt: string;
}

// ---- Quotes (office/admin) ----
// Session SC2C: the filters, search, sort and page GET /api/quotes takes when the Quotes page asks for 50 at a time.
export interface QuoteListPageOptions {
  ref?: string;
  customerId?: string;
  reason?: string;
  tier?: string;
  liftType?: string;
  month?: string;
  from?: string;
  to?: string;
  /** A status / funnel filter, or "groupable" (the quotes a portfolio can take). */
  filter?: string;
  q?: string;
  sort?: string;
  dir?: "asc" | "desc";
  limit: number;
  offset: number;
}

export interface QuoteListItem {
  id: string;
  quoteRef: string;
  customerName: string | null;
  customerEmail: string | null;
  status: string;
  createdAt: string;
  openedAt: string | null; // first time the customer opened the accept link (null = not opened)
  // Session AAV: when the quote was last issued to the customer (null = never sent). The funnel
  // filters read this so a lost/voided quote is classified by send evidence, not status alone.
  sentAt?: string | null;
  validUntil?: string | null; // Session AAT: the stored expiry instant (ISO); null for legacy rows
  token: string | null; // the customer accept-link token (/quote/:token)
  customer: { id: string; name: string } | null;
  site: { id: string; name: string } | null;
  // Session AIE: the address Send would actually use, resolved server-side by the same chain the
  // /send and /resend routes use (the quote's own contact email, else the payload copy, else the
  // linked customer's billing email). Null means Send would refuse, so the list disables the button
  // and says why instead of offering an action that 400s. Absent on a legacy cached response.
  recipientEmail?: string | null;
  // Session ABZ: the site name + address entered on the quote (from the payload), shown under the
  // quote reference on the office list so same-day quotes for one customer are told apart. Either
  // may be null (a quote without them).
  siteName?: string | null;
  siteAddress?: string | null;
  // Session AAO: true when the customer accepted at a frequency different from the quoted one and
  // the office has not yet confirmed it. The ESA is held until the office confirms via the gate.
  // Session AIK: a RENEWAL awaiting a cadence sign-off carries the same marker, deliberately, so the
  // office has one queue rather than two. Its ESA is NOT held (a renewal always signs at the current
  // cadence), and it is decided on the contract page, not in the AAO panel: see renewalOfContractId.
  frequencyChangePending?: boolean;
  // Session AIK: set when this quote renews a contract, naming the contract it renews. Null on new
  // business.
  renewalOfContractId?: string | null;
  // Session AAQ: present when a manual price override was applied, so the quotes list can show the
  // floor bypass, its reason and who set it. Null/absent when no override.
  override?: QuoteOverrideSummary | null;
  // Session AAT (QUOTING DEPTH Loop 1): win/loss capture. Present on a quote marked lost; the
  // status chip and the "lost" filter link read these (reason carries only id + label).
  lostReason?: { id: string; label: string } | null;
  lostNote?: string | null;
  lostAt?: string | null;
  // Session AAV (Loop 1): derived from the payload so the analytics dashboard's tier and
  // lift-type segments can deep-link to this list filtered by them.
  recommendedTier?: "core" | "care" | "complete";
  liftType?: string | null;
  // Session AQW (Q-4): the recommended tier's annual value, reusing the same server-side helper
  // (quoteAnnualValue) the dashboard's Active Quotes panel already derives this from.
  annualValue?: number | null;
  // Session ARP (Q-4): the tier the customer accepted, present only while the quote is accepted or
  // signed (server acceptedTierOf); null otherwise, when the list shows the recommended tier instead.
  acceptedTier?: "core" | "care" | "complete" | null;
  // Session ARP (Q-4): that accepted tier's annual figure (server acceptedTierValueOf), so the list's
  // Value column prices the tier its Tier column shows. Null when there is no accepted tier.
  acceptedAnnualValue?: number | null;
  // Session ARP (Q-4): the portfolio this quote is grouped into (id + name only), for the list's
  // portfolio column. Null when ungrouped; absent on a legacy cached response.
  portfolio?: { id: string; name: string } | null;
  // The contract rendered from this quote (annual value, billing cadence and status only). A same-tier
  // renewal record has no tier prices of its own, so the list prices it from this. Null when the quote
  // has produced no contract; absent on a legacy cached response.
  contract?: { annualValue: number; billingFrequency: string; status: string } | null;
}

// Session AAV (Loop 1): the quoting analytics aggregate (GET /api/quotes/analytics).
// The from/to range selects the quote cohort by creation date.
export interface QuoteAnalyticsSegment {
  count: number;
  annual: number;
}
export interface QuoteAnalytics {
  totals: { quotes: number; sent: number; averageAnnual: number | null; medianDaysToAccept: number | null };
  funnel: { sent: number; opened: number; accepted: number; signed: number };
  winLoss: {
    won: QuoteAnalyticsSegment;
    lost: QuoteAnalyticsSegment;
    declined: QuoteAnalyticsSegment;
    reasons: { id: string | null; label: string; count: number; annual: number }[];
  };
  pipeline: {
    live: QuoteAnalyticsSegment;
    awaitingSignature: QuoteAnalyticsSegment;
    signed: QuoteAnalyticsSegment;
  };
  byTier: { tier: string; label: string; count: number; annual: number }[];
  byLiftType: { key: string; label: string; count: number }[];
  monthly: { month: string; priced: number; sent: number; signed: number; signedAnnual: number }[];
  // Session ASH (Q-9): project quotations created in the same range, their OWN segment (never in the funnel above).
  // Optional so a response from a server not yet redeployed simply shows no project segment.
  projects?: ProjectQuotationAnalytics;
}

// Session ASH (Q-7, Q-9): the quote pipeline (GET /api/quotes/pipeline). Stages mirror src/lib/quoteFunnel.ts.
export type PipelineStageKey = "drafting" | "with_customer" | "won" | "lost" | "closed";
export type PipelineSourceKey = "quote" | "project" | "repair_estimate";
export interface PipelineTotals {
  count: number;
  value: number; // pounds
}
export interface QuotePipelineItem {
  source: PipelineSourceKey;
  id: string;
  ref: string;
  title: string | null;
  stage: PipelineStageKey;
  status: string;
  value: number | null;
  valueBasis: "annual" | "one_off";
  customerName: string | null;
  siteName: string | null;
  ownerName: string | null;
  date: string;
}
export interface QuotePipeline {
  stages: (PipelineTotals & { stage: PipelineStageKey; label: string; bySource: Record<PipelineSourceKey, PipelineTotals> })[];
  renewals: PipelineTotals & { items: QuotePipelineItem[] };
  items: QuotePipelineItem[];
}
export interface ProjectQuotationAnalytics {
  total: number;
  value: number;
  stages: Record<PipelineStageKey, PipelineTotals>;
  sent: number;
  won: number;
  conversionPct: number | null;
}

// Session ASH (Q-10): a quote's office-only owner and notes, and its office documents.
// Session AUJ: the person named to sign a quote's agreement when it is not the quote's own contact.
export interface SignatoryInput {
  name: string;
  email: string;
}

export interface QuoteSignatoryView {
  quoteId: string;
  quoteRef: string;
  status: string;
  contact: { name: string | null; email: string | null };
  officeSignatory: SignatoryInput | null;
  customerSignatory: SignatoryInput | null;
  signer: { name: string; email: string | null };
  signerSource: "customer" | "office" | "contact";
  envelopeSent: boolean;
  canChange: boolean;
}

export interface QuoteInternal {
  quoteId: string;
  quoteRef: string;
  owner: { id: string; name: string } | null;
  internalNotes: string | null;
}
export interface QuoteOwnerOption {
  id: string;
  name: string;
}
export interface QuoteDocumentItem {
  id: string;
  quoteId: string;
  label: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number | null;
  uploadedById: string | null;
  uploadedAt: string;
}

// Session AAT: a quote lost-reason lookup row (GET /api/quote-lost-reasons). quoteCount drives the
// delete-vs-deactivate control (a referenced reason cannot hard-delete).
export interface QuoteLostReason {
  id: string;
  label: string;
  active: boolean;
  quoteCount: number;
  createdAt: string;
}

// Session AAT: one append-only quote version, with the field-level diff to the state that followed
// it (the next version, or the live quote for the newest).
export interface QuoteVersionChange {
  field: string;
  from: string;
  to: string;
}
export interface QuoteVersionItem {
  id: string;
  versionNumber: number;
  createdAt: string;
  createdBy: string | null;
  changes: QuoteVersionChange[];
}

// Session AAT: a reusable quote template (a saved calculator config with no customer/site identity).
export interface QuoteTemplate {
  id: string;
  name: string;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

// One quote with its full record (GET /api/quotes/:id). Session AQW (Q-1, the Quote Hub): widened
// from the original 6-field type (the route sends ~25 fields; only 6 were ever typed) to cover
// what the Hub actually reads, reusing the SAME derived shape the list already computes
// (shapeQuoteListRow, server/src/modules/quotes/quote.routes.ts) plus the raw payload the
// calculator's Resume/Edit flow still needs. Every field below genuinely arrives on the wire today
// -- this is not aspirational.
export interface QuoteDetail {
  id: string;
  quoteRef: string;
  status: string;
  customerId: string | null;
  siteId: string | null;
  customerName: string | null;
  customerEmail: string | null;
  createdAt: string;
  openedAt: string | null;
  sentAt: string | null;
  validUntil: string | null;
  token: string | null;
  pdfUrl: string | null;
  signableEnvelopeId: string | null;
  lostNote: string | null;
  lostAt: string | null;
  lostReason: { id: string; label: string } | null;
  quoteNarrative: string | null;
  quoteExplanation: string | null;
  customer: { id: string; name: string } | null;
  site: { id: string; name: string } | null;
  // Session AIX: the real column (see prisma/schema.prisma Quote.portfolioId), not part of
  // payload. Forwarded to the calculator's resume state so its live pricing preview can resolve
  // this quote's actual portfolio band while editing, matching what the revise route already
  // resolves server-side on save.
  portfolioId?: string | null;
  portfolio: { id: string; name: string } | null;
  // The same derived fields the office list already shows (Q-4/Q-5's inputs): never a second,
  // independent computation.
  frequencyChangePending?: boolean;
  renewalOfContractId?: string | null;
  override?: QuoteOverrideSummary | null;
  recommendedTier?: "core" | "care" | "complete";
  // Session ARP: the accepted tier on an accepted or signed quote (see QuoteListItem).
  acceptedTier?: "core" | "care" | "complete" | null;
  liftType?: string | null;
  annualValue?: number | null;
  siteName?: string | null;
  siteAddress?: string | null;
  // Session ARP: the address Send / Resend would actually use, resolved server-side by the same chain
  // as the list row's recipientEmail, so the Quote Hub's send confirms name it. Null means Send would
  // refuse. Absent on a response from a server older than ARP.
  recipientEmail?: string | null;
  // The stored quote payload (incl. calculatorState, used to Resume/edit in the calculator; the
  // Hub also parses tiers/lifts/frequency/discount and the acceptance keys straight out of this,
  // the same way the PDF route and the calculator's own Resume flow already do -- those keys are
  // not columns, they are payload JSON, per the locked QUOTE DUPLICATE RULE).
  payload?: Record<string, unknown> | null;
}

// Session AAO: the office confirm-frequency panel. The reprice preview returns each tier's total at
// the candidate per-lift frequencies; the confirm result reports the ESA send + the bound total.
export interface QuoteFrequencyPreview {
  frequency: string; // the billing (shortest) cadence across the lifts
  visitsYr: number;
  tiers: Record<"core" | "care" | "complete", { annual: number; monthly: number }>;
}

export interface QuoteFrequencyConfirmResult {
  ok: boolean;
  esaSent: boolean;
  status: string;
  quoteRef: string;
  billingFrequency: string;
  annualValue: number;
}

export interface QuoteTierPricing {
  annual: number; // ex VAT -- the ALL-IN site total (lift maintenance + flat extras)
  // ACP care equipment model: the tier's annual EXCLUDING care equipment (the mixed-quote
  // tier-card figure; liftAnnual + careHomeTotal === annual). Optional so the client
  // tolerates an older server payload during a rolling deploy (falls back to annual).
  liftAnnual?: number;
  maintenanceAnnual: number; // lift maintenance only (annual minus the flat extras)
  monthly: number; // annual / 12, ex VAT
  monthlyIncVat: number; // annual x 1.2 / 12 (the billing display)
  rangeLow: number; // -15% indicative low
  rangeHigh: number; // +20% indicative high
  call1: number; // first-hour call-out rate
  callAdd: number; // additional-hour rate
  callOOH: number; // out-of-hours first-hour rate
  promoMonthly: number | null; // discounted monthly during the promo window, if any
}

export interface QuoteLineItem {
  description: string;
  price: number; // unit price per year, ex VAT (equipment: the catalogue price at the minimum frequency)
  qty?: number;
  // ACP care equipment model (care-home rows only): the item's LOLER minimum visits/yr
  // (1 = yearly for fixed-height items, 2 = six-monthly for people-lifting items) and
  // the chosen visits/yr (1 / 2 / 4, never below the minimum). Absent on additional
  // items and on legacy flat equipment rows.
  minVisitsYr?: number;
  chosenVisitsYr?: number;
  // Session ACR (care-home rows only): the office-declared last LOLER examination date, if
  // known ("yyyy-mm-dd"). Data only -- never priced; it rides the stored payload so contract
  // activation can seed the equipment asset's examination clock instead of fabricating one.
  lastExamDate?: string;
  // Session ACS (additional items only): true when the item rides the contract's tier
  // coverage (the entered price is its CORE price; Care/Complete scale proportionally on
  // the server). Absent/false = a flat maintenance-only line, the pre-ACS behaviour.
  tiersOn?: boolean;
  // Session ACV (covered additional items on a LIFT-LESS quote only): the item's service
  // cadence in visits/yr (1 yearly / 2 six-monthly / 4 quarterly). Scheduling data only,
  // never a price input; the calculator sends it only when the quote has no lifts.
  serviceVisitsYr?: number;
}

export interface QuotePromoInput {
  on: boolean;
  pct: number; // 0-100
  months: number;
  reason: string;
}

export interface QuoteOverrideInput {
  on: boolean;
  value: number; // forced annual price for `tier`
  tier: "core" | "care" | "complete";
  // Session AAQ: the office justification for the override (it bypasses the contract floor).
  // Optional on the preview; REQUIRED on save (the server rejects a save without it). Who set it
  // and when are stamped server-side, never sent from here.
  reason?: string;
}

// Session AAQ: the office-facing summary of a manual price override on a saved quote (derived from
// the payload by the list endpoint). Shown on the quotes list, the office's de facto quote detail.
export interface QuoteOverrideSummary {
  tier: string;
  value: number;
  reason: string | null;
  setByName: string | null;
  setAt: string | null;
}

// Soft floor (Session ANR, gap analysis item C59). A narrower control than QuoteOverrideInput: it
// does not force a figure, it only confirms the office wants a naturally-computed price to stand
// even though it falls below the contract floor. Same accepted-on-preview / required-on-save shape.
export interface QuoteFloorOverrideInput {
  on: boolean;
  reason?: string;
}

// Populated on the preview/save response whenever at least one lift's raw (unfloored) price falls
// below its contract floor, REGARDLESS of whether floorOverride was confirmed -- the calculator needs
// this to warn even before the office has decided. Null on the ordinary quote.
export interface QuoteFloorBreach {
  floorAnnual: number;
  rawAnnual: number;
  shortfall: number;
}

export interface QuotePreview {
  numLifts: number;
  visitsYr: number; // visits/year for the billing (shortest) frequency
  multiDiscount: number;
  // Session AHC: the lift count the multi-lift band was decided on. Greater than numLifts when the
  // customer's WIDER PORTFOLIO earned the band. Optional so the client tolerates an older server
  // payload during a rolling deploy.
  discountLiftCount?: number;
  discountBasis?: "quote" | "portfolio";
  // Office-only explanation of where discountLiftCount came from, so a money-moving number can be
  // checked rather than taken on trust. Preview only.
  portfolioBreakdown?: { total: number; contractedLifts: number; quotedLifts: number };
  // Combined per-visit surcharge derived from the site postcode (CCZ £18 + Westminster/
  // K&C £20; a postcode can attract both, e.g. W1K = £38). 0 outside the zones.
  surchargePerVisit: number;
  // The per-charge breakdown behind surchargePerVisit (Session AAE). Optional so the
  // client tolerates an older server payload during a rolling deploy.
  surcharges?: {
    ccz: boolean;
    westminsterParking: boolean;
    cczPerVisit: number;
    parkingPerVisit: number;
    cczAnnual: number;
    parkingAnnual: number;
    annualTotal: number;
  };
  // Session AAQ: how the surcharge is presented on this quote (default "show"). "fold" absorbs it
  // into the maintenance headline (no line); "waive" drops it (surchargePerVisit becomes 0).
  surchargePresentation?: "show" | "fold" | "waive";
  // The raw detection regardless of presentation (office/internal only), so the calculator can show
  // what was folded or waived. Equals `surcharges` in "show" mode; the customer never sees this.
  detectedSurcharges?: {
    ccz: boolean;
    westminsterParking: boolean;
    cczPerVisit: number;
    parkingPerVisit: number;
    cczAnnual: number;
    parkingAnnual: number;
    annualTotal: number;
  };
  // Extras, echoed for the breakdown/PDF: kept as separate totals (each is its own
  // section on the quote). Never multi-lift-discounted. Since Session ACS the totals
  // cover the FLAT extras only (tiers-off additional items + equipment); a tier-covered
  // additional item's money is per tier, in additionalCovered below.
  additionalItems: QuoteLineItem[];
  careHomeItems: QuoteLineItem[];
  additionalItemsTotal: number; // sum of the tiers-off additionalItems (qty x price), annual ex VAT
  careHomeTotal: number; // sum of the equipment row annuals (frequency-priced), whole pounds
  extrasTotal: number; // additionalItemsTotal + careHomeTotal (the flat extras), annual ex VAT
  // Session ACS: the tier-covered additional items (tiersOn rows) with their per-tier
  // prices and totals. Null when no row is covered; optional so the client tolerates an
  // older server payload during a rolling deploy. Each tier's annual/maintenanceAnnual
  // already includes its own figure from this block.
  additionalCovered?: {
    rows: {
      description: string;
      qty: number;
      unit: { core: number; care: number; complete: number };
      annual: { core: number; care: number; complete: number };
      // Session ACV: the item's service cadence on a LIFT-LESS quote (1 / 2 / 4);
      // null on a quote with a lift. Optional for older server payloads.
      serviceVisitsYr?: number | null;
    }[];
    totals: { core: number; care: number; complete: number };
  } | null;
  // ACP care equipment model: the separate equipment block (each row's cadence +
  // computed annual and the exact total). Null when no equipment rows; optional so
  // the client tolerates an older server payload during a rolling deploy.
  equipment?: {
    rows: {
      description: string;
      qty: number;
      cataloguePrice: number;
      minVisitsYr: number | null;
      visitsYr: number | null;
      unitAnnual: number;
      annual: number;
    }[];
    annualTotal: number;
    // Session ACS item A: set when the equipment single-item site minimum lifted the
    // lone row's annual to visitsYr x the Core visit rate (office workings note only).
    singleItemMinimum?: { perVisit: number; annual: number } | null;
  } | null;
  costFloor: number; // internal margin floor (never shown to the customer)
  // Session AAT: total planned SERVICE labour hours per year across the lifts. Office/internal only
  // (never shown to the customer); the basis for the office margin display. Optional so the client
  // tolerates an older server payload during a rolling deploy.
  serviceHoursYr?: number;
  // The billing frequency: the shortest (most frequent) across the lifts. Drives
  // the displayed billing period and the contract's billing cadence.
  billingFrequency: QuoteFrequencyKey;
  tiers: { core: QuoteTierPricing; care: QuoteTierPricing; complete: QuoteTierPricing };
  // Session ACB Item 9: per-lift annual maintenance per tier (in input order), AFTER the multi-lift
  // discount and including that lift's own manual-door fee. Optional so the client tolerates an older
  // server payload during a rolling deploy. The calculator's per-lift override control reads this to
  // pre-fill from the lift's TRUE in-quote contribution (not the undiscounted priced-alone figure).
  perLift?: { core: number; care: number; complete: number }[];
  promo: QuotePromoInput | null;
  override: (QuoteOverrideInput & { applied: true }) | null;
  // Soft floor (Session ANR, C59). Optional so the client tolerates an older server payload during a
  // rolling deploy.
  floorBreach?: QuoteFloorBreach | null;
  floorOverride?: (QuoteFloorOverrideInput & { applied: true }) | null;
}

// Age bands (V1 thresholds, restored by Director correction 10 July 2026): 0-15 /
// 16-25 / 25+. The client only ever sends the current bands (derived by
// ageBandFromYear); the legacy ACP-era keys ("0-20", "21-25") live only in payloads
// stored server-side during the hours the ACP thresholds were live.
export type QuoteAgeBand = "0-15" | "16-25" | "25+";
export type QuoteFrequencyKey = "monthly" | "bi_monthly" | "quarterly" | "six_monthly" | "annual";
export type QuoteDurationKey = "standard" | "extended" | "long";

// One configured lift on a quote. liftType is an engine key (the server validates
// it); stops, ageBand, duration and manualDoors are priced by the locked engine;
// frequency is per-unit. manufacturer/serial/year are recorded-only metadata that
// pre-populates the Asset on acceptance (not priced).
export interface QuoteLiftInput {
  label?: string; // optional lift name (shown on the contract spec table)
  liftType: string;
  customMultiplier?: number; // Custom / Other type only (clamped 0.10..5 server-side)
  stops?: number;
  ageBand?: QuoteAgeBand;
  duration?: QuoteDurationKey;
  manualDoors?: boolean;
  manufacturer?: string;
  serial?: string;
  year?: number;
  // Recorded-only documentation (not priced): capacity / controller / door type.
  // Shown on the quote PDF lift-specification table and logged for analytics.
  capacity?: string;
  controller?: string;
  door?: string;
  frequency: QuoteFrequencyKey;
  // Session ACB Item 9: a per-lift MANUAL price override. Forces this lift's annual maintenance to a
  // fixed figure for the recommended tier (QuotePreviewInput.recommendedTier). Office tool only.
  priceOverride?: number;
}

export interface QuotePreviewInput {
  lifts: QuoteLiftInput[];
  // Session ACB Item 9: the tier the office is quoting (default core). A per-lift priceOverride
  // applies to THIS tier only, so overriding one lift never flattens all three tiers to one figure.
  recommendedTier?: "core" | "care" | "complete";
  // Site postcode: the per-visit surcharge (CCZ £18 / Westminster and K&C £20) is
  // derived from it server-side. The frontend never computes pricing.
  sitePostcode?: string;
  // Session AAQ: per-quote surcharge presentation (default "show"). Presentation/inclusion only.
  surchargePresentation?: "show" | "fold" | "waive";
  anchor?: number; // base £/visit (default 160); the admin can move the whole model
  additionalItems?: QuoteLineItem[];
  careHomeItems?: QuoteLineItem[];
  promo?: QuotePromoInput;
  override?: QuoteOverrideInput;
  floorOverride?: QuoteFloorOverrideInput;
}

// Session AAU (Loop 4): AI site-config assist result (advisory suggested lifts).
export interface ConfigAssistLift {
  liftType: string;
  stops?: number;
  year?: number;
  frequency: string;
  label?: string;
  note?: string;
}
export interface ConfigAssistResult {
  lifts: ConfigAssistLift[];
  note: string;
}

// Session AAU (Loop 3): a scenario-matrix row (every tier priced at one frequency).
export interface ScenarioRow {
  frequency: string;
  label: string;
  allowed: boolean;
  visitsYr: number;
  tiers: { core: number; care: number; complete: number } | null;
}

// Session AAU (Loop 2): portfolio quoting.
export interface QuotePortfolioListItem {
  id: string;
  name: string;
  customerName: string | null;
  quoteCount: number;
  createdAt: string;
}
export interface QuotePortfolioSiteRollup {
  quoteId: string;
  quoteRef: string;
  siteName: string | null;
  status: string;
  tier: string;
  liftCount: number;
  maintenanceAnnual: number;
  surchargeAnnual: number;
  extrasAnnual: number;
  discount: { multiLift: number; loyalty: number; allowedLoyalty: number; combined: number; capped: boolean };
  loyaltyDiscount: number;
  siteAnnual: number;
  siteMonthly: number;
}
export interface QuotePortfolioRollup {
  customerSiteCount: number;
  sites: QuotePortfolioSiteRollup[];
  subtotalAnnual: number;
  totalLoyaltyDiscount: number;
  totalSurcharge: number;
  totalExtras: number;
  grandAnnual: number;
  grandMonthly: number;
  anyCapped: boolean;
}
export interface QuotePortfolioDetail {
  id: string;
  name: string;
  customerName: string | null;
  createdAt: string;
  rollup: QuotePortfolioRollup;
}

export interface QuoteCreateInput {
  lifts: QuoteLiftInput[];
  // Link the quote to an existing customer/site chosen from the pre-fill autocomplete (Session AAP).
  customerId?: string;
  siteId?: string;
  customerName?: string;
  customerEmail?: string;
  // Session AAU (Loop 1): copy recipients (information email + accept link, never sign).
  ccEmails?: string[];
  // Companies House company details auto-filled on the quote form's client block (Session AAP).
  // Recorded on the quote; the number is also written to a linked customer.
  companyNumber?: string;
  registeredAddress?: string;
  siteName?: string;
  siteAddress?: string;
  sitePostcode?: string;
  // Session AAQ: per-quote surcharge presentation (default "show").
  surchargePresentation?: "show" | "fold" | "waive";
  anchor?: number;
  additionalItems?: QuoteLineItem[];
  careHomeItems?: QuoteLineItem[];
  promo?: QuotePromoInput;
  override?: QuoteOverrideInput;
  // Soft floor (Session ANR, C59): see QuoteFloorOverrideInput. REQUIRED (a reason) on save whenever
  // the priced quote falls below the contract floor; the server refuses the save without it.
  floorOverride?: QuoteFloorOverrideInput;
  // ISO date (yyyy-mm-dd). Optional; commencement defaults to the signing date.
  preferredCommencementDate?: string;
  recommendedTier?: string;
  // false = Save Draft (do not email the customer); default/true = Send.
  sendEmail?: boolean;
  // true = save as a DRAFT (status draft, never emailed). The office Resumes it later.
  draft?: boolean;
  // When set, REVISE this existing draft in place (keep its ref + token) instead of
  // creating a new quote. Only a draft quote is editable this way.
  id?: string;
  // Session AAV (Loop 2): true for the calculator's background autosave. The server treats it
  // as a lightweight draft persist (no AI generation, no calc_log row, no per-save audit
  // noise) and will only REVISE a quote that is still a draft (409 otherwise).
  autosave?: boolean;
  // The calculator's own form state, stored verbatim so Resume can reload it exactly.
  calculatorState?: unknown;
}

export interface QuoteCreateResult {
  id: string;
  quoteRef: string;
  status: string;
  token: string | null;
  recommendedTier: string;
  emailed: boolean; // true when the branded accept-link email was sent to the customer
  acceptUrl: string;
  // The authoritative pricing the server persisted (same engine as the preview).
  // Surfaced so the confirmation can show the exact figure that was saved/emailed.
  pricing?: { tiers: { core: QuoteTierPricing; care: QuoteTierPricing; complete: QuoteTierPricing } };
}

// Result of a hard delete + cross-platform scrub (quotes / call-outs, admin only).
export interface ScrubSummary {
  localDeleted: boolean;
  xeroVoided: boolean; // a DRAFT Xero invoice was deleted (AUTHORISED ones are never touched)
  stripeDeleted: boolean; // a Stripe payment link was deactivated
  notes: string[]; // plain-English per-platform outcome
}

// ---- Invoices / contracts (office/admin) ----
export interface InvoiceListItem {
  id: string;
  customerName: string;
  // The linked platform customer id (Session ADI): the customer detail page filters the invoices
  // list to ?customerId=. Null for a legacy Xero-only invoice with no linked V2 Customer.
  customerId: string | null;
  siteName: string | null;
  amount: number;
  dueDate: string | null;
  status: string;
  xeroInvoiceId: string; // for the "View in Xero" deep link
}

// ---- Single-invoice detail (Session AO Item 1) ----
export interface InvoiceLineItem {
  description: string;
  quantity: number;
  unitAmount: number;
  lineAmount: number | null;
}
export interface InvoiceXeroDetail {
  total: number | null;
  amountDue: number | null;
  amountPaid: number | null;
  issueDate: string | null;
  lineItems: InvoiceLineItem[];
}
export interface InvoiceLinkedJob {
  jobId: string;
  type: string;
  eleNumber: string | null;
  siteName: string | null;
  completedAt: string | null;
}
export interface InvoiceLinkedCallout {
  calloutId: string;
  jobRef: string | null;
  eleNumber: string | null;
  siteName: string | null;
  reportedAt: string | null;
  // The completed call-out's job id (Session ADH): deep-links the invoice's call-out reference to
  // its visit record on the Call-outs page. Null until the call-out has a completed record.
  jobSheetJobId: string | null;
}
export interface InvoiceDetail {
  id: string;
  invoiceNumber: string | null;
  customerName: string;
  siteName: string | null;
  // Parent back-link handles (Session ADH): null for a legacy/Xero-only invoice with no linked
  // V2 Customer or Site.
  customerId: string | null;
  siteId: string | null;
  dueDate: string | null;
  status: string;
  amount: number;
  xeroInvoiceId: string;
  linkedJob: InvoiceLinkedJob | null;
  linkedCallout: InvoiceLinkedCallout | null;
  xeroDetail: InvoiceXeroDetail | null;
  // RCP1: whether the customer's payment receipt went out, worked out live on the server. Optional only for a stale cached reply.
  receipt?: InvoiceReceiptStatus;
}
// One quiet line (the server writes the words) and the state behind it: sent, waiting, failed, not_applicable, or not_checked when the
// platform could not read it (never an all-clear).
export interface InvoiceReceiptStatus {
  state: "sent" | "waiting" | "failed" | "not_applicable" | "unknown" | "not_checked";
  line: string;
  lineNoAddress: string;
  receipts: { amount: number; paidOn: string; state: "sent" | "waiting" | "failed"; sentAt: string | null; sentTo: string | null; attempts: number; reason: string | null }[];
}

// ---- Phase 8 (Session ATJ): money beyond Xero drafts ----
export interface PaymentLinkResult {
  preview: boolean;
  // Preview shape:
  invoiceId?: string;
  amount?: number;
  customerName?: string;
  to?: string | null;
  reference?: string;
  stripeConfigured?: boolean;
  alreadyHasLink?: boolean;
  message?: string;
  // Commit shape:
  payUrl?: string;
  drafted?: boolean;
}
export interface StatementResult {
  preview: boolean;
  customerId?: string;
  customerName?: string;
  statementDate?: string;
  totalOutstanding: number; // everything owed, overdue and not yet due
  totalOverdue?: number; // the overdue part of it
  invoiceCount?: number;
  hasBillingEmail?: boolean;
  message?: string;
  drafted?: boolean;
  to?: string;
}
export interface CreditNoteResult {
  preview: boolean;
  invoiceId?: string;
  amountExVat?: number;
  reason?: string;
  message?: string;
  creditNoteId?: string;
  creditNoteNumber?: string | null;
  total?: number | null;
}
export interface ChaserResult {
  preview: boolean;
  invoiceId?: string;
  chaseable?: boolean;
  lastChaserAt?: string | null;
  to?: string | null;
  message?: string;
  drafted?: boolean;
}

// ---- One-off (ad-hoc) draft invoice ----
// The route has existed since Session AGE (it is what the Guvnor create_draft_invoice tool
// dispatches); Session AQF gave the office a button for it on the Invoices page. One line, to a
// named Xero sales account, with no quote / contract / call-out / project behind it, and DRAFT only.
export interface OneOffDraftInvoiceInput {
  customerId: string;
  description: string;
  amountExVat: number; // pounds ex VAT; Xero adds the account's tax rate on top
  accountCode: string; // a Xero sales account, 3 to 4 digits (see src/lib/xeroSalesAccounts.ts)
  dueDate?: string; // omitted means the route's 30-day terms
  reference?: string;
}
export interface OneOffDraftInvoiceResult {
  invoiceId: string;
  invoiceNumber: string;
  customerId: string;
  customerName: string;
  xeroContactName: string;
  description: string;
  amountExVat: number;
  accountCode: string;
  reference: string | null;
  dueDate: string; // YYYY-MM-DD
  status: string; // always "draft"
}

// ---- Account on stop (Session SC9B): server/src/modules/invoices/accountOnStop.ts ----
export interface AccountStatusDto {
  onStop: true | false | "not_checked";
  /** Pounds more than 30 days past due; null when not checked. */
  overdueAmount: number | null;
  oldestDaysOverdue: number | null;
}

// ---- Aged debtor report (Session AK Item 4) ----
export type AgedBand = "current" | "d1_30" | "d31_60" | "d61_90" | "d90_plus";
export interface AgedDebtorRow {
  id: string;
  customerName: string;
  invoiceNumber: string | null;
  amount: number;
  dueDate: string | null;
  daysOverdue: number;
  xeroInvoiceId: string;
}
export interface AgedBandGroup {
  band: AgedBand;
  label: string;
  total: number;
  count: number;
  invoices: AgedDebtorRow[];
}
export interface AgedDebtorReport {
  totalOutstanding: number; // overdue only
  totalOwed?: number; // overdue plus not yet due
  overdueCount: number;
  groups: AgedBandGroup[];
  // Session SC9B: present only when the report was narrowed to the customers on stop (?onStop=true). not_checked = the invoice read is stale or failed.
  onStop?: { applied: true; state: "checked" | "not_checked"; customers: number; unlinkedOverdue: number };
}

// ---- Xero billing (Session ARY, Item 4): the contract-to-Xero-repeating-invoice link -------------
//
// Recurring contract billing runs on Xero's own repeating invoice templates (never the platform's
// own recurring invoicing, which Session AKF retired). The platform links a contract to the
// template(s) that bill it, keeps a cache of what Xero said, and derives a verdict on every read:
// is Xero billing what the contract says. Nothing here ever writes to Xero, and there is no "update
// the template" action (Xero's API cannot update a repeating invoice): the office corrects the
// amount in Xero itself, and the flag clears at the next check.
export type XeroBillingState =
  | "in_step"
  | "amount_mismatch"
  | "frequency_mismatch"
  | "template_missing"
  | "template_billing_ended_contract"
  | "check_failed"
  | "not_linked"
  | "not_applicable"
  | "billed_by_guvnor";
export type XeroBillingSeverity = "warning" | "info" | "ok";
export type XeroBillingMode = "unset" | "repeating_template" | "not_repeating" | "guvnor";

export interface XeroRepeatingLine {
  description: string;
  quantity: number;
  unitAmountPence: number;
  lineAmountPence: number | null;
  accountCode: string | null;
}

export interface XeroBillingLink {
  id: string;
  xeroRepeatingInvoiceId: string;
  reference: string | null;
  contactName: string | null;
  subTotalPence: number | null; // ex VAT, per invoice
  frequency: string | null; // "MONTHLY:3"
  frequencyLabel: string; // "Every 3 months"
  nextScheduledDate: string | null; // YYYY-MM-DD
  status: string | null; // AUTHORISED | DRAFT | DELETED | MISSING
  lines: XeroRepeatingLine[];
  linkedAt: string;
  linkedByUserId: string | null;
  carriedFromContractId: string | null; // set when a renewal carried it over from the previous agreement
  lastSeenAt: string | null; // last successful read
  lastCheckedAt: string | null; // last attempt
  lastCheckError: string | null; // why the last attempt failed
}

export interface XeroBillingResult {
  state: XeroBillingState;
  severity: XeroBillingSeverity;
  headline: string; // one line, plain English
  message: string; // the full explanation, plain English (already says what to do)
  periodLabel: string; // "quarter", "six months"
  expectedPerPeriodPence: number | null;
  actualPerPeriodPence: number | null;
  differencePence: number | null;
  templates: Array<{
    xeroRepeatingInvoiceId: string;
    reference: string | null;
    amountPence: number | null;
    frequency: string | null;
    frequencyMatches: boolean;
    nextScheduledDate: string | null;
    status: string | null;
  }>;
  effectiveFrom: string | null;
  timing: "wrong_now" | "update_after" | null;
  updateAfter: string | null;
  nextScheduledDate: string | null;
  lastCheckedAt: string | null; // the OLDEST successful read among the links
  stale: boolean; // that read is over 48 hours old
  // Session ASX (consolidated invoicing): the other active contracts sharing this contract's Xero repeating invoice.
  // When not empty, expectedPerPeriodPence is the COMBINED price and ownExpectedPerPeriodPence this contract's part.
  // Optional so a response from a server not yet redeployed still reads.
  sharedWith?: Array<{ contractId: string; siteName: string | null; expectedPerPeriodPence: number | null }>;
  ownExpectedPerPeriodPence?: number | null;
}

export interface XeroBillingView {
  contractId: string;
  mode: XeroBillingMode;
  modeReason: string | null;
  isIntermediary: boolean;
  links: XeroBillingLink[];
  result: XeroBillingResult;
  sweepCheckedAt: string | null; // when the daily sweep last read Xero successfully
  sweepError: string | null;
}

// Session BBZ: a replacement agreement on the current ESA terms at the contract's current price.
export interface ReplacementAgreementResult {
  preview: boolean;
  plan: {
    contractId: string;
    customerName: string | null;
    siteName: string;
    tier: string;
    billingFrequency: string;
    annualValue: number;
    enginePriceForThisTier: number;
    lifts: number;
    signatory: { name: string; email: string };
    currentRenewalDate: string | null;
    newReviewNote: string;
    oldPaperNote: string;
  };
  quoteId?: string;
  quoteRef?: string;
  next?: string;
}

// Session BBZ: Guvnor billing, the platform raising a contract's Xero DRAFT invoices itself.
export interface GuvnorBillingView {
  contractId: string;
  mode: XeroBillingMode;
  billedByGuvnor: boolean;
  scheduleStart: string | null; // YYYY-MM-DD, the first invoice date
  setAt: string | null;
  billingFrequency: string;
  upcoming: Array<{ invoiceDate: string; periodEnd: string; amount: number | null; heldReason: string | null; priceSource: string | null }>;
  ledger: Array<{ id: string; invoiceDate: string; status: string; amount: number; invoiceNumber: string | null; error: string | null; draftedAt: string | null }>;
  lastRunAt: string | null;
  lastRunNote: string | null;
  automaticDraftsOn: boolean;
}

export interface GuvnorSwitchPreview {
  preview: boolean;
  switched: boolean;
  firstInvoiceDate: string;
  billingFrequency: string;
  firstInvoices: Array<{ invoiceDate: string; periodEnd: string; amount: number | null; heldReason: string | null }>;
  templates: Array<{ reference: string | null; status: string | null; nextScheduledDate: string | null }>;
}

export interface GuvnorRunResult {
  preview: boolean;
  invoices: Array<{ invoiceDate: string; periodEnd: string; amount: number | null; outcome: string; detail: string | null; invoiceNumber: string | null }>;
  stoppedBecause: string | null;
}

export interface XeroTemplateCandidate {
  xeroRepeatingInvoiceId: string;
  reference: string | null;
  contactId: string | null;
  contactName: string | null;
  subTotalPence: number | null;
  frequency: string | null;
  frequencyLabel: string;
  nextScheduledDate: string | null;
  status: string | null;
  lines: XeroRepeatingLine[];
  onCustomerContact: boolean; // on this contract's customer's own Xero contact
  linkedToThisContract: boolean;
  // sameCustomer on every entry, with linkRefusal null, means linking it here SHARES it (Session ASX).
  linkedToActiveContracts: Array<{ contractId: string; siteName: string | null; customerName: string | null; sameCustomer?: boolean }>;
  // Why it cannot be linked to this contract, or null when it can. Optional for a server not yet redeployed.
  linkRefusal?: string | null;
}
export interface XeroTemplateCandidates {
  contractId: string;
  customerName: string | null;
  customerContactBasis: "xero_contact_id" | "name" | "none";
  suggestion: { xeroRepeatingInvoiceId: string; why: string } | null;
  noSuggestionBecause: string | null;
  templates: XeroTemplateCandidate[]; // the customer's own first
}

// One out-of-step contract, as every list surface shows it.
export interface XeroBillingActionRow {
  contractId: string;
  customerName: string | null;
  siteName: string | null;
  state: XeroBillingState;
  headline: string;
  detail: string;
  expectedPerPeriodPence: number | null;
  actualPerPeriodPence: number | null;
  periodLabel: string;
  nextScheduledDate: string | null;
  timing: "wrong_now" | "update_after" | null;
  updateAfter?: string | null; // with timing update_after: the last invoice that should still bill the previous price
  templates: Array<{ xeroRepeatingInvoiceId: string; reference: string | null; contactName: string | null }>;
  link: string; // "/office/contracts/<id>"
}

export interface ContractListItem {
  id: string;
  customerName: string;
  // The linked customer id (Session ADI): the customer detail page filters the contracts list to
  // ?customerId=. Present on any real contract (customer is a required FK); null only defensively.
  customerId: string | null;
  siteName: string;
  tier: string;
  annualValue: number;
  billingFrequency: string;
  status: string;
  renewalDate: string | null;
  escalatorPct: number | null;
  renewalDueSoon: boolean;
  // Session ARY, Item 4: the Xero-billing verdict for this contract, or null for a shape the check
  // does not cover (a draft, pending, or an ended contract with no link). Optional so an older
  // server response cannot crash this page.
  xeroBilling?: { state: XeroBillingState; severity: XeroBillingSeverity; headline: string } | null;
}

// The contract detail record (GET /api/contracts/:id, Session AAQ). The list item plus the fields
// the detail page shows and the attachment counts (so it can show what a delete is blocked on).
// Session AQW (Phase 2, W-11): the new-contract onboarding checklist, derived live server-side from
// existing columns (RULE 6 -- nothing here is stored). See server/src/modules/contracts/onboarding.ts
// for exactly what each key reads; this is its client mirror.
export type OnboardingKey =
  | "quote_accepted"
  | "esa_sent"
  | "signed"
  | "ele_numbers"
  | "drive_filed"
  | "xero_draft"
  | "welcome_email"
  | "portal_user"
  | "rfid_tags"
  | "first_ppm_visit";
export interface OnboardingItem {
  key: OnboardingKey;
  done: boolean;
  notApplicable: boolean;
  at: string | null;
  progress?: { done: number; total: number };
}

export interface ContractDetail extends ContractListItem {
  // Session AMW: the renewal-cycle check for THIS contract, derived live on every read. Optional so
  // older fixtures stay valid; null when the check could not identify the contract.
  renewalCycle?: RenewalCycleAssessment | null;
  // Session AQW (W-11): the onboarding checklist. Optional so older fixtures stay valid.
  onboarding?: OnboardingItem[] | null;
  // The site back-link handle (Session ADH). customerId is inherited from ContractListItem (ADI).
  siteId: string | null;
  contractType: string;
  visitValue: number | null;
  startDate: string | null;
  commencedAt: string | null;
  initialTermEnd: string | null;
  createdAt: string | null;
  notes: string | null;
  xeroAutoInvoice: boolean;
  // Session AEC: recurring-invoicing enrolment floor (null until first enrolled). The office
  // "Invoices due" batch that once proposed invoices from it was retired (AKF) and removed (EXP1).
  autoInvoiceFrom: string | null;
  customerEmail: string | null;
  siteAddress: string | null;
  quoteId: string | null;
  quoteRef: string | null;
  // Session ACN: the office promo billing plan (null unless the contract carries an active promo).
  // Computed server-side by the ONE shared promoPeriodPlan helper (the same maths that priced the
  // first-period Xero DRAFT), so the page renders it verbatim and never re-derives money.
  promoPlan: {
    pct: number;
    months: number;
    reason: string | null;
    promoPeriods: number;
    periodLabel: string;
    fullPeriodAmount: number;
    discountedPeriodAmount: number;
  } | null;
  // Session AER: the office "End contract" record. lapsedAt is when cover actually stopped and
  // endReason is why. inLapseGrace and graceLastDay are DERIVED SERVER-SIDE from the same helper the
  // call-out billing resolver uses, so the office reads the live rule rather than a second copy of
  // it that could drift.
  lapsedAt: string | null;
  endReason: string | null;
  inLapseGrace: boolean;
  graceLastDay: string | null;
  // Session AHN: everything the Cancel-contract modal needs, computed SERVER-SIDE (the offered
  // reasons per ESA variant, the initial-term judgement + the exact warning text, any pending
  // notice) so the client cannot drift from what POST /:id/cancel enforces.
  cancellation: {
    variant: "standard" | "intermediary";
    withinInitialTerm: boolean;
    initialTermWarning: string;
    reasons: Array<{ key: string; label: string; clauseRef: string; noticeDays: number }>;
    pendingNotice: { reasonKey: string; reasonLabel: string; noticeAt: string; effectiveAt: string; notes: string | null } | null;
  };
  counts: { invoices: number; jobs: number; lolerReports: number };
  // Session AJG: two facts DERIVED SERVER-SIDE on every read from the platform's own authoritative
  // records, never transcribed into `notes` and left to rot. Before this, "has the ESA been signed"
  // and "has the first invoice been paid" were only answerable by reading a free-text note that
  // nothing ever updated -- six live contracts said "Awaiting signature." about contracts signed
  // weeks or months earlier. `notes` is untouched and still shown; it is simply no longer the only
  // place either fact lives, and nothing has to read it to answer either question.
  signature: {
    state: "signed" | "out_for_signature" | "voided" | "not_sent" | "unknown";
    // Null on a signed contract whose instant was never recorded. That still means SIGNED: read
    // `state`, never the presence of a date.
    signedAt: string | null;
    signedAtSource: "activation_audit" | "site" | null;
    envelopeId: string | null;
    label: string;
  };
  firstInvoice: {
    // `unknown` means NO invoice is linked to this contract, so payment cannot be confirmed from
    // here. It does NOT mean unpaid, and must never be rendered as such.
    state: "paid" | "unpaid" | "none" | "unknown";
    paidAt: string | null;
    invoiceNumber: string | null;
    dueDate: string | null;
    label: string;
  };
}

// ---- Lift coverage ("Manage lifts", Session ACF) ----
// One lift as the coverage UI shows it. priceable=false means the stored lift type is legacy/free
// text and cannot be auto-priced, so it may not be added to a contract until the office sets a
// recognised lift type on the lift record.
export interface CoverageLift {
  id: string;
  eleNumber: string | null;
  liftType: string | null;
  assetClass: string;
  stops: number | null;
  priceable: boolean;
}
// One covered lift's price components in a coverage preview (parallel session, server-side).
// ageBand is null when the lift carries no usable year of manufacture; ageMult still defaults to
// 1.0 in that case, so the UI must say "not recorded" rather than name a band that isn't on file.
export interface CoverageLiftBreakdown {
  assetId: string;
  eleNumber: string | null;
  liftTypeLabel: string;
  liftType: string;
  typeMult: number;
  duration: string;
  durationMult: number;
  ageBand: string | null;
  ageMult: number;
  manualDoors: boolean;
  manualDoorsAnnual: number;
}
export interface CoverageAmendment {
  id: string;
  effectiveDate: string;
  addedEleNumbers: string[];
  removedEleNumbers: string[];
  addedCount: number;
  removedCount: number;
  previousAnnualValue: number;
  newAnnualValue: number;
  reason: string | null;
  createdAt: string;
  isMostRecent: boolean;
  // ACF follow-up: "pending" = a future-dated change not yet applied (nothing has moved); "applied" =
  // in force. A pending amendment's revert is a plain cancel; an applied one's is an atomic rollback.
  status: string;
  appliedAt: string | null;
  // Session AJJ: a "link coverage only" correction rather than a reprice. Its two annual figures are
  // equal by construction (nothing moved), and reverting it restores coverage without touching money.
  linkOnly: boolean;
}
export interface ContractCoverage {
  contract: { id: string; tier: string; annualValue: number; visitValue: number | null; billingFrequency: string; siteName: string | null };
  covered: CoverageLift[];
  available: CoverageLift[];
  // Session ACR: the contract's care-equipment assets (covered at signing). Read-only display
  // context -- equipment coverage is not managed through the lift flow. Optional so an older
  // cached response still parses.
  equipment?: Array<{
    id: string;
    eleNumber: string | null;
    name: string | null;
    // Session ACV: true for a gate/door/barrier from a gates-only tiered contract, so the
    // page heads the group honestly. Optional for older cached responses.
    additionalItem?: boolean;
    frequency: string | null;
    peopleLifting: boolean;
    lolerDue: string | null;
    retiredAt: string | null;
  }>;
  amendments: CoverageAmendment[];
  // The single outstanding future-dated change, if any (a contract holds at most one). While set, the
  // office cannot schedule another change until it is applied (on its date) or cancelled.
  pendingAmendment: { id: string; effectiveDate: string; newAnnualValue: number } | null;
}
export interface ContractCoveragePreview {
  previousAnnualValue: number;
  newAnnualValue: number;
  previousTierPricing: { core: number; care: number; complete: number };
  newTierPricing: { core: number; care: number; complete: number };
  newBillingFrequency: string;
  addedCount: number;
  removedCount: number;
  unpriceable: CoverageLift[];
  breakdown: CoverageLiftBreakdown[];
  noChange: boolean;
  // Session AJJ (Item 3). ADVISORY, never a block: non-null when this reprice would overwrite a price
  // the engine cannot reproduce from the coverage the contract already holds -- the signal that the
  // lift being added may have been priced into the stored figure all along, and that "link coverage
  // only" is the action the office actually wants. Null when the stored price IS a list price (so
  // repricing is exactly right) or when the price would not move.
  // `reason` decides the wording, and specifically whether "link coverage only" is offered as the
  // alternative at all: on a contract that also covers equipment or additional items it is NOT, since
  // linking would record cover nobody is billed for.
  legacyPriceRisk: {
    reason: "under_registered" | "not_list_priced" | "covers_more_than_lifts";
    currentAnnualValue: number;
    listPriceOfCurrentCoverage: number;
    coveredLiftCount: number;
    coveredEquipmentCount: number;
    newAnnualValue: number;
  } | null;
}

// ---- "Start a service now" (Session AMC) ----
// A one-off, manually-triggered visit today, separate from the automatic PPM forward schedule.
export interface StartServiceNowLift {
  assetId: string;
  ref: string;
  simReady: boolean;
  rfidReady: boolean;
  ready: boolean;
  warnings: string[];
  // Context only: the date the next visit would fall due under the normal cadence, unaffected by
  // this action -- shown so the office can see the generated schedule carries on untouched.
  nextDueUnderNormalCadence: string | null;
}
export interface StartServiceNowSnapshot {
  contract: { id: string; siteName: string | null };
  eligible: boolean;
  eligibilityMessage: string | null;
  lifts: StartServiceNowLift[];
}
export type StartServiceNowBilling = "standard_cycle" | "separate";
export interface StartServiceNowResult {
  ok: boolean;
  created: Array<{ jobId: string; assetId: string; ref: string; reference: string | null }>;
  skipped: Array<{ assetId: string; ref: string; reason: string }>;
  billing: { choice: StartServiceNowBilling; agreedPricePence: number | null; agreedPriceReason: string | null };
}

// ---- "Link coverage only" (Session AJJ) ----
// A pointer correction: Asset.activeContractId is set to this contract for a lift the contract was
// already priced for. No pricing engine runs and no contract money is written, so there is exactly
// ONE annual figure to show and it is the unchanged one.
export interface CoverageLinkLift {
  id: string;
  eleNumber: string | null;
  liftType: string | null;
  // Whether the stored lift type can be auto-priced. Advisory only -- a link never prices, so an
  // unpriceable lift CAN be linked; it is a later reprice that would refuse.
  priceable: boolean;
}
export interface ContractCoverageLinkPreview {
  annualValue: number;
  billingFrequency: string;
  siteName: string | null;
  priceChanges: false;
  link: CoverageLinkLift[];
  alreadyLinked: CoverageLinkLift[];
  unpriceable: CoverageLinkLift[];
}

// ---- Register confirmation gate (Session AIG, "Renewal A") ----
// The precondition for a future renewal link: the office confirms the lift register a renewal
// reprice would use is accurate. flags names which of liftType/stops/yearOfManufacture still carry
// an unedited value from the historical V1 migration (Asset.importSource + no AuditLog edit since).
export type RegisterReviewField = "liftType" | "stops" | "yearOfManufacture";
export interface RegisterReviewLift {
  id: string;
  eleNumber: string | null;
  assetClass: string;
  liftType: string | null;
  stops: number | null;
  yearOfManufacture: number | null;
  ageBand: string | null;
  importSource: string | null;
  flags: Record<RegisterReviewField, boolean>;
}
export interface ContractRegisterReview {
  contract: { id: string; siteName: string | null };
  lifts: RegisterReviewLift[];
  registerConfirmedAt: string | null;
  registerConfirmedBy: string | null;
  registerConfirmedByName: string | null;
}

// One active contract's tier-allowance usage for its current contract-year (GET /api/contracts/
// allowances, Session AAW). A read-only derived view: Care GBP250 / Complete GBP400 inclusive parts
// per COVERED ITEM per year, and Complete's six inclusive call-outs per year, against what the contract
// has actually used. `overLimit` flags a contract the office should bill the excess on by hand.
// Session AQA (D71): a covered item is a LIFT at the site or a TIER-COVERED ADDITIONAL ITEM on the
// contract's own schedule, which is what the signed agreement grants the allowance over; care equipment
// and maintenance-only items carry none and are excluded.
export interface AllowancePartsUsage {
  perLiftAllowance: number; // 0 | 250 | 400, per covered item
  allowance: number; // perLiftAllowance x coveredItemCount
  consumed: number;
  remaining: number;
  overLimit: boolean;
  hasAllowance: boolean;
  partCount: number;
}
export interface AllowanceCallOutUsage {
  included: number; // 6 for Complete, 0 otherwise
  used: number;
  remaining: number;
  overLimit: boolean;
  hasAllowance: boolean;
}
export interface ContractAllowanceRow {
  contractId: string;
  customerName: string;
  siteName: string;
  tier: string; // core | care | complete (UI maps care -> "Care")
  coveredItemCount: number; // lifts at the site + this contract's tier-covered additional items (Session AQA)
  contractYear: { start: string; end: string; index: number } | null;
  parts: AllowancePartsUsage;
  callOuts: AllowanceCallOutUsage;
  overLimit: boolean;
}

// A contract due to renew within 90 days, with the proposed escalated value.
export interface RenewalListItem {
  id: string;
  customerName: string;
  siteName: string;
  tier: string; // raw key (core/care/complete); UI maps to the Care label
  currentAnnual: number;
  proposedAnnual: number | null; // null when this contract has no escalator to apply
  indicative: boolean; // true when computed with no confirmed CPI
  renewalDate: string | null; // ISO
  // Session AMW: whole days the anchor is already past, null while it is still ahead. The list now
  // includes elapsed anchors (an overdue review stays due until something settles it). Optional so
  // older fixtures stay valid.
  daysOverdue?: number | null;
  // Director decision D6 (Session AIC): each contract escalates at its OWN signed rate. When there
  // is none, the reason matters: "intermediary" is correct and needs nothing, "rate_not_recorded"
  // is a contract that will never be proposed for a rise until someone sets it.
  escalatorPct: number | null;
  noEscalatorReason: "intermediary" | "rate_not_recorded" | "platform_not_configured" | null;
  // Session AQW (M-1): mechanism A's (the interactive customer renewal link) state in plain words,
  // derived server-side from the SAME renewalLinkSpent / renewalRestartRefusal rules
  // RenewalLinkPanel's own read already applies to one contract. Optional so older fixtures stay
  // valid; a caller with none renders as "Not issued" (RenewalsPage.tsx CUSTOMER_LINK_LABEL).
  customerLink?: {
    state: "not_issued" | "issued" | "opened" | "used" | "signed" | "restartable";
    at: string | null;
  };
}

// D13 (Session AIC): the CPI actually used, with provenance, or the reason there is none. Travels
// with the renewals list so the office is TOLD, never left to infer it from a blank cell.
export interface RenewalCpiStatus {
  rate: number | null; // fraction, e.g. 0.026
  source: "manual" | "ons" | null;
  period: string | null; // ONS reference month, e.g. "2026 JUN"
  series: string | null; // e.g. "D7G7/MM23"
  stale: boolean;
  message: string | null; // plain English for the office when something needs saying
  label: string | null; // e.g. "CPI 2.6 percent (ONS D7G7/MM23, 2026 JUN)"
}

export interface RenewalListResponse {
  items: RenewalListItem[];
  cpi: RenewalCpiStatus;
  // Session AMW: the renewal-cycle findings over the WHOLE active book (unhealthy assessments only),
  // not just the window. Optional so older fixtures stay valid.
  cycles?: RenewalCycleFinding[];
  // Session ARY, Item 4: every contract whose Xero billing needs a person, most urgent first.
  // Optional so older fixtures stay valid.
  xeroTemplateOutOfStep?: XeroBillingActionRow[];
  // "ok" when the check ran; otherwise the sentence saying it could not, so an empty list is never
  // read as "nothing out of step".
  xeroTemplateOutOfStepCheck?: string;
}

// D7 (Session AIC): a renewal the platform has PROPOSED and is holding for an office confirm.
// Nothing here has reached the customer or Xero: confirming is what applies the price and drafts
// the letter to Gmail, and a person still presses send there.
export interface RenewalProposal {
  id: string;
  contractId: string;
  customerName: string;
  siteName: string;
  renewalDate: string;
  currentAnnual: number;
  proposedAnnual: number;
  cpiRate: number;
  cpiSource: string;
  cpiPeriod: string | null;
  escalatorPct: number;
  letterSubject: string;
  letterBody: string;
  status: "ready" | "confirmed" | "skipped" | "failed";
  error: string | null;
  gmailDraftId: string | null;
  appliedAt: string | null;
  confirmedAt: string | null;
  skippedAt: string | null;
}

// Renewal-letter draft (AI, or templated fallback) for office review.
export interface RenewalDraft {
  subject: string;
  body: string;
  source: "ai" | "template";
  tierLabel: string;
  currentAnnual: number;
  newAnnual: number | null;
  indicative: boolean;
}

// ---- Call-outs (office/admin) ----
// Session AUT: an SLA hold on a call-out's response clock, and the clock as the office reads it with the holds.
export type SlaHoldReason = "awaiting_payment" | "awaiting_signature" | "awaiting_approval" | "other";
export interface SlaHoldItem {
  id: string;
  reason: SlaHoldReason;
  reasonOther: string | null;
  label: string;
  source: "automatic" | "office";
  heldFrom: string;
  releasedAt: string | null;
  releasedVia: string | null;
  placedByName: string | null;
  releasedByName: string | null;
}
export interface SlaHoldView {
  holds: SlaHoldItem[];
  onHold: boolean;
  heldLabel: string | null;
  heldMins: number | null;
  heldText: string | null;
  slaStartAt: string | null;
  slaDeadline: string | null;
  effectiveDueAt: string | null;
  responseOutcome: string | null;
  responseMins: number | null;
  responseTargetMins: number | null;
}

// Session SC2C: the filters, sort and page GET /api/callouts takes when the Call-outs page asks for 50 at a time.
export interface CalloutListPageOptions {
  /** "all" or a comma list of statuses. */
  status?: string;
  siteId?: string;
  /** YYYY-MM-DD, inclusive, London days of the raised time. */
  from?: string;
  to?: string;
  q?: string;
  sort?: string;
  dir?: "asc" | "desc";
  limit: number;
  offset: number;
}

export interface CalloutListItem {
  id: string;
  ref: string;
  // The canonical CO-ELE-{number}-{DD}-{MM}-{YY} reference (Session AJF). Null when no lift is
  // resolved (an emergency call-out with no asset chosen yet) -- fall back to `ref` then.
  reference: string | null;
  siteName: string;
  customerName: string;
  // Parent back-link handles (Session ADH). All null for an emergency (non-contract) call-out
  // with no linked lift; the row falls back to its plain-text site/customer name in that case.
  siteId: string | null;
  customerId: string | null;
  assetId: string | null;
  assetRef: string | null; // the lift's ELE number for the Lift column (null when no asset)
  faultDescription: string | null;
  reportedAt: string | null;
  attendedAt: string | null;
  status: string;
  isEmergency: boolean;
  // Session AFN: this call-out bills on the non-contract basis (a NON_CONTRACT customer, an AEP
  // walk-in whose site has no active contract, or a call-out flagged non-contract). Drives the
  // amber "Non-contract" badge on the list row -- visibility only.
  isNonContract: boolean;
  // Emergency entrapment (Session AK Item 10): the 60-minute SLA countdown + completion.
  isEntrapment: boolean;
  // The RESPONSE due instant (the countdown's deadline).
  slaDeadline: string | null;
  // Session ATC (D18): when an engineer was FIRST assigned, which stops the response countdown. Null
  // while nobody is assigned, and on every call-out assigned before ATC. Optional for an older cache.
  assignedAt?: string | null;
  // Session AUT: the SLA HOLD. slaDueAt is the response leg's EFFECTIVE due instant (the intake deadline plus the time
  // held), null while a hold is open (the clock is paused); slaOnHold is true while one is open; slaHeldLabel ("SLA held:
  // awaiting customer payment") whenever the call-out was or is held; slaHeldMins the held time. Optional for an older
  // cache, where absent reads as never held.
  slaDueAt?: string | null;
  slaOnHold?: boolean;
  slaHeldLabel?: string | null;
  slaHeldMins?: number | null;
  /** Session SC9A (one look for every state): why this call-out's dispatch job is on hold (else null). An older cache omits it. */
  holdReason?: JobHoldReasonKey | null;
  holdReasonText?: string | null;
  completedAt: string | null;
  engineerName: string | null;
  // Session ALW: has the dispatched engineer accepted this call-out on their phone? true = yes,
  // false = assigned and not yet confirmed (the office's "Not accepted" badge), null = nothing to
  // say (nobody is on it, or its dispatch job is already closed). Optional so an older cached
  // response still maps, and an ABSENT value reads as "the server did not say", never as "not
  // accepted" -- the same rule the calendar applies to its own acceptance flag.
  engineerAccepted?: boolean | null;
  // The id of this call-out's completed job that has a job sheet (deep-link target), or null.
  jobSheetJobId: string | null;
  // Session AHJ: the attendance report generated for this call-out, if any. reportRef gates
  // "Correct reference"; hasReportPdf gates "Open PDF" and keys on the filed Drive FILE, not on
  // having a reference. A report with a reference and no filed PDF (Save to Drive was unticked, or
  // Drive was unreachable when it was generated) therefore offers "Correct reference" and a
  // regenerate prompt, and withholds only the download that would 404.
  reportRef: string | null;
  hasReportPdf: boolean;
  // Session AJW; repurposed Session AQY (D-AQX-3) from "sent" to "released" -- same columns, same
  // names (the Job.emailSentAt precedent), now stamped by the office's RELEASE action
  // (POST /:id/release-report), which opens portal visibility and drafts the completion email
  // rather than sending it outright. Both null until the first release; single-fire.
  reportSentAt: string | null;
  reportSentTo: string | null;
  // Session ANP (D63): present only on the PATCH /callouts/:id/assign response, and only when the
  // engineer just assigned holds no on-file certification mentioning the lift's manufacturer.
  // Informational only -- the assignment always succeeds regardless. Absent everywhere else this
  // shape is used (the ordinary call-outs list never computes it).
  certificationWarning?: string | null;
}

// Session SC5: GET /api/schedule/proposal. Labels, minutes and reasons only: never a postcode or a home address.
export interface SlotProposal {
  status: "proposed" | "none";
  kind: "callout" | "repair" | "tech_visit" | null;
  calloutId: string | null;
  jobId: string | null;
  engineerId?: string;
  engineerName?: string;
  day?: string;
  dayLabel?: string;
  window?: "morning" | "afternoon" | "asap";
  windowLabel?: string;
  // A repair or technician visit: the instant Accept writes as the job date. null for a call-out (dispatched now) or a job that already has a date.
  scheduledFor?: string | null;
  summary?: string;
  reasons?: string[];
  warnings?: string[];
  accept?: { via: "callout_assign" | "job_schedule" };
  code?: string;
  reason?: string;
}

export interface AssignableEngineer {
  id: string;
  name: string;
}

// One of this call-out's follow-up tech visits (Session AQW, W-6): raised off THIS call-out's own
// diagnosis (Job.originCalloutId), never its own dispatch job.
export interface CalloutOriginatedTechVisit {
  jobId: string;
  status: string;
  scheduledFor: string | null;
  completedAt: string | null;
  description: string | null;
  engineerName: string | null;
}

// Single call-out detail (GET /api/callouts/:id) including the office-confirmed
// charge (all figures in pence). Charge fields are null until the office computes it.
// Session BAF (C2-017): the for-cause termination note the office sees beside a call-out's charge. Internal only.
export interface TerminatedForCauseNote {
  reason: string;
  effectiveDate: string | null;
  text: string;
}

export interface CalloutDetail {
  // Session BBJ: the live (active) contract at the lift's site, derived live; null when the site has none or the call-out has no lift.
  liftContract?: { state: "covered" | "unconfirmed" | "pending_signature" | "none"; contractId: string | null; activeCount: number } | null;
  id: string;
  ref: string;
  // The canonical reference (Session AJF), with the -INV suffix once linkedInvoiceNumber is set.
  reference: string | null;
  siteName: string;
  customerName: string;
  // Back-link handles (Session AQW, the Call-out Hub page): the site, customer and lift this
  // call-out belongs to. Null exactly where CalloutListItem's own equivalents are null.
  siteId: string | null;
  customerId: string | null;
  assetId: string | null;
  assetRef: string | null;
  faultDescription: string | null;
  reportedAt: string | null;
  attendedAt: string | null;
  completedAt: string | null; // office marked complete (CALL-OUT SLA CLOCK RULE: NOT the fix stop)
  status: string;
  isEmergency: boolean;
  isEntrapment: boolean;
  notes: string | null;
  engineerName: string | null;
  jobId: string | null; // for the photo gallery
  jobSheetJobId: string | null;
  // Session BCS: true while an engineer's job sheet on this call-out still needs Release for sending.
  reportApprovalRequired: boolean;
  photoCount: number;
  hasInvoice: boolean;
  // ---- SLA clock (CALL-OUT SLA CLOCK RULE, docs/reference/platform-rules.md) ----
  // slaStartAt is set ONLY when the intake carried a real time of day: null means the whole clock
  // is honestly UNMEASURED, never a fabricated pass. Since Session ATC (D18) the two legs are
  // SEQUENTIAL: RESPONSE runs from slaStartAt to assignedAt (logged to an engineer assigned), FIX from
  // attendedAt to workFinishedAt (arrival to sign-off); the travel between is not clocked.
  // slaDeadline is the RESPONSE due instant, slaFixDueAt the FIX due instant (known once the engineer
  // has arrived). The *Outcome fields are one of
  // met | breached | open | unmeasured | not_targeted, already judged and persisted server-side --
  // never recompute the judgement client-side. workFinishedAt is the engineer's real departure
  // (never completedAt above, which is office admin time).
  slaStartAt: string | null;
  slaDeadline: string | null;
  slaFixDueAt: string | null;
  assignedAt: string | null;
  slaZoneCode: string | null;
  slaResponseTargetMins: number | null;
  slaFixTargetMins: number | null;
  slaResponseOutcome: string | null;
  // Session AUT: the response time EXCLUDES time on SLA hold; slaHeldMins reports that time separately.
  slaResponseMins: number | null;
  slaDueAt?: string | null;
  slaOnHold?: boolean;
  slaHeldLabel?: string | null;
  slaHeldMins?: number | null;
  // Fix round 1 (SC9A): why the dispatch job is on hold (the hub's ONE state pill).
  holdReason?: JobHoldReasonKey | null;
  holdReasonText?: string | null;
  slaFixOutcome: string | null;
  slaFixMins: number | null;
  workFinishedAt: string | null;
  // The inbound call this call-out was raised from (Session ANV), when logged via the office's
  // call-first workflow. Null on a call-out raised any other way.
  callLogId: string | null;
  // The tech visits THIS call-out's diagnosis prompted, newest scheduled first. Empty when none.
  originatedTechVisits: CalloutOriginatedTechVisit[];
  arrivalTime: string | null;
  departureTime: string | null;
  isOutOfHours: boolean;
  isNonContract: boolean;
  // Session AGD: the in-contract maintenance tier this call-out bills at, so the charge panel can show the tier
  // + first-hour rates before any time is entered. It is the site's active contract tier for a lift, and since
  // Session APQ (D65) "core" for a MAINTENANCE-ONLY item (a flat, non-tier item on a contract with no lift),
  // whatever tier that contract holds: the agreement gives such an item no tier benefits, and the panel charges
  // what it shows. null when the call-out is non-contract, or the site has no active contract; the isNonContract
  // flag above takes precedence in the panel. Display-only.
  contractTier: "core" | "care" | "complete" | null;
  // Session BAF (C2-017): set when the lift's contract was terminated for cause (Clause 5.2). Derived live; INTERNAL, never on a customer document.
  terminatedForCause?: TerminatedForCauseNote | null;
  travelHours: number | null;
  labourChargePence: number | null;
  travelChargePence: number | null;
  totalChargePence: number | null;
  chargeNotes: string | null;
  // Session AEW: the one-off price agreed at log time (PENCE ex VAT) and the office's note on
  // why. Read-only here: the charge panel shows it so the office can see what was agreed, and it
  // is visible even when no DRAFT was raised.
  agreedPricePence: number | null;
  agreedPriceReason: string | null;
  // Session ASW (D22): the emergency caller check, and the matched contract rate the office applied. Optional so an
  // older server reads as none.
  callerMatch?: {
    checkedAt: string | null;
    failed: boolean;
    candidates: Array<{ customerId: string; customerName: string; matchedOn: string[]; siteName: string | null; contractRateAvailable: boolean; activeTiers: string[] }>;
  } | null;
  contractRateApplied?: { customerId: string; customerName: string | null } | null;
  breakdown: string | null;
  // The Xero invoice covering this call-out (Session AO Item 3). xeroInvoiceId -> "View in Xero";
  // linkedInvoiceCacheId (when the invoice is in the cache) -> the office invoice detail page.
  xeroInvoiceId: string | null;
  linkedInvoiceNumber: string | null;
  linkedInvoiceCacheId: string | null;
  // Session AEY: the Call-Out Attendance Report generated for this call-out, if any. reportUrl is
  // the saved PDF's Drive link, reportRef the reference, reportForm the last form (regenerate
  // pre-fill), hasReport the flag. hasReportPdf (Session AHJ) is narrower: whether a PDF is
  // actually filed in Drive, i.e. whether the download route has something to serve.
  hasReport: boolean;
  hasReportPdf: boolean;
  reportRef: string | null;
  reportUrl: string | null;
  reportForm: CalloutReportInput | null;
  // Session AJW: see CalloutListItem's identical fields.
  reportSentAt: string | null;
  reportSentTo: string | null;
  // The raw ad-hoc caller block (a one-off call-out with no linked Customer), for a report
  // regenerate's one-off pre-fill. All null for a linked-customer call-out.
  adhocCustomerName: string | null;
  adhocEmail: string | null;
  adhocPhone: string | null;
  adhocSiteAddress: string | null;
  // Session SC2B: what the attendance report form is seeded with from the lift and its site (null for a call-out with no lift), so the
  // report modal reads the lift's details from the call-out instead of a loaded register of lifts and sites.
  reportSeed?: { siteAddress: string | null; liftType: string | null; manufacturer: string | null; serialNumber: string | null; controllerDrive: string | null } | null;
}

export interface CalloutChargeInput {
  arrivalTime: string; // ISO
  departureTime: string; // ISO
  travelHours: number;
  isNonContract: boolean;
  isOutOfHours?: boolean;
  chargeNotes?: string;
  // The office confirmed replacing the recorded charge with a different figure (the server refuses 409
  // charge_change_unconfirmed without it).
  confirmChargeChange?: boolean;
}

export interface CalloutInvoiceResult {
  invoiced: boolean;
  invoiceId: string | null;
}

// ---- Call-Out Attendance Report (Session AEY) ----
export type CalloutReportStatus = "service" | "isolated" | "out";
export type CalloutReportTier = "core" | "care" | "complete" | "non-contract";

export interface CalloutReportOneOff {
  companyName?: string | null;
  contactName?: string | null;
  address?: string | null;
  email?: string | null;
  phone?: string | null;
}

// The full report generation body. Charge inputs (tier, addlHours, travelHours, isOOH) are always
// recomputed server-side; display fields are office-authored.
export interface CalloutReportInput {
  calloutId?: string | null; // set for a report on an existing call-out
  // The office confirmed replacing the call-out's recorded charge with a different figure (the generate route refuses
  // 409 charge_change_unconfirmed without it).
  confirmChargeChange?: boolean;
  // Session ASW (D22): true drops the matched contract rate the office applied at the emergency intake.
  dropMatchedContractRate?: boolean;
  customerId?: string | null; // Scenario A
  assetId?: string | null;
  oneOff?: CalloutReportOneOff | null; // one-off caller
  status: CalloutReportStatus;
  siteName?: string | null;
  siteAddress?: string | null;
  liftDesignation?: string | null;
  liftType?: string | null;
  locationInBuilding?: string | null;
  manufacturer?: string | null;
  serialNumber?: string | null;
  controllerDrive?: string | null;
  dateReported?: string | null;
  dateAttended?: string | null;
  reportedBy?: string | null;
  natureOfCallout?: string | null;
  timeOn?: string | null;
  timeOff?: string | null;
  travelHours?: number;
  addlHours?: number;
  isOOH?: boolean;
  faultReported?: string | null;
  findingsAndCause?: string | null;
  workCarriedOut?: string | null;
  partsUsed?: string | null;
  partsOnOrder?: string | null;
  furtherWorks?: string | null;
  additionalComments?: string | null;
  showCharges?: boolean;
  // Session AHJ: the office's own Xero invoice number (INV-2431), for a report whose charges are
  // hidden because the invoice was raised in Xero by hand. It becomes the report reference in place
  // of the internal "CO-<cuid>". Ignored by the server when charges are shown.
  invoiceReference?: string | null;
  tier: CalloutReportTier;
  // Session AOJ: the per-visit exception that bills ONE attendance for a customer classified
  // NON_CONTRACT at the Core in-contract rate instead of the published non-contract card. Scoped to
  // this report: nothing is written to the Customer, and their next call-out defaults back to the
  // card. The server ignores it for any other customer, and for a repair; it refuses the request
  // outright when the flag is set with no reason.
  nonContractBasisOverride?: boolean;
  nonContractOverrideReason?: string | null;
  // Session ASM: the one-off price agreed for this attendance, in PENCE ex VAT, and why (required with a figure,
  // zero included). It replaces the computed charge on the draft as one line; an agreed zero still raises a draft.
  // Session ASQ (Stream R, R6): on a report about an existing call-out, OMITTED keeps and bills the price the call-out
  // already carries; null clears it.
  agreedPricePence?: number | null;
  agreedPriceReason?: string | null;
  // Session ASM: where the engineer went, in plain words, for a report naming no lift. Falls back to siteName.
  // Session ASQ (Stream R, R3): on a report about an existing call-out, only this moves a visit already filed.
  siteLabel?: string | null;
  // Session ASQ (Stream R, R4): the office's deliberate move of an existing lift-less call-out to the customer now chosen.
  moveToCustomer?: boolean;
  lineDescriptions?: { firstHour?: string; additional?: string; travel?: string };
  clientSignatoryName?: string | null;
}

export interface CalloutReportPreviewInput {
  customerId?: string | null;
  // Session ASW (D22): the call-out, so the preview carries its intake's matched contract rate; and the drop.
  calloutId?: string | null;
  dropMatchedContractRate?: boolean;
  assetId?: string | null;
  tier: CalloutReportTier;
  addlHours?: number;
  travelHours?: number;
  isOOH?: boolean;
  // Session AOJ. The flag only: the reason is a record of the decision, not an input to the
  // arithmetic, so the preview prices the exception without one and the office can see the Core
  // figure before deciding whether to justify it.
  nonContractBasisOverride?: boolean;
}

export interface CalloutReportPreviewLine {
  key: string;
  description: string;
  rate: string;
  qty: string;
  amount: string;
}

export interface CalloutReportPreviewResult {
  tier: CalloutReportTier;
  tierLabel: string;
  isNonContract: boolean;
  isOutOfHours: boolean;
  // Basis banners (Session AFM): NON-CONTRACT / OUT OF HOURS notices, already ordered for display.
  banners?: string[];
  lineItems: CalloutReportPreviewLine[];
  subtotal: string;
  vat: string;
  total: string;
  subtotalPence: number;
  totalPence: number;
  chargeNote: string;
  // The default charge tier resolved from the selected lift's live coverage (an existing customer +
  // lift); null otherwise. The UI adopts it so a Care/Complete customer is not billed at Core.
  defaultTier: CalloutReportTier | null;
  // Session AOJ: whether the per-visit Core-basis exception ACTUALLY applied to this figure. The
  // server's answer, not the form's: a tick that reaches an in-contract customer or a repair comes
  // back false, so the office is never left believing they changed a price they did not.
  nonContractBasisOverridden?: boolean;
  // A report with charges shown on a call-out that already has a recorded charge, whose figure differs from it: the
  // generate route refuses it (409 charge_change_unconfirmed) unless the office confirms. Null or absent otherwise.
  chargeChange?: { storedTotalPence: number; newTotalPence: number; differencePence: number } | null;
  // Session BAF (C2-017): the for-cause termination note for the selected lift, else null.
  terminatedForCause?: TerminatedForCauseNote | null;
}

export interface CalloutReportResult {
  calloutId: string;
  reportRef: string;
  reportUrl: string | null;
  reportDriveId: string | null;
  filename: string;
  pdfBase64: string;
  xero: { created: boolean; invoiceNumber: string | null; status: "created" | "existing_kept" | "not_connected" | "error" | "no_number" | "none" };
  // Session AHJ: set when charges were hidden and the platform still holds a DRAFT invoice it
  // raised for this same attendance (hiding charges skips the void-and-re-raise entirely). The
  // invoice number when known, else true. Nothing was voided: which invoice stands is the office's
  // decision, and they are told rather than left to find a duplicate in Xero.
  unvoidedDraft?: string | boolean | null;
}

// One visit in a lift's combined photo history (latest ~20 photos across jobs).
export interface AssetPhotoHistoryItem {
  jobId: string;
  ref: string;
  date: string | null;
  jobType: string;
  photoCount: number;
}

// A part fitted to a lift, across all its jobs (Session AF, Item 6). No cost is exposed.
export interface AssetPartItem {
  id: string;
  description: string;
  quantity: number;
  partCode: string | null;
  dateIso: string | null; // job completion date, else when the part was logged
  jobId: string | null;
  jobRef: string | null; // job number for display (the row links to the job sheet by jobId)
}

// The "Log Part" form body (office/admin). description is required; the rest optional. unitCost +
// supplier + notes are recorded but not surfaced on the operational Parts History (cost is never
// exposed on the lift record). jobId optionally links the part to an existing job on the lift.
export interface LogPartInput {
  description: string;
  partCode?: string | null;
  supplier?: string | null;
  unitCost?: number | null;
  quantity?: number;
  fittedAt?: string | null; // ISO date the part was fitted
  jobId?: string | null;
  notes?: string | null;
}

export interface CalloutCreateInput {
  assetId: string;
  reportedAt: string;
  faultDescription?: string;
  outOfHours?: boolean;
  travelMins?: number;
  // Session AEW: an optional one-off price agreed for THIS call-out, in PENCE ex VAT. Omitted or
  // null means the computed Schedule of Fees figure applies, exactly as before. 0 is meaningful
  // and distinct from omitting it: it means agreed free of charge, and raises no invoice.
  agreedPricePence?: number | null;
  agreedPriceReason?: string;
  // Session ANV: the logged inbound call this call-out was raised from, set only when the office
  // came here from the Call log. Omitted by every other caller.
  callLogId?: string;
  // Session ATZ (Item 3): the property platform work order this call-out is raised from (the Work orders page).
  workOrderProposalId?: string;
}

export interface CalloutCreateResult extends CalloutListItem {
  invoiced: boolean; // a DRAFT invoice was raised at the in-contract tier rate
}

export interface EmergencyCalloutInput {
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  siteAddress: string;
  faultDescription?: string;
  reportedAt: string;
  outOfHours?: boolean;
  travelMins?: number;
  // Session AEW: as CalloutCreateInput. On this path it also drives the Stripe payment link and
  // the confirmation email, so the caller is never quoted one figure and charged another.
  agreedPricePence?: number | null;
  agreedPriceReason?: string;
  // Session AKA: true only right after the office confirms the branded "Log this emergency
  // call-out?" dialog -- sends the confirmation email/text for real instead of the usual
  // Gmail-draft-for-review AJI leaves as the default.
  notifyNow?: boolean;
  // Session ASW (D22): the office's case-by-case choice to bill this one attendance at a matched customer's contract
  // rate (the in-contract Core card). Absent keeps the non-contract card.
  matchedContractRateCustomerId?: string;
}

// Session ASW (D22): what the always-on emergency caller check found (POST /api/callouts/emergency/caller-match).
export interface CallerMatchCandidate {
  customerId: string;
  customerName: string;
  matchedOn: Array<"email" | "phone" | "site_postcode" | "name">;
  siteName: string | null;
  contractRateAvailable: boolean;
  activeTiers: string[];
}
export interface CallerMatchResult {
  checkedAt: string;
  failed: boolean;
  candidates: CallerMatchCandidate[];
}

export interface EmergencyCalloutResult {
  jobRef: string;
  invoiced: boolean;
  // Session AJI: was `emailed`. The confirmation is now a Gmail DRAFT the office reviews and sends
  // BY DEFAULT, so this means "a draft is waiting", never "the customer has it". True only when
  // `sent` (below) is false.
  drafted: boolean;
  // Session AKA: true when the office confirmed the send and the confirmation genuinely left as a
  // real email. Exactly one of `drafted` / `sent` is ever true.
  sent: boolean;
  paymentUrl: string | null; // Stripe "Pay now" link, when Stripe is connected
  estimateNet: number;
  estimateGross: number;
  // Session ASW (D22): optional so an older server reads as no match and no contract rate.
  callerMatch?: CallerMatchResult;
  contractRateApplied?: boolean;
}

// ---- Settings: company settings + integration status (admin only) ----
export interface BankDetails {
  bankName: string;
  sortCode: string;
  accountNumber: string;
}

export interface TenantSettings {
  escalatorRate: number | null; // fraction, e.g. 0.015 = 1.5% (locked)
  escalatorCap: number | null; // fraction, e.g. 0.10 = 10% (locked)
  escalatorFloor: number | null; // fraction, e.g. 0.00 (locked)
  // Session AJG: the MANUAL OVERRIDE, not "the current CPI". Null is the normal, healthy state and
  // means the live ONS reading below is what renewals are priced from. Clearable back to null by
  // sending `cpiRate: null` (never 0, which is a valid deliberate zero-inflation override).
  cpiRate: number | null; // fraction, e.g. 0.032 = 3.2%
  // The live ONS reading the daily cron maintains, so the office can see what an override is
  // masking. Read-only.
  cpiFetchedRate: number | null;
  cpiFetchedPeriod: string | null; // e.g. "2026 JUN"
  cpiFetchedAt: string | null; // ISO; null when no reading has ever succeeded
  cpiFetchError: string | null; // the last failure, so an override cannot mask a broken integration
  // WHAT RENEWAL PRICING WILL ACTUALLY USE, resolved server-side by the same function renewals call.
  // Read this rather than re-deriving from the two fields above: the resolver needs BOTH a fetched
  // rate and a fetchedAt before it uses the live reading, so judging usability from the rate alone
  // would tell the office renewals are priced from ONS in a state that prices nothing at all.
  cpiResolved: {
    rate: number | null;
    source: "manual" | "ons" | null;
    message: string | null;
    overridingRate: number | null;
    stale: boolean;
  };
  ppmRunwayDays: number; // PPM payment-overdue runway in days (editable; resolved, default 14)
  quoteValidityDays: number; // quote validity window in days (editable; resolved, default 30)
  labourCostPerHour: number; // office-only margin labour cost per hour (editable; resolved, default 62)
  callNpsDelayMinutes: number; // delay before a call-log NPS survey sends (editable; resolved, default 60)
  inboxChaseThresholdDays: number; // working days before an awaiting-reply email escalates to needs_chase (editable; resolved, default 5)
  // The EFFECTIVE Knowledge Base Gmail trusted-sender list: what the hourly sweep actually runs on,
  // never the raw stored column. `customised` false means these are the built-in defaults, so the
  // screen can say which it is showing rather than presenting the seed as a chosen list.
  kbTrustedSenders: string[];
  kbTrustedSendersCustomised: boolean;
  bank: BankDetails; // company bank details (editable; seed default until set)
}

// Automation settings (admin only): the daily PPM auto-generation toggle (Session AK Item 1)
// and the automatic Xero DRAFT billing toggle (Session AO Item 2). Both default on.
export interface AutomationSettings {
  ppmAutoGenerate: boolean;
  autoInvoiceEnabled: boolean;
}

// The copyable claude.ai connector instructions (Session AHG; versioning simplified Session AJA).
export interface McpManifest {
  toolCount: number;
  // "curated" = the real instructions from docs/MCP_SYSTEM_PROMPT.md, with the live tool count
  // spliced into their opening sentence and the doc's own revision stamped on the end. The tools
  // are NOT listed out (Session AHM): the connection advertises them to claude.ai itself.
  // "fallback" = that document could not be read on the server, so this is a thin generated block
  // that is little more than a tool list; pasting it would LOSE the curated guidance rather than
  // refresh it, which is why the page says so instead of offering it silently.
  source: "curated" | "fallback";
  // THE version, full stop: the prose revision marker (e.g. "v25"), read fresh off
  // docs/MCP_SYSTEM_PROMPT.md's own `**Revision vNN**` line on every request. Null in the fallback
  // state, where there is no document to read one from. There used to be a second, separately
  // counted number here too (a lazily-bumped TenantConfig counter) shown under the same "vNN" shape
  // with nothing distinguishing the two; Session AJA retired it because it could go stale exactly
  // when a tool's behaviour changed with no matching prose edit, which the Director could not tell
  // apart from "up to date" on screen. This is now the only version Settings shows.
  docRevision: string | null;
  promptCountSynced: boolean; // false when the prose's own tool count could not be located
  // Tool names the changelog names that the body never mentions at all (Session AJA): the
  // mechanical, permanent version of the v21-v24 bug this session fixed by hand. Empty when clean.
  staleToolMentions: string[];
  text: string;
  // Session P3: the one fixed line pasted ONCE into the claude.ai Project, telling Claude to call get_guvnor_guidance.
  // Optional only because a server older than P3 does not send it (the card then shows the CA1 layout).
  bootstrapLine?: string;
}

export interface IntegrationStatus {
  google: { connected: boolean; configured: boolean };
  xero: { connected: boolean; configured: boolean; tenantConfigured: string | null; tokenExpired: boolean; webhookConfigured: boolean };
  signable: { configured: boolean; webhookConfigured: boolean };
  stripe: { configured: boolean; webhookConfigured: boolean };
  twilio: { configured: boolean };
  maps: { configured: boolean; geocoding: boolean; driveTimes: boolean };
  // White-label inbound-call webhook (Session ACY): whether a per-tenant token is set and, if so,
  // the URL to give the phone provider. Null URL = dormant (ELE's own state).
  inboundCall: { webhookConfigured: boolean; webhookUrl: string | null };
}

// ---- Settings: SMS log (admin only) ----
export interface SmsLogItem {
  to: string;
  event: string;
  // Session AJI: "withheld" is a customer SMS the platform deliberately did not send, because no
  // human review step stood in front of its trigger. It is logged rather than dropped so the office
  // can see who was not texted.
  status: "sent" | "failed" | "withheld";
  sentAt: string;
  bodyPreview: string;
  error: string | null;
}

// The auditor PASSWORD-ACCOUNT types (Session K2) were deleted in Session AJI along with their
// three /api/admin/auditors routes and the unrouted AuditorsPage that was their only caller. That
// flow emailed a plaintext temporary password to an external assessor with no review step, and the
// magic-link types below supersede it.

// ---- Auditor magic-link access (Session AEJ, D29) ----
export interface AuditorTokenListItem {
  id: string;
  email: string;
  label: string | null;
  expiresAt: string; // ISO
  lastUsedAt: string | null; // ISO; null = never used
  createdAt: string; // ISO
}
// The generate result carries the raw link ONCE (it is never retrievable again).
export interface AuditorTokenCreateResult {
  url: string;
  email: string;
  expiresAt: string; // ISO
}
export interface AuditorTokenCreateInput {
  email: string;
  label?: string;
  expiryDays?: 1 | 7 | 14 | 30; // default 7
}

// ---- Connected AI (MCP) tokens (Session AET, admin only) ----
export interface McpTokenListItem {
  id: string;
  label: string;
  // "user" is a person's own standing connection; "service" is the unattended automation
  // credential (Session ALK), which is read-only and allow-listed server-side. The office needs
  // to see which is which BEFORE pressing Revoke, since the two are otherwise indistinguishable
  // rows in one table. "draft_only" (Session AQM, D80) is a person's connection that can never
  // send or notify anyone outside the business.
  kind: "user" | "service" | "draft_only";
  // Derived server-side from expiresAt/revokedAt so the page can never disagree with what the
  // auth path actually does with the row.
  status: "live" | "revoked" | "expired";
  userName: string;
  userEmail: string | null;
  expiresAt: string | null; // ISO; null = never expires
  revokedAt: string | null; // ISO; null = not revoked
  createdAt: string; // ISO
  lastUsedAt: string | null; // ISO
}
// The generate result carries the raw token ONCE (it is never retrievable again).
export interface McpTokenCreateResult {
  token: string;
  url: string; // the MCP endpoint URL to paste into the client (the draft-only URL for a draft-only token)
  label: string;
  kind?: "user" | "draft_only"; // Session AQM; absent from an older server
}
// An OAuth connection created by the claude.ai one-click flow (Session AEU).
export interface McpOAuthConnectionItem {
  id: string;
  clientName: string;
  clientHost: string | null; // the CIMD client's hostname (e.g. claude.ai); null for DCR clients
  scope: string;
  // Session AQM: true when the connection was made through the draft-only URL. Optional so an older
  // server's answer (no field) reads as an ordinary connection, which is what it was.
  draftOnly?: boolean;
  userName: string;
  userEmail: string | null;
  createdAt: string; // ISO
  lastUsedAt: string | null; // ISO
}

// ---- Office user (Google staff) offboarding (admin only, Session AAQ) ----
// One place the signed-in person is signed in (Session ASB, GET /api/auth/sessions).
export interface SignInSessionItem {
  device: string; // "Chrome on Windows"
  createdAt: string; // ISO: when they signed in there
  lastSeenAt: string; // ISO: last active (to the minute)
  absoluteExpiresAt: string; // ISO: the latest it can last
  current: boolean; // this browser
}

export interface OfficeUser {
  id: string;
  email: string | null;
  displayName: string;
  role: "office" | "admin";
  isActive: boolean;
  createdAt: string;
  isSelf: boolean; // the acting admin's own row (self-offboarding is disabled)
}

// ---- Defects (office/admin) ----
export type DefectStatusFilter = "open" | "resolved" | "all";

// ---- RIDDOR advisory flags (office/admin) ----
export type RiddorFlagStatus = "open" | "reviewed" | "dismissed" | "reported_by_director";

// The RIDDOR flag embedded on a defect (the Defects-page review banner).
export interface DefectRiddorFlag {
  id: string;
  status: RiddorFlagStatus;
  matchedCriterion: string;
  timescaleGuidance: string;
  reviewedByNote: string | null;
}

// The RIDDOR flag with its defect context (the compliance dashboard RIDDOR panel).
export interface RiddorFlagItem {
  id: string;
  defectId: string | null;
  status: RiddorFlagStatus;
  matchedCriterion: string;
  timescaleGuidance: string;
  reviewedByNote: string | null;
  createdAt: string | null;
  ele: string;
  siteName: string;
  customerName: string;
  defectDescription: string | null;
  defectSeverity: string | null;
}

// Session SC2C: the filters, search, sort and page GET /api/defects takes when the Defects page asks for 50 at a time.
// Session SC7A (Repairs, To fix): where a defect came from, and what has happened to it so far. Both derived live on the server.
export type DefectOrigin = "visit" | "callout" | "loler" | "insurer" | "office";
export type DefectFixState = "no_estimate" | "estimate_sent" | "approved" | "declined" | "job_raised" | "job_booked" | "paused" | "job_done" | "done";

// Session SC7A: one row of the Schedule hub's All jobs list (GET /api/scheduling/jobs), every job of every type.
export interface JobListItem {
  id: string;
  reference: string | null;
  type: string;
  status: string;
  onHold: boolean;
  /** Session SC9A: why it is on hold (else null), and the other facts the row's ONE state pill is chosen from. */
  holdReason?: JobHoldReasonKey | null;
  holdReasonText?: string | null;
  entrapment?: boolean;
  slaHeldLabel?: string | null;
  followOnWorks?: boolean;
  scheduledFor: string | null;
  engineerName: string | null;
  customerId: string | null;
  customerName: string | null;
  siteId: string | null;
  siteName: string | null;
  assetId: string | null;
  eleNumber: string | null;
  calloutId: string | null;
  projectId: string | null;
  /** A visit record (job sheet) exists, so /office/jobsheets/:id has something to show. */
  hasSheet: boolean;
}
export interface JobListPageOptions {
  /** Job types to include (empty = every type). */
  types?: string[];
  /** open | on_hold | scheduled | in_progress | pending_approval | complete | cancelled (any of them). */
  status?: string[];
  /** Session SC9A: only jobs on hold for these reasons. */
  holdReason?: JobHoldReasonKey[];
  engineerId?: string;
  customerId?: string;
  siteId?: string;
  assetId?: string;
  /** YYYY-MM-DD, inclusive London days of the job's scheduled date. */
  from?: string;
  to?: string;
  q?: string;
  sort?: "date" | "type" | "status" | "site" | "engineer";
  dir?: "asc" | "desc";
  limit: number;
  offset: number;
}

// Session SC7A: one row of the LOLER Due dates list (GET /api/loler/due).
export type LolerDueBand = "red" | "amber" | "green" | "none";
export interface LolerDueRow {
  assetId: string;
  eleNumber: string | null;
  siteId: string;
  siteName: string;
  customerId: string;
  customerName: string;
  due: string | null;
  lastExamined: string | null;
  band: LolerDueBand;
  arrangedBy: "customer" | "ELE";
}
export interface LolerDuePageOptions {
  customerId?: string;
  band?: LolerDueBand;
  q?: string;
  sort?: "grouped" | "due" | "lift" | "site";
  dir?: "asc" | "desc";
  limit: number;
  offset: number;
}

export interface DefectListPageOptions {
  status: DefectStatusFilter;
  category?: "A" | "B";
  riddor?: boolean;
  siteId?: string;
  /** Only the defects raised from this visit: the Follow-on works link (Session SC9A). */
  jobId?: string;
  /** Where the defect came from (SC7A). */
  origin?: DefectOrigin;
  /** One fix state, or "to_do" for every state but done (SC7A). */
  state?: DefectFixState | "to_do";
  q?: string;
  sort?: string;
  dir?: "asc" | "desc";
  limit: number;
  offset: number;
}

export interface DefectListItem {
  id: string;
  eleNumber: string;
  siteName: string;
  // The site the defect's lift belongs to (Session ADI): the Defects list Site column links to
  // /office/sites/:siteId. Present on any real defect (the lift -> site FK is required); null only
  // defensively.
  siteId: string | null;
  severity: "advisory" | "immediate";
  description: string;
  status: "open" | "resolved";
  ageDays: number;
  resolvedAt: string | null;
  notificationDrafted: boolean; // a Category A client notification was drafted to Gmail
  remedialJobCreated: boolean; // a Category B remedial follow-up job was auto-created
  // Session ARK (W-10): that remedial job is complete (office-approved), derived live from the job.
  // With the defect still open, the defect page offers to resolve it. It never resolves anything.
  remedialJobComplete: boolean;
  aiTriage: string | null; // advisory AI triage summary (null when none generated)
  riddorFlag: DefectRiddorFlag | null; // the RIDDOR advisory flag, if any
  // Session AVV (W9): the newest non-void quote raised for this defect's remedial work, read live from the
  // quote on every load (never stored on the defect), and how many quotes link it in all.
  linkedQuote?: { id: string; reference: string; status: string } | null;
  linkedQuoteCount?: number;
  // Where the defect came from. NOT supplied by the list route today (Session BAT: the Defect table has no
  // source column; a LOLER-logged one has a lolerItem relation the list does not select). The Defects page
  // shows a source badge only when this is present and never guesses it from the description.
  source?: "loler" | "checklist" | "engineer_flag" | null;
  // Session SC7A: where it came from (a visit, a call-out, a LOLER report, an insurer, or logged by the office) and what has
  // happened to it so far (no estimate yet ... done). Optional only so an older server and older test fixtures still read.
  origin?: DefectOrigin;
  fixState?: DefectFixState;
}

// Session BDG2: what reopening an approved job will do (server/src/modules/jobsheets/approvalReopen.routes.ts describePlan).
export interface ReopenApprovalPlan {
  jobId: string;
  reference: string;
  type: string;
  approvedAt: string | null;
  willBecome: string;
  defectsRemoved: { description: string; severity: string }[];
  timesheetEntriesRemoved: { engineer: string | null; weekStarting: string; weekStatus: string; entryType: string; minutes: number | null }[];
  surveyRequestRemoved: boolean;
  followUpRepairLinksCleared?: string[];
  lastServiced: { action: "rewind"; from: string; to: string | null } | { action: "left"; at: string; why: string } | { action: "untouched" };
  driveReport: string;
  leftInPlace: string[];
  nothingSent: string;
}

// A defect offered for resolution when the remedial job raised from it is approved (Session ARK,
// W-10): the approval response's defectsToReview. Resolving it is the ordinary api.defects.resolve.
export interface RemedialDefectOffer {
  id: string;
  description: string;
  severity: "advisory" | "immediate";
}

// The defect detail record (GET /api/defects/:id, Session AAQ). The list item plus the lift +
// remedial-job handles and the raised date.
export interface DefectDetail extends DefectListItem {
  assetId: string; // handle to the lift detail (/office/lifts/:assetId)
  // siteId (the site detail handle /office/sites/:siteId) is inherited from DefectListItem (ADI).
  createdAt: string;
  remedialJobId: string | null; // handle to the remedial follow-up job, if any
  // Session ADF: the remedial repair job has an openable service record, so the page links straight
  // to its ServiceRecordPanel; false means it is raised but not yet completed (no record to open).
  remedialJobHasSheet: boolean;
  resolvedBy: string | null;
  // Session ANU: the photos an engineer attached when flagging this from their job screen, as
  // { jobId, index } pairs for the office photo proxy (api.jobs.photoUrl). Empty for every defect
  // raised any other way.
  photos: DefectPhotoRef[];
  // Session ASM (W-12): the customer the defect's lift belongs to, for "Raise work". Optional so an older server's
  // payload still renders (the page then offers only what needs no customer).
  customerId?: string | null;
  customerName?: string | null;
}

// One photo backing a defect: the job whose sheet holds it and its position in that sheet's photo
// array, which is exactly what the existing office photo proxy takes. The raw Drive URL never
// crosses to the client.
export interface DefectPhotoRef {
  jobId: string;
  index: number;
}

// ---- Repair estimates (Session ARL, gap W-9) ----
// The price of a repair, recorded BEFORE the customer has agreed it, raised from a defect, a call-out,
// both or neither. Field for field the server's RepairEstimateDto
// (server/src/modules/repairEstimates/repairEstimates.ts), an explicit allow-list. Money is PENCE,
// ex VAT, throughout. Every send makes a Gmail DRAFT and nothing more: the office sends it.
// Session ARS (D91): no send creates a Xero object; the retired "xero_draft" value survives only on
// an estimate sent that way before ARS (its sentVia, and its legacy xeroInvoice* fields).
export type RepairEstimateStatus = "draft" | "sent" | "approved" | "declined";
export type RepairEstimateSendPath = "xero_draft" | "priced_document" | "quotation";
// The two emailed paths, the only ones a send can take. Each renders a PDF.
export type RepairEstimateDocumentVariant = Exclude<RepairEstimateSendPath, "xero_draft">;
// How a recorded answer arrived (Session ARS): the customer's own page, or the office recording it.
// null on an answer recorded before ARS, which only the office could record.
export type RepairEstimateResponseChannel = "office" | "online";

// Session ASC (A3): one reason the customer typed on a repeat decline (server repairEstimates.ts FurtherDeclineReason).
export interface RepairEstimateFurtherDeclineReason {
  at: string; // ISO instant it arrived
  reason: string; // plain text, never HTML
  emailed: boolean; // whether it was the one reason the office inbox was emailed about
}

// The repair JOB an approved estimate is linked to, or the one it could be attached to (Session ARS,
// D91). Read live from the job, never stored on the estimate. `type` and `status` are the job's own
// stored values (JobType / JobStatus).
export interface RepairEstimateJobSummary {
  id: string;
  type: string;
  status: string;
  scheduledFor: string | null; // ISO; null while it waits on Scheduling to be booked
  description: string | null;
  eleNumber: string | null; // where it is: the lift, or the lift-less job's site
  siteLabel: string | null;
}

// GET /api/repair-estimates/:id/job-options: read-only, what raising the job for an approved estimate
// would do right now, so the office decides with the job named in front of them.
export interface RepairEstimateJobOptions {
  estimateId: string;
  linkedJob: RepairEstimateJobSummary | null; // already linked: nothing else applies
  autoPlan: "attach_own" | "create" | "none"; // what the automatic raise would do right now
  // The job an attach would use: an open repair, or (Session ASC, D95 (g)) the defect's own repair job while it
  // has been submitted and waits for approval (its `status` says which). Null once that job is complete.
  openRepair: (RepairEstimateJobSummary & { isDefectsOwn: boolean }) | null;
  canAttach: boolean; // raiseJob { action: "attach", jobId: openRepair.id }
  canRaise: boolean; // raiseJob { action: "raise" }
  needsCustomer: boolean; // no lift and no customer: the raise must carry customerId
  needsSiteLabel: boolean; // no lift and no site: the raise must carry siteLabel
  blocked: { code: string; message: string } | null; // when neither is possible, why (office-facing)
  // Session ARS review: set whenever the attach target is not exclusively this defect's own job. Attaching
  // puts the WHOLE job on this estimate's fixed price (one invoice line, parts included), so other work on
  // it is not billed separately. An office-facing sentence the panel shows beside the Attach control.
  attachWarning: string | null;
  // Why raising a NEW job is not offered while attaching is (the server's addition to the build
  // contract's shape): a second open repair on one lift is impossible, or the defect already has its own.
  // Optional, so the panel falls back to its own sentence if it is ever absent.
  raiseRefusal?: { code: string; message: string } | null;
  // Session ASC (the confirmed new-work raise): the defect's own repair job is waiting for approval or complete, and a
  // separate repair job can be raised if the office confirms this estimate prices work that job did not do
  // (raiseJob { action: "raise", newWork: true }). newWorkConfirm is the sentence the office confirms. Optional, so an
  // older server reads as "not offered".
  canRaiseNewWork?: boolean;
  newWorkConfirm?: string | null;
  newWorkRefusal?: { code: string; message: string } | null;
}

// What raising the job did, verbatim the server's RaiseJobOutcome
// (server/src/modules/repairEstimates/repairEstimateJob.ts). `linked` is the only outcome that wrote.
export type RaiseJobOutcome =
  | { kind: "linked"; jobId: string; how: "own_remedial" | "attached" | "created" }
  | { kind: "needs_decision"; code: string; message: string; openRepairJobId?: string | null }
  | { kind: "refused"; status: 400 | 404 | 409; code: string; message: string };

// POST /api/repair-estimates/:id/job. An attach names the job the options named; a raise carries the
// customer and the site only when the options said they are missing.
export type RepairEstimateRaiseJobInput =
  | { action: "attach"; jobId: string }
  | { action: "raise"; customerId?: string; siteLabel?: string; newWork?: true };

export interface RepairEstimateRaiseJobResult {
  estimate: RepairEstimateDto;
  outcome: RaiseJobOutcome;
}

// DELETE /api/repair-estimates/:id/job (Session ARS review): the estimate taken off its job while that job is
// still open or (Session ASC) waiting for approval, the job's agreed price cleared with it. The estimate then
// reads approved with no job again.
export interface RepairEstimateDetachJobResult {
  estimate: RepairEstimateDto;
}

// POST /api/repair-estimates/:id/response: the estimate, plus, on an APPROVAL only, what the automatic
// raise of the repair job did (a decline carries none).
export type RepairEstimateResponseResult = RepairEstimateDto & { jobRaise?: RaiseJobOutcome };

export interface RepairEstimateDto {
  id: string;
  reference: string; // ELE-RE-YYMM-NNN
  defectId: string | null;
  calloutId: string | null;
  customerId: string | null;
  customerName: string | null; // live, from the linked customer
  assetId: string | null;
  eleNumber: string | null; // live, from the linked lift
  siteLabel: string | null;
  recipientName: string | null;
  recipientEmail: string | null;
  description: string;
  // Ex VAT. Session ATF: null while an estimate an engineer raised on site waits for the office's price.
  amountPence: number | null;
  // Session ATF. Optional so an older server's DTO reads as an office-raised, priced estimate.
  awaitingPrice?: boolean;
  raisedOnSite?: boolean; // raised by an engineer from a job on the PWA, never by the office
  raisedOnSiteByName?: string | null;
  raisedFromJobId?: string | null;
  raisedFromJob?: RepairEstimateJobSummary | null; // live; null once that job has been deleted
  partsNeeded?: string | null; // the engineer's own note for pricing, never printed for the customer
  estimatedHours?: number | null; // the engineer's rough time on site, for pricing
  // The EFFECTIVE status: approved / declined as recorded, otherwise "sent" once anything has been
  // drafted for the customer (sentAt set) and "draft" before that. Every server refusal reads it.
  status: RepairEstimateStatus;
  sentVia: RepairEstimateSendPath | null; // the LAST path used
  sentAt: string | null;
  xeroInvoiceId: string | null; // LEGACY: the Xero DRAFT the retired path raised before Session ARS
  xeroInvoiceNumber: string | null;
  hasGmailDraft: boolean;
  respondedAt: string | null;
  respondedBy: string | null; // a user id, for the audit trail; never shown as a name
  // The office's note on an answer it recorded, or, on an ONLINE decline, the customer's own reason
  // (plain text, shown as text and never as HTML).
  responseNote: string | null;
  // Session ASC (A3): reasons the customer gave online on declines AFTER the one on record, oldest first. The office
  // inbox is emailed about a decline once per estimate, so these are stored here instead; `emailed` says which one
  // (at most one) the office was emailed about. Plain text. Optional so an older server's DTO reads as none.
  furtherDeclineReasons?: RepairEstimateFurtherDeclineReason[];
  respondedVia: RepairEstimateResponseChannel | null; // Session ARS
  // Session ARS. The customer has a link to approve or decline online (the token itself never crosses).
  hasCustomerLink: boolean;
  validUntil: string | null; // YYYY-MM-DD, the last London day the issued price stands; null until sent
  jobId: string | null; // the repair job this approved estimate is linked to
  job: RepairEstimateJobSummary | null; // that job, live
  needsJobDecision: boolean; // approved and no job linked, derived live: the office decides on the panel
  // Session ARS review: approved under ARL, before online answers existed (approved, no channel), when the
  // office raised repairs by hand from Scheduling. Left out of the morning action; the panel cautions.
  approvedBeforeOnlineAnswers: boolean;
  createdAt: string;
  updatedAt: string;
}

// GET /api/repair-estimates filters, AND-ed.
export interface RepairEstimateListQuery {
  defectId?: string;
  calloutId?: string;
  customerId?: string;
}

// POST /api/repair-estimates. Everything but the works and the price is optional: the server derives
// the lift, site, customer and recipient from the defect or call-out when they are left out.
export interface RepairEstimateCreateInput {
  defectId?: string;
  calloutId?: string;
  customerId?: string;
  assetId?: string;
  siteLabel?: string;
  recipientName?: string;
  recipientEmail?: string;
  description: string;
  amountPence: number;
}

// PATCH /api/repair-estimates/:id (a draft only). null clears an optional text field.
export interface RepairEstimateUpdateInput {
  description?: string;
  amountPence?: number;
  recipientName?: string | null;
  recipientEmail?: string | null;
  siteLabel?: string | null;
}

export interface RepairEstimateSendResult {
  estimate: RepairEstimateDto;
  outcome: {
    via: RepairEstimateDocumentVariant;
    gmailDraftId?: string | null;
    recipient?: string | null;
  };
}

export interface RepairEstimateDeleteResult {
  ok: true;
  xeroVoided: boolean;
  notes: string[];
}

// ---- Prospects (office/admin) ----
// The canonical pipeline stages. The legacy "qualified" value can still appear in a
// row's raw `status`, but it is never set anew and the board buckets it under contacted.
export type ProspectStatus = "new" | "contacted" | "quoting" | "quote_sent" | "negotiating" | "won" | "lost";
export type ProspectSource = "referral" | "web" | "apollo" | "loler_enquiry" | "inbound_call" | "other" | "drive_import";

export interface ProspectListItem {
  id: string;
  name: string;
  company: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  borough: string | null;
  siteHint: string | null;
  notes: string | null;
  // The relationship contact (Session ALO): whoever actually drove the enquiry in, when known and
  // distinct from the primary contact above (e.g. a LOLER pre-contract enquiry's actual sender).
  secondaryContactName: string | null;
  secondaryContactEmail: string | null;
  source: string;
  status: string; // raw stored status (may be the legacy "qualified")
  stage: ProspectStatus; // canonical pipeline column (legacy values bucketed)
  estimatedValue: number | null;
  ownerUserId: string | null;
  ownerName: string | null;
  lostReason: string | null;
  convertedSiteId: string | null;
  outreachOptedOut: boolean;
  // Intent score (Session Z, Item 3): computed on read, present on the list + detail.
  score?: number;
  band?: ProspectScoreBand;
  // Session ADA: the valid next pipeline stages for this lead, so the list row can offer a
  // quick "edit status" without re-deriving the transition rules on the client. Present on the
  // list (the detail carries the required, non-optional field of the same name).
  allowedNextStatuses?: ProspectStatus[];
  createdAt: string;
  updatedAt: string;
}

export type ProspectScoreBand = "hot" | "warm" | "cool" | "new";

export interface HotProspect {
  id: string;
  name: string;
  company: string;
  score: number;
  band: ProspectScoreBand;
}

// ---- Per-customer CALL-OUT rate override (Session ADB) ----
// The effective call-out rates: all figures in pence (ex VAT); oohMultiplier is a plain factor.
export interface EffectiveCallOutRates {
  firstHourPence: number;
  additionalHourPence: number;
  oohMultiplier: number;
  travelPerHourPence: number;
}

// The customer's stored override (a null field falls back to the standard rate at charge time).
export interface RateOverrideDto {
  firstHourPence: number | null;
  additionalHourPence: number | null;
  oohMultiplier: number | null;
  travelPerHourPence: number | null;
  notes: string | null;
  updatedAt: string | null;
}

// GET / create / update response: the standard rates plus the override (or null when none).
export interface RateOverridePayload {
  standard: EffectiveCallOutRates;
  override: RateOverrideDto | null;
}

// Create / update body: every field is optional (omit to leave unchanged) and nullable (send null
// to clear a field back to the standard rate). Pence for the rate fields, a plain factor for OOH.
export interface RateOverrideInput {
  firstHourPence?: number | null;
  additionalHourPence?: number | null;
  oohMultiplier?: number | null;
  travelPerHourPence?: number | null;
  notes?: string | null;
}

// ---- Coverage zones + the call-out SLA (Session ADQ) ----
// A coverage zone decides how quickly ELE aims to attend a call-out at a given postcode. A site is
// placed in a zone by the OUTWARD CODE of its postcode (the first half, e.g. E17). Admin only.
export interface CoverageZone {
  id: string;
  code: string;
  name: string;
  outwardCodes: string[];
  attendanceTargetMins: number;
  // Null means nobody set a target for this leg. ELE publishes no fix-time promise, so this is the
  // normal state: a fix is then reported as "not targeted", never as a pass or a breach.
  fixTargetMins: number | null;
  entrapmentAttendanceTargetMins: number | null;
  // The zone used for a postcode no zone lists. At most one per tenant (enforced by the server).
  isFallback: boolean;
  sortOrder: number;
  notes: string | null;
  updatedAt: string | null;
}

// Create / update body. Every field is optional on update (omit to leave unchanged); the nullable
// targets take null to clear them back to the fallback rung.
export interface CoverageZoneInput {
  code?: string;
  name?: string;
  outwardCodes?: string[];
  attendanceTargetMins?: number;
  fixTargetMins?: number | null;
  entrapmentAttendanceTargetMins?: number | null;
  isFallback?: boolean;
  sortOrder?: number;
  notes?: string | null;
}

// POST /zones/seed: `seeded` is false when the tenant already had zones (the call is idempotent and
// changed nothing), in which case `zones` is simply what already exists.
export interface CoverageZoneSeedResult {
  seeded: boolean;
  zones: CoverageZone[];
}

// The platform default targets, echoed so a negotiated figure is always shown next to the figure it
// replaces. fixTargetMins is null because ELE publishes no fix-time promise.
export interface PlatformSlaTargets {
  attendanceTargetMins: number;
  fixTargetMins: number | null;
  entrapmentAttendanceTargetMins: number;
}

// The customer's stored response-time override. A null field falls back at call-out time down the
// chain: this override -> the site's coverage zone -> the platform default.
export interface CustomerSlaOverrideDto {
  attendanceTargetMins: number | null;
  fixTargetMins: number | null;
  entrapmentAttendanceTargetMins: number | null;
  notes: string | null;
  updatedAt: string | null;
}

// One zone this customer's sites actually resolve to, with how many sit in it. This is the rung a
// cleared override field REALLY falls back to, so the office is shown the true figure rather than a
// platform default that may apply to none of their sites.
export interface CustomerSlaZoneFallback {
  code: string;
  name: string;
  siteCount: number;
  attendanceTargetMins: number;
  fixTargetMins: number | null;
  entrapmentAttendanceTargetMins: number | null;
}

// The create / update response: the platform defaults plus the override (or null when none). NOTE
// the write routes deliberately do NOT return zoneFallbacks (only GET does), so a caller holding a
// GET payload must keep its existing zoneFallbacks rather than expect one back from a save.
export interface SlaOverrideWriteResult {
  platform: PlatformSlaTargets;
  override: CustomerSlaOverrideDto | null;
}

// The GET response: the write result plus the customer's real fallback rung.
export interface SlaOverridePayload extends SlaOverrideWriteResult {
  zoneFallbacks: {
    zones: CustomerSlaZoneFallback[];
    // Sites that could not be placed in a zone at all (no readable postcode, or a postcode no zone
    // claims with no fallback zone set). These use the platform default.
    unplacedSites: number;
  };
}

// Create / update body: every field is optional (omit to leave unchanged) and nullable (send null to
// clear it back down the fallback chain). Whole minutes.
export interface SlaOverrideInput {
  attendanceTargetMins?: number | null;
  fixTargetMins?: number | null;
  entrapmentAttendanceTargetMins?: number | null;
  notes?: string | null;
}

// ---- Call-out SLA performance report (Session ADQ) ----
// A READ-ONLY MIRROR of the DTO returned by GET /api/sla/report (server/src/modules/sla/
// slaReport.routes.ts, computed by the pure server/src/modules/sla/slaStats.ts). Office + admin,
// unlike the zone config above: the office reads performance, it just cannot change the promise.
//
// EVERY NULLABLE PERCENTAGE BELOW CARRIES THE LOCKED HONESTY RULE. Null means the denominator was
// empty, i.e. nothing could be measured. It is NEVER 0 and NEVER 100: a period with no measurable
// call-out has not failed and has not aced it. A consumer must render null as "No data".
export type SlaOutcome = "met" | "breached" | "open" | "unmeasured" | "not_targeted";

// Counts keyed by the outcome union itself, so a breakdown is exhaustive by construction.
export type SlaOutcomeCounts = Record<SlaOutcome, number>;

export interface SlaStats {
  // Every call-out reported in the window, whatever can or cannot be said about it.
  total: number;
  // The RESPONSE leg: logged to an engineer ASSIGNED (Session ATC, D18). The headline leg.
  responseBreakdown: SlaOutcomeCounts;
  // The FIX leg: the engineer's arrival to sign-off. ELE publishes no fix-time promise, so
  // `not_targeted` is the normal state here.
  fixBreakdown: SlaOutcomeCounts;
  // met / (met + breached). Null when nothing could be judged; open, unmeasured and not_targeted are
  // all outside the denominator, because none of them is a pass or a failure.
  responseWithinTargetPct: number | null;
  fixWithinTargetPct: number | null;
  // Two different numbers, never one: time to assign an engineer, and time on site until signed off.
  // Each over its own measured legs only; null when there are none.
  avgResponseMins: number | null;
  medianResponseMins: number | null;
  avgFixMins: number | null;
  medianFixMins: number | null;
  // What proportion of the window the response leg cannot speak to at all.
  unmeasuredPct: number | null;
  // Session AUT: the SLA holds, reported separately: how many call-outs spent time waiting on the customer, and the
  // total time held. The response figures above already leave that time out. Optional for an older cache.
  heldCallouts?: number;
  heldMins?: number;
}

export interface SlaFirstTimeFixStats {
  // fixed / (fixed + notFixed). Null when nothing could be assessed.
  firstTimeFixPct: number | null;
  fixed: number;
  notFixed: number;
  // Completed call-outs that cannot honestly be assessed (no linked lift, or no completion time),
  // reported openly rather than counted as passes.
  unassessable: number;
}

export interface SlaGroupStats extends SlaStats {
  // The grouping id (a customer id, a site id, or a zone code). NULL is the honest gap bucket, whose
  // `label` names the gap ("No zone set" means a site needs a postcode, not a zone called "none").
  key: string | null;
  label: string;
}

export interface SlaReport {
  generatedAt: string;
  window: ReportingWindow;
  groupBy: "all" | "customer" | "site" | "zone";
  // The first-time-fix recurrence rule, surfaced so the UI names the rule from one source rather
  // than restating the number in its own copy.
  recurrenceDays: number;
  defaultWindowDays: number;
  overall: SlaStats;
  firstTimeFix: SlaFirstTimeFixStats;
  byCustomer: SlaGroupStats[];
  bySite: SlaGroupStats[];
  byZone: SlaGroupStats[];
}

// An office/admin user available as a lead owner (the picker + filter source).
export interface ProspectOwner {
  id: string;
  displayName: string;
  role: Role;
}

export interface ProspectListFilters {
  status?: ProspectStatus | "all";
  owner?: string; // a user id, or "me", or "all"
  source?: ProspectSource | "all";
}

// A lightweight customer option for a picker (the full list endpoint returns more).
export interface CustomerOption {
  id: string;
  name: string;
}

// ---- Complaints (ISO 9001 clause 10.2: nonconformity and corrective action) ----
export type ComplaintSource = "customer" | "third_party" | "internal" | "regulatory";
export type ComplaintSeverity = "low" | "medium" | "high" | "critical";
export type ComplaintStatus = "open" | "investigating" | "resolved" | "closed";

export interface ComplaintListItem {
  id: string;
  ref: string;
  dateRaised: string;
  source: ComplaintSource;
  customerId: string | null;
  customerName: string | null;
  description: string;
  category: string | null;
  severity: ComplaintSeverity;
  status: ComplaintStatus;
  targetResolutionDate: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ComplaintDetail extends ComplaintListItem {
  rootCause: string | null;
  correctiveAction: string | null;
  preventiveAction: string | null;
  responsibleUserId: string | null;
  responsibleName: string | null;
  notes: string | null;
  allowedNextStatuses: ComplaintStatus[];
}

export interface ComplaintCreateInput {
  source?: ComplaintSource;
  customerId?: string | null;
  description: string;
  category?: string | null;
  severity?: ComplaintSeverity;
  responsibleUserId?: string | null;
  targetResolutionDate?: string | null;
  dateRaised?: string | null;
  notes?: string | null;
}

export interface ComplaintUpdateInput {
  source?: ComplaintSource;
  customerId?: string | null;
  description?: string;
  category?: string | null;
  severity?: ComplaintSeverity;
  rootCause?: string | null;
  correctiveAction?: string | null;
  preventiveAction?: string | null;
  responsibleUserId?: string | null;
  targetResolutionDate?: string | null;
  notes?: string | null;
  status?: ComplaintStatus;
}

// ---- Accidents and incidents (Session AOM: CHAS Q94/Q95, RIDDOR 2013) ----
// The register of workplace accidents, injuries and near misses TO A PERSON, their RIDDOR
// determination and the investigation that follows. OFFICE/ADMIN ONLY: these rows carry an injured
// person's name, employer, contact details and injury detail, and none of it reaches the engineer
// PWA. The PWA has a write-only quick-log whose response carries no stored field at all
// (api.engineer.reportAccident below).
export type AccidentEventType =
  | "employee_injury"
  | "non_worker_injury"
  | "dangerous_occurrence"
  | "near_miss"
  | "occupational_disease"
  | "other";
export type AccidentHospitalAdmission = "none" | "treated_discharged" | "admitted";
export type AccidentRiddorCategory =
  | "death"
  | "specified_injury"
  | "over_7_day"
  | "non_worker_hospital"
  | "occupational_disease"
  | "dangerous_occurrence"
  | "gas_incident"
  | "not_reportable";
export type AccidentStatus = "open" | "under_investigation" | "reported_to_hse" | "closed";

// The REGISTER row. Deliberately narrower than the detail: no injured person's name, no contact
// detail, no injury detail and no investigation text. injuredPersonRecorded says that somebody was
// hurt without saying who, so the list still reads correctly at a glance.
export interface AccidentListItem {
  id: string;
  ref: string;
  dateOfEvent: string;
  eventType: AccidentEventType;
  siteId: string | null;
  siteName: string | null;
  assetId: string | null;
  eleNumber: string | null;
  location: string | null;
  description: string;
  injuredPersonRecorded: boolean;
  riddorReportable: boolean | null;
  riddorCategory: AccidentRiddorCategory | null;
  status: AccidentStatus;
  hseReportedAt: string | null;
  // Read-only countdown to the written report. Null once reported, and null where the category
  // states no written window. Nothing schedules or chases from it.
  daysToWrittenReport: number | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AccidentDetail extends AccidentListItem {
  injuredName: string | null;
  injuredRole: string | null;
  injuredEmployer: string | null;
  injuredContact: string | null;
  natureOfInjury: string | null;
  bodyPartAffected: string | null;
  hospitalAdmission: AccidentHospitalAdmission | null;
  immediateAction: string | null;
  witnesses: string | null;
  photos: string[];
  reportedByUserId: string | null;
  reportedByName: string | null;
  riddorTimescaleGuidance: string | null;
  hseReportMethod: string | null;
  hseReferenceNumber: string | null;
  hseReportedByUserId: string | null;
  riddorAssessedByUserId: string | null;
  riddorAssessedByName: string | null;
  riddorAssessedAt: string | null;
  investigatorName: string | null;
  investigationStartedAt: string | null;
  investigationCompletedAt: string | null;
  sequenceOfEvents: string | null;
  rootCause: string | null;
  contributingFactors: string | null;
  immediateActionToPreventRecurrence: string | null;
  correctiveAction: string | null;
  evidenceAttached: string[];
  closedByUserId: string | null;
  closedByName: string | null;
  closedAt: string | null;
  allowedNextStatuses: AccidentStatus[];
  // Mirrors of the two server guards, so a control can be disabled WITH ITS REASON rather than the
  // office pressing it and reading a refusal.
  canMarkReportedToHse: boolean;
  canClose: boolean;
}

export interface AccidentCreateInput {
  dateOfEvent: string;
  eventType: AccidentEventType;
  siteId?: string | null;
  assetId?: string | null;
  location?: string | null;
  description: string;
  immediateAction?: string | null;
  witnesses?: string | null;
  injuredName?: string | null;
  injuredRole?: string | null;
  injuredEmployer?: string | null;
  injuredContact?: string | null;
  natureOfInjury?: string | null;
  bodyPartAffected?: string | null;
  hospitalAdmission?: AccidentHospitalAdmission | null;
}

// NOTE WHAT IS NOT HERE: riddorReportable and riddorTimescaleGuidance. Both are DERIVED server-side
// from riddorCategory (RULE 6), so the client has no way to send a value that contradicts the
// category. That is structural rather than a convention somebody has to remember.
export interface AccidentUpdateInput {
  dateOfEvent?: string | null;
  eventType?: AccidentEventType;
  siteId?: string | null;
  assetId?: string | null;
  location?: string | null;
  description?: string;
  immediateAction?: string | null;
  witnesses?: string | null;
  injuredName?: string | null;
  injuredRole?: string | null;
  injuredEmployer?: string | null;
  injuredContact?: string | null;
  natureOfInjury?: string | null;
  bodyPartAffected?: string | null;
  hospitalAdmission?: AccidentHospitalAdmission | null;
  riddorCategory?: AccidentRiddorCategory | null;
  hseReportedAt?: string | null;
  hseReportMethod?: "online" | "telephone" | null;
  hseReferenceNumber?: string | null;
  investigatorName?: string | null;
  investigationStartedAt?: string | null;
  investigationCompletedAt?: string | null;
  sequenceOfEvents?: string | null;
  rootCause?: string | null;
  contributingFactors?: string | null;
  immediateActionToPreventRecurrence?: string | null;
  correctiveAction?: string | null;
  evidenceAttached?: string[];
  status?: AccidentStatus;
}

export interface AccidentListFilters {
  status?: AccidentStatus | "all";
  eventType?: AccidentEventType | "all";
  reportable?: "yes" | "no" | "unassessed" | "all";
  siteId?: string;
  from?: string;
  to?: string;
  archived?: "include" | "only";
}

// The compliance dashboard widget (Session AOO): a small, targeted summary, not the full register.
export interface AccidentDashboardNeedsAttentionItem {
  id: string;
  ref: string;
  eventType: AccidentEventType;
  reason: "unassessed" | "report_due";
  daysToWrittenReport: number | null;
}
export interface AccidentDashboardSummary {
  openCount: number;
  // The literal CHAS Q95 figure: RIDDOR reportable accidents in the last 3 years.
  reportableLast3Years: number;
  needsAttention: AccidentDashboardNeedsAttentionItem[];
}

// HSE / Local Authority enforcement notices and prosecutions (Session AON), CHAS Q94's companion
// register to the accident types above.
export type EnforcementActionType = "improvement_notice" | "prohibition_notice" | "prosecution" | "other";
export type EnforcementActionStatus = "open" | "remedied" | "closed";

export interface EnforcementActionListItem {
  id: string; // the action handle -- never tenantId
  ref: string;
  actionType: EnforcementActionType;
  issuingBody: string;
  dateIssued: string;
  status: EnforcementActionStatus;
  // Whether a document is filed. NEVER the raw Drive link -- that never reaches the browser
  // (downloaded via the authenticated proxy), the identical EngineerCertification pattern.
  hasDocument: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EnforcementActionDetail extends EnforcementActionListItem {
  description: string;
  remedialAction: string | null;
  remedialActionCompletedAt: string | null;
  notes: string | null;
  allowedNextStatuses: EnforcementActionStatus[];
}

// The compliance dashboard widget (Session AOO).
export interface EnforcementActionDashboardSummary {
  openCount: number;
}

export interface EnforcementActionCreateInput {
  actionType: EnforcementActionType;
  issuingBody: string;
  dateIssued: string;
  description: string;
  remedialAction?: string | null;
  remedialActionCompletedAt?: string | null;
  notes?: string | null;
}

// NOTE WHAT IS NOT HERE: documentUrl. It is set only by the dedicated upload call below, never as a
// plain string here -- a client could otherwise point the record at an arbitrary URL with nothing
// actually filed to Drive.
export interface EnforcementActionUpdateInput {
  actionType?: EnforcementActionType;
  issuingBody?: string;
  dateIssued?: string;
  description?: string;
  remedialAction?: string | null;
  remedialActionCompletedAt?: string | null;
  notes?: string | null;
  status?: EnforcementActionStatus;
}

export interface EnforcementActionListFilters {
  status?: EnforcementActionStatus | "all";
}

// Purchase orders (Session ASY): supplier orders, upstream of van stock.
export type PurchaseOrderStatus = "draft" | "ordered" | "partially_received" | "received" | "cancelled";

export interface PurchaseOrderItem {
  id: string;
  partCode: string | null;
  description: string;
  quantity: number;
  unitCost: number | null;
  receivedQuantity: number;
  outstanding: number;
}

export interface PurchaseOrderListItem {
  id: string;
  reference: string;
  supplier: string;
  status: PurchaseOrderStatus;
  statusLabel: string;
  // Session PJ1F: a PROJECT order has no engineer (it is received against the project, never into a van).
  engineerId: string | null;
  engineerName: string | null;
  projectId?: string | null;
  projectRef?: string | null;
  projectSiteName?: string | null;
  supplierQuoteRef?: string | null;
  // When a Gmail DRAFT of the order was made to the supplier (never a send).
  supplierDraftedAt?: string | null;
  raisedById: string;
  raisedByName: string;
  notes: string | null;
  // Session AUH (Phase 7): when the delivery is expected (a London day), where a supplier chase goes, and the job it is for.
  // Optional for an older response.
  expectedAt?: string | null;
  supplierEmail?: string | null;
  supplierCopyEmail?: string | null;
  forJobId?: string | null;
  orderedAt: string | null;
  receivedAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
  updatedAt: string;
  items: PurchaseOrderItem[];
  itemCount: number;
  totalCostPence: number | null;
  canMarkOrdered: boolean;
  canCancel: boolean;
  canReceive: boolean;
  canEditDraft: boolean;
  canDelete: boolean;
}

export interface PurchaseOrderItemInput {
  partCode?: string | null;
  description: string;
  quantity: number;
  unitCost?: number | null;
}

export interface PurchaseOrderCreateInput {
  supplier: string;
  // A van stock order names the engineer; a project order names the project and no engineer.
  engineerId?: string;
  projectId?: string;
  supplierQuoteRef?: string | null;
  supplierEmail?: string | null;
  notes?: string | null;
  items: PurchaseOrderItemInput[];
}

export interface PurchaseOrderUpdateInput {
  supplier?: string;
  engineerId?: string;
  supplierQuoteRef?: string | null;
  notes?: string | null;
  items?: PurchaseOrderItemInput[];
}

// Session PJ1F: the Gmail draft of a purchase order to its supplier (never a send).
export interface PurchaseOrderSupplierDraftPreview {
  preview: true;
  to: string;
  subject: string;
  attachments: { name: string; bytes: number }[];
  itemCount: number;
  alreadyDraftedAt: string | null;
}
export interface PurchaseOrderSupplierDraftDone {
  preview: false;
  draftId: string;
  to: string;
  subject: string;
  attachments: { name: string; bytes: number }[];
  itemCount: number;
  supplierDraftedAt: string;
}

// Session PJ1F: what "Create purchase order" on a project's survey view fills in (a suggestion; nothing is stored).
export interface ProjectSurveyPurchaseOrderPrefill {
  project: { projectRef: string; siteName: string | null; siteAddress: string | null; sitePostcode: string | null };
  lifts: { surveyId: string; label: string; typeWords: string | null; reviewed: boolean; figures: { label: string; value: string }[] }[];
  items: { description: string; quantity: number; partCode: null; unitCost: null }[];
  notes: string;
  leftOut: string[];
}

export interface PurchaseOrderListFilters {
  status?: PurchaseOrderStatus;
  supplier?: string;
}

// The RIDDOR 2013 reference table, SERVED rather than mirrored here. One home for the criteria the
// office reads and the guidance snapshotted onto the record, so the two cannot drift.
export interface RiddorCategoryRef {
  key: AccidentRiddorCategory;
  label: string;
  reportable: boolean;
  criteria: string;
  immediacy: "without_delay" | "as_soon_as_reasonably_practicable" | "not_required";
  writtenReportWithinDays: number | null;
  writtenReportFrom: "date_of_event" | "diagnosis" | null;
  form: string | null;
  timescaleGuidance: string;
}

export interface RiddorReference {
  disclaimer: string;
  evidenceSuggestions: string[];
  categories: RiddorCategoryRef[];
}

// The WHOLE response an engineer gets from the PWA quick-log. Nothing stored is echoed back: not
// the description they typed, and certainly nothing the office adds later.
export interface AccidentQuickLogAck {
  ok: true;
  ref: string;
  message: string;
}

export interface AccidentQuickLogInput {
  eventType: AccidentEventType;
  description: string;
  dateOfEvent?: string;
  siteId?: string;
  location?: string | null;
  injuredName?: string | null;
}

export interface ComplaintListFilters {
  status?: ComplaintStatus | "all";
  source?: ComplaintSource | "all";
  severity?: ComplaintSeverity | "all";
  customerId?: string;
  from?: string;
  to?: string;
}

// ---- Internal audits (ISO 9001 clause 9.2) ----
// Personal compliance documents (Session AL): DBS / data-protection / insurance certs held
// against a named individual. status is derived server-side from the expiry date.
export type PersonalDocType = "DBS_CHECK" | "DATA_PROTECTION_CERT" | "INSURANCE_CERT" | "OTHER";
export type PersonalDocStatus = "valid" | "expiring" | "expired" | "no-expiry";

export interface PersonalDocDto {
  id: string;
  personName: string;
  documentType: PersonalDocType;
  documentTypeLabel: string;
  issuedDate: string;
  expiryDate: string | null;
  status: PersonalDocStatus;
  daysUntilExpiry: number | null;
  driveFileUrl: string | null;
  hasDownload: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PersonalDocGroup {
  personName: string;
  documents: PersonalDocDto[];
}

export interface PersonalComplianceList {
  groups: PersonalDocGroup[];
  total: number;
}

export interface PersonalComplianceCreateInput {
  personName: string;
  documentType: PersonalDocType;
  issuedDate: string;
  expiryDate?: string | null;
  driveFileId: string;
  driveFileUrl?: string | null;
  notes?: string | null;
}

export type InternalAuditStatus = "open" | "actions_pending" | "closed";

export interface InternalAuditListItem {
  id: string;
  auditRef: string;
  auditDate: string;
  auditedArea: string;
  auditorName: string;
  status: InternalAuditStatus;
  actionsDueDate: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InternalAuditDetail extends InternalAuditListItem {
  findings: string;
  actionsRequired: string | null;
  actionsCompletedDate: string | null;
  allowedNextStatuses: InternalAuditStatus[];
}

export interface InternalAuditCreateInput {
  auditDate: string;
  auditedArea: string;
  auditorName: string;
  findings: string;
  actionsRequired?: string | null;
  actionsDueDate?: string | null;
  actionsCompletedDate?: string | null;
  status?: InternalAuditStatus;
}

export interface InternalAuditUpdateInput {
  auditDate?: string;
  auditedArea?: string;
  auditorName?: string;
  findings?: string;
  actionsRequired?: string | null;
  actionsDueDate?: string | null;
  actionsCompletedDate?: string | null;
  status?: InternalAuditStatus;
}

// ---- Management reviews (ISO 9001 clause 9.3) ----
export type ManagementReviewStatus = "draft" | "finalised";

export interface ManagementReviewListItem {
  id: string;
  reviewRef: string;
  reviewDate: string;
  attendees: string;
  status: ManagementReviewStatus;
  nextReviewDate: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ManagementReviewDetail extends ManagementReviewListItem {
  inputSummary: string;
  decisions: string;
  actionsRequired: string | null;
  actionsDueDate: string | null;
  allowedNextStatuses: ManagementReviewStatus[];
}

export interface ManagementReviewCreateInput {
  reviewDate: string;
  attendees: string;
  inputSummary: string;
  decisions: string;
  actionsRequired?: string | null;
  actionsDueDate?: string | null;
  nextReviewDate?: string | null;
  status?: ManagementReviewStatus;
}

export interface ManagementReviewUpdateInput {
  reviewDate?: string;
  attendees?: string;
  inputSummary?: string;
  decisions?: string;
  actionsRequired?: string | null;
  actionsDueDate?: string | null;
  nextReviewDate?: string | null;
  status?: ManagementReviewStatus;
}

// ---- Environmental records and objectives (ISO 14001) ----
export type EnvironmentalRecordType = "waste_disposal" | "environmental_incident" | "near_miss" | "fuel_consumption" | "other";
export type ObjectiveStatus = "on_track" | "at_risk" | "achieved" | "missed";

export interface EnvRecordItem {
  id: string;
  ref: string;
  recordType: EnvironmentalRecordType;
  date: string;
  description: string;
  quantity: number | null;
  unit: string | null;
  disposalMethod: string | null;
  contractor: string | null;
  // The contractor's waste carrier registration/licence reference, if ELE holds one on file.
  contractorRegistrationRef: string | null;
  wasteCode: string | null;
  location: string | null;
  siteId: string | null;
  siteName: string | null; // resolved for display
  assetId: string | null;
  assetLabel: string | null; // the lift's ELE number, resolved for display
  severity: string | null;
  actionTaken: string | null;
  status: string | null;
  notes: string | null;
  // ISO string when the record is archived (attached statutory waste evidence), else null.
  archivedAt: string | null;
  // Attached waste consignment note count (populated on the detail GET; used to frame delete-vs-archive).
  wasteNoteCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface EnvRecordCreateInput {
  recordType: EnvironmentalRecordType;
  date: string;
  description: string;
  quantity?: number | null;
  unit?: string | null;
  disposalMethod?: string | null;
  contractor?: string | null;
  contractorRegistrationRef?: string | null;
  wasteCode?: string | null;
  location?: string | null;
  siteId?: string | null;
  assetId?: string | null;
  severity?: string | null;
  actionTaken?: string | null;
  status?: string | null;
  notes?: string | null;
}

export type EnvRecordUpdateInput = Partial<EnvRecordCreateInput>;

export interface EnvRecordListFilters {
  type?: EnvironmentalRecordType | "all";
  from?: string;
  to?: string;
  // When true, list ONLY archived records (kept for statutory retention), not the active default.
  archived?: boolean;
}

// A waste consignment note attached to an environmental record. The raw Drive link is never
// exposed; the file is fetched through the authenticated download proxy.
export interface WasteNoteItem {
  id: string;
  filename: string;
  uploadedAt: string;
}

export interface EnvObjectiveItem {
  id: string;
  ref: string;
  objective: string;
  target: string;
  targetDate: string;
  owner: string | null;
  status: ObjectiveStatus;
  progressNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EnvObjectiveCreateInput {
  objective: string;
  target: string;
  targetDate: string;
  owner?: string | null;
  status?: ObjectiveStatus;
  progressNotes?: string | null;
}

export type EnvObjectiveUpdateInput = Partial<EnvObjectiveCreateInput>;

// ---- Communications / CRM timeline (office/admin) ----
export type CommunicationContactType = "customer" | "prospect";
export type CommunicationChannel = "email" | "phone" | "whatsapp" | "meeting" | "note" | "sms";
export type CommunicationDirection = "inbound" | "outbound" | "internal";

// A timeline target: a customer/prospect contact (the polymorphic pair) or a standalone
// subcontractor (the dedicated subcontractorId).
export type CommunicationTarget =
  | { contactType: CommunicationContactType; contactId: string }
  | { subcontractorId: string };

// The query string for a target (used by list + timeline).
export function communicationTargetQuery(t: CommunicationTarget): string {
  return "subcontractorId" in t
    ? `subcontractorId=${encodeURIComponent(t.subcontractorId)}`
    : `contactType=${t.contactType}&contactId=${encodeURIComponent(t.contactId)}`;
}

export interface CommunicationItem {
  id: string;
  contactType: CommunicationContactType | null;
  contactId: string | null;
  channel: CommunicationChannel;
  direction: CommunicationDirection;
  subject: string | null;
  body: string;
  occurredAt: string;
  loggedByUserId: string | null;
  loggedByName: string | null;
  relatedQuoteId: string | null;
  relatedProjectId: string | null;
  systemGenerated: boolean;
  createdAt: string;
}

export type CommunicationCreateInput = CommunicationTarget & {
  channel: CommunicationChannel;
  direction: CommunicationDirection;
  subject?: string;
  body: string;
  occurredAt?: string;
  relatedQuoteId?: string;
  relatedProjectId?: string;
};

export interface CommunicationUpdateInput {
  channel?: CommunicationChannel;
  direction?: CommunicationDirection;
  subject?: string;
  body?: string;
  occurredAt?: string;
}

// A merged timeline entry (a logged communication, or a quote/project/contact event).
export type TimelineKind = "communication" | "quote" | "project" | "contact";
export interface TimelineItem {
  kind: TimelineKind;
  id: string;
  // Raw Communication id for a "communication" row (null for quote/project events); used
  // to delete the logged interaction (the `id` above is a prefixed display key).
  commId: string | null;
  at: string;
  title: string;
  detail: string | null;
  channel: string | null;
  direction: string | null;
  loggedByName: string | null;
  systemGenerated: boolean;
}

// ---- Natural-language fleet query (office/admin) ----
export interface AiQueryResult {
  answer: string;
  available: boolean;
  context?: Record<string, number>;
}

// ---- Assets: LOLER certificate upload (office/admin) ----
// ELE does not issue LOLER certificates -- the examining body (ELTI or another approved
// inspector) does. The office uploads that PDF; the outcome mirrors the inspection.
export type LolerUploadOutcome = "clear" | "advisory" | "immediate_defect";

export interface UploadLolerInput {
  inspectionDate?: string; // ISO date; defaults to today server-side
  nextDueDate?: string; // ISO date; defaults to 6 months after the inspection date
  inspectorName?: string; // the examining body; defaults to "ELTI (UK) Ltd"
  certificateRef?: string;
  outcome: LolerUploadOutcome;
  notes?: string;
}

export interface UploadedLolerCertificate {
  lolerCertUrl: string; // Drive link (stored on the asset)
  lolerCertExpiry: string; // ISO -- next examination due
  lastLolerDate: string; // ISO -- the examination date
  outcome: LolerUploadOutcome;
  inspector: string;
}

// ---- Statutory test certificates (load / hydraulic pressure / overspeed governor) ----
export type CertificateType = "load_test" | "hydraulic_pressure" | "overspeed_governor";
export type CertificateResult = "pass" | "fail" | "conditional_pass";

export interface GenerateCertificateInput {
  certificateType: CertificateType;
  testDate: string; // ISO
  result: CertificateResult;
  engineerName: string;
  conditions?: string;
  nextTestDue?: string; // ISO
}

export interface AssetCertificateItem {
  id: string;
  certNumber: string;
  type: string; // plain-English label
  result: string; // plain-English label
  testDateDisplay: string;
  nextTestDueDisplay: string | null;
  engineerName: string;
  conditions: string | null;
  issuedDisplay: string;
}

// ---- SAFed supplementary testing (office/admin) ----
export type SafedStatus = "overdue" | "due-soon" | "scheduled" | "no-date";

export interface SafedTestItem {
  id: string;
  testType: string;
  lastTestDate: string | null;
  intervalMonths: number;
  nextDueDate: string | null;
  status: SafedStatus;
  certificateRef: string | null;
  hasCertificate: boolean;
  notes: string | null;
}

export interface SafedTestInput {
  testType: string;
  lastTestDate?: string | null;
  intervalMonths: number;
  certificateRef?: string | null;
  certificateUrl?: string | null;
  notes?: string | null;
}

export interface SafedDueItem {
  id: string;
  assetId: string;
  ele: string;
  siteName: string;
  customerName: string;
  testType: string;
  lastTestDate: string | null;
  intervalMonths: number;
  nextDueDate: string | null;
  status: SafedStatus;
}

// ---- Site service history (office/admin) ----
export interface ServiceHistoryItem {
  id: string;
  assetId: string | null;
  eleNumber: string;
  // The canonical {TYPE}-ELE-{number}-{DD}-{MM}-{YY} reference (Session AJF). Null when no
  // canonical prefix applies (loler) or no real ELE number resolved -- fall back to `eleNumber`.
  reference: string | null;
  dateIso: string | null;
  type: string; // raw JobType key for colour/label (service | loler | callout | repair)
  status: string;
  engineerFirstName: string | null;
  outcome: string;
  defectCount: number;
  partsCount: number;
  photoCount: number;
  notes: string | null;
  defects: string[];
  // True when this visit has a service-record sheet the office can open (Session ADF); false for a
  // scheduled visit with no sheet yet. Surfaces gate the "Open service record" affordance off this.
  hasJobSheet: boolean;
}

export interface ServiceHistoryResponse {
  items: ServiceHistoryItem[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

// The Customer Overview tab (Phase 2, C-1/C-2/C-3/C-17): one screen showing a customer's whole
// footprint. Lift status is LIVE (never a stale stored date -- the STALE-DATA RULE); see
// GET /api/customers/:id/overview.
export interface CustomerOverviewLift {
  id: string;
  reference: string;
  liftType: string | null;
  assetClass: string;
  complianceStatus: "green" | "amber" | "red";
  nextServiceDue: string | null;
  lolerDue: string | null;
}
export interface CustomerOverviewSite {
  id: string;
  name: string;
  address: string | null;
  lifts: CustomerOverviewLift[];
}
export interface CustomerOverviewWorkItem {
  id: string;
  kind: "job" | "callout";
  type: string;
  status: string;
  date: string | null;
  engineerName: string | null;
  liftId: string | null;
  liftRef: string | null;
}
export interface CustomerOverviewDefect {
  id: string;
  severity: string;
  description: string;
  createdAt: string;
  liftId: string;
  liftRef: string;
}
export interface CustomerOverviewContract {
  id: string;
  siteId: string;
  siteName: string;
  tier: string;
  annualValue: number;
  status: string;
  renewalDate: string | null;
}
export interface CustomerOverviewQuote {
  id: string;
  quoteRef: string;
  siteName: string | null;
  value: number | null;
  status: string;
  sentAt: string | null;
}
export interface CustomerOverviewInvoice {
  id: string;
  invoiceNumber: string | null;
  amount: number;
  dueDate: string | null;
  status: string;
  daysOverdue: number;
}
export interface CustomerOverview {
  customerId: string;
  sites: CustomerOverviewSite[];
  openWork: CustomerOverviewWorkItem[];
  openDefects: CustomerOverviewDefect[];
  contracts: CustomerOverviewContract[];
  quotes: CustomerOverviewQuote[];
  invoices: {
    balanceOwed: number;
    overdueAmount: number;
    items: CustomerOverviewInvoice[];
  };
}

export interface SiteDetail {
  id: string;
  name: string;
  address: string;
  postcode: string | null;
  status: string;
  accessDetails: string | null;
  notes: string | null;
  customer: { id: string; name: string } | null;
  // The site's own current ACTIVE contract, if any (Session AQS, item C-5); null when the site has
  // none (a lead site, or one between contracts). A site can carry more than one Contract row over
  // its life, so this is deliberately the active one, not the most recent by any status.
  contract: { id: string; tier: string; renewalDate: string | null } | null;
  // Default engineer for this site's PPM visits (Session ARA, S-4; built to D84's standing
  // RECOMMENDATION, not a confirmed Director ruling -- see docs/ELE-V2-DIRECTOR-DECISIONS-2026-07-19.md).
  // Optional so older fixtures stay valid.
  defaultEngineerId?: string | null;
  // Session ATZ (Item 3): other names the site is known by on a property platform, so a work order matches it.
  aliases?: string[];
}

// ---- Compliance dashboard (office/admin) ----
export interface ComplianceDetailItem {
  id: string;
  assetId: string | null; // the lift id for LOLER/PPM rows (links to /office/lifts/:assetId); null for defect/contract rows
  label: string; // ELE number or contract reference
  siteName: string;
  customerName: string;
  dateIso: string | null;
  siteId: string | null; // links to the site detail; null for contract rows
}
export interface ComplianceTile {
  count: number;
  items: ComplianceDetailItem[];
}
export interface ComplianceOverview {
  totalLifts: number;
  fullyCompliant: number;
  lolerOverdue: ComplianceTile;
  lolerDueSoon: ComplianceTile;
  ppmOverdue: ComplianceTile;
  ppmDueSoon: ComplianceTile;
  defectsCatA: ComplianceTile;
  defectsCatB: ComplianceTile;
  renewalsDue90: ComplianceTile;
}

// ---- Consolidated compliance dashboard (Session X Item 4) ----
export type ComplianceHealthStatus = "red" | "amber" | "green";

export interface ComplianceLolerItem {
  id: string;
  assetId: string; // the lift id -- the row links straight to /office/lifts/:assetId
  ele: string;
  siteName: string;
  customerName: string;
  dateIso: string | null;
  status: "overdue" | "due_soon";
}
export interface ComplianceCertItem {
  certType: string;
  engineerName: string;
  expiryDate: string;
  status: string; // "expired" | "expiring"
  daysUntilExpiry: number | null;
}
export interface ComplianceCompetenceItem {
  recordId: string;
  engineerName: string;
  competenceArea: string;
  reviewStatus: string; // "overdue" | "due_soon"
  reviewDate: string | null;
}

export interface ComplianceDashboard {
  generatedAt: string;
  health: { status: ComplianceHealthStatus; totalOpen: number };
  loler: { overdue: number; dueSoon: number; items: ComplianceLolerItem[] };
  safed: { overdue: number; dueSoon: number; items: SafedDueItem[] };
  certs: { expired: number; expiring: number; items: ComplianceCertItem[] };
  competence: { overdue: number; dueSoon: number; items: ComplianceCompetenceItem[] };
  riddor: { open: number; items: RiddorFlagItem[] };
  modification: { open: number; items: ModReExamFlagItem[] };
}

// Session ACR: one row on the LOLER paperwork chase list -- a people-lifting care-equipment
// asset with UNKNOWN examination status (no recorded exam, so no due date is fabricated).
export interface LolerChaseItem {
  assetId: string;
  eleNumber: string | null;
  name: string | null; // the catalogue name (e.g. "Ceiling hoist (straight track)")
  siteId: string | null;
  siteName: string | null;
  customerName: string | null;
  contractId: string | null;
  contractRef: string | null; // the originating quote ref, where one exists
  since: string; // ISO: when the asset was created (how long the paperwork has been chased)
}

// ---- Service records / job sheets (office/admin, read-only) ----
export interface JobSheetChecklistEntry {
  id?: string; // template item id, preserved through an office amendment (certificate join)
  item: string;
  status: string;
  category?: string;
  note?: string;
}

export interface JobSheetPartUsed {
  partCode?: string;
  description: string;
  quantity: number;
  unitCost?: number;
}

// Session AQB (D78) / AQW: the engineer's answer for each additional item the contract covers
// (gates, doors, barriers, shutters that ride this lift's visits), read back off the stored sheet.
// NEVER a lift inspection area: this is a DIFFERENT thing the certificate states in its own
// sentence, per the ADDITIONAL ITEMS ON A LIFT CONTRACT rule. Empty on every record completed
// before D78 and on any contract that covers only lifts. Distinct from api.ts's own `CoveredItem`
// (the engineer's job-list shape, before an answer exists -- {id, description, qty,
// maintenanceOnly}); this is the ANSWERED shape, matching the server's CoveredItemCheckEntry.
export interface CoveredItemCheck {
  id: string;
  description: string;
  status: "pass" | "fail" | "na";
  maintenanceOnly: boolean;
  note?: string;
}

// One entry in a record's audit timeline (Session AQW, GET /api/audit-log). `before`/`after` are
// whatever shape that action's own `audit()` call wrote (server/src/lib/audit.ts) -- deliberately
// untyped here, since AuditLog.before/after are opaque Json and every writer shapes them
// differently; AuditTimeline renders them defensively.
export interface ActivityLogItem {
  id: string;
  at: string; // ISO
  actor: string;
  actorName: string;
  action: string;
  entity: string;
  entityId: string;
  recordPath: string | null; // an office route, or null when the record has no page
}
export interface ActivityLogPage {
  items: ActivityLogItem[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}
export interface ActivityLogFilters {
  users: { id: string; name: string; role: string }[];
  entities: string[];
}

export interface AuditTimelineEntry {
  actor: string; // a user id, or "system" for an automated change
  actorName: string; // resolved display name, "System", or "Unknown user"
  action: string;
  at: string; // ISO
  before: unknown;
  after: unknown;
}

// The office-entered retrospective completion payload (Session AEB). Deliberately carries NO
// signature fields and no partCode on parts (the server's strict schema rejects both): a
// backdated entry can never fabricate a signature or deduct today's van stock.
export interface BackdatedEntryBody {
  visitDate: string; // ISO datetime: the REAL past date the service was carried out
  engineerId?: string; // who did the work (Engineer register id); omit when not on the register
  checklist: JobSheetChecklistEntry[];
  defects?: string[];
  partsUsed?: { description: string; quantity: number; unitCost?: number }[];
  advisoryNotes?: string;
  engineerNotes?: string;
}

// Job-cost profitability (Session AK Item 2). All money in pence; null means "not recorded".
export interface JobProfitability {
  labourHours: number | null;
  // Session BDL (F3): labourCostPence, totalCostPence, marginPence and marginPercent are built from the engineer's cost
  // rate, which is admin only, so they are ABSENT (not null) for anyone who is not an admin.
  labourCostPence?: number | null;
  partsCostPence: number;
  otherCostsPence: number; // approved job-linked engineer expenses (mileage/materials/other), Session ATE
  totalCostPence?: number | null;
  chargePence: number | null;
  marginPence?: number | null;
  marginPercent?: number | null;
  // Which labour-cost inputs are present (Session ABU Part D): distinguishes a visit with no
  // recorded time from an engineer with no cost rate set. labourCostReason names the first to fix.
  hasTime: boolean;
  hasCostRate: boolean;
  labourCostReason: "ok" | "no_time" | "no_rate";
}

// Session BBH: who a job report goes to, and the server's preview of a send.
export interface ReportRecipients {
  to: string[];
  cc: string[];
}
export interface ReportSendBody {
  to?: string[];
  cc?: string[];
  sendAgain?: boolean;
}
export interface ReportSendPreview {
  preview: true;
  from: string | null;
  to: string[];
  cc: string[];
  suggestions: string[];
  alreadySent: boolean;
  sentToClientAt?: string | null;
  amendedSinceSent?: boolean;
  document: string;
}

// Session SC2B: the slim row GET /api/jobsheets?view=list answers (what the Service records table and cards read), plus whether removing
// the record archives it (the Archive / Delete label). The record behind it comes from GET /api/jobsheets/:jobId when a row is opened.
export type JobSheetSlimItem = Pick<
  JobSheetListItem,
  | "jobId" | "ref" | "reference" | "jobType" | "jobStatus" | "assetType" | "siteName" | "siteId" | "assetId" | "customerId" | "customerName"
  | "completedAt" | "receivedAt" | "emailSentAt" | "emailSentTo" | "driveFileUrl" | "defectsCount" | "equipmentOutOfService" | "archivedAt"
  | "sentToClientAt" | "invoiced" | "linkedInvoice" | "isCallout" | "riskAssessmentMissing" | "lateReason" | "openRemedialDefects"
> & { willArchive: boolean };

export type JobSheetListSortKey = "lift" | "type" | "site" | "completed" | "defects";

export interface JobSheetListItem {
  jobId: string | null;
  ref: string;
  // The canonical {TYPE}-ELE-{number}-{DD}-{MM}-{YY}[-INV{XXX}] reference (Session AJF): what a
  // human should actually read to identify this piece of work. Null only when no lift is
  // resolved -- fall back to `ref` above in that rare case.
  reference: string | null;
  jobType: string;
  // The job's status (pending_approval | complete | ...): drives the office approval UI.
  jobStatus: string;
  /** Session TV2: on a TECHNICIAN VISIT only, whether the office chose to bill time past its five hours as a further day (false/absent = by the hour). */
  techVisitOverageAsDay?: boolean;
  /** Session SC9A (one look for every state): why the job is on hold (null when not), and the other facts the visit header's ONE state pill is chosen from. */
  holdReason?: JobHoldReasonKey | null;
  holdReasonText?: string | null;
  entrapment?: boolean;
  followOnWorks?: boolean;
  assetType: string;
  siteName: string;
  // Parent back-link handles (Session ADH): the lift, site and customer this record belongs to, for
  // the record panel's header links. assetId is null for a call-out with no lift; siteId/customerId/
  // customerName are null when there is no linked asset.
  assetId: string | null;
  siteId: string | null;
  customerId: string | null;
  customerName: string | null;
  // The PPM contract this visit belongs to (Session AQW, the Visit Page's contract cross-link).
  // Null for a call-out, a backdated/manual entry or any visit with no PPM contract behind it.
  contractId: string | null;
  completedAt: string | null;
  // Session BAR: when the engineer's sheet reached the platform (absent from an older server).
  receivedAt?: string | null;
  // Session BBC: why the engineer sent the sheet in more than a week after the visit, in plain words.
  lateReason?: string | null;
  // Session PN1: whether the engineer on the job was told when it was assigned (GET /jobsheets/:jobId only).
  engineerTold?: EngineerToldLine | null;
  // Session BDG2L / FR4: whether this job's approval was ever reopened (GET /jobsheets/:jobId only; null = could not be read). The server archives such a record and never hard deletes it.
  approvalReopened?: boolean | null;
  // Session BBJ: the live (active) contract at the lift's site, derived live; null when none. Sent by the single-record read only.
  liftContract?: { state: "covered" | "unconfirmed" | "pending_signature" | "none"; contractId: string | null; activeCount: number } | null;
  // Office approval result (field loop): when the report was emailed + where it was filed.
  emailSentAt: string | null;
  emailSentTo: string | null;
  // Session BBH: every address the last platform send went to (absent or null for an older send or server).
  emailSentRecipients?: ReportRecipients | null;
  // Session BBH: a report a person recorded as sent outside Guvnor (from Gmail by hand), or null.
  sentOutside?: { sentAt: string; to: string[]; cc: string[]; note: string | null; recordedByName: string | null } | null;
  driveFileUrl: string | null;
  defectsCount: number;
  defects: string[];
  advisoryNotes: string | null;
  engineerNotes: string | null;
  checklist: JobSheetChecklistEntry[];
  // Session AQB (D78) / AQW: the additional items the CONTRACT covers (gates, doors, barriers,
  // shutters), as the engineer recorded them. See CoveredItemCheck above.
  coveredItems: CoveredItemCheck[];
  // Session ACU: true when a care-equipment visit left the equipment OUT OF SERVICE (the ELE-MS-002
  // service-state item marked Fail). Drives the prominent Category A out-of-service banner at office
  // approval; always false for a lift or a call-out.
  equipmentOutOfService: boolean;
  partsUsed: JobSheetPartUsed[];
  // How many photos are on file (fetched via the office proxy by index).
  photoCount: number;
  // When the Service Record PDF was emailed to the customer (Send to Client), or null.
  sentToClientAt: string | null;
  // Soft-delete marker (a retained, archived sheet); null for an active one.
  archivedAt: string | null;
  // True when the job carries a Xero invoice (a retain trigger; lets the UI frame the
  // delete dialog as an archive accurately, incl. a call-out invoiced at intake).
  invoiced: boolean;
  // The Xero invoice covering this job (Session AO Item 3), via InvoiceCache.jobId. null when
  // not yet invoiced. invoiceCacheId is the handle for the office invoice detail page.
  linkedInvoice: { invoiceCacheId: string; invoiceNumber: string | null } | null;
  // Call-out (reactive) sheet fields (null on a PPM service visit).
  isCallout: boolean;
  faultReported: string | null;
  faultFound: string | null;
  workCarriedOut: string | null;
  liftStatusOnLeaving: string | null;
  arrivalTime: string | null;
  departureTime: string | null;
  labourMinutes: number | null;
  labourCharge: number | null;
  // Customer signature (Session AK Item 8): served via the authenticated proxy (api.jobs.signatureUrl).
  hasSignature: boolean;
  signerName: string | null;
  signatureSkipReason: string | null;
  // Pre-work risk assessment (Session AK Item 9); null when none was completed.
  riskAssessment: RiskAssessmentView | null;
  // Session ASW (D10): a completed service or repair record with no risk assessment on file. A flag, never a block.
  // Optional so an older cached record or a hand-built fixture reads as not flagged.
  riskAssessmentMissing?: boolean;
  // Session SC1: on the release queue's Awaiting release list only, how many OPEN defects point at this job as the repair meant to fix
  // them. The approve route offers to resolve them in its first answer, so such a visit is released on the record, not in a batch.
  openRemedialDefects?: number;
  // Job-cost profitability (Session AK Item 2); office/admin only.
  profitability: JobProfitability;
  // Post-submit amendment audit trail (Session ABD): what changed, who and when. lastAmendedAt is
  // the most recent amendment time; amendedSinceSent is true when the sheet was amended after it was
  // last sent/approved, so the office should re-send the corrected version.
  amendments: JobSheetAmendment[];
  lastAmendedAt: string | null;
  amendedSinceSent: boolean;
  // Session AQW (W-7, ENGINEER JOB SHEET RULES): the mandatory RAMS acknowledgement, when the
  // engineer (or an admin driving the PWA) confirmed they had read the applicable RAMS before
  // starting work. Null until acknowledged, and permanently null for a job with no RAMS to open.
  // NOT the optional Site Risk Assessment (`riskAssessment` above), a separate, service-visit-only
  // record.
  ramsAcknowledgedAt: string | null;
}

// One entry in a sheet's amendment trail (Session ABD). byName is the amending user's display name.
export interface JobSheetAmendment {
  at: string;
  byName: string | null;
  summary: string;
  afterSend: boolean;
  // Session ASM (the Director's D135 (b) ruling): the fault-note texts this amendment removed and added. Optional so
  // an older server, or a record cached before ASM, that sends neither still renders.
  defectsRemoved?: string[];
  defectsAdded?: string[];
}

export interface RiskAssessmentView {
  hazards: string[];
  otherHazards: string | null;
  controlMeasures: string | null;
  signedAt: string | null;
  // Session ASW (D10): set when the office added this assessment after the visit. Optional so an older cached record maps.
  officeRecorded?: { at: string | null; byName: string | null } | null;
}

// Session ASW (D10): what the office sends to add or correct a risk assessment on a completed record.
export interface RiskAssessmentInput {
  hazardElectrical?: boolean;
  hazardFalling?: boolean;
  hazardTrapped?: boolean;
  hazardManualHandling?: boolean;
  hazardSlip?: boolean;
  hazardPublic?: boolean;
  hazardConfined?: boolean;
  hazardNoise?: boolean;
  otherHazards?: string;
  controlMeasures?: string;
}

// ---- Monthly portfolio report (admin) ----
export interface ReportRate {
  rate: number | null; // percentage 0-100 (one decimal), or null when not applicable
  numerator: number;
  denominator: number;
}

// First-time fix additionally reports the completed visits it could NOT honestly assess, which sit
// outside the denominator rather than being counted as passes (Session ADT). numerator/denominator
// therefore describe the ASSESSED visits only.
export interface ReportFirstTimeFixRate extends ReportRate {
  unassessable: number;
}

export interface ReportFigures {
  month: string; // "YYYY-MM"
  monthLabel: string; // "June 2026"
  jobsCompleted: { thisMonth: number; lastMonth: number };
  firstTimeFix: ReportFirstTimeFixRate;
  lolerCompliance: ReportRate;
  ppmCompliance: ReportRate;
  revenue: { amount: number; source: "xero" | "cache" };
  breakdowns: { thisMonth: number; lastMonth: number };
  topBreakdownSites: { siteName: string; count: number }[];
  atRiskClients: number;
  contractsRenewing: number;
}

export interface MonthlyReport {
  figures: ReportFigures;
  narrative: string;
}

// ---- LOLER remedial workflow ----
export type LolerDefectCategory = "immediate" | "timed" | "advisory";
export type LolerTreatment = "covered" | "chargeable" | "absorbed";
export type LolerDefectLifecycle = "open" | "quoted" | "scheduled" | "in_progress" | "complete" | "signed_off";
export type ReportCompleteness = "full" | "partial";
export type LolerReportFilter = "open" | "closed" | "all";

export interface TriageProposal {
  treatment: LolerTreatment | null;
  grey: boolean;
  honoured: boolean;
  leadGenAbsorbCandidate: boolean;
  ruleId: string;
  reason: string;
}

export interface LolerDefectItem {
  id: string;
  description: string;
  category: LolerDefectCategory;
  deadline: string | null;
  overdue: boolean;
  status: LolerDefectLifecycle;
  treatment: LolerTreatment | null;
  treatmentConfirmedAt: string | null;
  remedialQuoteAmount: number | null;
  subcontractCost: number | null;
  concessionNote: string | null;
  concessionValue: number | null;
  xeroInvoiceId: string | null;
  hasJob: boolean;
  signedOffAt: string | null;
  proposal: TriageProposal;
}

export interface LolerReportListItem {
  id: string;
  ele: string | null;
  siteName: string | null;
  customerName: string | null;
  inspectorName: string | null;
  examinationDate: string | null;
  nextExaminationDue: string | null;
  completeness: ReportCompleteness;
  hasContract: boolean;
  hasReportPdf: boolean;
  openCount: number;
  totalCount: number;
  overdueCount: number;
  open: boolean;
  receivedAt: string;
}

// The lift's own technical specification, as printed on a LOLER report (Session ALO). Mirrors
// server/src/modules/loler/aiExtract.ts's ExtractedHeader lift-spec fields and the equivalent
// LolerReportDetail columns.
export interface LolerLiftSpec {
  liftType?: string;
  liftSerialNumber?: string;
  liftFloors?: number;
  liftInstallYear?: number;
  liftAgeYears?: number;
}

// Ready-to-use inputs for a NEW maintenance quote, built from a confirmed report's own recorded
// specification (Session ALO, LOLER spec Item 2). Mirrors server's LolerQuotePrefillDto
// (maintenanceQuotePrefill.ts). Never itself creates anything.
export interface LolerQuotePrefill {
  customerId: string | null;
  customerName: string | null;
  customerEmail: string | null;
  siteId: string | null;
  siteName: string | null;
  siteAddress: string | null;
  liftType: string | null; // the engine's LiftTypeKey, or null when nothing mapped
  unmappedLiftType: string | null; // the raw text, when it could not be recognised
  stops: number | null;
  duration: string | null; // the engine's DurationKey ("standard" | "extended" | "long")
  year: number | null;
  serial: string | null;
}

export interface LolerReportDetail {
  id: string;
  // Parent back-link handles (Session ADH): the lift and its site. Both null for a pre-contract
  // report with a free-text site and no linked asset.
  assetId: string | null;
  siteId: string | null;
  // The linked customer's id (Session ALO), when this report is already tied to one.
  customerId: string | null;
  ele: string | null;
  siteName: string | null;
  siteAddress: string | null;
  customerName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  inspectorName: string | null;
  examinationDate: string | null;
  nextExaminationDue: string | null;
  reportRef: string | null;
  completeness: ReportCompleteness;
  hasContract: boolean;
  hasReportPdf: boolean;
  reportUrl: string | null;
  notes: string | null;
  // The lift's own technical specification, as stated on this report (Session ALO).
  liftType: string | null;
  liftSerialNumber: string | null;
  liftFloors: number | null;
  liftInstallYear: number | null;
  liftAgeYears: number | null;
  quotePrefill: LolerQuotePrefill;
  receivedAt: string;
  canFinaliseQuote: boolean;
  items: LolerDefectItem[];
}

export interface LolerDefectInput {
  description: string;
  category?: LolerDefectCategory;
  deadline?: string | null;
}

export interface ExtractedDefect {
  description: string;
  category: LolerDefectCategory;
  // The report's own remedy-by date (YYYY-MM-DD) where it states one. Optional and never
  // guessed by the model, so a "timed" defect with no stated date stays undated for the
  // office to set. Mirrors ExtractedDefect in server/src/modules/loler/aiExtract.ts.
  deadline?: string;
}

// The report header fields the AI extraction reads from the report (PDF or text). Every
// field is optional -- a DRAFT the office confirms/edits before the report is created.
export interface ExtractedHeader extends LolerLiftSpec {
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  siteName?: string;
  siteAddress?: string;
  inspectorName?: string;
  examinationDate?: string;
  nextExaminationDue?: string;
  reportRef?: string;
  completeness?: ReportCompleteness;
}

export interface ExtractedReport {
  header: ExtractedHeader;
  defects: ExtractedDefect[];
}

// ---- LOLER Gmail sweep (Session AGF) ----
// "download_failed" (the PDF could not be fetched from Gmail) is kept distinct from "ai_failed" (the
// AI could not read it), so the office is pointed at the right fault. "extraction_capped" (Session
// ALQ) is a PDF that was found but never even attempted this run, because the AI-extraction budget
// was already spent on more recent candidates -- deferred, not failed; sweep again to check it.
export type LolerSweepSkipReason = "too_large" | "download_failed" | "ai_failed" | "extraction_capped" | "no_pdf";
// What matched an existing report. A possible duplicate, never a certainty: one LOLER visit can
// examine several lifts on the same day, and a ref may be per-visit rather than per-examination.
export type LolerDuplicateMatchReason = "report_ref" | "exam_date_and_inspector";

// One review-queue candidate: a single PDF attachment on an email the sweep read. Discovery is by
// CONTENT, not sender (Session ALP): every attachment in the window is read and the AI decides which
// ones are genuinely LOLER reports. The AI extraction pre-fills; the office confirms each before a
// report is created (nothing is auto-made).
export interface LolerSweepCandidate {
  gmailMessageId: string;
  gmailAttachmentId: string;
  filename: string;
  emailFrom: string;
  // The From header's display name (Session ALO). DISPLAY ONLY -- see gmail.ts's
  // parseFromDisplayName. Since Session ALP this is usually the ACTUAL sender (a duty holder, an RTM
  // director), not the inspection body: discovery no longer requires a known inspection-body address.
  emailFromName: string;
  emailSubject: string;
  emailDate: string | null;
  extractedHeader: ExtractedHeader;
  extractedDefects: ExtractedDefect[];
  skipReason: LolerSweepSkipReason | null;
  alreadyImported: boolean;
  duplicateReason: LolerDuplicateMatchReason | null;
}

export interface LolerGmailSweepResult {
  candidates: LolerSweepCandidate[];
  messageCount: number;
  candidateCount: number;
  skippedCount: number;
  alreadyImportedCount: number;
  // Session ALP: PDFs that were read and extracted but the AI determined were not genuinely a LOLER
  // report (an invoice, a random document), so they were excluded rather than shown as a false-
  // positive card. A non-zero count means the sweep worked, not that it found nothing.
  notReportCount: number;
  // Messages listed but not readable (a transient Gmail error). Surfaced so the sweep never implies
  // an exhaustive all-clear it cannot back.
  readFailedCount: number;
  // True when the message-listing cap or the AI-extraction cap was reached this run (Session ALQ:
  // two SEPARATE ceilings -- see gmailSweep.ts). Never show this alone: use capNotice below.
  capped: boolean;
  // The loud, complete explanation of WHY capped is true and what to do about it, or null when the
  // run was not capped. Built once server-side so this screen never carries a stale hand-typed number.
  capNotice: string | null;
}

export interface LolerReportCreateInput extends LolerLiftSpec {
  assetId?: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  siteName?: string;
  siteAddress?: string;
  // The relationship contact (Session ALO): who actually sent this enquiry in, when known and
  // different from the duty holder above. Never office-typed -- populated only by a caller that
  // genuinely reads an email's own sender (the Gmail sweep card).
  senderName?: string;
  senderEmail?: string;
  inspectorName?: string;
  examinationDate?: string | null;
  nextExaminationDue?: string | null;
  reportRef?: string;
  completeness?: ReportCompleteness;
  notes?: string;
  defects?: LolerDefectInput[];
  // Provenance (Session AVR): the Gmail message and the attachment's source name this report is recorded from, so the same email is
  // never recorded twice. Never an attachment id.
  sourceMessageId?: string;
  sourceFilename?: string;
}

// Session BAD: the LOLER review list (a report found in the mailbox that was not filed automatically) and the Gmail picker.
export type LolerCandidateState = "open" | "dismissed" | "recorded";
export interface LolerCandidate {
  id: string;
  state: LolerCandidateState;
  reportId: string | null;
  reason: string;
  matchNote: string | null;
  gmailMessageId: string;
  filename: string;
  emailFrom: string | null;
  emailSubject: string | null;
  emailDate: string | null;
  header: {
    siteName: string | null;
    siteAddress: string | null;
    customerName: string | null;
    inspectorName: string | null;
    examinationDate: string | null;
    nextExaminationDue: string | null;
    reportRef: string | null;
    liftSerialNumber: string | null;
    liftType: string | null;
  };
  defectCount: number;
  suggestions: Array<{ assetId: string; ele: string | null; siteName: string | null }>;
  dismissedAt: string | null;
  dismissedReason: string | null;
  createdAt: string;
}
export interface LolerCandidateList {
  items: LolerCandidate[];
  counts: { open: number; dismissed: number; recorded: number };
}
export interface LolerGmailMessage {
  messageId: string;
  from: string;
  fromName: string;
  subject: string;
  date: string | null;
  attachments: Array<{ filename: string; size: number | null }>;
}
export interface LolerGmailExtract {
  header: ExtractedReport["header"];
  defects: ExtractedReport["defects"];
  isLolerReport: boolean;
  emailFrom: string;
  emailSubject: string;
  emailDate: string | null;
  filename: string;
}

export interface LolerDefectPatch {
  description?: string;
  category?: LolerDefectCategory;
  deadline?: string | null;
  status?: LolerDefectLifecycle;
  signedOff?: boolean;
}

export interface RemedialQuoteInput {
  labour?: { description?: string; amount: number; quantity?: number };
  parts?: { description: string; amount: number; quantity?: number }[];
  subcontract?: { description?: string; cost?: number; chargeOn: number };
  sendEmail?: boolean;
}

export interface ScopeProfileConcession {
  id?: string;
  description: string;
  note?: string | null;
  value?: number | null;
  addedBy?: string;
  addedAt?: string;
}
export interface ScopeProfile {
  inclusions: string[];
  concessions: ScopeProfileConcession[];
  notes: string | null;
}

// ---- Projects ----
export type ProjectStatus =
  | "enquiry"
  | "surveying"
  | "quoting"
  | "quoted"
  | "accepted"
  | "works_scheduled"
  | "in_progress"
  | "on_hold"
  | "complete"
  | "invoiced"
  | "lost"
  | "cancelled";
// The single project taxonomy (Session AAA). The five selectable values plus the legacy
// 'modification' which survives on existing rows only (display; no longer selectable).
export type WorksClassification = "modernisation" | "installation" | "repair" | "condition_report" | "post_loler_remedial" | "modification";
export type ProjectSubcontractorStatus =
  | "proposed"
  | "agreed"
  | "agreement_sent"
  | "agreement_signed"
  | "on_site"
  | "complete";
export type ProjectDocType =
  | "scope_of_works"
  | "quotation_cover_letter"
  | "subcontractor_instruction"
  | "completion_report"
  | "quotation_pdf"
  | "supplier_quote"
  | "other";
export type DocStatus = "draft" | "final" | "sent" | "signed";
export type ProjectFilter = "active" | "all" | "complete" | "lost" | "archived";

export interface ProjectListItem {
  id: string;
  projectRef: string;
  title: string;
  status: ProjectStatus;
  worksClassification: WorksClassification | null;
  customerName: string | null;
  // The linked platform customer id (Session ADI): the customer detail page filters the projects
  // list to ?customerId=. Null for a project with only a free-text client name and no linked Customer.
  customerId: string | null;
  siteName: string | null;
  contractValue: number | null;
  bsrRequired: boolean;
  bsrCleared: boolean;
  section20Required: boolean;
  active: boolean;
  archivedAt: string | null; // soft-delete marker (null = active)
  startDate: string | null;
  targetCompletion: string | null;
  createdAt: string;
}

export interface ProjectMargin {
  contractValue: number | null;
  subcontractorCost: number;
  margin: number | null;
  marginPct: number | null;
}

export interface ProjectSubcontractor {
  id: string;
  company: string;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  scope: string | null;
  cost: number | null; // admin-only (null for a non-admin office user)
  startDate: string | null;
  endDate: string | null;
  status: ProjectSubcontractorStatus;
  agreementSentAt: string | null;
  agreementSignedAt: string | null;
  hasAgreement: boolean;
  agreementPdfUrl: string | null;
  notes: string | null;
}

export interface ProjectDocument {
  id: string;
  docType: ProjectDocType;
  title: string;
  status: DocStatus;
  content: string | null;
  driveUrl: string | null;
  // Byte size of an uploaded supplier quote PDF, for display (Session AS, Item 4). null otherwise.
  fileSizeBytes: number | null;
  generatedByAi: boolean;
  // The subcontractor a "subcontractor instruction" was generated for (Session AU, Item 2), so the
  // Send action addresses it correctly. null for a client-facing document.
  subcontractorId: string | null;
  // Send tracking (Session AU, Item 2): when it was emailed from V2 and to which address (null = unsent).
  sentAt: string | null;
  sentTo: string | null;
  createdAt: string;
  updatedAt: string;
}

// The resolved recipient + subject for a document send (Session AU, Item 2), driving the confirmation
// modal. canSend is false with a plain-English reason when there is no recipient email.
export interface DocumentSendPreview {
  canSend: boolean;
  to: string | null;
  subject: string | null;
  recipientKind: "customer" | "subcontractor" | null;
  reason: string | null;
}

// The structured quotation breakdown the Generate Quotation form captures (Session AM). The
// ex-VAT total is always the sum of the line item prices; the form computes it live and the
// server recomputes it on save, so it can never be typed out of sync with the line items.
export interface QuotationLineItem {
  refLabel: string;
  element: string;
  scopeDescription: string;
  notes: string;
  basis: string;
  priceExVat: number;
}
export interface QuotationExistingInstall {
  liftRef: string;
  type: string;
  controller: string;
  emergencyComms: string;
}
export interface QuotationSupplier {
  name: string;
  supplies: string;
  leadTime: string;
  complianceNote: string;
}
export interface QuotationDetail {
  clientRef: string | null;
  // The office-overridable quote reference and issue date (Session AS, Item 1). quoteRef null = use
  // the auto project ref; quotationDate null (YYYY-MM-DD) = use today when the PDF is generated.
  quoteRef: string | null;
  quotationDate: string | null;
  liftsInScope: number | null;
  lineItems: QuotationLineItem[];
  existingInstall: QuotationExistingInstall[];
  supplier: QuotationSupplier | null;
  additionalTerms: string[];
}

export interface ProjectDetail extends ProjectListItem {
  // The contact person (distinct from customerName, the linked customer's company name).
  clientName: string | null;
  clientEmail: string | null;
  clientPhone: string | null;
  siteAddress: string | null;
  sitePostcode: string | null;
  customerId: string | null;
  siteId: string | null;
  liftDetails: string | null;
  scopeSummary: string | null;
  exclusions: string | null;
  // The Survey tab's site findings (quotation-flow redesign).
  conditionNotes: string | null;
  applicableStandard: string | null;
  buildingHeightM: number | null;
  buildingStoreys: number | null;
  residentialUnits: number | null;
  leaseholderCharge: number | null;
  bsrGateway2Date: string | null;
  section20Stage: string | null;
  // Payment-milestone inputs + the generated schedule (Session Z, Item 5). hasThirdParty +
  // thirdPartyCostPence are now auto-derived from the supplier + subcontractor cost.
  hasThirdParty: boolean;
  thirdPartyCostPence: number | null;
  milestones: ProjectMilestone[];
  quotedAt: string | null;
  acceptedAt: string | null;
  completedAt: string | null;
  hasQuotation: boolean;
  // A REAL Signable envelope is outstanding (sent, unsigned). A crash-orphaned pending send
  // sentinel counts as hasQuotation but NOT as outstanding, so the panel offers Send (which
  // self-heals the stale sentinel), never a dead-end Re-send.
  quotationOutstanding: boolean;
  quotationSentAt: string | null;
  quotationSignedAt: string | null;
  // Where a quotation send will go (the shared server-side recipient rule: the project's own
  // contact email, else the linked customer's billing email). Null = sends are blocked until
  // a contact email is added.
  quotationRecipient: string | null;
  quotationDetail: QuotationDetail | null;
  allowedNextStatuses: ProjectStatus[];
  notes: string | null;
  // Session ABN: the latest admin reopen (null when never reopened). Drives the amber re-send
  // banner, visible while the quotation has not been re-sent since the reopen.
  reopenedAt: string | null;
  reopenReason: string | null;
  // Session ABS: when the post-works LOLER examination offer was last emailed (null = never).
  // Drives the "Offer sent [date]" line on the offer card.
  lolerOfferSentAt: string | null;
  // Session AEI: the optional second signatory the office set for the acceptance envelope (null
  // when none), and whether the branded acceptance link has been sent (its token is allocated on
  // the first acceptance-link send).
  secondSignatory: { name: string; email: string; role: string } | null;
  acceptanceLinkSent: boolean;
  // Count of milestone invoices against this project (Session AAY): drives the rename caution.
  invoiceCount: number;
  // Prior references this project was renamed FROM, oldest first (Session AAY). Empty when it has
  // never been renamed; the quiet audit-trail note shows only when there is more than one.
  referenceHistory: string[];
  subcontractors: ProjectSubcontractor[];
  // The LOLER re-examination advisory flag, present when the project is a modification.
  modificationReExamFlag: ProjectModReExamFlag | null;
  // Admin-only -- present only when the caller is admin. The internal cost working document
  // (labour + supplier cost lines) and the cost rollup / margin never reach a non-admin office
  // user or any client-facing surface (the Costs tab gates these on their presence).
  margin?: ProjectMargin;
  costBreakdown?: ProjectCostBreakdown;
  costSummary?: ProjectCostSummary;
}

// The Costs tab's internal cost working document (quotation-flow redesign). Labour cost lines +
// per-supplier material cost lines. Subcontractor costs live on the project's subcontractors.
export type ProjectLabourBasis = "fixed" | "day_rate";
export interface ProjectLabourLine {
  description: string;
  basis: ProjectLabourBasis;
  quantity: number; // days (day_rate) or 1 (fixed)
  unitCost: number; // day rate (day_rate) or the fixed amount (fixed), pounds ex VAT
  // Markup percent for this line (Session AAA Item 3; missing on a legacy line reads as 30).
  markupPercent?: number;
}
export interface ProjectSupplierCostLine {
  company: string;
  supplies: string;
  leadTime: string;
  cost: number; // pounds ex VAT
  // Markup percent for this row (Session AAA Item 3; missing on a legacy row reads as 30).
  markupPercent?: number;
  // Session AV, Item 1: an attached supplier quote PDF, managed on the Costs tab (a ProjectDocument
  // of type supplier_quote). Present only when a PDF is attached to this cost row.
  quoteDocId?: string;
  quoteUrl?: string;
  quoteName?: string;
}
export interface ProjectCostBreakdown {
  labour: ProjectLabourLine[];
  suppliers: ProjectSupplierCostLine[];
  // Markup percent per subcontractor row (keyed by the ProjectSubcontractor id) + the overall
  // contingency margin percent applied on top of all charges (Session AAA Item 3). Absent on a
  // legacy breakdown saved before the markup model.
  subcontractorMarkups?: Record<string, number>;
  overallMarginPercent?: number;
}
export interface ProjectCostSummary {
  labourCost: number;
  supplierCost: number;
  subcontractorCost: number;
  totalCostToEle: number;
  projectValue: number | null;
  margin: number | null;
  marginPct: number | null;
}
// The Costs save body (Session AAA Item 3). The project value is COMPUTED server-side from the
// line charges + the overall margin; it is no longer part of the body.
export interface ProjectCostsInput {
  labour: ProjectLabourLine[];
  suppliers: ProjectSupplierCostLine[];
  subcontractorMarkups?: Record<string, number>;
  overallMarginPercent?: number;
}

export type ModReExamStatus = "open" | "re_exam_arranged" | "confirmed_not_required" | "dismissed";

// The modification re-exam flag embedded on the project detail.
export interface ProjectModReExamFlag {
  id: string;
  status: ModReExamStatus;
  note: string | null;
  completionPrompted: boolean;
}

// The modification re-exam flag with project context (the compliance dashboard panel).
export interface ModReExamFlagItem {
  id: string;
  projectId: string;
  status: ModReExamStatus;
  note: string | null;
  completionPrompted: boolean;
  createdAt: string | null;
  advisory: string;
  projectRef: string | null;
  projectTitle: string | null;
  projectStatus: string | null;
  customerName: string | null;
  siteName: string | null;
}

export interface ProjectCreateInput {
  title: string;
  worksClassification?: WorksClassification;
  customerId?: string;
  siteId?: string;
  clientName?: string;
  clientEmail?: string;
  clientPhone?: string;
  siteName?: string;
  siteAddress?: string;
  sitePostcode?: string;
  liftDetails?: string;
  scopeSummary?: string;
  exclusions?: string;
  contractValue?: number;
  hasThirdParty?: boolean;
  thirdPartyCostPence?: number;
  applicableStandard?: string;
  buildingHeightM?: number;
  buildingStoreys?: number;
  residentialUnits?: number;
  leaseholderCharge?: number;
  startDate?: string;
  targetCompletion?: string;
  notes?: string;
}

// One generated payment milestone (Session Z, Item 5). amountPence is ex VAT.
export type ProjectMilestoneKey = "with_order" | "third_party_with_order" | "balance_on_completion";
export interface ProjectMilestone {
  key: ProjectMilestoneKey;
  label: string;
  amountPence: number;
}

// Every field optional; the nullable ones (the server uses .nullish()) accept null to
// clear. Defined explicitly because an Omit+intersection collapses the optional |
// nullable union back to a plain optional and loses the null branch.
export interface ProjectUpdateInput {
  title?: string;
  status?: ProjectStatus;
  worksClassification?: WorksClassification | null;
  // Link (or unlink, via null) an existing Customer / Site record (Session AU, Item 1).
  customerId?: string | null;
  siteId?: string | null;
  clientName?: string | null;
  clientEmail?: string | null;
  clientPhone?: string | null;
  siteName?: string | null;
  siteAddress?: string | null;
  sitePostcode?: string | null;
  liftDetails?: string | null;
  scopeSummary?: string | null;
  exclusions?: string | null;
  conditionNotes?: string | null;
  // contractValue is NOT patchable (Session AAA Item 3): the value is computed on the Costs tab
  // (cost + markup) or owned by the quotation line items (the TOTALS RULE).
  hasThirdParty?: boolean;
  thirdPartyCostPence?: number | null;
  applicableStandard?: string | null;
  buildingHeightM?: number | null;
  buildingStoreys?: number | null;
  residentialUnits?: number | null;
  leaseholderCharge?: number | null;
  bsrRequired?: boolean;
  section20Required?: boolean;
  section20Stage?: string | null;
  bsrGateway2Date?: string | null;
  startDate?: string | null;
  targetCompletion?: string | null;
  completedAt?: string | null;
  notes?: string | null;
  // Session ATI (Q-8): required only when this PATCH moves status TO "lost"; ignored otherwise.
  lostReasonCode?: ProjectQuotationLostReasonCode;
  lostReasonNote?: string | null;
}

// Session ATI (Q-8): the project quotation version/option ledger. Mirrors
// server/src/modules/projects/projectQuotationVersions.ts's DTO and reason set.
export type ProjectQuotationVersionStatus = "draft" | "sent" | "opened" | "signed" | "lost" | "expired";
export type ProjectQuotationLostReasonCode =
  | "price"
  | "timing"
  | "scope_changed"
  | "went_elsewhere"
  | "no_longer_proceeding"
  | "other";
export const PROJECT_QUOTATION_LOST_REASON_LABELS: Record<ProjectQuotationLostReasonCode, string> = {
  price: "Price",
  timing: "Timing did not work",
  scope_changed: "Scope changed",
  went_elsewhere: "Went with another contractor",
  no_longer_proceeding: "No longer proceeding with the works",
  other: "Other",
};
export interface ProjectQuotationVersion {
  id: string;
  label: string | null;
  status: ProjectQuotationVersionStatus;
  statusLabel: string;
  isCurrent: boolean;
  valuePounds: number | null;
  sentAt: string | null;
  openedAt: string | null;
  signedAt: string | null;
  expiresAt: string | null;
  lostAt: string | null;
  lostReasonCode: string | null;
  lostReasonLabel: string | null;
  lostReasonNote: string | null;
  createdAt: string;
}

export interface ProjectSubcontractorInput {
  company: string;
  contactName?: string;
  email?: string;
  phone?: string;
  address?: string;
  scope?: string;
  cost?: number;
  startDate?: string;
  endDate?: string;
  status?: ProjectSubcontractorStatus;
  notes?: string;
}

export interface ProjectDocumentInput {
  docType: ProjectDocType;
  title: string;
  content?: string;
}

export interface ProjectInvoice {
  id: string;
  xeroInvoiceId: string;
  contactName: string | null;
  amount: number;
  dueDate: string | null;
  status: string;
  // The named milestone stage this invoice covered (with_order / third_party_with_order /
  // balance_on_completion), or null for a custom milestone. Lets the raise dropdown mark
  // already-invoiced stages instead of a guaranteed-409 surprise (Session AED).
  projectStage: string | null;
}

export interface MilestoneInvoiceInput {
  stage: ProjectMilestoneKey | "custom";
  amount?: number;
  percent?: number;
  description?: string;
}

// One row of the compliance report generation audit trail (no PDF content).
export interface ComplianceReportHistoryItem {
  id: string;
  reportRef: string;
  reportType: string;
  reportTypeLabel: string;
  generatedAt: string; // ISO 8601
  generatedBy: string;
  // Session AOY: the office-only internal classification. A row an office user has marked as a
  // verification / testing render stays fully visible HERE (annotated) and is excluded from the
  // auditor's response server-side, so an auditor's items always read internal === false.
  // Optional on the type because a cached response from before AOY carries none of them, and
  // "absent" must read as "not internal" rather than crash the row.
  internal?: boolean;
  markedInternalAt?: string | null; // ISO 8601
  markedInternalBy?: string; // display name; "" when not marked, and always "" for an auditor
  markedInternalNote?: string | null;
}

// IMS document library (Session AR Item 17).
export interface ComplianceDocItem {
  filename: string;
  displayName: string;
  kind: string; // DOCX | PDF
}
export interface ComplianceDocCategoryGroup {
  category: string;
  label: string;
  documents: ComplianceDocItem[];
}
export interface ComplianceDocsCatalogue {
  categories: ComplianceDocCategoryGroup[];
}

// Session AUY: a GET that returns a PDF (the subcontractor welcome pack's document previews). Fetch it as a blob and
// trigger a browser download; the filename comes from Content-Disposition, falling back to the given one. Throws
// ApiError on a non-2xx so the caller can show the server's own message (a missing rate card, say).
export async function downloadPdf(url: string, fallbackFilename: string): Promise<void> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      message = body?.message || body?.error || message;
    } catch {
      // non-JSON error body; keep the status text
    }
    throw new ApiError(res.status, message);
  }
  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.toLowerCase().includes("application/pdf")) throw new ApiError(res.status, "The document could not be generated. Please try again.");
  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") ?? "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  const filename = m?.[1] ?? fallbackFilename;
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

// POST /api/compliance-reports/generate returns a PDF (not JSON). Fetch it as a blob
// and trigger a browser download. Throws ApiError on a non-2xx so the caller can show
// a clear error (never a silent failure). The filename is taken from the
// Content-Disposition header, falling back to the report type.
export async function downloadComplianceReport(
  reportType: string,
  parameters?: Record<string, unknown>,
  // Session AOY. { dryRun: true } renders and downloads the report exactly as normal but
  // records NOTHING in the generation audit trail -- for verifying a template change without
  // leaving an entry an external assessor reads as compliance activity. Admin/office only; the
  // server ignores it for an auditor and logs their generation anyway.
  opts?: { dryRun?: boolean },
): Promise<void> {
  refuseWriteInPhoneView("POST", "/download");
  const startedAt = Date.now();
  const res = await fetch("/api/compliance-reports/generate", {
    credentials: "include",
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // dryRun is omitted unless it is actually true, so an ordinary download's request body is
    // byte-identical to what it was before this parameter existed.
    body: JSON.stringify({ reportType, ...(parameters ? { parameters } : {}), ...(opts?.dryRun ? { dryRun: true } : {}) }),
  });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      message = body?.message || body?.error || message;
      reportIfSessionEnded("/compliance-reports/generate", res.status, body, startedAt);
    } catch {
      // non-JSON error body; keep the status text
    }
    throw new ApiError(res.status, message);
  }
  // Defence in depth: the generate endpoint returns application/pdf on success and a
  // JSON error with a non-2xx status on failure, so we should never reach here with a
  // non-PDF body. Guard anyway so a misconfigured response can never download a JSON
  // (or HTML) error masquerading as a PDF.
  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.toLowerCase().includes("application/pdf")) {
    throw new ApiError(res.status, "The report could not be generated. Please try again.");
  }
  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") ?? "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  const filename = m?.[1] ?? `${reportType}.pdf`;
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

// ---- Data retention (Session ASQ, Stream G, Item 4; admin only, office UI only) ----
// The weekly retention sweep's proposals (server/src/modules/gdpr/retentionReview.ts and retention.routes.ts): what is
// past its retention period, grouped by rule, each record re-checked live and planned. Nothing is erased until an admin
// confirms a rule's group; the sweep itself only proposes.

export type RetentionRuleKind = "hold" | "retention" | "never" | "automatic" | "lift_life";
export type RetentionLiveState = "due" | "not_due" | "gone";
export type RetentionPlannedAction = "delete" | "anonymise" | "held" | "not_found";

export interface RetentionPlannedFile {
  field: string;
  fileId: string;
  action: "delete" | "keep";
  owned: boolean;
}

export interface RetentionRecordView {
  proposalId: string;
  model: string;
  recordType: string;
  recordId: string;
  // read live from the record; null when it is no longer on the platform
  label: string | null;
  dueSince: string;
  firstProposedAt: string;
  state: RetentionLiveState;
  inPlan: boolean;
  action: RetentionPlannedAction | null;
  reason: string | null;
  liftsAt: string | null;
  fields: string[];
  files: RetentionPlannedFile[];
}

export interface RetentionGroupView {
  ruleId: string;
  label: string;
  kind: RetentionRuleKind;
  directorConfirmed: boolean;
  open: number;
  counts: { delete: number; anonymise: number; held: number; notDue: number; gone: number; waiting: number };
  // null when nothing in the group can be confirmed now
  planHash: string | null;
  error: string | null;
  records: RetentionRecordView[];
}

export interface RetentionResolvedView {
  proposalId: string;
  ruleId: string;
  ruleLabel: string;
  model: string;
  recordType: string;
  recordId: string;
  label: string | null;
  status: "open" | "dismissed" | "actioned" | "lapsed";
  resolvedAt: string | null;
  resolvedBy: string | null;
  dismissReason: string | null;
}

export interface RetentionSweepTotals {
  due: number;
  opened: number;
  reopened: number;
  stillOpen: number;
  lapsed: number;
  dismissedKept: number;
  unknown: number;
  // records with no retention date to count from: kept, never proposed (review F4); absent from an older sweep's row
  notApplicable?: number;
}

export interface RetentionOverview {
  lastSweepAt: string | null;
  lastSweep: RetentionSweepTotals | null;
  // what the last weekly check could not read or work out, in words; null when it checked everything (review F1)
  lastSweepIncomplete: { recordTypes: string[]; rules: string[] } | null;
  xeroNote: string;
  automaticCleanup: string;
  // notApplicable / unknown: the last check's counts of records under the rule that are never proposed (kept with no
  // retention date; no recorded date to count from); null when no check has recorded them (review F4)
  rules: { id: string; label: string; kind: RetentionRuleKind; directorConfirmed: boolean; open: number; notApplicable: number | null; unknown: number | null }[];
  groups: RetentionGroupView[];
  dismissed: RetentionResolvedView[];
  lapsed: RetentionResolvedView[];
  lapsedTotal: number;
  actionedTotal: number;
}

export interface RetentionConfirmResult {
  ruleId: string;
  planHash: string;
  result: { deleted: number; anonymised: number; held: number; notFound: number; filesDeleted: number; alreadyCarriedOut: boolean };
  actioned: number;
  lapsed: number;
  filesToRemoveByHand: { recordType: string; label: string | null; fileId: string; why: "not_filed_by_platform" | "drive_refused"; error: string | null }[];
  stillOpen: number;
  plannedRecords: number;
}

// ---- Personal data requests (Session ASQ, Stream G, Item 2; admin only, office UI only) ----
// The person search and the subject access export. The server finds the person across every record type the personal
// data inventory lists; the office confirms the records; the export is a ZIP built on request and stored nowhere.

export interface GdprIdentifiers {
  emails: string[];
  phones: string[];
  names: string[];
}

export type GdprMatchBasis = "email" | "phone" | "name" | "text" | "link";

export interface GdprRecordRef {
  model: string;
  id: string;
}

export interface GdprPersonMatch extends GdprRecordRef {
  basis: GdprMatchBasis;
  field: string;
  via?: GdprRecordRef;
  preselected: boolean;
  label: string;
  note?: string;
}

export interface GdprSearchResult {
  identifiers: GdprIdentifiers;
  matches: GdprPersonMatch[];
  // model -> its plain-English name
  recordTypes: Record<string, string>;
  // record types (or "Model via Anchor" links) where more matched than the search lists
  truncated: string[];
}

// ---- Erasure (Session ASQ, Stream G, Item 3; admin only, office UI only) ----
// The preview says what would happen to every chosen record; the commit carries out exactly that preview (its planHash)
// or answers 409 plan_changed. A record is referred to with the kinds of subject the office confirmed for it (today only
// "residential_customer": a private individual's own record).

export type GdprErasureAction = "delete" | "anonymise" | "held" | "not_found";

export interface GdprErasureRef extends GdprRecordRef {
  subjects?: string[];
}

export interface GdprErasureFile {
  field: string;
  fileId: string;
  action: "delete" | "keep";
  owned: boolean;
  driveUrl: string;
}

export interface GdprErasureRecord {
  model: string;
  id: string;
  recordType: string;
  label: string;
  action: GdprErasureAction;
  reason: string;
  ruleLabel: string;
  liftsAt: string | null;
  liftsOn: string | null;
  fields: { field: string; label: string }[];
  // What an anonymise leaves that may be the person's, masked, and why (the engine's PlannedRecord.unresolved): an email,
  // phone or name nobody searched for ("not_searched"), or one in a part of the record that may be someone else's
  // ("not_confirmed"). The reason sentence says the same in words.
  unresolved?: { field: string; label: string; masked: string; why: "not_searched" | "not_confirmed" }[];
  files: GdprErasureFile[];
  subjects: string[];
  canConfirmPrivateIndividual: boolean;
}

export interface GdprErasurePreview {
  planHash: string;
  plannedAt: string;
  xeroNote: string;
  counts: { delete: number; anonymise: number; held: number; notFound: number };
  records: GdprErasureRecord[];
  files: {
    toDelete: (GdprErasureFile & { model: string; id: string; label: string })[];
    kept: (GdprErasureFile & { model: string; id: string; label: string })[];
    notOwned: (GdprErasureFile & { model: string; id: string; label: string })[];
  };
  recreatable: { model: string; recordType: string; note: string }[];
  notChanged: { model: string; id: string; recordType: string; label: string; reason: string }[];
  tombstone: { emails: string[]; phones?: string[]; note: string };
}

export interface GdprErasureResult {
  deleted: number;
  anonymised: number;
  held: number;
  notFound: number;
  notChanged: number;
  filesDeleted: number;
  fileFailures: { model: string; id: string; fileId: string; error: string; recordType: string; label: string; driveUrl: string }[];
  alreadyCarriedOut?: boolean;
  tombstone: { recorded: number; emails: string[]; phones?: string[]; error?: string };
}

export interface GdprTombstoneLiftResult {
  removed: number;
  lifted: { scope: "full" | "mailbox"; erasedOn: string }[];
}

// Download the subject access export ZIP for the chosen records: fetch + blob + object URL + anchor, as the
// compliance report download does, so a refusal surfaces as the page's own inline error and a JSON error body is never
// saved to disk as a mislabelled .zip.
export async function downloadSubjectAccessExport(identifiers: GdprIdentifiers, records: GdprRecordRef[]): Promise<void> {
  refuseWriteInPhoneView("POST", "/download");
  const startedAt = Date.now();
  const res = await fetch("/api/gdpr/export", {
    credentials: "include",
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ identifiers, records }),
  });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      message = body?.message || body?.error || message;
      reportIfSessionEnded("/gdpr/export", res.status, body, startedAt);
    } catch {
      // non-JSON error body; keep the status text
    }
    throw new ApiError(res.status, message);
  }
  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.toLowerCase().includes("application/zip")) {
    throw new ApiError(res.status, "The export could not be made. Please try again.");
  }
  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") ?? "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  const filename = m?.[1] ?? "ELE-subject-access.zip";
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Download the accident report and investigation document (Session AOM). Fetched rather than linked
// so a refusal surfaces as the page's own inline error instead of a browser tab showing raw JSON,
// and so the non-PDF guard below applies: this document carries an injured person's details and must
// never be saved to disk as a mislabelled error body.
export async function downloadAccidentReportPdf(id: string, ref: string): Promise<void> {
  const startedAt = Date.now();
  const res = await fetch(`/api/accidents/${encodeURIComponent(id)}/document.pdf`, { credentials: "include" });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      message = body?.message || body?.error || message;
      reportIfSessionEnded(`/accidents/${encodeURIComponent(id)}/document.pdf`, res.status, body, startedAt);
    } catch {
      // non-JSON error body; keep the status text
    }
    throw new ApiError(res.status, message);
  }
  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.toLowerCase().includes("application/pdf")) {
    throw new ApiError(res.status, "The document could not be generated. Please try again.");
  }
  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") ?? "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = m?.[1] ?? `${ref}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

// POST the (edited) renewal letter and download the branded PDF the server renders. Throws
// ApiError on a non-2xx, and guards against a non-PDF body (mirrors downloadComplianceReport).
export async function downloadRenewalLetterPdf(id: string, payload: { subject: string; body: string }): Promise<void> {
  refuseWriteInPhoneView("POST", "/download");
  const startedAt = Date.now();
  const res = await fetch(`/api/contracts/${encodeURIComponent(id)}/renewal-letter.pdf`, {
    credentials: "include",
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      message = body?.message || body?.error || message;
      reportIfSessionEnded(`/contracts/${encodeURIComponent(id)}/renewal-letter.pdf`, res.status, body, startedAt);
    } catch {
      // non-JSON error body; keep the status text
    }
    throw new ApiError(res.status, message);
  }
  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.toLowerCase().includes("application/pdf")) {
    throw new ApiError(res.status, "The renewal letter PDF could not be generated. Please try again.");
  }
  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") ?? "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  const filename = m?.[1] ?? "renewal-letter.pdf";
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

// GET /api/timesheets/export?week=YYYY-MM-DD returns a payroll CSV (not JSON). Fetch it as a blob
// and trigger a browser download (mirrors downloadComplianceReport / downloadRenewalLetterPdf).
// ADMIN only server-side; a non-admin (or office user) gets a 403 the caller surfaces. The filename
// comes from the Content-Disposition header, falling back to a week-stamped default.
export async function downloadPayrollCsv(week: string): Promise<void> {
  const startedAt = Date.now();
  const res = await fetch(`/api/timesheets/export?week=${encodeURIComponent(week)}`, {
    credentials: "include",
  });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      message = body?.message || body?.error || message;
      reportIfSessionEnded("/timesheets/export", res.status, body, startedAt);
    } catch {
      // non-JSON error body; keep the status text
    }
    throw new ApiError(res.status, message);
  }
  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") ?? "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  const filename = m?.[1] ?? `ELE-Payroll-${week}.csv`;
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

// ---- CSV export of six office lists (Session GAP9) ----
// GET /api/exports/<list>.csv returns the list the screen is showing, with the screen's filters, search and sort, as a CSV (not JSON). Fetched as a
// blob and handed to the browser as a download, like the payroll export above. Office and admin only; the money columns arrive for an admin only.
// The answer carries how many rows it wrote and whether the list was cut at the server's cap, so the page can say so.
export type ExportListKey = "customers" | "sites" | "lifts" | "contracts" | "defects" | "service-records" | "invoices" | "quotes" | "callouts" | "loler-reports";
export interface CsvExportResult {
  rows: number;
  total: number;
  truncated: boolean;
  /** The list's own total moved while the pages were read, so a row may be missing or repeated (fix round 1, review L6). */
  changed: boolean;
}
export async function downloadListCsv(list: ExportListKey, params: Record<string, string | undefined> = {}): Promise<CsvExportResult> {
  // A GET, but not a plain read: the server records every export on the audit trail (csv_export). The Phone view is
  // read only, so the export is refused inside its frame exactly as the POST downloads are (Session FIX2, D-FIX2-1).
  refuseWriteInPhoneView("POST", "/download");
  const startedAt = Date.now();
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== "") qs.set(k, v);
  const path = `/exports/${list}.csv`;
  const res = await fetch(`/api${path}${qs.toString() ? `?${qs.toString()}` : ""}`, { credentials: "include" });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      message = body?.message || body?.error || message;
      reportIfSessionEnded(path, res.status, body, startedAt);
    } catch {
      // non-JSON error body; keep the status text
    }
    throw new ApiError(res.status, message);
  }
  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") ?? "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  const filename = m?.[1] ?? `ELE-${list}.csv`;
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
  const rows = Number(res.headers.get("x-export-rows") ?? "0");
  const total = Number(res.headers.get("x-export-total") ?? String(rows));
  return { rows, total, truncated: res.headers.get("x-export-truncated") === "true", changed: res.headers.get("x-export-changed") === "true" };
}

// ---- Global search (Session AE, Item 2) ----
export interface SearchResultItem {
  id: string;
  label: string;
  subtitle: string;
  url: string; // the office route to this record
}
export interface SearchResults {
  sites: SearchResultItem[];
  customers: SearchResultItem[];
  lifts: SearchResultItem[];
  jobs: SearchResultItem[];
  defects: SearchResultItem[];
  quotes: SearchResultItem[];
  prospects: SearchResultItem[];
  lolerReports: SearchResultItem[];
}

// ---- Address lookup (Session AV, Item 3): UK postcode -> address autocomplete via Google Places ----
export interface AddressPrediction {
  id: string; // opaque Google place_id
  label: string; // description shown in the dropdown
}
export interface AddressLookupResult {
  configured: boolean; // false when no Google Maps key is set (form stays plain manual entry)
  results: AddressPrediction[];
}
export interface AddressDetails {
  address: string;
  postcode: string;
}

// ---- Free address lookup for the office quote forms (Session AAP): postcodes.io + Nominatim ----
// SEPARATE from `addressLookup` above (Google Places). No API key, no billing; a provider failure
// degrades to plain manual entry.
export interface PostcodeLookup {
  postcode: string; // canonical, spaced (e.g. "W1K 2AZ")
  town: string | null; // provider admin_ward
  district: string | null; // provider admin_district (borough / local authority)
  region: string | null;
  country: string | null;
  lat: number | null;
  lng: number | null;
}
export interface PostcodeLookupResponse {
  ok: boolean;
  cached: boolean;
  result: PostcodeLookup | null;
}
export interface AddressSuggestion {
  label: string; // human-readable display name
  postcode: string | null; // normalised "OUTWARD INWARD", or null
  lat: number | null;
  lng: number | null;
}
export interface AddressSearchResponse {
  results: AddressSuggestion[];
}

// ---- Companies House company lookup for the office quote form client block (Session AAP) ----
// Free, keyed server-side; `configured` is false when the key is unset (the form stays manual entry).
export interface CompanySearchItem {
  companyNumber: string;
  title: string; // company name as registered
  addressSnippet: string | null;
  status: string | null; // active / dissolved / ...
}
export interface CompanySearchResponse {
  configured: boolean;
  results: CompanySearchItem[];
}
export interface CompanyProfile {
  companyNumber: string;
  companyName: string;
  registeredOfficeAddress: string | null;
  status: string | null;
}

// ---- Quote pre-fill from existing records (Session AAP, Loop 3) ----
export interface CustomerPrefill {
  id: string;
  name: string;
  email: string | null;
  companyNumber: string | null;
}
export interface SitePrefill {
  id: string;
  name: string;
  address: string;
  postcode: string | null;
  customerId: string;
  customerName: string;
  customerEmail: string | null;
  customerCompanyNumber: string | null;
}

// ---- Office NPS view (Session AE, Item 3) ----
export type NpsCategory = "promoter" | "passive" | "detractor";
export interface NpsListItem {
  id: string;
  score: number;
  category: NpsCategory;
  comment: string | null;
  customerName: string;
  siteName: string;
  jobId: string | null; // links to /office/jobsheets?job=:jobId; null for a manual/ad-hoc survey
  respondedAt: string;
}
export interface NpsOfficeSummary {
  npsScore: number | null; // -100..100
  totalResponses: number;
  sent: number;
  responseRate: number | null; // percentage
  average: number | null; // 0..10
  distribution: { promoters: number; passives: number; detractors: number };
  trend: { thisMonth: number | null; lastMonth: number | null; direction: "up" | "down" | "flat" };
}

// Part identification (Session AP): the structured AI best-guess for one photo. Each field is a
// plain-language line; any may be empty. `raw` is the whole (sanitised) model text, a fallback the
// UI shows only if none of the four labels parsed.
export interface PartIdentificationResult {
  componentType: string;
  visibleText: string;
  observation: string;
  followUp: string;
  raw: string;
}

// ---- Inbound call log (Session ABT) ----
export type CallOutcome = "resolved" | "follow_up_needed" | "quote_requested" | "complaint" | "other";
// How the call log row was created: the office logged it by hand, or a phone provider's webhook did.
export type CallSource = "manual" | "webhook";

export const CALL_OUTCOMES: { value: CallOutcome; label: string }[] = [
  { value: "resolved", label: "Resolved" },
  { value: "follow_up_needed", label: "Follow-up needed" },
  { value: "quote_requested", label: "Quote requested" },
  { value: "complaint", label: "Complaint" },
  { value: "other", label: "Other" },
];

export interface CallLogDto {
  id: string;
  customerId: string | null;
  customerName: string | null;
  // The sales lead linked to this call: made automatically for every unmatched call until Session
  // ARM (D88), and since then only when a site inspection is raised for the caller. Null otherwise.
  prospectId: string | null;
  callerName: string | null;
  callerPhone: string | null;
  calledAt: string; // ISO
  durationMinutes: number | null;
  topic: string | null;
  outcome: CallOutcome;
  outcomeLabel: string;
  source: CallSource;
  npsQueued: boolean;
  npsQueuedAt: string | null;
  npsSent: boolean;
  createdByUserId: string | null;
  createdAt: string;
  // Session ANV: the work the office raised from this call, newest first. Empty is the honest
  // answer both for a call nobody has acted on and for every call logged before this shipped;
  // nothing infers it from the outcome field or anything else, so it only ever reflects a real
  // office decision. Optional here so an older cached/mocked payload still typechecks.
  raisedWork?: RaisedWorkRef[];
}

// Session ANV. The two kinds of work a call can become open on DIFFERENT office pages, which is
// what `kind` is for: a job on the Scheduling page, a call-out on the Call-outs list.
export interface RaisedWorkRef {
  kind: "job" | "callout";
  id: string;
  typeLabel: string;
  statusLabel: string;
  reference: string | null; // the lift's ELE number, or the call-out's own reference; null where it genuinely has neither
  siteName: string | null;
}

export interface CallLogListResponse {
  items: CallLogDto[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CallLogListParams {
  outcome?: CallOutcome;
  customerId?: string;
  // Session ARM: calls with no customer, the unknown callers the Enquiries page lists. Not combined
  // with customerId (the server refuses the pair).
  unmatched?: boolean;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

// ---- Internal office <-> engineer messaging (Session ACZ) ----
export type MessageDirection = "to_office" | "to_engineer";

export interface MessageDto {
  id: string;
  fromUserId: string;
  fromUserName: string | null;
  toUserId: string;
  body: string;
  readAt: string | null;
  jobId: string | null;
  jobNumber: string | null;
  createdAt: string;
  direction: MessageDirection;
}

export interface MessageThreadSummary {
  engineerUserId: string;
  techId: string | null;
  displayName: string;
  unreadCount: number;
  lastMessagePreview: string | null;
  lastMessageAt: string | null;
  lastMessageDirection: MessageDirection | null;
}

export interface MessageThread {
  engineer: { userId: string; techId: string | null; displayName: string };
  sendToUserId: string | null; // who a reply is addressed to (null = no recipient available)
  messages: MessageDto[];
}

export interface MessageJobOption {
  id: string;
  jobNumber: string | null;
  type: string;
  eleNumber: string | null;
  siteName: string | null;
  scheduledFor: string | null;
  label: string;
}

export interface SendMessageBody {
  // Required when the office sends (which engineer). Omitted when an engineer replies -- the server
  // resolves the office recipient, so an offline queued reply needs no recipient.
  toUserId?: string;
  body: string;
  jobId?: string;
  // Client-supplied idempotency key (Session AFF). The engineer PWA's offline outbox stamps a UUID;
  // a lost-response retry carries the same key and the server returns the existing row instead of
  // writing a duplicate. Office sends omit it.
  clientKey?: string;
}

// ---- Web Push (browser notifications) ----
export interface PushStatus {
  configured: boolean;
  // The VAPID application server public key, present only when push is configured. The browser
  // needs it to create a subscription. Never carries the private key.
  publicKey: string | null;
}

export interface PushSubscribeBody {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  userAgent?: string;
}

export interface CallLogCreateBody {
  customerId?: string | null;
  callerName?: string | null;
  callerPhone?: string | null;
  calledAt?: string; // ISO
  durationMinutes?: number | null;
  topic?: string | null;
  outcome: CallOutcome;
  nps?: boolean;
}

export interface CallLogUpdateBody {
  customerId?: string | null;
  callerName?: string | null;
  callerPhone?: string | null;
  calledAt?: string;
  durationMinutes?: number | null;
  topic?: string | null;
  outcome?: CallOutcome;
}

function callLogQuery(params: CallLogListParams): string {
  const q = new URLSearchParams();
  if (params.outcome) q.set("outcome", params.outcome);
  if (params.customerId) q.set("customerId", params.customerId);
  if (params.unmatched) q.set("unmatched", "true");
  if (params.from) q.set("from", params.from);
  if (params.to) q.set("to", params.to);
  if (params.page) q.set("page", String(params.page));
  if (params.pageSize) q.set("pageSize", String(params.pageSize));
  const s = q.toString();
  return s ? `?${s}` : "";
}

// Xero contact naming (Session ADV). `linked` false = never sent to Xero (nothing to correct);
// `found` false = the stored contact id no longer resolves in Xero. `wouldChange` is false when
// the contact is already named correctly, so the office is never offered a pointless write.
export interface XeroContactFields {
  name: string;
  firstName: string | null;
  lastName: string | null;
}
export interface XeroContactPreview {
  linked: boolean;
  xeroContactId: string | null;
  found?: boolean;
  current: XeroContactFields | null;
  proposed: XeroContactFields | null;
  wouldChange: boolean;
}
export interface XeroContactResyncResult {
  ok: boolean;
  changed: boolean;
  current: XeroContactFields | null;
  proposed: XeroContactFields;
}

// Change or clear the stored Xero contact link (Session BCG). Never writes to Xero.
export interface XeroLinkPlan {
  action: "clear" | "relink";
  customer: { id: string; name: string };
  current: { xeroContactId: string; name: string | null; status: string | null } | null;
  next: { xeroContactId: string; name: string; status: string } | null;
  effects: string[];
  cautions: string[];
}
export interface XeroLinkRequest {
  action: "clear" | "relink";
  xeroContactId?: string;
  reason?: string;
  expectedCurrentXeroContactId?: string | null;
  preview: boolean;
}
export interface XeroLinkPreviewResult {
  preview: true;
  allowed: true;
  plan: XeroLinkPlan;
}
export interface XeroLinkCommitResult {
  changed: true;
  plan: XeroLinkPlan;
}


// ---- The customer renewal surface (Session AII) ----
export interface PublicRenewalLift {
  eleNumber: string | null;
  liftType: string | null;
  stops: number | null;
}
export interface PublicRenewal {
  siteName: string;
  siteAddress: string | null;
  lifts: PublicRenewalLift[];
  liftCount: number;
  current: { tier: PublicTierKey; tierLabel: string; frequency: string; frequencyLabel: string; annual: number };
  // Session AIK: `basis` says HOW the selected price was arrived at. "escalation" is the customer's
  // current charge uplifted by CPI plus their contract's own rate (an unchanged cover level);
  // "reprice" is a fresh calculator price (a change of cover level). The page says which, because
  // they are different promises. Session AJO adds "estimate": the customer has picked a visit
  // frequency other than the one they are on, and this is a live engine PROJECTION at that cadence,
  // never the figure `accept` would actually submit -- the commit always prices at the CURRENT
  // frequency (AIK decision 3, unchanged). It degrades to "escalation"/"reprice" -- frozen at the
  // current cadence's figure -- when no honest estimate exists for the chosen one. Session AKO adds
  // "override": an unchanged cover level priced from a figure the office has agreed for this renewal
  // cycle, replacing the CPI calculation.
  selected: { tier: PublicTierKey; frequency: string; annual: number; basis: "escalation" | "reprice" | "estimate" | "override" };
  // Null against a tier means that option is not offered on this contract (see tierChangeAvailable).
  tierPricing: { core: number | null; care: number | null; complete: number | null };
  delta: number;
  frequencyOptions: { value: string; label: string }[];
  tierOptions: { value: PublicTierKey; label: string; annual: number | null }[];
  // Session AIK: the customer has picked a visit frequency other than the one they are on. It is a
  // REQUEST the office confirms, it does not move the price shown, and the page must say so.
  frequencyChangeRequested: boolean;
  // Whether a different cover level can be quoted online for this contract at all.
  tierChangeAvailable: boolean;
  renewalDateDisplay: string | null;
  // `quote_issued` is the tier-change handoff: the customer asked to change cover and a real
  // quotation was raised for them. Nothing is signed or awaiting signature until they accept it.
  status: "open" | "accepted" | "awaiting_signature" | "quote_issued";
  // The quotation raised for a tier change, and where to read it. Null on every other renewal, and
  // null too when a recorded quotation can no longer be loaded.
  issuedQuote: { quoteRef: string; url: string } | null;
  // What the customer actually agreed, once they have. The confirmation panel reads THIS, never
  // `selected`, which is priced from the contract's CURRENT terms -- on a tier change those are the
  // ones they moved away from.
  accepted: { tier: PublicTierKey; tierLabel: string; frequency: string; frequencyLabel: string; annual: number } | null;
  requestedFrequencyLabel: string | null;
  // Session APD: the same decision as PublicQuote.standardTermsApply, for this contract's customer. Optional
  // for the same reason: absent reads as standard.
  standardTermsApply?: boolean;
}
export interface PublicRenewalAcceptResult {
  // TWO OUTCOMES, because a tier change is handed a real quotation rather than signed here (the
  // Director's correction to AII/AIK). Every renewal still ends in a signature; a tier change simply
  // reaches it through the ordinary quote page, where the customer reads the full document first.
  //   "awaiting_signature" -- unchanged cover: the ESA is out for signature now.
  //   "quote_issued"       -- changed cover: a quotation is waiting to be read and accepted.
  status: "awaiting_signature" | "quote_issued";
  tier: PublicTierKey;
  frequency?: string;
  requestedFrequency: string | null;
  annual: number;
  basis: "escalation" | "reprice" | "override";
  envelopeSent?: boolean;
  // Present only on "quote_issued": where to read the quotation, and its reference.
  quoteUrl?: string;
  quoteRef?: string;
  message: string;
}


export interface ContractRenewalLink {
  token: string | null;
  actionedAt: string | null;
  // Session AQG: the office starting a spent renewal again. `actionedAt` is never cleared (it is the
  // record that the customer used their link, which the renewal ledger reads), so `spent` is the
  // server's own answer to "can this link still be used", and the panel acts on THAT rather than
  // comparing dates of its own. GET only.
  restartedAt?: string | null;
  restartReason?: string | null;
  spent?: boolean;
  // What became of the attempt the customer's use started: the tier-change quotation's reference (null
  // on the same-tier shape, which raises no tokened quotation) and whether the agreement or quotation
  // it raised is still live. Null unless the link has been used and nothing is signed. GET only.
  renewalAttempt?: { quoteRef: string | null; live: boolean } | null;
  acceptedAt: string | null;
  acceptedTier: string | null;
  acceptedFrequency: string | null;
  acceptedAnnual: number | null;
  registerConfirmedAt: string | null;
  reissued?: boolean;
  // Session AKT. Null when the link (if any) is currently actionable; the specific reason otherwise,
  // in the office's own words. Only the GET read computes this (it re-runs the customer page's own
  // readiness check); the POST issue response never carries it, since issuing does not change when a
  // link is allowed to be created, only whether an existing one still works.
  notReadyReason?: string | null;
  // Session ALB, Director decision. Whether the invitation/confirmation drafts were actually SENT --
  // checked LIVE against Gmail on every GET, never cached -- and whether the customer ever opened the
  // link. Only the GET read computes these (the same reasoning as notReadyReason above): the POST
  // issue response has its own `invitationDrafted` for the one moment a draft is composed.
  // Session ANC adds "sent": the PLATFORM sent the invitation itself, from the contract page. It is
  // the only unambiguous state of the five and the only one that does not depend on Gmail being
  // reachable, because it is answered from the platform's own send stamp. "sent_or_removed" remains
  // the weaker reading a drafted invitation resolves to.
  invitationDraftState?: "sent" | "not_drafted" | "drafted" | "sent_or_removed" | "unknown";
  confirmationDraftState?: "sent" | "not_drafted" | "drafted" | "sent_or_removed" | "unknown";
  renewalLinkOpenedAt?: string | null;
  // Session ANC. GET only: when the platform last sent the invitation, and the address a send would
  // go to now (read live from the customer, so the office is always shown the current one). The
  // recipient is what lets the panel's two-step confirm NAME who it is about to email, which the
  // platform's branded-confirm rule requires of every outward customer action.
  invitationSentAt?: string | null;
  invitationRecipient?: string | null;
  // Session ANC. POST only, and only when the office asked to send: whether the email actually left.
  // FALSE IS THE STATE THAT MATTERS -- the link exists but the customer has NOT been contacted --
  // which is the exact failure this session was built to stop happening silently. `invitationResent`
  // distinguishes a first send from a resend so the banner never has to guess.
  invitationSent?: boolean;
  invitationResent?: boolean;
  // Session AKP. POST only, on a fresh mint that was NOT asked to send: a Gmail draft is waiting.
  // Declared here at last (Session ANC) -- the server has returned it since AKP and the interface's
  // own comment named it, but no client code could see it.
  invitationDrafted?: boolean;
  // Session APB (Director decisions 1 and 2). GET only: set when this cycle's CPI rise has already been
  // applied through the ledger. The agreement has renewed for the coming year, the customer's page
  // refuses the link until linkReopensOn, and the panel's send becomes the RENEWAL NOTICE (both figures,
  // no link). Null otherwise.
  alreadyRenewed?: {
    cycle: string;
    priceAppliedAt: string | null;
    previousAnnual: number | null;
    newAnnual: number | null;
    linkReopensOn: string;
    // Session APB review: the next renewal date the notice states, when the platform itself last emailed
    // the notice (its own audit record, never the invitation's send stamp), and which renewal email is
    // waiting in Gmail Drafts, read from its subject ("other" for an edited subject, "unknown" when Gmail
    // could not be read, null when none).
    nextRenewalDate?: string | null;
    noticeSentAt?: string | null;
    waitingDraft?: "notice" | "invitation" | "letter" | "other" | "unknown" | null;
  } | null;
  // Session APB. GET only, and only in the renewed state: the latest use of the link SINCE the rise was
  // applied (the page opened, or a choice submitted; each refused and audited, at most one record every
  // few minutes; an open can be an email security scanner). Null when there has been none.
  lastRefusedAttemptAt?: string | null;
  // Session APB. POST only: set when the email composed (or sent) was the renewal notice, with the
  // figures it states. Null or absent when it was the invitation.
  renewalNotice?: { previousAnnual: number; newAnnual: number; effectiveFrom: string | null } | null;
}

// Session AIK (Director decision 3): the visit-frequency change a customer asked for at renewal, and
// the office sign-off it waits for. `contractId` is the contract that was RENEWED; the decision is
// applied to its successor.
export interface RenewalFrequencyReview {
  contractId: string;
  successorContractId: string | null;
  successorStatus: string | null;
  state: "none" | "awaiting_signature" | "ready" | "confirmed";
  currentFrequency: string;
  currentFrequencyLabel: string;
  requestedFrequency: string | null;
  requestedFrequencyLabel: string | null;
  currentAnnual: number;
  confirmedAt: string | null;
  confirmedBy: string | null;
  guidance: string;
  // Session AJQ: what the engine would price the successor's register at, at the REQUESTED frequency,
  // and whether that clears the platform's internal cost floor (never customer-facing). Office-only
  // advisory -- present only in the "ready" state and only when the register could be priced; null
  // otherwise, so "cannot tell" is never rendered as "ok".
  requestedAnnualEstimate: number | null;
  requestedCostFloor: number | null;
  requestedCostFloorStatus: "below" | "tight" | "ok" | null;
}

// Session AKO (Director decision): a manual annual figure that replaces the CPI + escalator
// calculation for one contract's next renewal cycle. `cpiWould` is what CPI would otherwise have
// priced this at, so the office can see what an active override is masking (RULE 6) whether or not
// one is currently set.
// Session ARF: correcting an already-issued quote's price in place (quotePriceOverride.ts). Sends nothing.
export type QuoteOverrideTier = "core" | "care" | "complete";
export interface QuotePriceTierFigures {
  annual: number;
  maintenanceAnnual: number;
  monthlyIncVat: number;
}
export type QuotePriceTierSet = Record<QuoteOverrideTier, QuotePriceTierFigures>;
export interface QuotePriceOverrideView {
  quoteId: string;
  quoteRef: string;
  status: string;
  customerName: string | null;
  siteName: string | null;
  recommendedTier: QuoteOverrideTier;
  eligible: boolean;
  refusal: { error: string; message: string } | null;
  current: QuotePriceTierSet | null;
  calculated: QuotePriceTierSet | null;
  atCurrentPortfolioBand: { portfolioLiftCount: number; multiLiftDiscountPct: number; tiers: QuotePriceTierSet } | null;
  override: {
    tier: QuoteOverrideTier;
    value: number;
    reason: string | null;
    setById: string | null;
    setByName: string | null;
    setAt: string | null;
  } | null;
  changed?: boolean;
}

export interface RenewalPriceOverrideView {
  contractId: string;
  status: string;
  eligible: boolean;
  siteName: string | null;
  currentAnnual: number;
  cpiWould: { available: true; annualValue: number } | { available: false; reason: string };
  override: { annualValue: number; setAt: string; setBy: string | null; setByName: string | null; reason: string | null } | null;
}

// Inbox triage (Session AMR): a Gmail thread's live-resolved state. `ageWorkingDays` is only
// meaningful context for awaiting_reply/needs_chase; contentReason/contentHighConfidence only for
// action_owed (see server/src/modules/inbox/triageClassifier.ts buildResolvedTriageList, the single
// home this shape mirrors).
export interface InboxTriageThread {
  id: string;
  mailbox: string;
  gmailThreadId: string;
  subject: string | null;
  counterpartyEmail: string | null;
  counterpartyName: string | null;
  lastMessageAt: string;
  lastMessageSnippet: string | null;
  state: "awaiting_reply" | "needs_chase" | "action_owed";
  ageWorkingDays: number;
  // Session ANE: orthogonal to state, never a fourth state. An aged thread is still action_owed or
  // needs_chase; it is simply older than the aged threshold and is shown in its own section rather
  // than in the daily working list.
  aged: boolean;
  contentHighConfidence: boolean | null;
  contentReason: string | null;
}
export interface InboxTriageExcludedSample {
  id: string;
  subject: string | null;
  counterpartyEmail: string | null;
  reason: string;
}
export interface InboxTriageExcluded {
  total: number;
  byReason: { reason: string; count: number }[];
  sample: InboxTriageExcludedSample[];
}
export interface InboxTriageDismissedThread {
  id: string;
  subject: string | null;
  counterpartyEmail: string | null;
  counterpartyName: string | null;
  dismissedAt: string;
}
// Enquiry (Session ARE, D82): an actionable InboxTriageThread row, extended with a customer/site
// match and the reply-drafting trust-gate. Same underlying table as InboxTriage above -- "Enquiry"
// is the product-facing name, matching the Prisma-model-vs-customer-facing-label pattern this
// platform already uses for the Care pricing tier.
export type EnquiryStatus = "new" | "drafted" | "awaiting_review" | "approved" | "sent";
export type EnquiryDraftTier = "templated" | "novel";

export interface EnquiryListRow {
  id: string;
  subject: string | null;
  counterpartyEmail: string | null;
  counterpartyName: string | null;
  customerId: string | null;
  customerName: string | null;
  lastMessageAt: string;
  status: EnquiryStatus;
  draftTier: EnquiryDraftTier | null;
  contentReason: string | null;
  // Session ATM, Part A: only present when the request carried includeNoise: true. A row with no
  // override and no automated-sender match reads null, same as a non-noise row.
  noiseReason?: string | null;
  // Session AUA: where the current draft's wording came from ("template" names the approved template and version; "free"
  // is the labelled fallback). Sent on the detail route; optional here so a list row without them shows nothing.
  draftKind?: "template" | "free" | "agent" | null;
  freeDraft?: boolean;
  draftTemplateKey?: string | null;
  draftTemplateVersion?: number | null;
  /**
   * Session BCF: where the conversation stands in the Mail workspace's own derived lanes ("reply" = the engine's draft is
   * ready, "answered" = ELE has replied, from anywhere, "person" = needs a person), so the two pages cannot disagree. Null
   * when the conversation has no engine record.
   */
  mailState?: string | null;
}

// Session ATM, Part A: a manual correction to the automatic noise classification, keyed on the
// sender's own address. GUESSED SHAPE -- the backend agent's real route was not readable at the
// time this file was written; reconcile against server/src/modules/inbox/enquiry.routes.ts once
// Part A lands if the field names differ.
export type EnquirySenderTreatment = "not_noise" | "noise";
export interface EnquirySenderOverride {
  id: string;
  email: string;
  treatment: EnquirySenderTreatment;
  reason: string | null;
  createdAt: string;
  // Present on the list route's own rows (GET /sender-overrides); the create/update route's
  // response omits createdByUserId/updatedAt below it in the object it returns, so both are
  // optional here rather than assumed present everywhere.
  createdByUserId?: string | null;
  updatedAt?: string;
}
export interface EnquiryNoiseSuppressed {
  count: number;
  byReason: Record<string, number>;
}

// Session ATM, Part D: a proposed structured write derived from an enquiry's own text, held for
// office approval before anything is written. GUESSED SHAPE, same caveat as above.
export type EnquiryFactType = "contact_change" | "site_access_change" | "price_confirmation";
export type EnquiryFactProposalStatus = "pending" | "approved" | "dismissed";
export interface EnquiryFactProposal {
  id: string;
  enquiryId: string;
  factType: EnquiryFactType;
  summary: string;
  status: EnquiryFactProposalStatus;
  createdAt: string;
  decidedAt: string | null;
}

export interface EnquiryDraftContent {
  subject: string;
  bodyHtml: string;
}

// Session ATW: GET /api/enquiries/:id/send-preview. Exactly who approve-and-send would reach and what it says, read fresh
// from the Gmail conversation: the reply goes to whoever wrote (their Reply-To, else their From), in that conversation.
export interface EnquirySendPreview {
  status: EnquiryStatus;
  sendable: boolean;
  refusal: string | null;
  to: string | null;
  cc: string | null;
  subject: string | null;
  threaded: boolean;
  text: string | null;
  signatureFromGmail: boolean;
  // Session ATZ (Item 7): the ELE address the reply goes FROM (the address the customer wrote to, when Gmail lets the
  // account send from it; null means Gmail's own default), and why, in plain words.
  from?: string | null;
  fromReason?: string | null;
  // Session AUA (DE-3): the one signature every reply carries, always named "Company signature". Null when unsendable.
  signature?: string | null;
}

// ---- The Mail workspace (Session AUB, email system Phase 4) ----------------------------------------------------------------
// server/src/modules/mail/mail.routes.ts and dto.ts. Lanes and states are decided by the server, live, on every read.
export type MailLane = "reply" | "person" | "work" | "filed" | "all";
export type MailMessageLane = "reply" | "person" | "filed" | "all";
export type MailState = "filed" | "work" | "pending" | "superseded" | "answered" | "dismissed" | "snoozed" | "reply" | "person" | "no_reply";
export type MailSort = "received" | "subject" | "from" | "category" | "relevance";
export type MailDismissReason = "no_action_needed" | "handled_outside_email" | "not_for_ele" | "spam_or_marketing" | "duplicate" | "other";

/** Who a message is from, as far as the platform's records say: for display only, never for what a draft may quote. */
export type MailSenderMatch =
  | { kind: "confirmed"; customerId: string; customerName: string; siteId: string | null; siteName: string | null }
  | {
      kind: "exact";
      customerId: string;
      customerName: string;
      siteId: string | null;
      siteName: string | null;
      via: "billing" | "secondary" | "contact" | "site_contact";
      contactName: string | null;
      address: string;
    }
  | { kind: "ambiguous"; candidates: Array<{ customerId: string; customerName: string }>; address: string }
  | { kind: "suggestion"; candidates: Array<{ customerId: string; customerName: string }>; domain: string }
  | { kind: "none" };

export interface MailListRow {
  kind: "message" | "enquiry";
  /** The message id, or "enquiry:<id>" for an older enquiry from before the email engine. */
  id: string;
  messageId: string | null;
  enquiryId: string | null;
  gmailThreadId: string;
  gmailUrl: string;
  subject: string | null;
  from: string | null;
  fromName: string | null;
  receivedAt: string;
  snippet: string | null;
  category: string | null;
  categoryLabel: string | null;
  outcome: string | null;
  outcomeReason: string | null;
  state: MailState;
  stateLabel: string;
  lane: string | null;
  fileKindLabel: string | null;
  hasAttachments: boolean;
  readIssue: string | null;
  draftKind: "template" | "free" | "agent" | null;
  /** "From template <key>, version N", or "Free draft", or null when there is no draft. */
  draftLabel: string | null;
  freeDraft: boolean;
  assigned: { userId: string; name: string | null } | null;
  snoozedUntil: string | null;
  match: MailSenderMatch;
  replyOverdue: boolean;
  /** Session AUE (D-LATE-1): "Late reply: N working days" while a reply is owed and would go out late, else null. Optional
   * so an older server reads as not late. */
  lateReply?: string | null;
  firstReplyTargetHours: number | null;
  workOrderProposalId: string | null;
  beforeEngine: boolean;
}

export interface MailDraft {
  source: "engine" | "enquiry";
  subject: string | null;
  /** The reply as plain text, without the sign-off and signature (added when it is sent). */
  text: string;
  kind: "template" | "free" | null;
  label: string | null;
  /** The approved template this draft filled; id is null when the template row has since been deleted. */
  template: { id: string | null; key: string; version: number | null } | null;
  /** Why no template was used (a free draft): the engine's own record, shown as it is. */
  templateMiss: unknown;
  attachments: Array<{ key: string; title: string }>;
  generatedAt: string | null;
  instruction: string | null;
  /** Always "Company signature" (DE-3). */
  signature: string;
}

export type MailSendRefusalCode =
  | "newer_message"
  | "already_answered"
  | "routed"
  | "no_draft"
  | "attachment_in_gmail"
  | "person_draft_in_gmail"
  | "being_decided";

export interface MailMessageDetail extends MailListRow {
  replyTo: string | null;
  to: string[];
  cc: string[];
  /** What the message said (stored when it was recorded); null for one recorded from its headers alone. */
  body: { text: string; truncated: boolean } | null;
  attachments: Array<{ filename: string; mimeType: string; size: number | null }>;
  /** The checks a needs-you draft failed, when it was one. */
  checks: Array<{ check: string; detail: string }>;
  draft: MailDraft | null;
  send: { allowed: boolean; code: MailSendRefusalCode | null; reason: string | null };
  enquiry: {
    id: string;
    status: EnquiryStatus | null;
    lastSentAt: string | null;
    dismissal: { at: string; reason: MailDismissReason; reasonLabel: string; note: string | null } | null;
    assignedAt: string | null;
    snoozedAt: string | null;
  } | null;
  facts: { isLatest: boolean; isAnswered: boolean; isDismissed: boolean; isSnoozed: boolean };
  /** Older enquiries only: why the triage sweep flagged it. */
  whyFlagged?: string | null;
}

export interface MailListParams {
  lane: MailMessageLane;
  q?: string;
  customerId?: string;
  siteId?: string;
  category?: string;
  outcome?: "pending" | "drafted" | "needs_you" | "no_reply_needed";
  state?: MailState;
  /** YYYY-MM-DD, London days, both inclusive. */
  from?: string;
  to?: string;
  hasAttachment?: boolean;
  /** A user id, "me" or "unassigned". */
  assignedTo?: string;
  snoozed?: "hide" | "only";
  source?: "engine" | "legacy";
  sort?: MailSort;
  dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export interface MailCounts {
  reply: number;
  person: number;
  filed: number;
  snoozed: number;
  /** Work orders waiting for a decision. */
  work: number;
  /** Session AUD: job-stage notices owed to customers, waiting in the Outbox. Optional for a server older than AUD. */
  outbox?: number;
}

// ---- The Mail workspace Outbox (Session AUD, email system Phase 5, Item 2; DE-1) ----------------------------------------
// Job-stage notices owed to customers, derived live by the server (server/src/modules/customerNotices/outbox.ts). A person
// sends them in a batch: preview (the default) re-checks each against the fingerprint the page showed and sends nothing;
// preview: false sends each one by one and returns a result per notice.
export type CustomerNoticeKind = "callout_received" | "engineer_booked" | "engineer_updated";
export type CustomerNoticeStatus = "sending" | "sent" | "failed" | "dismissed";

export interface OutboxNotice {
  key: string;
  kind: CustomerNoticeKind;
  kindLabel: string;
  sourceType: "callout" | "job";
  callOutId: string | null;
  jobId: string | null;
  jobType: string | null;
  customerId: string | null;
  customerName: string | null;
  siteName: string;
  eleNumber: string | null;
  engineerFirstName: string | null;
  /** ISO instant: when the fault was reported, or the booked day. */
  about: string;
  to: string | null;
  cc: string | null;
  /** Why it cannot be sent as it stands (no address on record, an archived customer); null when it can. */
  blockedReason: string | null;
  sendable: boolean;
  subject: string;
  text: string;
  /** The branded email exactly as it would go (render in a sandboxed frame only). */
  html: string;
  fingerprint: string | null;
  /** The last decision a person made about it: a failed send (with the reason) or a send still in progress. */
  decision: { id: string; status: CustomerNoticeStatus; failure: string | null; attempts: number; updatedAt: string } | null;
  /** For a "Booking changed" notice: what the customer was last told. */
  previous: { engineerName: string | null; bookedFor: string | null } | null;
}

export interface OutboxPreviewItem {
  key: string;
  wouldSend: boolean;
  reason?: "no_longer_owed" | "not_sendable" | "changed";
  message?: string;
  to?: string | null;
  cc?: string | null;
  subject?: string;
  kindLabel?: string;
  siteName?: string;
  /** Why the last attempt failed, for a notice being sent again (e.g. "check Gmail's Sent folder first"). */
  warning?: string | null;
}

/** A notice a person dismissed in the last 30 days, with the decision id that undoes it (RULE 1's way back). */
export interface OutboxDismissal {
  decisionId: string;
  kind: CustomerNoticeKind;
  kindLabel: string;
  customerName: string | null;
  siteName: string | null;
  eleNumber: string | null;
  dismissedAt: string;
  dismissedBy: string | null;
}

export type OutboxSendResult =
  | { key: string; outcome: "sent"; to: string; noticeId: string; note?: string }
  | { key: string; outcome: "failed"; noticeId: string; message: string }
  | { key: string; outcome: "refused"; reason: string; message: string };

// ---- Chasing (Session AUH, email system Phase 7) ----------------------------------------------------------------------------
// One chase per thing ELE is waiting on (server/src/modules/chases/). The daily run (06:15 UTC) only DRAFTS; a person sends,
// through api.chases.previewSend then api.chases.send (the preview names every To, Cc and From). Mirrors chaseDto.ts.
export type ChaseTone = "soft" | "hard";
export type ChaseStatus = "open" | "satisfied" | "stopped" | "closed";
export interface ChaseFailure {
  check: string;
  line: string;
  detail: string;
}
export interface ChaseDraft {
  step: number | null;
  tone: ChaseTone | null;
  subject: string | null;
  body: string | null;
  kind: "template" | "free" | null;
  label: string | null;
  templateKey: string | null;
  templateVersion: number | null;
  miss: { reason: string; text: string; templateKey: string; missingSlots?: string[] } | null;
  failures: ChaseFailure[] | null;
  to: string | null;
  cc: string | null;
  from: string | null;
  attachment: string | null;
  generatedAt: string | null;
  fingerprint: string | null;
  inGmail: boolean;
  sendable: boolean;
}
export interface ChaseCustomerHold {
  customer: string;
  active: boolean;
  until: string | null;
  reason: string | null;
  setBy: string | null;
  setAt: string | null;
}

export interface Chase {
  id: string;
  status: ChaseStatus | string;
  sourceKind: string;
  sourceLabel: string;
  sourceId: string | null;
  link: string | null;
  detector: string;
  counterpartyKind: string;
  counterpartyLabel: string;
  owedKind: string;
  owedLabel: string;
  ladder: string;
  ladderLabel: string;
  title: string;
  reference: string | null;
  customerId: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  copyEmail: string | null;
  gmailThreadId: string | null;
  sinceAt: string;
  sinceLabel: string;
  owedWorkingDays: number;
  lastContactAt: string;
  deadlineAt: string | null;
  deadlineLabel: string | null;
  promiseText: string | null;
  promiseDate: string | null;
  step: number;
  nextDueAt: string;
  due: boolean;
  tone: ChaseTone;
  toneReason: string | null;
  dueReason: string | null;
  toneOverride: ChaseTone | null;
  dueOverrideAt: string | null;
  overrideByName: string | null;
  snoozedUntil: string | null;
  // Session BAB: why the daily run is not drafting this chase right now (handled by hand, customer hold, duplicate). Null when clear.
  heldKind?: "handled_by_hand" | "customer_hold" | "duplicate" | "dispute" | null;
  heldReason?: string | null;
  mode: "email" | "call" | string;
  stoppedReason: string | null;
  endedAt: string | null;
  endedByName: string | null;
  callLoggedAt: string | null;
  closeOfferAt: string | null;
  finalNoticeSentAt: string | null;
  finalNoticePayBy: string | null;
  referralSentAt: string | null;
  referralReady: boolean;
  draft: ChaseDraft | null;
  sends: Array<{ step: number; tone: string; at: string; via: string; kind: string }>;
  createdByName: string | null;
  createdAt: string;
}
export interface ChaseStepMetric {
  ladder: string;
  ladderLabel: string;
  step: number;
  sent: number;
  repliedWithin5: number;
  resolved: number;
  endedAsCall: number;
  replyRate: number | null;
}
export interface ChaseTotals {
  sent: number;
  repliedWithin5: number;
  resolved: number;
  endedAsCall: number;
  replyRate: number | null;
}
export interface ChaseMetrics {
  from: string;
  to: string;
  open: number;
  dueNow: number;
  calls: number;
  rows: ChaseStepMetric[];
  totals: ChaseTotals;
}
export interface ChaseListResponse {
  total: number;
  items: Chase[];
  metrics: { open: number; dueNow: number; calls: number; totals: ChaseTotals; windowDays: number };
  lastRunAt: string | null;
  lastRunProblem: string | null;
}
export type ChaseSendPreviewItem =
  | { id: string; wouldSend: true; to: string; cc: string | null; from: string | null; fromReason: string | null; subject: string; attachment: string | null; title: string }
  | { id: string; wouldSend: false; reason: string; message: string; title?: string };
export type ChaseSendResult =
  | { id: string; outcome: "sent"; to: string; cc: string | null; note?: string }
  | { id: string; outcome: "failed"; message: string }
  | { id: string; outcome: "refused"; reason: string; message: string };
export interface ChaseLadderSetting {
  key: string;
  label: string;
  description: string;
  steps: number[];
  defaultSteps: number[];
  editable: boolean;
  after: string;
}
export interface ChaseSupplierFlag {
  /** An email address, a domain, or a supplier name as typed on purchase orders. */
  match: string;
  doNotChase: boolean;
  doNotChaseHard: boolean;
}
// Session BBN: scheduled report delivery. A schedule leaves a Gmail DRAFT for the office; nothing is ever sent by it.
export interface ReportScheduleItem {
  id: string;
  reportType: string;
  reportLabel: string;
  cadence: string;
  cadenceLabel: string;
  recipient: string;
  enabled: boolean;
  lastRunAt: string | null;
  lastRunStatus: "drafted" | "skipped" | "failed" | null;
  lastRunMessage: string | null;
  dueNextRun: boolean;
  createdAt: string;
}
export interface ReportScheduleList {
  schedules: ReportScheduleItem[];
  reportTypes: Array<{ value: string; label: string }>;
  cadences: Array<{ value: string; label: string }>;
}
export interface ReportScheduleRunResult {
  result: { scheduleId: string; status: "drafted" | "skipped" | "failed"; message: string; draftId?: string; subject?: string };
  schedule: ReportScheduleItem | null;
}
/** A Bank of England official rate at the close of 30 June or 31 December (date YYYY-06-30 or YYYY-12-31). */
export interface ChaseReferenceRate {
  date: string;
  baseRatePct: number;
}

export interface ChaseSettings {
  ladders: ChaseLadderSetting[];
  ladderUpdatedAt: string | null;
  ladderUpdatedByName: string | null;
  suppliers: ChaseSupplierFlag[];
  recovery: { name: string | null; email: string | null; isDefault?: boolean; updatedAt: string | null; updatedByName: string | null };
  /** 22 October 2026: the Bank of England reference rates the final notice's statutory late payment claim reads. Absent from an older server. */
  lateInterest?: { referenceRates: ChaseReferenceRate[]; upliftPct: number; updatedAt: string | null; updatedByName: string | null };
}
export type ChaseCreateSource =
  | { kind: "thread"; gmailThreadId: string; mailbox?: string }
  | { kind: "record"; sourceKind: "quote" | "project_quotation" | "repair_estimate" | "renewal" | "purchase_order" | "loler" | "invoice"; sourceId: string }
  | { kind: "manual"; title: string; contactEmail: string; contactName?: string; owedKind?: "reply" | "decision" | "information" | "document" | "delivery" };

export interface MailAssignee {
  id: string;
  name: string;
  role: "admin" | "office";
  me: boolean;
}

export interface MailPrepared {
  enquiryId: string;
  /** "person": a person's own words for a message with no ready draft. */
  source: "engine" | "enquiry" | "person";
  changed: boolean;
  subject: string;
  text: string;
}

export interface MailAfterSend {
  removedEngineDrafts: number;
  leftInGmail: number;
  compared: boolean;
  note: string | null;
}

export interface CustomerEmailRow {
  direction: "inbound" | "sent";
  id: string;
  at: string;
  gmailThreadId: string;
  gmailUrl: string;
  subject: string | null;
  counterpart: string | null;
  snippet: string | null;
  category: string | null;
  // Session AUG (Phase 6, Item 3, D221). Optional so a server older than AUG still reads.
  /** "recorded": the email engine's record or the reply library; "sent_folder": read from Gmail's Sent folder now. */
  source?: "recorded" | "sent_folder";
  /** Who composed a sent email; null for one received. */
  origin?: "platform" | "person" | null;
  platformKind?: string | null;
  /** A payment details email: listed, never shown (D114). */
  withheld?: boolean;
}

/** Session AUG: how the Sent folder read went for this page; null for received mail only (which never reads it). */
export interface CustomerSentFolderRead {
  state: "complete" | "incomplete" | "failed" | "not_connected" | "no_addresses";
  message: string | null;
}

/** Session BBO: an engineer's recent email (GET /api/mail/engineers/:userId/email). */
export interface EngineerRecentEmail {
  engineerId: string;
  address: string | null;
  state: "no_address" | "complete" | "incomplete" | "failed";
  message: string | null;
  emails: { direction: "inbound" | "sent"; at: string; subject: string | null; counterpart: string | null; gmailThreadId: string; gmailUrl: string; withheld: boolean }[];
}

export interface CustomerEmailPage {
  customerId: string;
  addresses: string[];
  emails: CustomerEmailRow[];
  /** "offset" for received mail only (total is a number); "cursor" for all and sent (page on with nextBefore). */
  paging?: "offset" | "cursor";
  total: number | null;
  limit: number;
  offset: number;
  nextBefore?: string | null;
  sentFolder?: CustomerSentFolderRead | null;
}

/** A conversation action's target: the message, or (an older enquiry with no engine record) the enquiry. */
export interface MailTarget {
  messageId?: string | null;
  enquiryId?: string | null;
}
export function mailTarget(t: MailTarget): string {
  if (t.messageId) return `/mail/messages/${encodeURIComponent(t.messageId)}`;
  if (t.enquiryId) return `/mail/enquiries/${encodeURIComponent(t.enquiryId)}`;
  throw new Error("A mail action needs a message or an enquiry.");
}

// Session ATZ (Item 4): the Enquiries backlog clearance.
export type EnquiryTriageState = "action_owed" | "needs_chase" | "awaiting_reply";
export interface EnquiryBulkFilter {
  states?: EnquiryTriageState[];
  categories?: string[];
  minAgeWorkingDays?: number;
  agedOnly?: boolean;
  senderDomain?: string;
  enquiryStatuses?: Array<"new" | "drafted" | "awaiting_review" | "sent">;
}
export interface EnquiryBulkPreviewRow {
  id: string;
  subject: string | null;
  counterpartyEmail: string | null;
  counterpartyName: string | null;
  lastMessageAt: string;
  state: EnquiryTriageState;
  ageWorkingDays: number;
  aged: boolean;
  category: string;
  categoryLabel: string;
  enquiryStatus: string;
}
export interface EnquiryBulkPreview {
  total: number;
  fingerprint: string;
  filterDescription: string;
  offset: number;
  limit: number;
  rows: EnquiryBulkPreviewRow[];
  categories: { key: string; label: string }[];
}
export interface EnquiryBulkBatch {
  batchId: string;
  stillHandled: number;
  markedAt: string | null;
  markedByUserId: string | null;
}

// Session ATZ (Item 6): GET/PUT /api/settings/ele-addresses.
export interface EleAddressesSettings {
  addresses: string[];
  customised: boolean;
  connectedMailboxes: string[];
  effective: string[];
  seed: string[];
}

// Session AUD (Item 5): GET /api/settings/email-deliverability, the sending domain's SPF, DKIM and DMARC read live from DNS.
// "unknown" means the lookup did not complete: never an all-clear.
export type EmailDeliverabilityStatus = "pass" | "warn" | "fail" | "unknown";
export interface EmailDeliverabilityCheck {
  key: "spf" | "dkim" | "dmarc";
  label: string;
  status: EmailDeliverabilityStatus;
  summary: string;
  lookedUp: string;
  record: string | null;
  whatToDo: string | null;
}
export interface EmailDeliverabilityReport {
  checkedAt: string;
  overall: EmailDeliverabilityStatus;
  overallSummary: string;
  domains: { domain: string; overall: EmailDeliverabilityStatus; checks: EmailDeliverabilityCheck[] }[];
  notes: string[];
}

// Session ATZ (Item 3): a work order a property platform assigned to ELE, as the server's allow-list shapes it.
export type WorkOrderStatusFilter = "pending" | "accepted" | "dismissed" | "all";
export interface WorkOrder {
  id: string;
  source: string;
  sourceLabel: string;
  externalRef: string;
  title: string;
  description: string | null;
  platformSiteName: string | null;
  managedBy: string | null;
  authorisationLimitPence: number | null;
  dueBy: string | null;
  priority: string | null;
  jobType: string | null;
  platformAddress: string | null;
  platformPostcode: string | null;
  platformInstance: string | null;
  respondInPlatform: boolean;
  latestEvent: string;
  latestEventLabel: string;
  latestEventAt: string;
  receivedAt: string;
  status: "pending" | "accepted" | "dismissed";
  site: { id: string; name: string; customerId: string; customerName: string | null } | null;
  siteMatch: "auto" | "person" | null;
  callOut: { id: string; ref: string | null } | null;
  job: { id: string; type: string; ref: string | null } | null;
  acceptedAt: string | null;
  acceptedByUserId: string | null;
  dismissedAt: string | null;
  dismissedByUserId: string | null;
  dismissReason: string | null;
  messageCount: number;
}
export interface WorkOrderDetail {
  workOrder: WorkOrder;
  lifts: { id: string; eleNumber: string | null; label: string }[];
  messages: { id: string; subject: string | null; receivedAt: string; gmailThreadId: string; event: string | null; eventLabel: string | null; needsReply: boolean }[];
}

// Session ATZ (Item 1): one notice the platform put on the office dashboard instead of emailing the office.
export interface OfficeNotice {
  id: string;
  kind: string;
  title: string;
  body: string;
  linkPath: string | null;
  severity: "info" | "action";
  raisedAt: string;
  raiseCount: number;
  read: boolean;
  readAt: string | null;
  readByUserId: string | null;
}

export interface EnquiryDetail extends EnquiryListRow {
  mailbox: string;
  gmailThreadId: string;
  lastMessageSnippet: string | null;
  lastMessageDirection: "inbound" | "outbound";
  contentFlag: "none" | "action_owed";
  contentHighConfidence: boolean | null;
  siteId: string | null;
  siteName: string | null;
  matchedAt: string | null;
  matchedByUserId: string | null;
  draftContent: EnquiryDraftContent | null;
  draftTemplateKey: string | null;
  draftGeneratedAt: string | null;
  draftModel: string | null;
  sentAt: string | null;
  sentByUserId: string | null;
  convertedJobs: { id: string; type: string; ref: string | null }[];
  convertedCallOuts: { id: string; ref: string | null }[];
}

export interface InboxTriageThreadsResponse {
  threads: InboxTriageThread[];
  chaseThresholdDays: number;
  agedThresholdDays: number;
  excluded: InboxTriageExcluded;
  recentlyDismissed: InboxTriageDismissedThread[];
}
export interface InboxTriageSweepOutcome {
  mailbox: string;
  isBacklogRun: boolean;
  threadsSeen: number;
  threadsProcessed: number;
  actionOwedCount: number;
  needsChaseCount: number;
  awaitingReplyCount: number;
  excludedCount: number;
  readFailedCount: number;
  capped: boolean;
}
export interface InboxTriageStandingCounts {
  actionOwed: number;
  needsChase: number;
  awaitingReply: number;
  aged: number;
  excluded: number;
  dismissed: number;
  total: number;
}
export interface InboxTriageSweepResult {
  outcomes: InboxTriageSweepOutcome[];
  exclusionsReconciled?: { scanned: number; set: number; cleared: number };
  standing?: InboxTriageStandingCounts;
}

// The engineer's own view of their open check-in on one job. Live only: never cached, never in the
// offline job store, because a stale "you are checked in" is the one answer that must not be shown.
export interface LoneWorkerOwnSession {
  sessionId: string;
  startedAt: string;
  lastCheckInAt: string;
  checkInCount: number;
  nextCheckInDueAt: string;
}

// ---- Lone worker safety (Session AOB) ----
// One attendance: an explicit arrival tap, an explicit departure tap, and the escalation in between.
export interface LoneWorkerSessionDto {
  id: string;
  jobId: string;
  engineerId: string | null;
  engineerName: string;
  engineerMobile: string | null;
  // The office's fallback contact if this call goes unanswered (Session AOU). Null whenever the
  // engineer has not supplied one, which is the normal, common case.
  engineerNextOfKinName: string | null;
  engineerNextOfKinRelationship: string | null;
  engineerNextOfKinPhone: string | null;
  siteName: string;
  ref: string;
  jobType: string;
  startedAt: string;
  lastCheckInAt: string;
  checkInCount: number;
  // DERIVED live from lastCheckInAt by the server, never stored (RULE 6). Null once ended.
  nextCheckInDueAt: string | null;
  // Null until the deadline passes. The office sees this 30 minutes BEFORE the alert fires, which is
  // while a phone call can still resolve it.
  overdueMins: number | null;
  alerted: boolean;
  alertCount: number;
  lastAlertAt: string | null;
  endedAt: string | null;
  endedReason: string | null;
  endedReasonLabel: string;
  officeNote: string | null;
}

export interface LoneWorkerSessions {
  overdue: LoneWorkerSessionDto[];
  onSite: LoneWorkerSessionDto[];
  recent: LoneWorkerSessionDto[];
  // Sent by the server rather than hardcoded in the page, so the copy and the sweep cannot drift.
  policy: { firstAlertAfterMins: number; repeatEveryMins: number };
}

// ---- Session BAQ: the examiner's LOLER report items, worked to closure ("Insurer items") ----
export type LolerItemState = "raised" | "quoted" | "booked" | "done" | "verified";
export type LolerItemCategory = "immediate" | "timed" | "advisory";
export type LolerItemSort = "urgency" | "age" | "deadline" | "site" | "state";

export interface LolerTrackedItem {
  id: string;
  description: string;
  category: LolerItemCategory;
  categoryLabel: string;
  remedyBy: string | null;
  pastRemedyBy: boolean;
  state: LolerItemState;
  stateLabel: string;
  status: string;
  treatment: string | null;
  ageDays: number;
  reportId: string;
  reportRef: string | null;
  examinationDate: string | null;
  inspector: string | null;
  assetId: string | null;
  ele: string | null;
  siteId: string | null;
  siteName: string | null;
  defectId: string | null;
  followUpJob: { id: string; status: string; engineerName: string | null; scheduledFor: string | null } | null;
  checkOutcome: "checked" | "needs_work" | "no_access" | null;
  checkOutcomeLabel: string | null;
  checkNote: string | null;
  checkedAt: string | null;
  verifiedAt: string | null;
}

export interface LolerItemCounts {
  raised: number;
  quoted: number;
  booked: number;
  done: number;
  verified: number;
  overdue: number;
  immediate: number;
}

export interface LolerItemsQuery {
  assetId?: string;
  siteId?: string;
  reportId?: string;
  state?: LolerItemState[];
  category?: LolerItemCategory;
  overdue?: boolean;
  q?: string;
  sort?: LolerItemSort;
  dir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export interface LolerItemsPage {
  rows: LolerTrackedItem[];
  total: number;
  page: number;
  pageSize: number;
  counts: LolerItemCounts;
  capped?: boolean;
}

export interface LolerItemActionResult {
  ok: boolean;
  item: LolerTrackedItem;
  defectResolved?: boolean;
}

export interface LolerItemsBackfillPreview {
  preview: true;
  reports: Array<{ reportId: string; reportRef: string | null; examinationDate: string | null; assetId: string | null; ele: string | null; siteName: string | null; untracked: number }>;
  lifts: number;
  items: number;
  liftsNeedingJob: number;
  note: string;
}

export interface LolerItemsBackfillResult {
  preview: false;
  created: number;
  alreadyTracked: number;
  duplicates: number;
  reports: number;
  note: string;
}

// ---- Session PJ2: a project survey visit, as the engineer's phone reads it (no customer contact, no price) ----
export interface ProjectSurveyVisitSurveyView {
  id: string;
  kind: "site" | "lift";
  label: string | null;
  surveyType: import("./surveyTemplate").SurveyType | null;
  addedOnSite: boolean;
  formVersion: string;
  /** The flat cell record (src/lib/surveyRecord.ts); read with readRecord. */
  record: unknown;
  /** The derived status (not_started, in_progress, submitted, reviewed, superseded). */
  status: string;
  submittedAt: string | null;
  reviewedAt: string | null;
  photos: { id: string; slotKey: string; caption: string | null }[];
}
export interface ProjectSurveyVisitView {
  jobId: string;
  projectRef: string | null;
  siteName: string | null;
  siteAddress: string | null;
  sitePostcode: string | null;
  accessNote: string | null;
  status: string;
  /** True for a crew member (read only, decision D-PJ2-2) or a visit no longer open. */
  readOnly: boolean;
  surveys: ProjectSurveyVisitSurveyView[];
}
export interface ProjectSurveyRecordSaveResult {
  record: unknown;
  changed: string[];
  surveyType: string | null;
  status: string;
}

// ---- Session PJ2: the OFFICE's side of a project's site surveys (raise, book, review, correct, delete, supplier data sheet) ----
// Reached from the project's Details tab. Nothing here is a price, and no customer contact or Drive link is ever in a reply.
export type ProjectSurveyStatusView = "not_started" | "in_progress" | "submitted" | "reviewed" | "superseded";
export interface ProjectSurveyVisitLinkView {
  jobId: string;
  engineerName: string | null;
  scheduledFor: string | null;
  jobStatus: string;
  outcome: string | null;
  notSurveyedReason: string | null;
}
export interface ProjectSurveySummaryView {
  id: string;
  kind: "site" | "lift";
  assetId: string | null;
  label: string | null;
  surveyType: string | null;
  addedOnSite: boolean;
  formVersion: string;
  status: ProjectSurveyStatusView;
  submittedAt: string | null;
  reviewedAt: string | null;
  supersededById: string | null;
  supplierSheetDraftedAt: string | null;
  // Session PJ2F3: the survey as read; a review sends it back and is refused 409 survey_changed if the survey moved since.
  version?: string | null;
  photoCount: number;
  gapsCount: number;
  visits: ProjectSurveyVisitLinkView[];
}
export interface ProjectSurveyVisitSummaryView {
  jobId: string;
  engineerId: string | null;
  engineerName: string | null;
  scheduledFor: string | null;
  status: string;
  accessNote: string | null;
  surveyIds: string[];
}
export interface ProjectSurveysView {
  project: {
    id: string;
    projectRef: string | null;
    title: string;
    status: string;
    siteName: string | null;
    siteAddress: string | null;
    sitePostcode: string | null;
    hasAddress: boolean;
    assetIds: string[];
    lifts: { assetId: string; label: string }[];
  };
  surveys: ProjectSurveySummaryView[];
  visits: ProjectSurveyVisitSummaryView[];
}
export interface ProjectSurveyPhotoView {
  id: string;
  slotKey: string;
  caption: string | null;
  capturedAt: string | null;
  uploadedAt: string;
}
/** A figure worked out from the measurements: the shape of server/src/modules/projectSurveys/surveyFigures.ts SurveyFigure, exactly.
 *  A lift survey's detail carries all nine; a site survey's carries none. */
export type ProjectSurveyFigureKey =
  | "totalTravelMm"
  | "shaftHeightMm"
  | "smallestClearWidthMm"
  | "smallestClearDepthMm"
  | "largestOutOfPlumbMm"
  | "landingCount"
  | "carEntranceCount"
  | "landingButtonCount"
  | "carButtonCount";
export type ProjectSurveyFigureDetailView =
  | { kind: "plumb"; row: string; rowLabel: string; direction: string | null; directionLabel: string | null; recorded: number }
  | { kind: "entrances"; sides: string[]; perFloor: { row: string; label: string; side: string }[] };
export type ProjectSurveyFigureView = {
  key: ProjectSurveyFigureKey;
  label: string;
  result: { ok: true; value: number; unit: "mm" | "count"; workings: string; detail?: ProjectSurveyFigureDetailView } | { ok: false; missing: string[] };
};
export interface ProjectSurveyDetailView extends ProjectSurveySummaryView {
  projectId: string;
  projectRef: string | null;
  siteName: string | null;
  siteAddress: string | null;
  record: unknown;
  photos: ProjectSurveyPhotoView[];
  /** `how`: "corrected by the office", or the photo-confirmed words when the office accepted a value read from a plate. */
  audit: { at: string; byName: string | null; path: string; before: unknown; after: unknown; how: string }[];
  figures: ProjectSurveyFigureView[];
}
export interface ProjectSurveyBookBody {
  surveyIds?: string[];
  addAssetIds?: string[];
  addLabels?: string[];
  engineerId: string;
  date: string;
  accessNote?: string;
  preview?: boolean;
}
export interface ProjectSurveyBookResult {
  preview: boolean;
  jobId?: string;
  /** On a commit: the surveys the visit covers, by id. */
  surveyIds?: string[];
  surveys: string[];
  willCreate?: string[];
  engineerName: string;
  date: string;
  address: string;
  warnings: { capacityWarning: string | null; leaveWarning: string | null; certificationWarning: string | null };
}
export interface ProjectSurveyDeleteCheck {
  mode: "delete";
  allowed: boolean;
  refusals: string[];
  survey: { id: string; kind: "site" | "lift"; label: string | null; status: "submitted" | "open"; superseded: boolean };
  goesWith: { photos: number; driveFilesLeft: number; visitsCancelled: number; visitsKept: number; supplierDraft: boolean };
  confirmLabelRequired: boolean;
}
export interface ProjectSurveyPlateProposal {
  fieldKey: string;
  label: string;
  unit: string | null;
  value: string | number;
  readText: string;
  /** What the survey records for the field now (a SurveyValue, or null). */
  current: unknown;
}
export interface ProjectSurveyPlateRead {
  plateKind: string;
  plateLabel: string;
  proposals: ProjectSurveyPlateProposal[];
  /** The plate's fields this survey does not hold (or not for its type), with why. */
  skipped: { fieldKey: string; reason: string }[];
  /** Set when nothing was read because none of the plate's values are on this survey (no AI call was made). */
  message?: string;
}
export interface ProjectSurveySupplierOptions {
  project: { projectRef: string; siteName: string | null };
  lifts: { surveyId: string; label: string; surveyType: string | null; reviewed: boolean; eligible: boolean; reason?: string; draftedAt: string | null }[];
  /** The site survey. It is included in the sheet only once reviewed (`included`), whatever is ticked; `reason` says why it is left out. */
  site: { surveyId: string; reviewed: boolean; included?: boolean; reason?: string } | null;
  /** bytes is null when Drive could not say the file's size. */
  photos: { id: string; surveyId: string; slotKey: string; label: string; defaultOn: boolean; bytes: number | null }[];
}
export interface ProjectSurveySupplierDraftPreview {
  preview: true;
  to: string;
  subject: string;
  lifts: string[];
  /** Whether the site survey is in the sheet (only once reviewed), and why not when it exists but is left out. */
  siteSurveyIncluded?: boolean;
  siteSurveyLeftOut?: string | null;
  attachments: { name: string; bytes: number; kind: "sheet" | "photo" }[];
  photosRequested: number;
  totalBytes: number;
  photosLeftOut: boolean;
  limitBytes: number;
}
export interface ProjectSurveySupplierDraftDone {
  preview: false;
  draftId: string;
  to: string;
  subject: string;
  lifts: string[];
  attachments: { name: string; bytes: number }[];
  photosLeftOut: boolean;
  totalBytes: number;
  surveysStamped: number;
}

// Session ENR: an ELE number held for a site before signing. Read only in the office app; made and released from chat.
export interface EleReservationView {
  reference: string;
  status: "reserved" | "assigned" | "released";
  site: string;
  customer: string | null;
  project: string | null;
  lifts: { eleNumber: string; for: string; state: "held" | "assigned" | "released" }[];
  issuedExternally: { to: string; on: string | null; recordedBy: string | null } | null;
  unusedLiftNumbers: string[];
}

export const api = {
  eleReservations: {
    list: (filter: { siteId?: string; projectId?: string }) => {
      const q = new URLSearchParams();
      if (filter.siteId) q.set("siteId", filter.siteId);
      if (filter.projectId) q.set("projectId", filter.projectId);
      return request<{ reservations: EleReservationView[] }>(`/ele-reservations?${q.toString()}`, { noCache: true });
    },
  },
  // /auth/me returns 401 when unauthenticated (never 200 + null), so a success
  // always carries a user; the logged-out path is the thrown ApiError.
  // Session ASB: never served from the request cache. It is the question "is my session still alive?", and a cached
  // answer up to five minutes old would hide an ended session (and the pop tag scan relies on it asking the server).
  me: () => request<{ user: User }>("/auth/me", { noCache: true }),
  // Office users sign in with Google (a full-page redirect to /api/auth/google, not a
  // fetch -- see googleSignInUrl). Engineers sign in with a username + password.
  engineerLogin: (username: string, password: string) =>
    request<{ user: User }>("/auth/engineer/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  // External auditor magic-link sign-in (Session AEJ, D29): validate a clicked access link. On
  // success the server issues the standard auditor session cookie and returns the session user.
  auditorAccess: (token: string) =>
    request<{ user: User }>("/auditor-tokens/validate", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),
  // Office management of auditor magic links (Session AEJ, D29; office/admin). create returns the
  // raw link ONCE; list never carries a hash or the raw token; revoke invalidates a link at once.
  auditorTokens: {
    list: () => request<{ items: AuditorTokenListItem[] }>("/auditor-tokens"),
    create: (body: AuditorTokenCreateInput) =>
      request<AuditorTokenCreateResult>("/auditor-tokens", { method: "POST", body: JSON.stringify(body) }),
    revoke: (id: string) =>
      request<{ ok: boolean }>(`/auditor-tokens/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },
  // Connected AI (MCP) tokens (Session AET, admin only). create returns the raw token ONCE;
  // list never carries a hash or the raw token; revoke kills a connection at once.
  // Session BDA: the SAFed test job (job-specific DTO, the gated start, the record, the failure path, the authorisation).
  safedJobs: {
    get: (jobId: string) => request<SafedJobView>(`/safed-jobs/by-job/${encodeURIComponent(jobId)}`, { noCache: true }),
    start: (jobId: string) => request<{ ok: true }>(`/safed-jobs/by-job/${encodeURIComponent(jobId)}/start`, { method: "POST", body: JSON.stringify({}) }),
    saveRecord: (jobId: string, record: unknown) =>
      request<SafedJobView>(`/safed-jobs/by-job/${encodeURIComponent(jobId)}/record`, { method: "PUT", body: JSON.stringify(record) }),
    failure: (jobId: string, body: { testId: string; facts: string }) =>
      request<{ ok: true; defectRaised: boolean; steps: string[] }>(`/safed-jobs/by-job/${encodeURIComponent(jobId)}/failure`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    setAuthorisation: (jobId: string, body: { signatoryName: string; signatoryRole: "duty_holder" | "authorised_signatory"; signedAt: string; documentUrl?: string | null; receivedVia?: "email" | "post" | "in_person" | "other" | null }) =>
      request<SafedJobView>(`/safed-jobs/by-job/${encodeURIComponent(jobId)}/authorisation`, { method: "PUT", body: JSON.stringify(body) }),
    removeAuthorisation: (jobId: string) =>
      request<{ ok: true; removed: boolean }>(`/safed-jobs/by-job/${encodeURIComponent(jobId)}/authorisation`, { method: "DELETE" }),
    byAsset: (assetId: string) =>
      request<{ authorisationWordingCleared: boolean; jobs: SafedAssetJobView[] }>(`/safed-jobs/by-asset/${encodeURIComponent(assetId)}`, { noCache: true }),
    setWordingCleared: (approved: boolean) =>
      request<{ ok: true; authorisationApproved: boolean }>("/safed-jobs/settings", { method: "PUT", body: JSON.stringify({ authorisationApproved: approved }) }),
    panel: (assetId: string) => request<{ driveType: string; rows: SafedPanelRowView[] }>(`/safed-jobs/panel/${encodeURIComponent(assetId)}`, { noCache: true }),
    tests: (assetId: string) =>
      request<{ driveType: string; tests: { id: string; name: string; printedTitle?: string; cls: "STANDARD" | "STRESS"; clause: string; interval: string; dueBasis?: string; suggested?: boolean; defaultIntervalMonths: number | null }[] }>(
        `/safed-jobs/tests?assetId=${encodeURIComponent(assetId)}`,
        { noCache: true },
      ),
    raise: (body: { assetId: string; testIds: string[]; callForKind: "examiner_report" | "customer_request"; callForRef?: string | null; quoteRef?: string | null; scheduledDay?: string | null; engineerId?: string | null; preview?: boolean }) =>
      request<{ preview: boolean; jobId?: string; plan: { stress: boolean; notes: string[] } }>("/safed-jobs", { method: "POST", body: JSON.stringify(body) }),
  },
  // Session PJ2: a project survey visit on the engineer's phone. Reached only through the engineer's own visit job.
  projectSurveys: {
    visit: (jobId: string) =>
      request<ProjectSurveyVisitView>(`/project-surveys/visit/${encodeURIComponent(jobId)}`, { noCache: true }),
    // A lift added on site, with the id the phone made, so a replay returns the same survey and never makes two.
    addLift: (jobId: string, body: { id: string; label: string; surveyType?: string | null }) =>
      request<{ created: boolean; survey: ProjectSurveySummaryView }>(`/project-surveys/visit/${encodeURIComponent(jobId)}/lifts`, { method: "POST", body: JSON.stringify(body) }),
    // Session PJ2F: rename a lift THIS engineer added on site, once the office holds it. 409 label_taken when another lift has the name.
    renameLift: (jobId: string, surveyId: string, label: string) =>
      request<{ changed: boolean; label: string }>(`/project-surveys/visit/${encodeURIComponent(jobId)}/lifts/${encodeURIComponent(surveyId)}`, { method: "PATCH", body: JSON.stringify({ label }) }),
    // The cells changed since the last good save. `background` for the sheet's timed autosave (the Session ASB idle rule).
    // jobId names the visit the phone is writing through: the server then stamps the cells as the engineer's, at the phone's
    // own times, even for an admin login that works as the visit's engineer (D90); without it such a login writes as the office.
    saveRecord: (surveyId: string, body: { cells: Record<string, unknown>; surveyType?: string | null; jobId?: string }, opts: { background?: boolean } = {}) =>
      request<ProjectSurveyRecordSaveResult>(`/project-surveys/${encodeURIComponent(surveyId)}/record`, {
        method: "PUT",
        body: JSON.stringify(body),
        ...(opts.background ? { background: true } : {}),
      }),
    // One survey photo (multipart). The phone-made photoId makes a replay the same row on the server.
    uploadPhoto: (
      surveyId: string,
      photoId: string,
      blob: Blob,
      filename: string,
      meta: { slotKey: string; capturedAt?: string; caption?: string },
    ) => {
      const fd = new FormData();
      // The fields go before the file, so the server has them when it reads the file part.
      fd.append("photoId", photoId);
      fd.append("slotKey", meta.slotKey);
      if (meta.capturedAt) fd.append("capturedAt", meta.capturedAt);
      if (meta.caption) fd.append("caption", meta.caption);
      fd.append("file", blob, filename);
      return request<{ id: string; created: boolean; photo: { id: string; slotKey: string; caption: string | null; capturedAt: string | null; uploadedAt: string } }>(`/project-surveys/${encodeURIComponent(surveyId)}/photos`, { method: "POST", body: fd });
    },
    // A same-origin URL an <img> loads directly (the cookie rides); the server streams the bytes, never a Drive link.
    photoUrl: (surveyId: string, photoId: string) =>
      `/api/project-surveys/${encodeURIComponent(surveyId)}/photos/${encodeURIComponent(photoId)}`,
    // Session PJ2F: set (or, with an empty string, clear) the caption of a photo the office already holds.
    setPhotoCaption: (surveyId: string, photoId: string, caption: string) =>
      request<{ changed: boolean; photo: { id: string; slotKey: string; caption: string | null } }>(
        `/project-surveys/${encodeURIComponent(surveyId)}/photos/${encodeURIComponent(photoId)}`,
        { method: "PATCH", body: JSON.stringify({ caption }) },
      ),
  },
  // Session PJ2: the office side of a project's site surveys. Every write is a commit unless preview: true is sent.
  projectSurveysOffice: {
    view: (projectId: string) => request<ProjectSurveysView>(`/projects/${encodeURIComponent(projectId)}/surveys`, { noCache: true }),
    addLift: (projectId: string, body: { assetId?: string; label?: string; surveyType?: string; preview?: boolean }) =>
      request<
        | { preview: true; label: string | null; willCreateSiteSurvey: boolean; clash: { error: "asset_taken" | "label_taken"; existingSurveyId: string } | null }
        | { preview: false; created: boolean; createdSiteSurvey: boolean; survey: ProjectSurveySummaryView }
      >(`/projects/${encodeURIComponent(projectId)}/surveys/lifts`, { method: "POST", body: JSON.stringify(body) }),
    book: (projectId: string, body: ProjectSurveyBookBody) =>
      request<ProjectSurveyBookResult>(`/projects/${encodeURIComponent(projectId)}/surveys/visits`, { method: "POST", body: JSON.stringify(body) }),
    detail: (surveyId: string) => request<ProjectSurveyDetailView>(`/project-surveys/${encodeURIComponent(surveyId)}`, { noCache: true }),
    patch: (surveyId: string, body: { label?: string; assetId?: string | null; surveyType?: string | null }) =>
      request<{ preview: boolean; changed?: boolean; survey?: ProjectSurveyDetailView }>(`/project-surveys/${encodeURIComponent(surveyId)}`, { method: "PATCH", body: JSON.stringify(body) }),
    // The office's cells. The server times them itself, so none carries an `at`.
    saveRecord: (surveyId: string, body: { cells: Record<string, { v: unknown; na?: { reason: string; note?: string }; src?: "typed" | "photo_confirmed" }>; surveyType?: string | null }) =>
      request<ProjectSurveyRecordSaveResult>(`/project-surveys/${encodeURIComponent(surveyId)}/record`, { method: "PUT", body: JSON.stringify(body) }),
    review: (surveyId: string, reopen = false, version?: string | null) =>
      request<{ changed: boolean; survey: ProjectSurveySummaryView }>(`/project-surveys/${encodeURIComponent(surveyId)}/review`, {
        method: "POST",
        body: JSON.stringify({ reopen, ...(!reopen && version ? { version } : {}) }),
      }),
    resurvey: (surveyId: string) =>
      request<{ supersededId: string; survey: ProjectSurveySummaryView }>(`/project-surveys/${encodeURIComponent(surveyId)}/resurvey`, { method: "POST", body: JSON.stringify({}) }),
    deleteCheck: (surveyId: string) => request<ProjectSurveyDeleteCheck>(`/project-surveys/${encodeURIComponent(surveyId)}/delete-check`, { noCache: true }),
    remove: (surveyId: string, confirmLabel?: string) =>
      // visitsNotCancelled and message: a visit booked only for the survey that could not be cancelled stays booked, and is said.
      request<{
        ok: true;
        deleted: true;
        cancelledJobIds: string[];
        visitsNotCancelled?: Array<{ jobId: string; reason: string }>;
        message?: string;
        driveFilesLeft: number;
      }>(`/project-surveys/${encodeURIComponent(surveyId)}`, {
        method: "DELETE",
        body: JSON.stringify(confirmLabel ? { confirmLabel } : {}),
      }),
    // The row always goes; the Drive file is removed best effort, and the reply says whether it was (`message` says it in words).
    deletePhoto: (surveyId: string, photoId: string) =>
      request<{ ok: true; deleted: true; driveFileRemoved: boolean; message: string }>(`/project-surveys/${encodeURIComponent(surveyId)}/photos/${encodeURIComponent(photoId)}`, { method: "DELETE" }),
    // Reads a plate photo and PROPOSES values. It writes nothing: accepting one is an ordinary saveRecord with src "photo_confirmed".
    readPlate: (surveyId: string, photoId: string) =>
      request<ProjectSurveyPlateRead>(`/project-surveys/${encodeURIComponent(surveyId)}/photos/${encodeURIComponent(photoId)}/read-plate`, { method: "POST", body: JSON.stringify({}) }),
    supplierOptions: (projectId: string) =>
      request<ProjectSurveySupplierOptions>(`/projects/${encodeURIComponent(projectId)}/surveys/supplier-sheet/options`, { noCache: true }),
    // The data sheet as a PDF, for the office to look at before the draft is made. A same-origin address a link opens.
    supplierSheetUrl: (projectId: string, surveyIds: string[], photoIds: string[]) =>
      `/api/projects/${encodeURIComponent(projectId)}/surveys/supplier-sheet.pdf?surveyIds=${surveyIds.map(encodeURIComponent).join(",")}&photoIds=${photoIds.map(encodeURIComponent).join(",")}`,
    // Session PJ2F3: fetched first, so a refusal (the customer's name or contact details in the typed words) is an ApiError the modal
    // shows on screen, never a JSON reply in a new tab.
    downloadSupplierSheet: (projectId: string, surveyIds: string[], photoIds: string[]) =>
      downloadPdf(
        `/api/projects/${encodeURIComponent(projectId)}/surveys/supplier-sheet.pdf?surveyIds=${surveyIds.map(encodeURIComponent).join(",")}&photoIds=${photoIds.map(encodeURIComponent).join(",")}`,
        "Survey_data_sheet.pdf",
      ),
    // Session PJ1F: the "Create purchase order" prefill from the REVIEWED lift surveys. A suggestion; nothing is stored.
    purchaseOrderPrefill: (projectId: string) =>
      request<ProjectSurveyPurchaseOrderPrefill>(`/projects/${encodeURIComponent(projectId)}/surveys/purchase-order-prefill`, { noCache: true }),
    // A Gmail DRAFT, never a send. With preview: true it only says what the draft would hold.
    supplierDraft: (projectId: string, body: { surveyIds: string[]; photoIds: string[]; to: string; preview: boolean }) =>
      request<ProjectSurveySupplierDraftPreview | ProjectSurveySupplierDraftDone>(`/projects/${encodeURIComponent(projectId)}/surveys/supplier-sheet/draft`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },
  // Session BDB: the SAFed lifecycle (recommendations, quotations, the due view, engineer sign-off, customer drafts, invoice draft).
  safedFlow: {
    lift: (assetId: string) =>
      request<{ lifecycle: SafedLiftLifecycleView; recommendations: SafedRecommendationView[]; quotes: SafedQuoteView[]; extraLabels: Record<string, string> }>(
        `/safed-flow/lift/${encodeURIComponent(assetId)}`,
        { noCache: true },
      ),
    due: (filter: { customerId?: string; siteId?: string; state?: SafedLifecycleStateView } = {}) => {
      const q = new URLSearchParams();
      if (filter.customerId) q.set("customerId", filter.customerId);
      if (filter.siteId) q.set("siteId", filter.siteId);
      if (filter.state) q.set("state", filter.state);
      const qs = q.toString();
      return request<{ items: SafedAttentionItemView[]; truncated: boolean }>(`/safed-flow/due${qs ? `?${qs}` : ""}`, { noCache: true });
    },
    worklist: (filter: { customerId?: string; siteId?: string; state?: SafedWorklistStateView } = {}) => {
      const q = new URLSearchParams();
      if (filter.customerId) q.set("customerId", filter.customerId);
      if (filter.siteId) q.set("siteId", filter.siteId);
      if (filter.state) q.set("state", filter.state);
      const qs = q.toString();
      return request<{ items: SafedWorklistItemView[]; truncated: boolean }>(`/safed-flow/worklist${qs ? `?${qs}` : ""}`, { noCache: true });
    },
    recommend: (body: {
      assetId: string;
      source: SafedRecommendationSourceView;
      documentRef?: string | null;
      documentDate?: string | null;
      recommendedText?: string | null;
      testIds: string[];
      preview?: boolean;
    }) => request<{ preview: boolean; id?: string }>("/safed-flow/recommendations", { method: "POST", body: JSON.stringify(body) }),
    dismissRecommendation: (id: string, reason: string) =>
      request<{ ok: true }>(`/safed-flow/recommendations/${encodeURIComponent(id)}/dismiss`, { method: "POST", body: JSON.stringify({ reason }) }),
    restoreRecommendation: (id: string) => request<{ ok: true }>(`/safed-flow/recommendations/${encodeURIComponent(id)}/restore`, { method: "POST", body: JSON.stringify({}) }),
    deleteRecommendation: (id: string) => request<{ ok: true }>(`/safed-flow/recommendations/${encodeURIComponent(id)}`, { method: "DELETE" }),
    quotePreview: (body: { assetId: string; testIds: string[]; dayType: "full_day" | "half_day"; engineers?: 1 | 2; recipientEmail?: string | null; recommendationId?: string | null }) =>
      request<{ preview: true; plan: SafedQuotePlanView }>("/safed-flow/quotes", { method: "POST", body: JSON.stringify({ ...body, preview: true }) }),
    quoteCreate: (body: { assetId: string; testIds: string[]; dayType: "full_day" | "half_day"; engineers?: 1 | 2; recipientEmail?: string | null; recommendationId?: string | null }) =>
      request<{ preview: false; id: string; reference: string }>("/safed-flow/quotes", { method: "POST", body: JSON.stringify({ ...body, preview: false }) }),
    quoteDraft: (id: string, preview: boolean) =>
      request<SafedDeliveryPreviewView | SafedDeliveryDoneView>(`/safed-flow/quotes/${encodeURIComponent(id)}/draft`, { method: "POST", body: JSON.stringify({ preview }) }),
    quoteAnswer: (id: string, body: { answer: "accepted" | "declined" | "cleared"; byName?: string | null; via?: string | null; note?: string | null }) =>
      request<SafedQuoteView>(`/safed-flow/quotes/${encodeURIComponent(id)}/answer`, { method: "POST", body: JSON.stringify(body) }),
    quoteExtras: (id: string, extras: { description: string; amountPence: number }[]) =>
      request<SafedQuoteView>(`/safed-flow/quotes/${encodeURIComponent(id)}/extras`, { method: "PUT", body: JSON.stringify({ extras }) }),
    deleteQuote: (id: string) => request<{ ok: true }>(`/safed-flow/quotes/${encodeURIComponent(id)}`, { method: "DELETE" }),
    signoffs: () =>
      request<{ groups: ("traction" | "hydraulic")[]; engineers: { id: string; name: string }[]; signoffs: SafedSignOffView[] }>("/safed-flow/signoffs", { noCache: true }),
    setSignoff: (body: { engineerId: string; testGroup: "traction" | "hydraulic"; on: boolean }) =>
      request<{ ok: true; changed: boolean; signoffs: SafedSignOffView[] }>("/safed-flow/signoffs", { method: "PUT", body: JSON.stringify(body) }),
    authorisationDraft: (jobId: string, preview: boolean) =>
      request<SafedDeliveryPreviewView | SafedDeliveryDoneView>(`/safed-flow/jobs/${encodeURIComponent(jobId)}/authorisation-draft`, { method: "POST", body: JSON.stringify({ preview }) }),
    resultsDraft: (jobId: string, preview: boolean) =>
      request<SafedDeliveryPreviewView | SafedDeliveryDoneView>(`/safed-flow/jobs/${encodeURIComponent(jobId)}/results-draft`, { method: "POST", body: JSON.stringify({ preview }) }),
    invoiceDraft: (jobId: string, preview: boolean) =>
      request<
        | { preview: true; plan: SafedInvoicePlanView; notes: string[] }
        | { preview: false; invoiceNumber: string | null; plan: SafedInvoicePlanView }
      >(`/safed-flow/jobs/${encodeURIComponent(jobId)}/invoice-draft`, { method: "POST", body: JSON.stringify({ preview }) }),
  },
  mcpTokens: {
    list: () => request<{ items: McpTokenListItem[]; url: string }>("/mcp-tokens"),
    create: (body: { label: string; kind?: "user" | "draft_only" }) =>
      request<McpTokenCreateResult>("/mcp-tokens", { method: "POST", body: JSON.stringify(body) }),
    revoke: (id: string) =>
      request<{ ok: boolean }>(`/mcp-tokens/${encodeURIComponent(id)}`, { method: "DELETE" }),
    // OAuth connections created by the claude.ai one-click flow (Session AEU).
    oauthList: () => request<{ items: McpOAuthConnectionItem[]; url: string }>("/mcp-tokens/oauth-connections"),
    oauthRevoke: (id: string) =>
      request<{ ok: boolean }>(`/mcp-tokens/oauth-connections/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },
  logout: () => request<{ ok: boolean }>("/auth/logout", { method: "POST" }),
  // Where the signed-in person is signed in, and "Sign out everywhere" (Session ASB). The list carries a device
  // summary and the times only: never an id, the address or the raw browser string.
  authSessions: {
    list: () => request<{ sessions: SignInSessionItem[] }>("/auth/sessions", { noCache: true }),
    revokeAll: () => request<{ ok: boolean; revoked: number }>("/auth/sessions/revoke-all", { method: "POST" }),
  },

  // ---- The customer renewal surface (Session AII, "Renewal B") ----
  // Reached only by Contract.renewalToken. The register is READ-ONLY here by construction: neither
  // preview nor accept takes a lift field, and the server refuses a payload that carries one. Every
  // price is the server's -- nothing on this surface is computed in the browser.
  publicRenewal: {
    get: (token: string) => request<PublicRenewal>(`/public/renewal/${encodeURIComponent(token)}`),
    // Live reprice at a chosen tier and cadence. DISPLAY ONLY: nothing is written.
    preview: (token: string, tier: PublicTierKey, frequency: string) =>
      request<PublicRenewal>(`/public/renewal/${encodeURIComponent(token)}/preview`, {
        method: "POST",
        body: JSON.stringify({ tier, frequency }),
      }),
    accept: (token: string, tier: PublicTierKey, frequency: string, signatory?: SignatoryInput) =>
      request<PublicRenewalAcceptResult>(`/public/renewal/${encodeURIComponent(token)}/accept`, {
        method: "POST",
        body: JSON.stringify({ tier, frequency, ...(signatory ? { signatory } : {}) }),
      }),
  },

  publicQuote: {
    get: (token: string) => request<PublicQuote>(`/public/quote/${encodeURIComponent(token)}`),
    // Reprice the quote at customer-chosen per-lift frequencies (Session AAG, DISPLAY-ONLY): returns
    // a fresh PublicQuote with updated tiers/surcharges. Nothing is written; the signed contract
    // keeps the office-set frequency until the office confirms a change.
    // includedLiftIndexes (Session ACF): when the customer un-ticks a lift, the price is genuinely
    // repriced for the INCLUDED subset (the returned view lists the included lifts only, at the true
    // discount bracket). Omitted -> the whole quote is repriced at the chosen frequencies as before.
    preview: (token: string, frequencies: string[], includedLiftIndexes?: number[]) =>
      request<PublicQuote>(`/public/quote/${encodeURIComponent(token)}/preview`, {
        method: "POST",
        body: JSON.stringify({ frequencies, ...(includedLiftIndexes ? { includedLiftIndexes } : {}) }),
      }),
    // The customer's on-page frequencies are sent for the office to confirm; the ESA is rendered at
    // the office-set frequency, not repriced here. preferredCommencement ("YYYY-MM-DD", Session AAN)
    // is the customer's optional preferred start date, shown on the ESA and used for the later-of
    // contract start; omitted when not chosen (the now + 30-day default then applies). includedLiftIndexes
    // (Session ACF): the lifts the customer chose to keep on the contract; excluded lifts reprice OUT of
    // the contract and become uncontracted lifts the office can add later.
    accept: (
      token: string,
      tier: PublicTierKey,
      frequencies?: string[],
      preferredCommencement?: string,
      includedLiftIndexes?: number[],
      signatory?: SignatoryInput,
    ) =>
      request<AcceptResult>(`/public/quote/${encodeURIComponent(token)}/accept`, {
        method: "POST",
        body: JSON.stringify({
          tier,
          ...(frequencies ? { frequencies } : {}),
          ...(preferredCommencement ? { preferredCommencement } : {}),
          ...(includedLiftIndexes ? { includedLiftIndexes } : {}),
          ...(signatory ? { signatory } : {}),
        }),
      }),
  },

  // The Schedule of Fees rate card, linked from the quote and renewal accept pages (no login,
  // no token: the document carries no customer or tenant data, the same cached PDF the office
  // downloads at api.documents.scheduleOfFeesHref).
  publicDocuments: {
    scheduleOfFeesHref: () => "/api/public/schedule-of-fees",
    // Session AKR: the same rates as JSON, for the in-page Schedule of Fees modal. Reads the
    // server's GET /api/public/schedule-of-fees/rates, which returns the exact fees.ts objects
    // the PDF is built from -- never a client-side recalculation.
    scheduleOfFeesRates: () => request<PublicScheduleOfFeesRates>("/public/schedule-of-fees/rates"),
  },

  publicNfc: {
    get: (token: string) => request<PublicNfc>(`/public/nfc/${encodeURIComponent(token)}`),
    // Session AFH: an engineer/office user asks for a sign-in link from the lift's RFID page. The
    // reply is always { sent: true } (opaque by design), whether or not the email was recognised.
    engineerLogin: (token: string, email: string) =>
      request<{ sent: boolean }>(`/public/nfc/${encodeURIComponent(token)}/engineer-login`, {
        method: "POST",
        body: JSON.stringify({ email }),
      }),
  },

  // Session AMZ: the INTERNAL attendance tap. The tag resolves to the SPA page at /tap/<token>,
  // which calls record() once on open. It is a POST because it WRITES: a GET that recorded would be
  // fired by every link preview of a tag URL the office emails or WhatsApps to an engineer, which
  // would manufacture attendance for lifts nobody had visited. See publicTap.routes.ts.
  //
  // Session APF: record() takes an OPTIONAL second argument, sent only when given, so the sticker's
  // own page (which still calls record(token) with none, since it has no direction or job to offer)
  // is byte-identical to before this session -- no body at all, not an empty object. A caller with a
  // direction (an "arrival" / "departure" choice) or a jobId (posted from a job sheet, once a future
  // session wires that in) passes it here.
  publicTap: {
    record: (token: string, body?: { direction?: "arrival" | "departure"; jobId?: string }) =>
      request<PublicTapResult>(`/public/tap/${encodeURIComponent(token)}`, {
        method: "POST",
        ...(body ? { body: JSON.stringify(body) } : {}),
      }),
    engineerLogin: (token: string, email: string) =>
      request<{ sent: boolean }>(`/public/tap/${encodeURIComponent(token)}/engineer-login`, {
        method: "POST",
        body: JSON.stringify({ email }),
      }),
  },

  // Session AFH: validate a staff RFID sign-in link. On success the server sets the standard
  // session cookie and returns the role-correct redirect; any failure is a generic 401.
  staffAccess: {
    validate: (token: string) =>
      request<{ ok: boolean; redirect: string }>("/auth/staff-access/validate", {
        method: "POST",
        body: JSON.stringify({ token }),
      }),
  },

  // The public project quotation page (Session ABN, Item 3): the HTML view content + the
  // project works T&Cs, and the direct PDF download link.
  publicProjectQuotation: {
    get: (token: string) => request<PublicProjectQuotationView>(`/public/project-quotation/${encodeURIComponent(token)}`),
    pdfHref: (token: string) => `/api/public/project-quotation/${encodeURIComponent(token)}/pdf`,
  },

  // Session AEI (D1): the public modernisation ACCEPTANCE page (view + Sign and Accept).
  publicProjectAccept: {
    get: (token: string) => request<PublicProjectAcceptView>(`/public/project-accept/${encodeURIComponent(token)}`),
    // Sign and Accept: trigger the Signable envelope. Returns { status: "sent" } once the signing
    // link has been (or already was) emailed to the customer.
    accept: (token: string) =>
      request<{ status: "sent" }>(`/public/project-accept/${encodeURIComponent(token)}/accept`, { method: "POST" }),
  },

  publicNps: {
    // The post-service NPS survey state for a token (open / already responded) + the site.
    get: (token: string) => request<PublicNpsView>(`/public/nps/${encodeURIComponent(token)}`),
    // Record a 0 to 10 score (and an optional comment) against the token.
    submit: (token: string, score: number, comment?: string) =>
      request<{ status: "recorded" | "responded" }>(`/public/nps/${encodeURIComponent(token)}`, {
        method: "POST",
        body: JSON.stringify({ score, comment }),
      }),
  },

  // Session ARS (D91): the customer's own repair estimate page. The view is read live (never from the
  // request cache: the customer may come back to it after answering). Each answer returns the updated
  // view; a refusal (409) carries the view of what stands as `estimate` on the ApiError body.
  publicRepairEstimate: {
    get: (token: string) => request<PublicRepairEstimateView>(`/public/repair-estimate/${encodeURIComponent(token)}`, { noCache: true }),
    approve: (token: string) =>
      request<PublicRepairEstimateView>(`/public/repair-estimate/${encodeURIComponent(token)}/approve`, { method: "POST" }),
    decline: (token: string, reason?: string) =>
      request<PublicRepairEstimateView>(`/public/repair-estimate/${encodeURIComponent(token)}/decline`, {
        method: "POST",
        body: JSON.stringify(reason ? { reason } : {}),
      }),
  },

  // Session ASQ (Stream G, Item 5): the public privacy notice. Read live, never from the request cache, so a notice the
  // admin clears is gone on the next page load; src/lib/privacyNotice.ts makes it one request per page load however
  // many links a page carries.
  public: {
    privacyNotice: () => request<PublicPrivacyNotice>("/public/privacy-notice", { noCache: true }),
  },

  // Session BCE: the engineer's sign-in. Email, then a 6-digit code sent to it (no password). requestCode resolves
  // { ok: false, reason: rate_limited | send_failed, message } for a refusal the page should show. verifyCode throws
  // ApiError 401 invalid_or_expired_code or 423 locked, each with the server's own plain sentence as its message, and
  // on success the server has set the session cookie. The auth context's engineerCodeSignIn wraps verifyCode.
  engineerSignIn: {
    requestCode: (email: string) =>
      request<{ ok: boolean; reason?: string; message?: string }>("/public/engineer-signin/request", {
        method: "POST",
        body: JSON.stringify({ email }),
      }),
    // Session BCK: on an address shared by engineers of ONE subcontractor the verified code answers { select } (first
    // names, opaque ids and a one-time token) instead of { user }: no session exists until selectEngineer succeeds.
    verifyCode: (email: string, code: string) =>
      request<{ user: User } | { select: EngineerSelectionStep }>("/public/engineer-signin/verify", {
        method: "POST",
        body: JSON.stringify({ email, code }),
      }),
    selectEngineer: (email: string, token: string, engineerId: string) =>
      request<{ user: User }>("/public/engineer-signin/select", {
        method: "POST",
        body: JSON.stringify({ email, token, engineerId }),
      }),
  },

  publicSubcontractor: {
    get: (token: string) => request<SubcontractorOnboardingView>(`/public/subcontractor-onboarding/${encodeURIComponent(token)}`),
    // Multipart: the FormData carries the scalar fields plus indexed docFile_<n> /
    // docType_<n> / docDescription_<n> / docExpiry_<n> parts. No Content-Type header is
    // set (the browser adds the multipart boundary; the request wrapper skips JSON for FormData).
    submit: (token: string, form: FormData) =>
      request<SubcontractorOnboardingResult>(`/public/subcontractor-onboarding/${encodeURIComponent(token)}`, { method: "POST", body: form }),
  },

  // Session ARZ, Item 3: the payment-details-only page a "Request payment details" email links to (no login). Never
  // cached: the request cache is bypassed on both calls.
  publicPaymentDetails: {
    get: (token: string) =>
      request<PaymentDetailsRequestPageView>(`/public/subcontractor-payment-details/${encodeURIComponent(token)}`, { noCache: true }),
    submit: (token: string, body: PaymentDetailsForm) =>
      request<{ ok: true; message: string }>(`/public/subcontractor-payment-details/${encodeURIComponent(token)}`, {
        method: "POST",
        body: JSON.stringify(body),
        noCache: true,
      }),
  },

  // Session RC1: the company standard rate card (ADMIN ONLY). Every write previews first.
  standardRateCard: {
    get: () => request<StandardCardView>("/standard-rate-card", { noCache: true }),
    create: (body: StandardCardWriteBody) =>
      request<StandardCardPreview | StandardCardWriteResult>("/standard-rate-card", { method: "POST", body: JSON.stringify(body) }),
    update: (versionId: string, body: StandardCardWriteBody) =>
      request<{ preview: true; version: number; changes: RateCardChange[] } | StandardCardWriteResult>(`/standard-rate-card/${encodeURIComponent(versionId)}`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    remove: (versionId: string) =>
      request<{ ok: true; deleted: { version: number; effectiveFrom: string } }>(`/standard-rate-card/${encodeURIComponent(versionId)}`, { method: "DELETE" }),
  },
  subcontractors: {
    engineers: (id: string) => request<SubcontractorEngineersView>(`/subcontractors/${encodeURIComponent(id)}/engineers`, { noCache: true }),
    list: (params?: { status?: string; trade?: string; search?: string; type?: string; archived?: "archived" | "all" }) => {
      const qs = new URLSearchParams();
      if (params?.status) qs.set("status", params.status);
      if (params?.trade) qs.set("trade", params.trade);
      if (params?.search) qs.set("search", params.search);
      if (params?.type) qs.set("type", params.type);
      if (params?.archived) qs.set("archived", params.archived);
      const q = qs.toString();
      return request<SubcontractorListItem[]>(`/subcontractors${q ? `?${q}` : ""}`);
    },
    get: (id: string) => request<SubcontractorDetail>(`/subcontractors/${encodeURIComponent(id)}`),
    // Session RC2: the invite is a Gmail DRAFT the office sends from Gmail. inviteDrafted / inviteDraftError /
    // inviteLinkExpiresAt are transient fields on the create response only: whether a draft was made (and, when not, the
    // plain reason), and when the registration link stops working (7 days from the draft).
    create: (body: SubcontractorCreateInput) =>
      request<SubcontractorDetail & SubcontractorInviteDraftOutcome>("/subcontractors", { method: "POST", body: JSON.stringify({ ...body, preview: undefined }) }),
    // Session RC1: what the create would do, in the save button's words. Writes nothing.
    previewCreate: (body: SubcontractorCreateInput) =>
      request<SubcontractorCreatePreview>("/subcontractors", { method: "POST", body: JSON.stringify({ ...body, preview: true }) }),
    // Session RC1: set the type, card source or Technician tick on an existing subcontractor (office and admin). Preview first.
    previewType: (id: string, body: SubcontractorTypeInput) =>
      request<{ preview: true; summary: string[] }>(`/subcontractors/${encodeURIComponent(id)}/type`, { method: "PUT", body: JSON.stringify({ ...body, preview: true }) }),
    setType: (id: string, body: SubcontractorTypeInput) =>
      request<SubcontractorDetail & { summary: string[] }>(`/subcontractors/${encodeURIComponent(id)}/type`, { method: "PUT", body: JSON.stringify({ ...body, preview: undefined }) }),
    update: (id: string, body: SubcontractorUpdateInput) =>
      request<SubcontractorDetail>(`/subcontractors/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    // Session AUR: set or change the status (null = not set). Audited server-side; the office's own setting is what
    // approves a Bona Fide the sign-up form recorded as pending.
    // Session AUZ: the status applies FROM A DATE (today when omitted); preview: true answers the approved weeks it would
    // move and writes nothing; a commit that would move one needs affectsApprovedWeeks: true.
    previewSubcontractorStatus: (id: string, status: SubcontractorType | null, effectiveFrom?: string) =>
      request<SubcontractorStatusPreview>(`/subcontractors/${encodeURIComponent(id)}/subcontractor-status`, {
        method: "PUT",
        body: JSON.stringify({ status, ...(effectiveFrom ? { effectiveFrom } : {}), preview: true }),
      }),
    setSubcontractorStatusFrom: (id: string, status: SubcontractorType | null, opts: { effectiveFrom?: string; affectsApprovedWeeks?: boolean }) =>
      request<SubcontractorDetail>(`/subcontractors/${encodeURIComponent(id)}/subcontractor-status`, {
        method: "PUT",
        body: JSON.stringify({ status, ...(opts.effectiveFrom ? { effectiveFrom: opts.effectiveFrom } : {}), ...(opts.affectsApprovedWeeks ? { affectsApprovedWeeks: true } : {}) }),
      }),
    statusHistory: (id: string) => request<SubcontractorStatusHistory>(`/subcontractors/${encodeURIComponent(id)}/subcontractor-status/history`, { noCache: true }),
    redateStatusPeriod: (id: string, periodId: string, effectiveFrom: string, opts: { preview?: boolean; affectsApprovedWeeks?: boolean } = {}) =>
      request<SubcontractorStatusHistory | { preview: true; from: string; to: string; toLabel: string; approvedWeeksMoved: RateCardApprovedWeek[] }>(
        `/subcontractors/${encodeURIComponent(id)}/subcontractor-status/history/${encodeURIComponent(periodId)}`,
        { method: "PATCH", body: JSON.stringify({ effectiveFrom, ...(opts.preview ? { preview: true } : {}), ...(opts.affectsApprovedWeeks ? { affectsApprovedWeeks: true } : {}) }) },
      ),
    removeStatusPeriod: (id: string, periodId: string, opts: { affectsApprovedWeeks?: boolean } = {}) =>
      request<SubcontractorStatusHistory>(`/subcontractors/${encodeURIComponent(id)}/subcontractor-status/history/${encodeURIComponent(periodId)}`, {
        method: "DELETE",
        body: JSON.stringify(opts.affectsApprovedWeeks ? { affectsApprovedWeeks: true } : {}),
      }),
    // Session AUZ: the office review of documents.
    documentReview: () => request<SubcontractorDocumentReview>("/subcontractors/certificates/review", { noCache: true }),
    approveCertificate: (id: string, certId: string) =>
      request<SubcontractorCertificateApproval>(`/subcontractors/${encodeURIComponent(id)}/certificates/${encodeURIComponent(certId)}/approve`, { method: "POST" }),
    rejectCertificate: (id: string, certId: string, reason: string) =>
      request<{ certificate: SubcontractorCertificate }>(`/subcontractors/${encodeURIComponent(id)}/certificates/${encodeURIComponent(certId)}/reject`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
    setSubcontractorStatus: (id: string, status: SubcontractorType | null) =>
      request<SubcontractorDetail>(`/subcontractors/${encodeURIComponent(id)}/subcontractor-status`, { method: "PUT", body: JSON.stringify({ status }) }),
    // Session AUU: the subcontractor's rate card (admin only). Every write previews with preview: true (the difference and
    // the approved weeks it would move) and commits only without it; a commit that would move an approved week needs
    // affectsApprovedWeeks: true, which the office confirms on the page.
    rateCards: {
      get: (id: string) => request<SubcontractorRateCardView>(`/subcontractors/${encodeURIComponent(id)}/rate-cards`, { noCache: true }),
      create: (id: string, body: RateCardWriteBody) =>
        request<RateCardPreview | RateCardWriteResult>(`/subcontractors/${encodeURIComponent(id)}/rate-cards`, { method: "POST", body: JSON.stringify(body) }),
      update: (id: string, cardId: string, body: RateCardWriteBody) =>
        request<RateCardPreview | RateCardWriteResult>(`/subcontractors/${encodeURIComponent(id)}/rate-cards/${encodeURIComponent(cardId)}`, { method: "PUT", body: JSON.stringify(body) }),
      remove: (id: string, cardId: string) =>
        request<{ ok: true; deleted: { version: number; effectiveFrom: string } }>(`/subcontractors/${encodeURIComponent(id)}/rate-cards/${encodeURIComponent(cardId)}`, { method: "DELETE" }),
    },
    // Session AUY: the welcome pack. Readiness, a document preview (a PDF download) and the prepare (preview by default).
    welcomePack: {
      get: (id: string) => request<SubcontractorWelcomePackReadiness>(`/subcontractors/${encodeURIComponent(id)}/welcome-pack`, { noCache: true }),
      download: (id: string, key: WelcomePackDocumentKey) => downloadPdf(`/api/subcontractors/${encodeURIComponent(id)}/welcome-pack/documents/${key}`, `${key}.pdf`),
      prepare: (id: string, preview: boolean) =>
        request<{ preview: true; to: string | null; subject: string; attachments: string[]; agreementSource: WelcomePackAgreementSource; agreementNote: string; warnings: string[]; blockers: string[]; ok: boolean } | SubcontractorWelcomePackPrepared>(
          `/subcontractors/${encodeURIComponent(id)}/welcome-pack/prepare`,
          { method: "POST", body: JSON.stringify({ preview }) },
        ),
    },
    // Session AVB: the subcontractor's invoice status exactly as their portal shows it (admin only), read from Xero bills.
    invoiceStatus: (id: string, fresh = false) =>
      request<SubInvoiceStatusOffice>(`/subcontractors/${encodeURIComponent(id)}/invoice-status${fresh ? "?fresh=1" : ""}`, { noCache: true }),
    // Session RC2: a supply and fit company's Xero purchase orders (admin only), a separate read from the invoice status.
    xeroPurchaseOrders: (id: string, fresh = false) =>
      request<{ xeroPurchaseOrders: XeroPurchaseOrdersView }>(`/subcontractors/${encodeURIComponent(id)}/xero-purchase-orders${fresh ? "?fresh=1" : ""}`, { noCache: true }),
    // Permanent hard delete (data-entry mistakes only). 409 when a signed agreement or linked
    // projects exist; the client hides Delete in those cases, but the server enforces the guard.
    remove: (id: string) =>
      request<{ ok: boolean; archived: boolean; message?: string }>(`/subcontractors/${encodeURIComponent(id)}`, { method: "DELETE" }),
    // Archive (soft-delete, the normal offboarding). Reversible via restore.
    archive: (id: string) =>
      request<SubcontractorDetail>(`/subcontractors/${encodeURIComponent(id)}/archive`, { method: "PATCH" }),
    // Restore an archived subcontractor (clears archivedAt so it returns to the active list).
    restore: (id: string) =>
      request<SubcontractorDetail>(`/subcontractors/${encodeURIComponent(id)}/restore`, { method: "PATCH" }),
    // Session RC2: redrafts the invite with a NEW link (an earlier unsent draft's link stops working).
    resendInvite: (id: string) =>
      request<{ ok: boolean; drafted: boolean; inviteDraftError: string | null; inviteLinkExpiresAt: string; earlierDraftNote: string }>(
        `/subcontractors/${encodeURIComponent(id)}/resend-invite`,
        { method: "POST" },
      ),
    // Session RC2: coveringDraftNote says what became of the covering note (a Gmail draft), null when it was not reached.
    sendAgreement: (id: string) => request<{ ok: boolean; result: string; coveringDraftNote: string | null }>(`/subcontractors/${encodeURIComponent(id)}/agreement/send`, { method: "POST" }),
    resendAgreement: (id: string) => request<{ ok: boolean; result: string; coveringDraftNote: string | null }>(`/subcontractors/${encodeURIComponent(id)}/agreement/resend`, { method: "POST" }),
    // Push the subcontractor to Xero as a payable contact (no transaction). Idempotent server-side.
    // Session ARZ, Item 4: the Xero payable contact, preview then confirm (the one-press create is retired). The preview
    // is read only and masks bank details on both sides; the confirm sends back the preview's token and is refused (409,
    // with the new preview) if Xero or the platform moved since. Never cached.
    xeroContactPreview: (id: string) =>
      request<SubcontractorXeroContactPreview>(`/subcontractors/${encodeURIComponent(id)}/xero-contact/preview`, { noCache: true }),
    pushXeroContact: (id: string, previewToken: string) =>
      request<XeroContactPushResult>(`/subcontractors/${encodeURIComponent(id)}/xero-contact`, { method: "POST", body: JSON.stringify({ previewToken }), noCache: true }),
    projects: (id: string) => request<SubcontractorProjectLink[]>(`/subcontractors/${encodeURIComponent(id)}/projects`),
    uploadDoc: (id: string, file: File, meta: { docType: string; description?: string; expiryDate?: string }) => {
      const fd = new FormData();
      fd.append("document", file, file.name);
      fd.append("docType", meta.docType);
      if (meta.description) fd.append("description", meta.description);
      if (meta.expiryDate) fd.append("expiryDate", meta.expiryDate);
      return request<{ ok: boolean; id: string }>(`/subcontractors/${encodeURIComponent(id)}/docs`, { method: "POST", body: fd });
    },
    // A raw href for an <a> download (authenticated proxy; raw Drive link never exposed).
    // Only offer this for a certificate whose hasFile is true: a recorded expiry with no
    // document has nothing to download and the server answers 422.
    docDownloadUrl: (id: string, docId: string) => `/api/subcontractors/${encodeURIComponent(id)}/docs/${encodeURIComponent(docId)}/download`,

    // ---- Payment (bank) details (Session ARZ, Item 3) ----
    // Masks only, except revealPaymentDetails (admin, audited, never cached). `version` is the stamp the panel showed;
    // Verify and Clear send it back so they act on the set the person saw. Every call bypasses the request cache.
    paymentDetails: (id: string) => request<PaymentDetailsView>(`/subcontractors/${encodeURIComponent(id)}/payment-details`, { noCache: true }),
    verifyPaymentDetails: (id: string, body: { note: string; version: string }) =>
      request<PaymentDetailsView>(`/subcontractors/${encodeURIComponent(id)}/payment-details/verify`, { method: "POST", body: JSON.stringify(body), noCache: true }),
    revealPaymentDetails: (id: string) =>
      request<RevealedPaymentDetails>(`/subcontractors/${encodeURIComponent(id)}/payment-details/reveal`, { method: "POST", noCache: true }),
    enterPaymentDetails: (id: string, body: OfficePaymentDetailsEntry) =>
      request<OfficePaymentDetailsEntryResult>(`/subcontractors/${encodeURIComponent(id)}/payment-details`, { method: "PUT", body: JSON.stringify(body), noCache: true }),
    clearPaymentDetails: (id: string, version: string) =>
      request<PaymentDetailsView>(`/subcontractors/${encodeURIComponent(id)}/payment-details`, { method: "DELETE", body: JSON.stringify({ version }), noCache: true }),
    requestPaymentDetails: (id: string) =>
      request<PaymentDetailsRequestResult>(`/subcontractors/${encodeURIComponent(id)}/payment-details/request`, { method: "POST", noCache: true }),

    // ---- Certificates and insurance (Session AEL, D37) ----
    // A certificate IS a compliance document row (see the SubcontractorDoc model comment):
    // these endpoints read and write the same rows the upload path creates, with the
    // structured type, issuer and reference number, and a status derived server-side.
    certificates: (id: string) =>
      request<{ certificates: SubcontractorCertificate[] }>(`/subcontractors/${encodeURIComponent(id)}/certificates`),
    createCertificate: (id: string, body: SubcontractorCertificateInput) =>
      request<{ certificate: SubcontractorCertificate; isPast: boolean }>(`/subcontractors/${encodeURIComponent(id)}/certificates`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    updateCertificate: (id: string, certId: string, body: Partial<SubcontractorCertificateInput>) =>
      request<{ certificate: SubcontractorCertificate; isPast: boolean }>(
        `/subcontractors/${encodeURIComponent(id)}/certificates/${encodeURIComponent(certId)}`,
        { method: "PATCH", body: JSON.stringify(body) },
      ),
    // Two-step delete. Called with no token it RETURNS a token and a summary and deletes
    // nothing; called again with that token it performs the delete. The token expires after
    // 60 seconds, in which case the server answers 409 and the office simply starts again.
    deleteCertificate: (id: string, certId: string, confirmationToken?: string) =>
      request<{ confirmationToken: string; summary: string } | void>(
        `/subcontractors/${encodeURIComponent(id)}/certificates/${encodeURIComponent(certId)}`,
        { method: "DELETE", body: JSON.stringify(confirmationToken ? { confirmationToken } : {}) },
      ),
    // items holds ONLY expired or soon-to-expire certificates, so an empty list alone cannot
    // tell "all valid" from "none recorded". trackedCount is how many dated certificates exist
    // at all: use it to decide between a green all-clear and a neutral "nothing recorded", and
    // never show green when it is zero.
    expiringCertificates: () =>
      request<{
        items: SubcontractorExpiringCertificate[];
        expiredCount: number;
        expiringCount: number;
        trackedCount: number;
        // Session AUR: documents on file the office has not yet approved (never an all-clear while any are waiting).
        unverifiedCount?: number;
      }>("/subcontractors/certificates/expiring"),
    // Draft a renewal request for one document on demand (outside the daily run): a Gmail draft, since Session RC2.
    sendDocRenewal: (id: string, docId: string) =>
      request<{ ok: boolean; drafted: boolean }>(`/subcontractors/${encodeURIComponent(id)}/docs/${encodeURIComponent(docId)}/send-renewal`, { method: "POST" }),
  },

  // On-site training records (Session AFL): sessions, attendees, lift links, ELE-TC-NNN
  // certificate issue + PDF download. Office/admin; hard delete is admin-only server-side.
  training: {
    // Session AGG: the office "assign an engineer" picker options (active engineers with a login).
    engineers: () => request<TrainingAssignableEngineer[]>("/training/engineers"),
    list: (q: TrainingListQuery = {}) => {
      const params = new URLSearchParams();
      if (q.siteId) params.set("siteId", q.siteId);
      if (q.status) params.set("status", q.status);
      if (q.type) params.set("type", q.type);
      if (q.from) params.set("from", q.from);
      if (q.to) params.set("to", q.to);
      if (q.renewalDue) params.set("renewalDue", "true"); // Session AFN: the "renewals due" pill filter
      if (q.page) params.set("page", String(q.page));
      if (q.pageSize) params.set("pageSize", String(q.pageSize));
      const qs = params.toString();
      return request<TrainingSessionListResult>(`/training${qs ? `?${qs}` : ""}`);
    },
    get: (id: string) => request<TrainingSessionDetailDto>(`/training/${encodeURIComponent(id)}`),
    create: (body: TrainingSessionCreateInput) =>
      request<TrainingSessionDetailDto>("/training", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: TrainingSessionUpdateInput) =>
      request<TrainingSessionDetailDto>(`/training/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    // One plain hard delete (Session AFM): removes the session, its attendees and its lift links.
    delete: (id: string) =>
      request<{ ok: boolean }>(`/training/${encodeURIComponent(id)}`, { method: "DELETE" }),
    // Idempotent: completing twice returns the same certificate ref.
    complete: (id: string) =>
      request<TrainingCompleteResult>(`/training/${encodeURIComponent(id)}/complete`, { method: "POST" }),
    // Same-origin download link; the server generates the certificate PDF on demand.
    certificateHref: (id: string) => `/api/training/${encodeURIComponent(id)}/certificate`,
    addAttendee: (id: string, body: TrainingAttendeeInput) =>
      request<TrainingAttendeeDto>(`/training/${encodeURIComponent(id)}/attendees`, { method: "POST", body: JSON.stringify(body) }),
    removeAttendee: (id: string, aid: string) =>
      request<{ ok: boolean }>(`/training/${encodeURIComponent(id)}/attendees/${encodeURIComponent(aid)}`, { method: "DELETE" }),

    // ---- Engineer PWA (Session AGG) ----
    // Live-only by design: training is not cached in IndexedDB, so these are plain calls with no
    // offline outbox behind them.
    mine: () => request<EngineerTrainingSessionDto[]>("/training/sessions/mine"),
    // Replaces the attendee list. A row sent back with its `id` and no signatureData keeps the
    // signature already stored for it.
    setAttendees: (id: string, attendees: EngineerTrainingAttendeeInput[]) =>
      request<EngineerTrainingAttendeesResult>(`/training/sessions/${encodeURIComponent(id)}/attendees`, {
        method: "PATCH",
        body: JSON.stringify({ attendees }),
      }),
    // Issues the certificate, then files it to Drive. Safe to retry: a session already completed
    // answers 409 carrying the reference that was issued, never a second certificate.
    completeFromPwa: (id: string) =>
      request<TrainingCompleteFromPwaResult>(`/training/sessions/${encodeURIComponent(id)}/complete-from-pwa`, {
        method: "POST",
      }),
  },

  portal: {
    // Always resolves with 200 whether or not the email is known: the reply never reveals whether
    // an address has a portal login. Since Session AGG it DOES report a send failure -- ok:false
    // with reason "send_failed" when the platform could not email the code (decided from whether
    // the mailer is reachable, not from whether the email was found, so the two stay
    // indistinguishable), or reason "rate_limited" (Session AHH: at most 3 sends per email per
    // hour). Show `message` in either case instead of "check your email".
    requestCode: (email: string) =>
      request<{ ok: boolean; reason?: string; message?: string }>("/portal/auth/request", {
        method: "POST",
        body: JSON.stringify({ email }),
      }),
    // Session AHH: the magic link is replaced by a 6-digit code the customer types. A wrong-but-
    // not-yet-locked code and an unknown email both throw a generic 401 "Incorrect or expired
    // code."; a locked email throws 423 with a minutes-remaining message. Both are caught via
    // ApiError.message on the login page.
    verifyCode: (email: string, code: string) =>
      request<{ customer: { name: string } }>("/portal/auth/verify-code", {
        method: "POST",
        body: JSON.stringify({ email, code }),
      }),
    me: () => request<{ email: string; customer: { name: string } }>("/portal/me"),
    overview: () => request<PortalOverview>("/portal/overview"),
    // One site's detail (Session AG Item 5), scoped server-side to the signed-in customer (404 if not theirs).
    site: (id: string) => request<PortalSite>(`/portal/sites/${encodeURIComponent(id)}`),
    logout: () => request<{ ok: boolean }>("/portal/auth/logout", { method: "POST" }),
    // The site page LOLER certificate download: a same-origin link the browser navigates to. The server
    // proxy-streams the PDF from Drive (the raw Drive URL never reaches the browser) and audits it,
    // and only for a lift that belongs to the signed-in customer.
    lolerCertificateHref: (assetId: string) => `/api/portal/assets/${encodeURIComponent(assetId)}/loler-certificate`,
    // The signed service agreement download (Session ADZ): same pattern - ownership-scoped,
    // proxy-streamed (Drive, else the signed Signable envelope), audited.
    serviceAgreementHref: (contractId: string) => `/api/portal/contracts/${encodeURIComponent(contractId)}/agreement`,
    // The site's history areas (Session AFE): service visits, call-outs, repairs + project
    // works, supplementary test records + annexes. Customer-scoped server-side (404 if not theirs).
    siteHistory: (id: string) => request<PortalSiteHistory>(`/portal/sites/${encodeURIComponent(id)}/history`),
    // Session BDB: the site's SAFed supplementary tests per lift, and the covering certificate of an approved job (same-origin link).
    siteSafed: (id: string) => request<PortalSiteSafed>(`/portal/sites/${encodeURIComponent(id)}/safed`),
    safedCertificateHref: (jobId: string) => `/api/portal/safed/${encodeURIComponent(jobId)}/certificate`,
    // The customer's cross-site history feeds (Session AFH): SERVICE and CALL-OUT visits across
    // ALL their sites, for the two top-level portal sections; since Session AMX also REPAIRS, for
    // the third. Customer-scoped server-side.
    history: () => request<PortalHistory>("/portal/history"),
    // Session AMX: the visit report a history row opens. Two routes because there are genuinely two
    // documents on two unrelated models -- a Job's field-loop completion certificate, and a
    // CallOut's filed Attendance Report. A row's `reportSource` says which one to use, and a row
    // with reportAvailable false has neither yet and must not be linked at all.
    //
    // Both are same-origin proxy links the browser navigates to (or the portal viewer frames),
    // inline by default; the viewer's downloadHref() appends ?download=true for the save link.
    jobReportHref: (jobId: string) => `/api/portal/jobs/${encodeURIComponent(jobId)}/report`,
    calloutReportHref: (calloutId: string) => `/api/portal/callouts/${encodeURIComponent(calloutId)}/report`,
    // Document downloads (Session AFE): same-origin proxy links, audited server-side; the raw
    // Drive URL never reaches the browser.
    companyDocumentHref: (id: string) => `/api/portal/company-documents/${encodeURIComponent(id)}/download`,
    siteDocumentHref: (siteId: string, docId: string) =>
      `/api/portal/sites/${encodeURIComponent(siteId)}/documents/${encodeURIComponent(docId)}/download`,
    // Static compliance document library (Session AFH): ELE's on-disk Health and Safety (ohs) and
    // RAMS (rams) documents. Returns the two groups in reading order (a bare array). The download is
    // a same-origin proxy link the browser opens (PDF inline, DOCX downloads), gated by the cookie.
    complianceDocs: () => request<PortalComplianceDocGroup[]>("/portal/compliance-docs"),
    complianceDocHref: (category: string, filename: string) =>
      `/api/portal/compliance-docs/${encodeURIComponent(category)}/${encodeURIComponent(filename)}/download`,
    // Training records (Session AFL): COMPLETED sessions for one of the customer's sites
    // (ownership-scoped server-side), and the certificate PDF download - a same-origin proxy
    // link, ownership-validated and audited server-side, generated on demand.
    siteTraining: (siteId: string) =>
      request<{ sessions: PortalTrainingSessionDto[] }>(`/portal/sites/${encodeURIComponent(siteId)}/training`),
    trainingCertificateHref: (id: string) => `/api/portal/training/${encodeURIComponent(id)}/certificate`,
    // Every COMPLETED session across ALL the customer's sites (Session AGJ), for the top-level
    // Training section. Capped server-side; `cap` comes back so the page can say so on screen
    // rather than presenting a truncated list as complete.
    training: () =>
      request<{ sessions: PortalTrainingFeedSessionDto[]; cap: number }>("/portal/training"),
    // Training reference documents (Session AFP): the tenant's CURRENT, CUSTOMER-FACING reference
    // material, grouped by training type. The download is a same-origin proxy link, audited
    // server-side; an internal-only document is unreachable (the server filters customerFacing).
    trainingDocuments: () => request<{ groups: PortalTrainingDocGroup[] }>("/portal/training-documents"),
    trainingDocumentHref: (id: string) => `/api/portal/training-documents/${encodeURIComponent(id)}/download`,
    // Phase 8 (Session ATJ, M-9a): outstanding + paid invoices, live status via the ledger-provider
    // seam. Pay now reuses the same Stripe mechanism as the office's own "Send payment link" action.
    invoices: () => request<{ invoices: PortalInvoiceListRow[] }>("/portal/invoices"),
    payInvoice: (id: string) => request<{ payUrl: string }>(`/portal/invoices/${encodeURIComponent(id)}/pay`, { method: "POST" }),
    // ---- Session AUD (email system Phase 5, Items 3 and 6) ----
    // The customer's open work, read fresh every time (a stage or a booked day changes under them).
    openWork: () => request<{ rows: PortalOpenWorkRow[] }>("/portal/open-work", { noCache: true }),
    // Report a fault on one of the customer's own lifts. NEVER an entrapment or an emergency: the
    // server's body is strict and has no such field (400), and the page tells anyone trapped to phone.
    // A lift that is not theirs, or retired, is 404; 10 reports an hour per login (429).
    reportFault: (body: { assetId: string; description: string; outOfService?: boolean }) =>
      request<PortalFaultReported>("/portal/faults", { method: "POST", body: JSON.stringify(body) }),
  },

  // Staff-only management of customer portal logins (office/admin session cookie).
  portalAdmin: {
    // Optionally scoped to one customer (?customerId=), for the customer record's Portal access card.
    listUsers: (customerId?: string) =>
      request<PortalAdminUser[]>(`/portal/admin/users${customerId ? `?customerId=${encodeURIComponent(customerId)}` : ""}`),
    // Session PU1: a customer with no ACTIVE contract is refused 409 portal_no_active_contract (see isNoActiveContractRefusal)
    // unless the office confirms it, in the branded confirm, with confirmNoActiveContract.
    createUser: (body: { customerId: string; email: string; name?: string; confirmNoActiveContract?: boolean }) =>
      request<CreatedPortalUser>("/portal/admin/users", { method: "POST", body: JSON.stringify(body) }),
    // Disable a portal login (revokes the customer's access immediately) or re-enable it. The
    // row is kept for the audit trail. (Since Session BCC an admin can remove every login of an ARCHIVED customer,
    // customers.removePortalUsers; otherwise a PortalUser is never hard-deleted, as it is a Customer-delete
    // blocker), so disable is the "remove access" control (Session AB, Item 6).
    setDisabled: (id: string, disabled: boolean) =>
      request<{ id: string; email: string; disabledAt: string | null }>(`/portal/admin/users/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify({ disabled }),
      }),
    // Session AFH: send (or re-send) the one-time portal sign-in invite email. office/admin on the
    // server since Session AFI (matching create + disable); rate-limited to 3 per login per hour (429).
    sendInvite: (id: string, opts?: { confirmNoActiveContract?: boolean }) =>
      request<{ sent: boolean }>(`/portal/admin/users/${encodeURIComponent(id)}/invite`, {
        method: "POST",
        ...(opts?.confirmNoActiveContract ? { body: JSON.stringify({ confirmNoActiveContract: true }) } : {}),
      }),
  },

  // Session ATA: the Subcontractor Portal, its own sign-in and cookie. The code request always resolves (the reply never
  // reveals whether an address has a login), reporting a send failure or the rate limit as ok:false with a message.
  subcontractorPortal: {
    requestCode: (email: string) =>
      request<{ ok: boolean; reason?: string; message?: string }>("/subcontractor-portal/auth/request", { method: "POST", body: JSON.stringify({ email }) }),
    verifyCode: (email: string, code: string) =>
      request<{ company: string }>("/subcontractor-portal/auth/verify-code", { method: "POST", body: JSON.stringify({ email, code }) }),
    logout: () => request<{ ok: boolean }>("/subcontractor-portal/auth/logout", { method: "POST" }),
    me: () => request<{ email: string; company: string; engineers: { name: string; active: boolean }[]; suppliesEngineers?: boolean; engineerAppUrl?: string }>("/subcontractor-portal/me", { noCache: true }),
    jobs: () => request<SubPortalJobsView>("/subcontractor-portal/jobs", { noCache: true }),
    timesheet: (week?: string) =>
      request<SubPortalTimesheetView>(`/subcontractor-portal/timesheet${week ? `?week=${encodeURIComponent(week)}` : ""}`, { noCache: true }),
    // Session AUZ: the documents on file and THE ONE WRITE (an upload), and the subcontractor's own rates.
    documents: () => request<SubPortalDocumentsView>("/subcontractor-portal/documents", { noCache: true }),
    uploadDocument: (file: File, fields: { documentType: string; engineerName?: string; certificateNumber?: string; expiryDate?: string; description?: string }) => {
      const fd = new FormData();
      fd.append("documentType", fields.documentType);
      if (fields.engineerName) fd.append("engineerName", fields.engineerName);
      if (fields.certificateNumber) fd.append("certificateNumber", fields.certificateNumber);
      if (fields.expiryDate) fd.append("expiryDate", fields.expiryDate);
      if (fields.description) fd.append("description", fields.description);
      fd.append("file", file, file.name);
      return request<{ ok: true; document: SubPortalDocument }>("/subcontractor-portal/documents", { method: "POST", body: fd });
    },
    rates: () => request<SubPortalRatesView>("/subcontractor-portal/rates", { noCache: true }),
    invoices: () => request<SubInvoiceStatusView>("/subcontractor-portal/invoices", { noCache: true }),
    // Session AVA: a weekly statement of hours the office released (hours only, never money).
    downloadStatement: (ref: string, filename: string) => downloadPdf(`/api/subcontractor-portal/statements/${encodeURIComponent(ref)}`, filename),
  },

  // Session ATA: the office's management of a subcontractor's portal logins (staff cookie, admin and office).
  subcontractorPortalAdmin: {
    list: (subcontractorId: string) =>
      request<SubcontractorPortalLogin[]>(`/subcontractors/${encodeURIComponent(subcontractorId)}/portal-users`, { noCache: true }),
    create: (subcontractorId: string, body: { email: string; name?: string }) =>
      request<SubcontractorPortalLogin>(`/subcontractors/${encodeURIComponent(subcontractorId)}/portal-users`, { method: "POST", body: JSON.stringify(body) }),
    sendCode: (subcontractorId: string, id: string) =>
      request<{ sent: boolean }>(`/subcontractors/${encodeURIComponent(subcontractorId)}/portal-users/${encodeURIComponent(id)}/invite`, { method: "POST" }),
    setDisabled: (subcontractorId: string, id: string, disabled: boolean) =>
      request<SubcontractorPortalLogin>(`/subcontractors/${encodeURIComponent(subcontractorId)}/portal-users/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify({ disabled }),
      }),
    remove: (subcontractorId: string, id: string) =>
      request<{ deleted: boolean }>(`/subcontractors/${encodeURIComponent(subcontractorId)}/portal-users/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },

  dashboard: {
    // noCache (Clearance 3): the tiles carry the open entrapment count and the other things that need attention now.
    get: () => request<DashboardTiles>("/dashboard", { noCache: true }),
    // Action panels (Session AAJ).
    outstandingJobs: () => request<OutstandingJobs>("/dashboard/outstanding-jobs"),
    activeQuotes: () => request<ActiveQuotesAndProjects>("/dashboard/active-quotes"),
    // The "launchpad" layout (Session ADC): the ONE shared office layout. null = the default
    // dashboard. setLayout saves the arranged layout (customise mode's exit-to-save);
    // resetLayout clears it back to the default (the layout's delete path).
    layout: () => request<{ layout: DashboardLayoutConfig | null }>("/dashboard/layout"),
    setLayout: (layout: DashboardLayoutConfig) =>
      request<{ layout: DashboardLayoutConfig | null }>("/dashboard/layout", { method: "PUT", body: JSON.stringify(layout) }),
    resetLayout: () => request<{ layout: null }>("/dashboard/layout", { method: "DELETE" }),
  },

  customers: {
    // Session SC9B: the batched account status (on stop / up to date / not checked) for at most 100 customers. Never cached: it feeds a money warning.
    accountStatus: (ids: string[]) =>
      request<{ items: Array<{ customerId: string } & AccountStatusDto> }>(`/customers/account-status?ids=${ids.map(encodeURIComponent).join(",")}`, { noCache: true }),
    // The customer register (active by default), trimmed at the call site to a picker shape.
    list: () => request<CustomerOption[]>("/customers"),
    // Session AUF (DL-1, DL-3): who arranges the customer's LOLER examinations (feeds an editable control: noCache), and the
    // re-bill of an examining body's invoice as a Xero DRAFT. Both writes preview unless preview is false.
    lolerArrangement: (id: string) => request<LolerArrangement>(`/customers/${encodeURIComponent(id)}/loler-arrangement`, { noCache: true }),
    lolerArrangements: () =>
      request<{ customers: LolerArrangement[]; recommendedExaminer: { name: string; email: string } }>("/customers/loler-arrangements", { noCache: true }),
    setLolerArrangement: (id: string, body: { arranged: boolean; reason: string; preview: boolean }) =>
      request<LolerArrangementResponse>(`/customers/${encodeURIComponent(id)}/loler-arrangement`, { method: "PUT", body: JSON.stringify(body) }),
    lolerRebill: (id: string, body: LolerRebillRequest) =>
      request<LolerRebillResponse>(`/customers/${encodeURIComponent(id)}/loler-rebill`, { method: "POST", body: JSON.stringify(body) }),
    // Client risk flags. atRisk lists every flagged client (sorted by flag count);
    // risk(id) returns one client's flags with a plain-English summary each.
    atRisk: () => request<AtRiskClient[]>("/customers/at-risk"),
    risk: (id: string) => request<{ customerId: string; flags: RiskFlag[] }>(`/customers/${encodeURIComponent(id)}/risk`),
    // One customer with its site/contract counts + archive marker (works for archived too).
    get: (id: string) => request<ResourceCustomer>(`/customers/${encodeURIComponent(id)}`),
    // Session BCC (D-BCB-4): remove EVERY portal login of an ARCHIVED customer, admin only. Previews unless preview is
    // false; refused 409 (not archived, nothing to remove, or changed since the preview). Emails come back masked.
    // A commit names the logins the preview showed (expectedLoginIds); a different set is refused 409 changed.
    removePortalUsers: (id: string, preview: boolean, expectedLoginIds?: string[]) =>
      request<RemovePortalUsersResponse>(`/customers/${encodeURIComponent(id)}/portal-users/remove`, {
        method: "POST",
        body: JSON.stringify(preview ? { preview } : { preview, expectedLoginIds }),
      }),
    // The Overview tab's account footprint (Phase 2, C-1/C-2/C-3/C-17): sites and lifts with live
    // status, open work, open defects, contracts, open quotes and the outstanding invoice balance.
    overview: (id: string) => request<CustomerOverview>(`/customers/${encodeURIComponent(id)}/overview`),
    // A direct, cookie-authenticated link to the welcome-email preview (Session AHI): renders
    // the HTML the customer would be drafted on contract activation, using real data. The
    // server 404s (with a plain-English page) a customer the welcome email is off for, which
    // since Session AOZ is decided by resolvesWelcomePackEligible rather than the client type
    // alone, and a customer with no contract to preview.
    welcomeEmailPreviewHref: (id: string) => `/api/customers/${encodeURIComponent(id)}/welcome-email-preview`,
    // "Draft welcome email now" (Session APC). The status says whether the action is on offer for this
    // customer's current active contract and, when it is not, why; the POST grants the portal login and drafts
    // the welcome into Gmail Drafts for the office to review and send. It NEVER sends. A refusal is an ApiError
    // whose message is a plain-English sentence to show as written.
    welcomeEmailDraftStatus: (id: string) =>
      request<WelcomeDraftStatus>(`/customers/${encodeURIComponent(id)}/welcome-email/draft`),
    draftWelcomeEmail: (id: string) =>
      request<WelcomeDraftResult>(`/customers/${encodeURIComponent(id)}/welcome-email/draft`, { method: "POST" }),
    // Delete a customer. Only an empty customer can be removed; the server returns 409 (an
    // ApiError carrying a plain-English message listing the blockers) when sites/contracts/
    // quotes/jobs/projects/retained-invoices are attached.
    remove: (id: string) =>
      request<{ ok: boolean }>(`/customers/${encodeURIComponent(id)}`, { method: "DELETE" }),
    // Archive a customer: hide it from the active register, reversibly. Session AGK. Nothing is
    // destroyed and no contract ends; use it for a customer ELE no longer works for, whose history
    // means the delete above will always be refused.
    archive: (id: string) =>
      request<{ ok: boolean; archived: boolean }>(`/customers/${encodeURIComponent(id)}/archive`, { method: "POST" }),
    // Reactivate an archived customer.
    reactivate: (id: string) =>
      request<{ ok: boolean }>(`/customers/${encodeURIComponent(id)}/reactivate`, { method: "POST" }),
    // Promote a NON_CONTRACT one-off caller to a full customer (Session AFM). Changes only the
    // classification (customerType CONTRACT, isOneOff cleared); every report/invoice/site link
    // survives, and no ELE number is issued (that still waits for a signing).
    promote: (id: string) =>
      request<ResourceCustomer>(`/customers/${encodeURIComponent(id)}/promote`, { method: "POST" }),
    // Manually record an NPS score (0 to 10) a customer gave by phone (email-reply fallback).
    recordNps: (id: string, score: number) =>
      request<{ recorded: boolean; score: number }>(`/customers/${encodeURIComponent(id)}/nps-score`, {
        method: "POST",
        body: JSON.stringify({ score }),
      }),
    // Enrich the customer register from the Xero contact list (link existing customers to their
    // Xero contact / update details / archive). Never creates customers (the platform owns who is
    // a customer). Returns how many were linked / updated / archived, plus how many were
    // flaggedForReview: customers Xero says are archived that the sync deliberately did NOT archive,
    // because somebody at that customer can still sign in to the customer portal and archiving would
    // shut them out (Session ANA). The office archives those by hand if that is right, so this count
    // has to reach the banner on the Customers page or the outcome is invisible to the only person
    // who can act on it.
    //
    // flaggedForReviewNames carries WHICH customers those were, by name. The count alone told the
    // office to go and archive somebody by hand without being able to say who: the held-back
    // customers are not archived (so the Archived filter cannot find them), the list has no
    // portal-login column, and the only other record is an audit row that no office page shows. The
    // list can be SHORTER than the count if a name could not be read, so the count stays the
    // authoritative figure and the names are what make it actionable.
    // erasedContactsSkipped / warnings (Session ASQ, Stream G, Item 3): contacts NOT re-imported because the office erased
    // that person; present only when there were some.
    syncXero: () =>
      request<{
        linked: number;
        updated: number;
        archived: number;
        flaggedForReview: number;
        flaggedForReviewNames: string[];
        erasedContactsSkipped?: number;
        warnings?: string[];
      }>("/customers/sync-xero", {
        method: "POST",
      }),
    // One-time admin cleanup: remove unlinked Xero imports (customers with no V2 activity).
    cleanupUnlinked: () =>
      request<{ removed: number }>("/customers/cleanup-unlinked", { method: "POST" }),
    // Xero contact naming (Session ADV), admin only. The preview is READ-ONLY: it reports what
    // Xero calls this customer's contact today against what it should be called (the company, with
    // the billing contact as the named person), so the office sees the change before it happens.
    xeroContactPreview: (id: string) => request<XeroContactPreview>(`/customers/${encodeURIComponent(id)}/xero-contact`),
    // Apply the correction to the EXISTING Xero contact (it keeps its id, invoices and history).
    // Only ever called from an explicit button press; never on a schedule, never in bulk.
    xeroContactResync: (id: string) =>
      request<XeroContactResyncResult>(`/customers/${encodeURIComponent(id)}/xero-contact/resync`, { method: "POST" }),
    // Clear or change which Xero contact this customer is linked to (Session BCG). Office and admin. Writes
    // nothing to Xero. `preview: true` states what would change (and refuses what the commit would refuse);
    // a commit needs `preview: false` and a reason. A refusal arrives as an ApiError carrying the plain sentence.
    xeroLink: (id: string, body: XeroLinkRequest) =>
      request<XeroLinkPreviewResult | XeroLinkCommitResult>(`/customers/${encodeURIComponent(id)}/xero-link`, {
        method: "POST",
        body: JSON.stringify(body),
        noCache: true,
      }),
    // The inbound call log for one customer (Session ABT), newest first, with optional filters.
    callLogs: (id: string, params: CallLogListParams = {}) =>
      request<CallLogListResponse>(`/customers/${encodeURIComponent(id)}/call-logs${callLogQuery(params)}`),
    // This customer's service records across every site + lift (Session ADF), paginated newest
    // first. Each row deep-links to that visit's ServiceRecordPanel when it has a sheet on file.
    serviceHistory: (id: string, params: { page?: number; limit?: number } = {}) => {
      const q = new URLSearchParams();
      if (params.page) q.set("page", String(params.page));
      if (params.limit) q.set("limit", String(params.limit));
      const qs = q.toString();
      return request<ServiceHistoryResponse>(`/customers/${encodeURIComponent(id)}/service-history${qs ? `?${qs}` : ""}`);
    },
    // Per-customer CALL-OUT rate override (Session ADB, admin only). The payload carries the
    // standard Schedule of Fees rates plus the customer's override (or null when none).
    rateOverride: {
      get: (id: string) => request<RateOverridePayload>(`/customers/${encodeURIComponent(id)}/rate-override`),
      create: (id: string, body: RateOverrideInput) =>
        request<RateOverridePayload>(`/customers/${encodeURIComponent(id)}/rate-override`, { method: "POST", body: JSON.stringify(body) }),
      update: (id: string, body: RateOverrideInput) =>
        request<RateOverridePayload>(`/customers/${encodeURIComponent(id)}/rate-override`, { method: "PATCH", body: JSON.stringify(body) }),
      remove: (id: string) =>
        request<{ deleted: boolean }>(`/customers/${encodeURIComponent(id)}/rate-override`, { method: "DELETE" }),
    },
    // Per-customer CALL-OUT response-time (SLA) override (Session ADQ, admin only). GET carries the
    // platform defaults, the override (or null) AND zoneFallbacks: the zones this customer's sites
    // actually sit in, which is what a cleared field really falls back to. The write calls return
    // the platform + override only (no zoneFallbacks): keep the ones from the GET.
    slaOverride: {
      get: (id: string) => request<SlaOverridePayload>(`/customers/${encodeURIComponent(id)}/sla-override`),
      create: (id: string, body: SlaOverrideInput) =>
        request<SlaOverrideWriteResult>(`/customers/${encodeURIComponent(id)}/sla-override`, { method: "POST", body: JSON.stringify(body) }),
      update: (id: string, body: SlaOverrideInput) =>
        request<SlaOverrideWriteResult>(`/customers/${encodeURIComponent(id)}/sla-override`, { method: "PATCH", body: JSON.stringify(body) }),
      remove: (id: string) =>
        request<{ deleted: boolean }>(`/customers/${encodeURIComponent(id)}/sla-override`, { method: "DELETE" }),
    },
  },

  // Coverage zones (Session ADQ, admin only): the response-time targets ELE aims for, by postcode
  // area. A zone is tenant config; a call-out snapshots the zone CODE it was measured against, so
  // editing or deleting a zone never rewrites a past call-out's target.
  sla: {
    zones: () => request<{ zones: CoverageZone[] }>("/sla/zones"),
    createZone: (body: CoverageZoneInput) =>
      request<CoverageZone>("/sla/zones", { method: "POST", body: JSON.stringify(body) }),
    updateZone: (id: string, body: CoverageZoneInput) =>
      request<CoverageZone>(`/sla/zones/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    removeZone: (id: string) =>
      request<{ deleted: boolean }>(`/sla/zones/${encodeURIComponent(id)}`, { method: "DELETE" }),
    // Create the three starting zones. Idempotent: only ever writes when the tenant has none.
    seedZones: () => request<CoverageZoneSeedResult>("/sla/zones/seed", { method: "POST" }),
    // Call-out response performance + first-time fix. Office AND admin (unlike the zone config
    // above), read-only: it re-judges nothing, it reports the outcomes the SLA clock already
    // persisted. from/to (yyyy-mm-dd) default server-side to the last 90 days; groupBy is a payload
    // control (all three breakdown arrays are always present in the response, empty when not asked
    // for), and defaults to "all".
    report: (opts: { from?: string; to?: string; groupBy?: SlaReport["groupBy"] } = {}) => {
      const q = new URLSearchParams();
      if (opts.from) q.set("from", opts.from);
      if (opts.to) q.set("to", opts.to);
      if (opts.groupBy) q.set("groupBy", opts.groupBy);
      const qs = q.toString();
      return request<SlaReport>(`/sla/report${qs ? `?${qs}` : ""}`);
    },
  },

  // Inbound call log (Session ABT): manual logging of inbound calls with an optional NPS trigger.
  // Internal office <-> engineer messaging (Session ACZ). Shared by the office Messages page and
  // the engineer PWA Messages tab. A thread is keyed by the engineer's User id.
  messages: {
    // `background` (Session ASB review, F4): pass it from a poll (a timer), never from a person's action.
    threads: (opts: { background?: boolean } = {}) =>
      request<MessageThreadSummary[]>("/messages/threads", opts.background ? { background: true } : {}),
    thread: (engineerId: string, opts: { background?: boolean } = {}) =>
      request<MessageThread>(`/messages/threads/${encodeURIComponent(engineerId)}`, opts.background ? { background: true } : {}),
    threadJobs: (engineerId: string) => request<MessageJobOption[]>(`/messages/threads/${encodeURIComponent(engineerId)}/jobs`),
    send: (body: SendMessageBody) => request<MessageDto>("/messages", { method: "POST", body: JSON.stringify(body) }),
    markRead: (engineerId: string, opts: { background?: boolean } = {}) =>
      request<{ updated: number }>(`/messages/threads/${encodeURIComponent(engineerId)}/read`, {
        method: "PATCH",
        ...(opts.background ? { background: true } : {}),
      }),
    unreadCount: (opts: { background?: boolean } = {}) =>
      request<{ count: number }>("/messages/unread-count", opts.background ? { background: true } : {}),
    remove: (id: string) => request<{ ok: boolean }>(`/messages/${encodeURIComponent(id)}`, { method: "DELETE" }),
    smsPreference: () => request<{ optIn: boolean; hasMobile: boolean }>("/messages/sms-preference"),
    setSmsPreference: (optIn: boolean) =>
      request<{ optIn: boolean }>("/messages/sms-preference", { method: "PATCH", body: JSON.stringify({ optIn }) }),
  },

  // Web Push (browser notifications). status reports whether the server has VAPID keys and, when it
  // does, the public key the browser needs to subscribe. subscribe/unsubscribe register and remove
  // the browser's PushSubscription against the signed-in user.
  push: {
    status: () => request<PushStatus>("/push/status"),
    subscribe: (body: PushSubscribeBody) => request<{ ok: boolean }>("/push/subscribe", { method: "POST", body: JSON.stringify(body) }),
    unsubscribe: (endpoint: string) =>
      request<{ ok: boolean }>("/push/unsubscribe", { method: "POST", body: JSON.stringify({ endpoint }) }),
  },

  callLogs: {
    // The paginated, filtered list of calls across all customers (Session ARM: the Enquiries page
    // reads the unknown callers from it with `unmatched: true`).
    list: (params: CallLogListParams = {}) => request<CallLogListResponse>(`/call-logs${callLogQuery(params)}`),
    get: (id: string) => request<CallLogDto>(`/call-logs/${encodeURIComponent(id)}`),
    create: (body: CallLogCreateBody) => request<CallLogDto>("/call-logs", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: CallLogUpdateBody) =>
      request<CallLogDto>(`/call-logs/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    remove: (id: string) => request<{ ok: boolean }>(`/call-logs/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },

  // Engineer self-service (the singular /engineer, distinct from the office /engineers management).
  engineer: {
    // The signed-in engineer's jobs for the current ISO week (Monday to Sunday), date order.
    diary: () => request<EngineerDiaryRow[]>("/engineer/diary"),
    // Site and lift history (Session AEK, Director decision D33): read-only, so an engineer attending
    // an unfamiliar lift can see what the last engineer found and did. Since Session ASB (D-SEC-6)
    // only the sites where the engineer holds open work. `fresh` bypasses the in-memory request
    // cache, so the PWA's site-list SYNC (historyStore.syncSiteList) reads the server's current
    // answer, which is what decides which saved site pages leave the device.
    sites: (opts: { fresh?: boolean } = {}) =>
      request<EngineerSiteListItem[]>("/engineer/sites", opts.fresh ? { noCache: true } : {}),
    // noCache (Session ASB review, finding I6-3) on these three: their one caller, historyStore.ts, keeps its own
    // IndexedDB cache with its own lifetimes, and the in-memory GET cache in front of it served a page the site-list
    // sync had just pruned straight back (a background 404 is swallowed there), so the purge never happened.
    siteHistory: (siteId: string, page = 1) =>
      request<EngineerSiteHistory>(
        `/engineer/sites/${encodeURIComponent(siteId)}/history?page=${page}&limit=${ENGINEER_HISTORY_PAGE_SIZE}`,
        { noCache: true },
      ),
    liftHistory: (assetId: string, page = 1) =>
      request<EngineerLiftHistory>(
        `/engineer/assets/${encodeURIComponent(assetId)}/history?page=${page}&limit=${ENGINEER_HISTORY_PAGE_SIZE}`,
        { noCache: true },
      ),
    // One completed job sheet in full (Session AEM), keyed on the JOB id: JobSheet.jobId is
    // unique and the history rows already carry it.
    jobSheet: (jobId: string) =>
      request<EngineerJobSheetDetail>(`/engineer/job-sheets/${encodeURIComponent(jobId)}`, { noCache: true }),
    // A same-origin URL an <img> loads directly, NOT a fetch: the bytes are proxied so the raw
    // Drive URL never reaches the device. Mirrors api.jobs.photoUrl on the office side.
    jobSheetPhotoUrl: (jobId: string, index: number) =>
      `/api/engineer/job-sheets/${encodeURIComponent(jobId)}/photos/${index}`,
    // Report an accident or a near miss from the field (Session AOM). WRITE ONLY, and the absence of
    // a matching read is the feature: an accident record carries an injured person's details, the
    // office's RIDDOR determination and an investigation in progress, none of which may reach a
    // phone. The response is an acknowledgement built from the reference alone. There is no
    // engineer-side list or detail call anywhere in this file, and there must never be one.
    reportAccident: (body: AccidentQuickLogInput) =>
      request<AccidentQuickLogAck>("/engineer/accident-reports", { method: "POST", body: JSON.stringify(body) }),
    // Session APG: which of MY open jobs at this lift today a scanned POP tag belongs to, and which
    // end of each. READ-ONLY: the tap itself is recorded by api.publicTap.record, the one route that
    // writes a tap, so the Home button and the job sheet cannot record two different things.
    popTagMatch: (token: string) =>
      request<PopTagMatch>("/engineer/pop-tag/match", { method: "POST", body: JSON.stringify({ token }) }),
    // Session ASB (D-SEC-6): the signed-in engineer's OWN completed visits, newest first, each with its
    // site's name, so a record stays reachable after its site has left the site list. Always read from
    // the server (noCache): the list is short-lived on screen and a stale copy would hide a visit the
    // office has just approved. `signal` lets the caller give up on a stalled request (historyStore.loadMyVisits'
    // timeout, Session ASB, the review of fix round 2).
    myVisits: (page = 1, opts: { signal?: AbortSignal } = {}) =>
      request<{
        items: Array<EngineerHistoryJob & { siteName: string | null }>;
        page: number;
        limit: number;
        total: number;
        hasMore: boolean;
      }>(`/engineer/my-visits?page=${page}&limit=${ENGINEER_HISTORY_PAGE_SIZE}`, { noCache: true, signal: opts.signal }),
  },

  jobs: {
    // noCache (Session ASB review, finding I6-3): every PWA sync must read the server. Its one caller
    // (jobsStore.refreshFromServer) writes the answer into IndexedDB, so a job cancelled or reassigned minutes ago
    // must not come back from the in-memory GET cache and be written back to the phone, access details included.
    mine: () => request<EngineerJob[]>("/jobs/mine", { noCache: true }),
    complete: (id: string, body: JobCompletionBody) =>
      request<{ status: string; jobId: string; completedAt: string; photos: string[] }>(
        `/jobs/${encodeURIComponent(id)}/complete`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    // Session BDM: what the engineer records for ONE working day of a multi-day job: arrived, left, an optional note, booked overtime.
    // A save of the same day again replaces it. The phone queues it offline (lib/workDayTimesQueue.ts).
    workDayTime: (
      id: string,
      day: string,
      body: { arrivedAt?: string | null; leftAt?: string | null; note?: string | null; overtimeBooked?: boolean; lateReason?: string; lateReasonText?: string },
    ) =>
      request<{ day: string; arrivedAt: string | null; leftAt: string | null; note: string | null; overtimeBooked: boolean; overtimeApproved?: boolean }>(
        `/jobs/${encodeURIComponent(id)}/work-days/${encodeURIComponent(day)}/time`,
        { method: "PUT", body: JSON.stringify(body) },
      ),
    // Session AFZ: record the engineer's RAMS acknowledgement (idempotent; first-acknowledgement-wins
    // server-side). Live-only; an offline visit records it via the completion payload instead.
    acknowledgeRams: (id: string) =>
      request<{ ramsAcknowledgedAt: string | null }>(`/jobs/${encodeURIComponent(id)}/acknowledge-rams`, { method: "POST" }),
    // Session ALW: the engineer confirms (or refuses) a job assigned to them. accept is idempotent
    // and first-acceptance-wins server-side; it is the ONLINE path, and an offline acceptance rides
    // the completion payload instead (exactly as the RAMS acknowledgement above does).
    // Session APF: accepting now folds in the RAMS acknowledgement too, so the server can stamp both
    // facts from one tap. opts is genuinely optional and the no-opts call sends NO body at all, byte-
    // identical to how this route has always been called, so an older call site keeps working
    // unchanged. Only pass { ramsAcknowledged: true } from a screen that has actually shown the
    // engineer the RAMS statement they are confirming -- see the server route's own comment for why.
    accept: (id: string, opts?: { ramsAcknowledged?: boolean }) =>
      request<{ acceptedAt: string | null; ramsAcknowledgedAt: string | null }>(
        `/jobs/${encodeURIComponent(id)}/accept`,
        opts ? { method: "POST", body: JSON.stringify(opts) } : { method: "POST" },
      ),
    // Declining hands the job straight back to the office: it clears the engineer, the acceptance
    // and (for the undated types) the date, so the job reappears on the Scheduling page's "Needs
    // scheduling" panel immediately. LIVE ONLY, deliberately: a decline is a dispatch decision the
    // office must see now, and queueing one offline would leave a job nobody is going to that
    // still looks assigned. The PWA says so rather than failing silently.
    decline: (id: string, reason?: string) =>
      request<{ declined: boolean; backOnBacklog: boolean }>(`/jobs/${encodeURIComponent(id)}/decline`, {
        method: "POST",
        body: JSON.stringify(reason?.trim() ? { reason: reason.trim() } : {}),
      }),
    // ---- Lone worker safety check-in (Session AOB) ----
    // ONLINE ONLY, deliberately, and this is the one place in the PWA where that is a safety
    // property rather than a convenience. A check-in says "I am alive at this moment". Queueing one
    // in the outbox and delivering it when signal returns would let a tap made before an engineer
    // went into a basement silence an alert about every minute since, which is exactly the interval
    // the office needs to hear about. Neither call is ever added to the offline outbox; the screen
    // says plainly when it could not send and that the engineer is still shown as overdue.
    checkInState: (id: string) =>
      request<{ session: LoneWorkerOwnSession | null }>(`/jobs/${encodeURIComponent(id)}/check-in`, { noCache: true }),
    checkIn: (id: string) =>
      request<{
        sessionId: string;
        started: boolean;
        checkInCount: number;
        lastCheckInAt: string;
        nextCheckInDueAt: string;
        closedElsewhere: number;
      }>(`/jobs/${encodeURIComponent(id)}/check-in`, { method: "POST" }),
    checkOut: (id: string) => request<{ ended: number }>(`/jobs/${encodeURIComponent(id)}/check-out`, { method: "POST" }),
    // The site documents filed against this job's site (names always available; the download proxies
    // through the server). An empty list for a call-out with no lift/site.
    siteDocuments: (id: string) => request<{ documents: EngineerSiteDocument[] }>(`/jobs/${encodeURIComponent(id)}/site-documents`),
    // Same-origin hrefs an <a> opens directly (cookies ride). The RAMS is served from ELE's compliance
    // store; a site document is proxied so the browser never needs Drive auth.
    ramsHref: (id: string) => `/api/jobs/${encodeURIComponent(id)}/rams`,
    siteDocumentHref: (id: string, docId: string) =>
      `/api/jobs/${encodeURIComponent(id)}/site-documents/${encodeURIComponent(docId)}/download`,
    // Upload one job photo (multipart). The stable photoId makes the upload
    // idempotent server-side, so a replay after a lost response never duplicates the
    // photo. Returns the full updated photo list.
    uploadPhoto: (
      id: string,
      photoId: string,
      blob: Blob,
      filename: string,
      meta?: { capturedAt?: string; lat?: number; lng?: number },
    ) => {
      const fd = new FormData();
      fd.append("photoId", photoId);
      fd.append("photos", blob, filename);
      // Capture metadata (geotag + timestamp). Only sent when present, so an older client or
      // a location-denied capture simply omits them.
      if (meta?.capturedAt) fd.append("capturedAt", meta.capturedAt);
      if (typeof meta?.lat === "number") fd.append("lat", String(meta.lat));
      if (typeof meta?.lng === "number") fd.append("lng", String(meta.lng));
      return request<{ photos: string[] }>(`/jobs/${encodeURIComponent(id)}/photos`, { method: "POST", body: fd });
    },
    // The office photo proxy: a same-origin URL the office <img> loads directly (the
    // server streams the bytes from Drive). NOT a fetch.
    photoUrl: (id: string, index: number) => `/api/jobs/${encodeURIComponent(id)}/photos/${index}`,
    // The authenticated office signature image URL (Session AK Item 8): an <img src> served by the
    // proxy (cookie auth), never the raw Drive URL.
    signatureUrl: (id: string) => `/api/jobs/${encodeURIComponent(id)}/signature`,
    // Delete one job photo by index (admin/office): removes it from the sheet + Drive.
    // Returns the updated photo list.
    deletePhoto: (id: string, index: number) =>
      request<{ photos: string[] }>(`/jobs/${encodeURIComponent(id)}/photos/${index}`, { method: "DELETE" }),
    // Persist a drag-reordered photo order (Session ADE): `order` is a full permutation of the
    // current indices. The stored array IS the order (the proxy addresses by index and the PDFs
    // embed in array order); a 409 means the photos changed underneath (refresh and retry).
    reorderPhotos: (id: string, order: number[]) =>
      request<{ photos: string[] }>(`/jobs/${encodeURIComponent(id)}/photos/order`, { method: "PUT", body: JSON.stringify({ order }) }),
    // Send the "visit scheduled" confirmation email to the customer (office/admin), after the office
    // confirms it in the reschedule "Notify customer?" modal. 400 if the customer has no email.
    notifyScheduled: (id: string) =>
      request<{ sent: boolean }>(`/jobs/${encodeURIComponent(id)}/notify-scheduled`, { method: "POST" }),
    // Session ANU: the engineer flags a defect from their own job screen. photoIndexes are positions
    // in the job sheet's photo array (the photos already captured through the one existing capture
    // path on that screen), never URLs. Severity is decided server-side from the wording; the
    // engineer is offered no category control.
    flagDefect: (id: string, body: { description: string; photoIndexes?: number[] }) =>
      request<{ id: string; severity: "advisory" | "immediate"; photoCount: number }>(
        `/jobs/${encodeURIComponent(id)}/defects`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    // Session ATF: raise a repair estimate for extra work found on this job. The work, parts and time only: the
    // server REFUSES a price (the office prices it) and sends nothing to the customer. Answers with the reference.
    raiseRepairEstimate: (id: string, body: { description: string; partsNeeded?: string; estimatedHours?: number }) =>
      request<{ reference: string; message: string }>(`/engineer/jobs/${encodeURIComponent(id)}/repair-estimate`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  schedule: {
    // Session AQS, X-6: board/approve/reject/runPpm client wrappers removed. They backed the old
    // per-engineer DispatchBoard.tsx (the "proposal" model), retired and unrouted since Session O
    // and deleted outright this session -- these were their only caller. Session ARA, S-17 finished
    // the job: the server routes they hit (GET /schedule/board, POST /schedule/blocks/:id/
    // approve|reject, POST /schedule/run-ppm) are removed too. Generate PPM's own client call is
    // api.scheduling.generatePpm, a different, live endpoint, untouched throughout.
    // notifyNow (Session AKA): true only right after the office confirms the branded "Assign
    // engineer?" dialog -- sends the "engineer on the way" email/text for real instead of the usual
    // Gmail-draft-for-review AJI leaves as the default.
    // workDay (Session BDM): on a MULTI-DAY job a drag moves ONE day, so the day being moved ("YYYY-MM-DD") is named.
    // Session SC5: the proposed engineer, day and window for one unassigned call-out, repair or technician visit, derived on read. Read only; Accept is the ordinary assign (callouts.assign or schedule.reassign) with notifyNow off.
    proposal: (target: { calloutId: string } | { jobId: string }) => {
      const params = new URLSearchParams();
      if ("calloutId" in target) params.set("callOutId", target.calloutId);
      else params.set("jobId", target.jobId);
      return request<SlotProposal>(`/schedule/proposal?${params.toString()}`, { noCache: true });
    },
    reassign: (id: string, body: { scheduledFor?: string | null; assignedTo?: string | null; notifyNow?: boolean; workDay?: string }) =>
      request<DispatchJob>(`/schedule/jobs/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    // ---- Session BDM: a job over several working days. ONE job, ONE reference, ONE invoice. Every write takes preview: true first. ----
    days: {
      get: (id: string) => request<JobDaysView>(`/schedule/jobs/${encodeURIComponent(id)}/days`, { noCache: true }),
      set: (id: string, body: { days?: string[]; startDate?: string; workingDays?: number; includeWeekends?: boolean; includeBankHolidays?: boolean; preview?: boolean }) =>
        request<JobDaysResult>(`/schedule/jobs/${encodeURIComponent(id)}/days`, { method: "POST", body: JSON.stringify(body) }),
      extend: (id: string, body: { count: number; includeWeekends?: boolean; includeBankHolidays?: boolean; preview?: boolean }) =>
        request<JobDaysResult>(`/schedule/jobs/${encodeURIComponent(id)}/days/extend`, { method: "POST", body: JSON.stringify(body) }),
      shorten: (id: string, body: { count: number; preview?: boolean }) =>
        request<JobDaysResult>(`/schedule/jobs/${encodeURIComponent(id)}/days/shorten`, { method: "POST", body: JSON.stringify(body) }),
      move: (id: string, body: { from: string; to: string; preview?: boolean }) =>
        request<JobDaysResult>(`/schedule/jobs/${encodeURIComponent(id)}/days/move`, { method: "POST", body: JSON.stringify(body) }),
      // Session OTA: previews BY DEFAULT; only preview: false writes. Office and admin.
      overtime: (id: string, day: string, body: { engineerId: string; decision: "approve" | "refuse"; preview?: boolean; arrivedAt?: string | null; leftAt?: string | null }) =>
        request<OvertimeDecisionResult>(`/schedule/jobs/${encodeURIComponent(id)}/days/${encodeURIComponent(day)}/overtime`, { method: "POST", body: JSON.stringify(body) }),
      people: (id: string, day: string, body: { engineerIds: string[]; preview?: boolean }) =>
        request<{ preview: boolean; days: JobDaysView | null }>(`/schedule/jobs/${encodeURIComponent(id)}/days/${encodeURIComponent(day)}/people`, { method: "PUT", body: JSON.stringify(body) }),
    },
    // Delete an unstarted scheduled visit (Session AR Item 9); blocked (409) for anything worked on.
    deleteJob: (id: string) => request<{ ok: true }>(`/schedule/jobs/${encodeURIComponent(id)}`, { method: "DELETE" }),
    // Put a job on hold, with a reason (Session AQY, D-AQX-1; the reason list is Session SC9A). Works for any job type. Other needs words.
    pause: (id: string, holdReason: JobHoldReasonKey, reasonText?: string) =>
      request<DispatchJob>(`/schedule/jobs/${encodeURIComponent(id)}/pause`, {
        method: "PATCH",
        body: JSON.stringify({ holdReason, ...(reasonText && reasonText.trim() ? { reasonText: reasonText.trim() } : {}) }),
      }),
    // Resume a paused job, back to in_progress (Session AQY, D-AQX-1). No reason needed.
    resume: (id: string) => request<DispatchJob>(`/schedule/jobs/${encodeURIComponent(id)}/resume`, { method: "PATCH", body: JSON.stringify({}) }),
    // Cancel a job, any type (Session AQY, D-AQX-4). If it is a call-out's dispatch job, the linked
    // CallOut is cancelled in the same atomic write. attended:true optionally logs travel/arrival
    // time, which flows into the engineer's timesheet -- see travelLogged/travelSkippedReason.
    cancelJob: (id: string, body?: { attended?: boolean; travelMins?: number; arrivalTime?: string; notes?: string }) =>
      request<{ ok: true; calloutCancelled: boolean; travelLogged: boolean; travelSkippedReason: string | null; travelNotRechecked?: string[] }>(
        `/schedule/jobs/${encodeURIComponent(id)}/cancel`,
        { method: "PATCH", body: JSON.stringify(body ?? {}) },
      ),

    // ---- Raise undated work onto the "Needs scheduling" backlog (Session AMF) ----
    // The three routes behind the office "Raise..." control. Each has existed and been tested
    // since it was written (planned repair Session ALS, the two inspection types Session ALW);
    // until now the ONLY caller of any of them was an MCP tool, so the browser could not raise
    // work the assistant could. These are the browser's half, dispatching the same routes.
    //
    // All three answer `reused: true` rather than erroring when an open job of that type already
    // exists on the lift, so the caller reports "already raised" rather than "created" (a repair
    // and a condition report are guarded by Job_open_nonservice_per_asset_type; a site inspection
    // is deliberately exempt and never reuses).
    // Session ANH: every raise accepts an optional `twoManBooked`, the office's booking of the job as
    // two-man, which is the only thing that lets the rate card price a second engineer on it.
    // Session AOH: a lift on the register, OR a customer plus a free-text site for a one-off,
    // non-contract repair at a lift that is not on it. The route refuses both and neither, and
    // refuses a customer with no site (or a site with no customer) -- see plannedRepairBody's two
    // refinements. Modelled as a union rather than three optional fields so the type itself cannot
    // express a request the server will reject.
    plannedRepair: (
      body: ({ assetId: string } | { customerId: string; siteLabel: string }) & {
        description: string;
        raisedBy?: string;
        twoManBooked?: boolean;
        callLogId?: string;
        // Session ASM (W-12): the defect this repair is raised FROM ("Raise work" on the defect's page). The server
        // links the defect to the repair exactly as the automatic remedial path does. Lift repairs only.
        defectId?: string;
        // Session ATZ (Item 3): the property platform work order this repair is raised from (the Work orders page).
        workOrderProposalId?: string;
      },
    ) =>
      request<RaisedJobResult>("/schedule/planned-repair", { method: "POST", body: JSON.stringify(body) }),
    conditionReport: (body: { assetId: string; description?: string; raisedBy?: string; twoManBooked?: boolean; callLogId?: string }) =>
      request<RaisedJobResult>("/schedule/condition-report", { method: "POST", body: JSON.stringify(body) }),
    // A lift OR a sales lead, never both and never neither: the route refuses the other two cases.
    // Session ARM (D88): `leadFromCall: true` with a callLogId (and no lift or lead) raises it for the
    // UNKNOWN CALLER on that call: the server makes the caller's sales lead, or reuses an older call's,
    // in the same transaction as the inspection.
    siteInspection: (body: {
      assetId?: string;
      prospectId?: string;
      reason: string;
      description?: string;
      raisedBy?: string;
      twoManBooked?: boolean;
      callLogId?: string;
      leadFromCall?: true;
    }) => request<RaisedJobResult>("/schedule/site-inspection", { method: "POST", body: JSON.stringify(body) }),
    // Session ANG: the tech visit -- the return visit a call-out diagnosed as beyond routine repair
    // work, charged to the customer as a FLAT DAY RATE (fees.ts techVisitDayRate, Session AOX; ELE's own
    // cost is hourly on the subcontractor card). `originCalloutId` is the call-out whose diagnosis
    // prompted it and is deliberately NOT the job's `calloutId` (see Job.originCalloutId in the
    // schema). `assignedTo` is optional because the office may raise one before deciding who takes
    // it; the route checks it against the same assignable-engineer rule the pickers use.
    techVisit: (body: { assetId: string; description: string; originCalloutId?: string; assignedTo?: string; raisedBy?: string; twoManBooked?: boolean; callLogId?: string }) =>
      request<RaisedJobResult>("/schedule/tech-visit", { method: "POST", body: JSON.stringify(body) }),
    // Session ANH: record, or clear, that the office booked a job as two-man, after it was raised.
    // Allowed at any live status, because the rate engine's un-booked two-man flag can only be
    // cleared honestly by correcting the entries or by recording that the job WAS booked two-man.
    // Session ASM (Phase 6, D83): name one more engineer on a job (up to three besides the primary; fills an
    // unnamed booked slot first), or take one off. The add answers with the same certification and capacity
    // warnings an assignment does, about the engineer being added.
    // Session RC1: record (or clear) that time past a technician visit's block was agreed with the office on the day.
    // Preview first; a commit that would move an approved week needs affectsApprovedWeeks (409 approved_weeks_affected).
    techOvertime: (id: string, body: { agreed: boolean; preview?: boolean; affectsApprovedWeeks?: boolean }) =>
      request<
        | { preview: true; before: { agreed: boolean; agreedAt: string | null; agreedBy: string | null }; agreed: boolean; message: string; approvedWeeksMoved: RateCardApprovedWeek[] }
        | { ok: true; agreed: boolean; message: string }
      >(`/schedule/jobs/${encodeURIComponent(id)}/tech-overtime`, { method: "POST", body: JSON.stringify(body) }),
    // Session TV2 (Director ruling, 29 October 2026): how the CUSTOMER is billed for time past a technician visit's five
    // hours: by the hour (the default) or as a further day. Allowed until the visit is approved; a draft already raised is
    // never repriced (409 already_billed). Preview first. The subcontractor pay side (techOvertime above) is separate.
    techVisitOverage: (id: string, body: { furtherDay: boolean; preview?: boolean }) =>
      request<
        | { preview: true; before: { furtherDay: boolean }; furtherDay: boolean; message: string; unchanged: boolean }
        | { ok: true; furtherDay: boolean; message: string }
      >(`/schedule/jobs/${encodeURIComponent(id)}/tech-visit-overage`, { method: "POST", body: JSON.stringify(body) }),
    addCrew: (id: string, engineerId: string) =>
      request<DispatchJob>(`/schedule/jobs/${encodeURIComponent(id)}/crew`, { method: "POST", body: JSON.stringify({ engineerId }) }),
    removeCrew: (id: string, engineerId: string) =>
      request<DispatchJob>(`/schedule/jobs/${encodeURIComponent(id)}/crew/${encodeURIComponent(engineerId)}`, { method: "DELETE" }),
    twoMan: (id: string, twoManBooked: boolean) =>
      request<{ jobId: string; twoManBooked: boolean }>(`/schedule/jobs/${encodeURIComponent(id)}/two-man`, {
        method: "PATCH",
        body: JSON.stringify({ twoManBooked }),
      }),
    // Set or clear a site inspection's discretionary charge (Session ALW's route, Session AMF's
    // first UI caller). Pence, as every money column on Job is. null clears it back to "never
    // priced"; 0 is the real recorded decision "attended, waived" and raises no invoice; above
    // zero raises a Xero DRAFT when the visit is approved. A figure of any kind needs a reason.
    inspectionCharge: (id: string, body: { agreedPricePence: number | null; agreedPriceReason?: string | null }) =>
      request<{ agreedPricePence: number | null; agreedPriceReason: string | null }>(
        `/schedule/jobs/${encodeURIComponent(id)}/inspection-charge`,
        { method: "PATCH", body: JSON.stringify(body) },
      ),
  },

  // The Scheduling page (the all-job-types week/month calendar). Distinct from `schedule`
  // (the engineer-lane dispatch board). Reschedule reuses schedule.reassign (PATCH /schedule/jobs/:id).
  scheduling: {
    calendar: (fromIso: string, toIso: string) =>
      request<SchedulingCalendar>(`/scheduling/calendar?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`, { noCache: true }),
    // The active engineers alone (Session AEB): the backdated-completion "who did the work"
    // picker. Engineer register rows (Job.assignedTo targets), not office User accounts.
    engineers: () => request<ScheduleEngineer[]>("/scheduling/engineers"),
    // Session SC7A: one PAGE of every job of every type, filtered and sorted on the server, with the true total.
    jobList: (opts: JobListPageOptions) => {
      const p = new URLSearchParams();
      if (opts.types && opts.types.length > 0) p.set("types", opts.types.join(","));
      if (opts.status && opts.status.length > 0) p.set("status", opts.status.join(","));
      if (opts.holdReason && opts.holdReason.length > 0) p.set("holdReason", opts.holdReason.join(","));
      for (const k of ["engineerId", "customerId", "siteId", "assetId", "from", "to", "q"] as const) {
        const v = opts[k];
        if (v) p.set(k, v);
      }
      if (opts.sort) {
        p.set("sort", opts.sort);
        p.set("dir", opts.dir ?? "asc");
      }
      p.set("limit", String(opts.limit));
      p.set("offset", String(opts.offset));
      return request<{ items: JobListItem[]; total: number }>(`/scheduling/jobs?${p.toString()}`, { noCache: true });
    },
    // Generate the forward PPM schedule. With no argument it runs across every active
    // contract (the office-wide button); with an assetId it generates for that lift only.
    generatePpm: (assetId?: string) =>
      request<{ created: number }>("/scheduling/generate-ppm", {
        method: "POST",
        body: JSON.stringify(assetId ? { assetId } : {}),
      }),
    // Clear the unstarted auto-generated PPM visits nobody has taken up so a fresh Generate PPM run re-anchors the
    // schedule on the corrected dates (Session P). Started/completed work is kept. Session BDG (M2): the default is a
    // PREVIEW (counts, nothing deleted); a visit the engineer accepted or that has a crew is never removed; one with an
    // engineer who has not accepted goes only with includeAssigned.
    clearAutoPpm: (opts: { preview?: boolean; includeAssigned?: boolean } = {}) =>
      request<ClearAutoPpmResult>(
        `/scheduling/ppm-visits?preview=${opts.preview === false ? "false" : "true"}&includeAssigned=${opts.includeAssigned === true ? "true" : "false"}`,
        { method: "DELETE" },
      ),
    // Session ARA (S-14): one day's dispatched work, grouped by engineer and put into a sensible
    // visit order. `date` is YYYY-MM-DD in the same UTC-calendar-day convention this page's own
    // cursor uses throughout; `engineerId` narrows to one engineer.
    dayRoute: (date: string, engineerId?: string) => {
      const params = new URLSearchParams({ date });
      if (engineerId) params.set("engineerId", engineerId);
      return request<DayRoute>(`/scheduling/day-route?${params.toString()}`, { noCache: true });
    },
  },

  quotes: {
    list: () => request<QuoteListItem[]>("/quotes"),
    // Session SC2C: one PAGE of the list, filtered, searched and sorted on the server, with the TRUE total (and the label of a loss-reason filter).
    listPage: (opts: QuoteListPageOptions) => {
      const params = new URLSearchParams();
      for (const k of ["ref", "customerId", "reason", "tier", "liftType", "month", "from", "to", "filter", "q"] as const) {
        const v = opts[k];
        if (v) params.set(k, v);
      }
      if (opts.sort) {
        params.set("sort", opts.sort);
        params.set("dir", opts.dir ?? "asc");
      }
      params.set("limit", String(opts.limit));
      params.set("offset", String(opts.offset));
      return request<{ items: QuoteListItem[]; total: number; reasonLabel?: string | null }>(`/quotes?${params.toString()}`);
    },
    // Session ASH (Q-7): every quote, project quotation and repair estimate, staged. Never cached: it summarises
    // records the office changes from many pages.
    pipeline: () => request<QuotePipeline>("/quotes/pipeline", { noCache: true }),
    // Session ASH (Q-10): owner and internal notes (office-only), and the office documents kept against a quote.
    owners: () => request<{ owners: QuoteOwnerOption[] }>("/quotes/owners", { noCache: true }),
    internal: (id: string) => request<QuoteInternal>(`/quotes/${encodeURIComponent(id)}/internal`, { noCache: true }),
    updateInternal: (id: string, body: { ownerId?: string | null; internalNotes?: string | null }) =>
      request<QuoteInternal>(`/quotes/${encodeURIComponent(id)}/internal`, { method: "PATCH", body: JSON.stringify(body) }),
    // Session AUJ: who signs the agreement when it is not the quote's contact.
    signatory: (id: string) => request<QuoteSignatoryView>(`/quotes/${encodeURIComponent(id)}/signatory`, { noCache: true }),
    setSignatory: (id: string, signatory: SignatoryInput | null) =>
      request<QuoteSignatoryView & { customerWins: boolean }>(`/quotes/${encodeURIComponent(id)}/signatory`, {
        method: "PUT",
        body: JSON.stringify({ signatory }),
      }),
    documents: (id: string) => request<{ documents: QuoteDocumentItem[] }>(`/quotes/${encodeURIComponent(id)}/documents`, { noCache: true }),
    uploadDocument: (id: string, file: File, label: string) => {
      const form = new FormData();
      form.append("label", label);
      form.append("file", file);
      return request<{ document: QuoteDocumentItem }>(`/quotes/${encodeURIComponent(id)}/documents`, { method: "POST", body: form });
    },
    deleteDocument: (id: string, docId: string) =>
      request<{ ok: boolean }>(`/quotes/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}`, { method: "DELETE" }),
    documentDownloadUrl: (id: string, docId: string) =>
      `/api/quotes/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/download`,
    // Session AAV (Loop 1): the quoting analytics aggregate. from/to are whole dates
    // (yyyy-mm-dd, inclusive) selecting the quote cohort by creation date.
    analytics: (range?: { from?: string; to?: string }) => {
      const params = new URLSearchParams();
      if (range?.from) params.set("from", range.from);
      if (range?.to) params.set("to", range.to);
      const qs = params.toString();
      return request<QuoteAnalytics>(`/quotes/analytics${qs ? `?${qs}` : ""}`);
    },
    // One quote with its full record (incl. the AI narrative column).
    // noCache (Session ARU): pass it where the answer is about to seed the calculator's form, which a
    // save then writes back over the stored quote. A change made elsewhere (another office screen, the
    // Connected AI's set_quote_price_override) does not clear this browser's cache, so a cached copy up
    // to five minutes old would be edited and saved back over it. Every other caller only displays.
    get: (id: string, opts?: { noCache?: boolean }) =>
      request<QuoteDetail>(`/quotes/${encodeURIComponent(id)}`, opts?.noCache ? { noCache: true } : undefined),
    // Regenerate the AI narrative for a quote (admin/office). regenerated is false when the
    // AI is not configured or generation failed (then quoteNarrative is the unchanged
    // existing value -- a transient failure never wipes a good narrative).
    regenerateNarrative: (id: string) =>
      request<{ quoteNarrative: string | null; regenerated: boolean }>(`/quotes/${encodeURIComponent(id)}/regenerate-narrative`, { method: "POST" }),
    preview: (body: QuotePreviewInput) =>
      request<QuotePreview>("/quotes/preview", { method: "POST", body: JSON.stringify(body) }),
    // Session AAU (Loop 3): read-only scenario matrix (every tier priced at each frequency).
    scenario: (body: QuotePreviewInput) =>
      request<{ rows: ScenarioRow[] }>("/quotes/scenario", { method: "POST", body: JSON.stringify(body) }),
    // Session AAU (Loop 4): advisory AI site-config assist. Suggests lifts from a description for the
    // office to review + apply. Throws ApiError (503) when the AI is not connected.
    configAssist: (description: string) =>
      request<ConfigAssistResult>("/quotes/config-assist", { method: "POST", body: JSON.stringify({ description }) }),
    create: (body: QuoteCreateInput) =>
      request<QuoteCreateResult>("/quotes", { method: "POST", body: JSON.stringify(body) }),
    // Send a DRAFT quote: flip it to pending and email the stored accept link (no calculator).
    send: (id: string) =>
      request<{ id: string; quoteRef: string; status: string; emailed: boolean; recipient: string | null; acceptUrl: string }>(
        `/quotes/${encodeURIComponent(id)}/send`,
        { method: "POST" },
      ),
    // Resend a sent (not-yet-accepted) quote: re-email the same accept link, reset to pending and
    // clear the Opened timestamp. No new record, same quote ref (Session AAG).
    resend: (id: string) =>
      request<{ id: string; quoteRef: string; status: string; emailed: boolean; recipient: string | null; acceptUrl: string }>(
        `/quotes/${encodeURIComponent(id)}/resend`,
        { method: "POST" },
      ),
    // Void a sent-but-unsigned quote's Signable envelope + cancel its draft contract, marking
    // the quote `voided`. A signed quote is rejected (409). The office then builds a new quote.
    voidEnvelope: (id: string) =>
      request<{ ok: boolean; status: string; envelopeVoided: boolean; envelopeNotCancelled?: boolean }>(`/quotes/${encodeURIComponent(id)}/void`, {
        method: "POST",
      }),
    // Mark a sent quote followed-up: snoozes its morning-action follow-up for 7 days (admin/office).
    markFollowedUp: (id: string) =>
      request<{ snoozedUntil: string }>(`/quotes/${encodeURIComponent(id)}/follow-up`, { method: "POST" }),
    // Session AAT: mark a quote lost with a required reason id + optional note; reopen clears it and
    // returns the quote to draft. A signed/accepted/voided quote is rejected (409).
    markLost: (id: string, body: { lostReasonId: string; note?: string }) =>
      request<{ id: string; quoteRef: string; status: string }>(`/quotes/${encodeURIComponent(id)}/lost`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    reopen: (id: string) =>
      request<{ id: string; quoteRef: string; status: string }>(`/quotes/${encodeURIComponent(id)}/reopen`, { method: "POST" }),
    // Session AAT: push a sent/expired quote's validity forward by the tenant's validity window; an
    // expired quote is revived to sent so the customer can accept the same link again.
    extendValidity: (id: string) =>
      request<{ id: string; quoteRef: string; status: string; validUntil: string | null }>(
        `/quotes/${encodeURIComponent(id)}/extend-validity`,
        { method: "POST" },
      ),
    // Session AAT: append-only version history (each version carries a field-level diff to the state
    // that followed it). Deleting a version (admin) never touches the live quote.
    versions: (id: string) =>
      request<{ quoteRef: string; versions: QuoteVersionItem[] }>(`/quotes/${encodeURIComponent(id)}/versions`),
    // Session ARF: correct an issued quote's price in place. Never emails the customer.
    priceOverride: (id: string) => request<QuotePriceOverrideView>(`/quotes/${encodeURIComponent(id)}/price-override`),
    previewPriceOverride: (id: string, body: { tier: QuoteOverrideTier; value: number }) =>
      request<{ view: QuotePriceOverrideView; after: QuotePriceTierSet }>(`/quotes/${encodeURIComponent(id)}/price-override/preview`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    setPriceOverride: (id: string, body: { tier: QuoteOverrideTier; value: number; reason: string }) =>
      request<QuotePriceOverrideView>(`/quotes/${encodeURIComponent(id)}/price-override`, { method: "POST", body: JSON.stringify(body) }),
    clearPriceOverride: (id: string) =>
      request<QuotePriceOverrideView>(`/quotes/${encodeURIComponent(id)}/price-override`, { method: "DELETE" }),
    deleteVersion: (id: string, versionId: string) =>
      request<{ ok: boolean }>(`/quotes/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}`, { method: "DELETE" }),
    // Session AAT: duplicate a quote into an independent DRAFT (fresh ref + token, no version history).
    duplicate: (id: string) =>
      request<{ id: string; quoteRef: string; status: string }>(`/quotes/${encodeURIComponent(id)}/duplicate`, { method: "POST" }),
    // Hard delete (admin or office). A draft quote deletes straight away; a quote that has
    // rendered a contract (SIGNED, or ACCEPTED with its ESA out) is blocked server-side unless
    // force is set -- the double-confirmation flow passes force:true after a type-to-confirm
    // step, which archives the contract and voids the Xero draft. Callers decide the flag with
    // quoteDeleteIsForced (src/lib/quoteActions.ts, Session ARP).
    delete: (id: string, force = false) =>
      request<ScrubSummary>(`/quotes/${encodeURIComponent(id)}`, {
        method: "DELETE",
        ...(force ? { body: JSON.stringify({ force: true }) } : {}),
      }),
    // Session AAO: reprice a quote at candidate per-lift frequencies for the confirm panel. Returns
    // each tier's annual + monthly at those frequencies (priced server-side by the ONE engine).
    frequencyPreview: (id: string, frequencies: string[]) =>
      request<QuoteFrequencyPreview>(`/quotes/${encodeURIComponent(id)}/frequency-preview`, {
        method: "POST",
        body: JSON.stringify({ frequencies }),
      }),
    // Session AAO: office gate. Confirm (or override) the frequency and send the ESA. Binds the
    // confirmed frequency to the contract + ESA + Xero draft. Throws ApiError on a per-step failure
    // (the quote stays unsent + pending for a retry).
    confirmFrequency: (id: string, frequencies: string[]) =>
      request<QuoteFrequencyConfirmResult>(`/quotes/${encodeURIComponent(id)}/confirm-frequency`, {
        method: "POST",
        body: JSON.stringify({ frequencies }),
      }),
  },

  // Session AAU (Loop 2): portfolio quoting. Group several site sub-quotes under one customer and
  // roll them up with the two-layer discount (per-site multi-lift + multi-site loyalty, capped 20%).
  quotePortfolios: {
    list: () => request<QuotePortfolioListItem[]>("/quote-portfolios"),
    get: (id: string) => request<QuotePortfolioDetail>(`/quote-portfolios/${encodeURIComponent(id)}`),
    create: (body: { name: string; customerId?: string; customerName?: string; quoteIds?: string[] }) =>
      request<{ id: string; name: string }>("/quote-portfolios", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: { name?: string; customerId?: string | null; customerName?: string | null }) =>
      request<{ ok: boolean }>(`/quote-portfolios/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    attachQuote: (id: string, quoteId: string) =>
      request<{ ok: boolean }>(`/quote-portfolios/${encodeURIComponent(id)}/quotes`, { method: "POST", body: JSON.stringify({ quoteId }) }),
    detachQuote: (id: string, quoteId: string) =>
      request<{ ok: boolean }>(`/quote-portfolios/${encodeURIComponent(id)}/quotes/${encodeURIComponent(quoteId)}`, { method: "DELETE" }),
    // detach-or-delete: deleteQuotes false detaches the sub-quotes and keeps them; true also hard-deletes
    // the deletable (draft/pending/sent, no contract) sub-quotes and detaches the rest.
    delete: (id: string, deleteQuotes = false) =>
      request<{ ok: boolean; deleted: number; detached: number }>(`/quote-portfolios/${encodeURIComponent(id)}`, {
        method: "DELETE",
        body: JSON.stringify({ deleteQuotes }),
      }),
  },

  // Session AAT: reusable quote templates (admin/office). A template stores the calculator config
  // (no customer/site identity) for "New quote from template".
  quoteTemplates: {
    list: () => request<QuoteTemplate[]>("/quote-templates"),
    create: (name: string, config: Record<string, unknown>) =>
      request<QuoteTemplate>("/quote-templates", { method: "POST", body: JSON.stringify({ name, config }) }),
    update: (id: string, patch: { name?: string; config?: Record<string, unknown> }) =>
      request<QuoteTemplate>(`/quote-templates/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(patch) }),
    delete: (id: string) =>
      request<{ ok: boolean }>(`/quote-templates/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },

  // Session AAT: the quote lost-reason lookup (admin/office). ?active=true limits to the picker set.
  quoteLostReasons: {
    list: (activeOnly = false) =>
      request<QuoteLostReason[]>(`/quote-lost-reasons${activeOnly ? "?active=true" : ""}`),
    create: (label: string) =>
      request<QuoteLostReason>("/quote-lost-reasons", { method: "POST", body: JSON.stringify({ label }) }),
    update: (id: string, patch: { label?: string; active?: boolean }) =>
      request<QuoteLostReason>(`/quote-lost-reasons/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(patch) }),
    delete: (id: string) =>
      request<{ ok: boolean }>(`/quote-lost-reasons/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },

  invoices: {
    list: () => request<InvoiceListItem[]>("/invoices"),
    // Session SC2C: one PAGE of the list, filtered, searched and sorted on the server, with the TRUE total of every matching invoice.
    listPage: (opts: { status?: string; customerId?: string; q?: string; sort?: string; dir?: "asc" | "desc"; limit: number; offset: number }) => {
      const params = new URLSearchParams();
      if (opts.status) params.set("status", opts.status);
      if (opts.customerId) params.set("customerId", opts.customerId);
      if (opts.q) params.set("q", opts.q);
      if (opts.sort) {
        params.set("sort", opts.sort);
        params.set("dir", opts.dir ?? "asc");
      }
      params.set("limit", String(opts.limit));
      params.set("offset", String(opts.offset));
      return request<{ items: InvoiceListItem[]; total: number }>(`/invoices?${params.toString()}`);
    },
    // Single-invoice detail (Session AO Item 1): cached fields + reverse job/call-out
    // link + best-effort live Xero detail (line items + amount paid/outstanding).
    // noCache: the payment receipt line changes by itself (the sweep sends it), so a saved copy would show yesterday's answer.
    get: (id: string) => request<InvoiceDetail>(`/invoices/${encodeURIComponent(id)}`, { noCache: true }),
    // Authorise a Xero DRAFT invoice (admin only). Returns the updated row.
    approve: (id: string) =>
      request<InvoiceListItem>(`/invoices/${encodeURIComponent(id)}/approve`, { method: "POST" }),
    // Raise a one-off draft invoice (office/admin, Session AQF's button on the Invoices page).
    // DRAFT only: the route hardcodes Status "DRAFT" and never authorises, so the office still
    // reviews and authorises it in Xero, and a mistake is voided THERE (the platform has no void).
    createDraft: (body: OneOffDraftInvoiceInput) =>
      request<OneOffDraftInvoiceResult>("/invoices", { method: "POST", body: JSON.stringify(body) }),
    // Aged debtor report (Session AK Item 4): outstanding invoices by overdue age band.
    agedDebtors: (opts?: { onStop?: boolean }) => request<AgedDebtorReport>(opts?.onStop ? "/invoices/aged-debtors?onStop=true" : "/invoices/aged-debtors"),

    // ---- Phase 8 (Session ATJ) ----
    // M-9b: raise a Stripe payment link and draft (never send) the customer email carrying it.
    // Previewed by default; pass preview: false to actually raise the link.
    paymentLink: (id: string, preview: boolean = true) =>
      request<PaymentLinkResult>(`/invoices/${encodeURIComponent(id)}/payment-link`, {
        method: "POST",
        body: JSON.stringify({ preview }),
      }),
    // M-6: generate an account statement PDF and draft (never send) the covering email.
    statement: (customerId: string, preview: boolean = true) =>
      request<StatementResult>(`/invoices/statement/${encodeURIComponent(customerId)}`, {
        method: "POST",
        body: JSON.stringify({ preview }),
      }),
    // M-8: raise a Xero DRAFT credit note (never authorised, never allocated). The reason is
    // internal only.
    creditNote: (id: string, amountExVat: number, reason: string, preview: boolean = true) =>
      request<CreditNoteResult>(`/invoices/${encodeURIComponent(id)}/credit-note`, {
        method: "POST",
        body: JSON.stringify({ amountExVat, reason, preview }),
      }),
    // M-7: draft (never send) a chaser for ONE invoice, on demand. Reuses the same
    // isChaseable/buildChaserEmail the automatic daily sweep uses.
    chaser: (id: string, preview: boolean = true) =>
      request<ChaserResult>(`/invoices/${encodeURIComponent(id)}/chaser`, {
        method: "POST",
        body: JSON.stringify({ preview }),
      }),
  },

  billing: {
    // Run the Xero overdue-invoice discovery now (admin only). The daily cron also runs
    // it at 06:00 UTC. Throws ApiError(503) when Xero is not connected.
    syncInvoices: () => request<InvoiceSyncResult>("/billing/sync-invoices", { method: "POST" }),
  },

  contracts: {
    list: () => request<ContractListItem[]>("/contracts"),
    // Session SC2C: one PAGE of the list, filtered and sorted on the server, with the TRUE total and the counts the page prints (over the whole book).
    listPage: (opts: { filter?: "due-90-days" | "xero-billing"; customerId?: string; sort?: string; dir?: "asc" | "desc"; limit: number; offset: number }) => {
      const params = new URLSearchParams();
      if (opts.filter) params.set("filter", opts.filter);
      if (opts.customerId) params.set("customerId", opts.customerId);
      if (opts.sort) {
        params.set("sort", opts.sort);
        params.set("dir", opts.dir ?? "asc");
      }
      params.set("limit", String(opts.limit));
      params.set("offset", String(opts.offset));
      return request<{ items: ContractListItem[]; total: number; counts: { dueSoon: number; xeroWarning: number | null; xeroInfo: number | null } }>(`/contracts?${params.toString()}`);
    },
    get: (id: string) => request<ContractDetail>(`/contracts/${encodeURIComponent(id)}`),
    // Default engineer override for THIS contract's PPM visits (Session ARA, S-4; built to D84's
    // standing RECOMMENDATION, not a confirmed Director ruling). A small, separately-registered
    // pair (contractDefaultEngineer.routes.ts) rather than folded into ContractDetail/get above,
    // since the general PATCH /:id route that backs `get`'s edit path already carries a
    // tightly-scoped schema and money-safety gates unrelated to this field.
    defaultEngineer: (id: string) =>
      request<{ contractId: string; defaultEngineerId: string | null }>(`/contracts/${encodeURIComponent(id)}/default-engineer`),
    updateDefaultEngineer: (id: string, defaultEngineerId: string | null) =>
      request<{ contractId: string; defaultEngineerId: string | null }>(`/contracts/${encodeURIComponent(id)}/default-engineer`, {
        method: "PATCH",
        body: JSON.stringify({ defaultEngineerId }),
      }),
    // Tier allowance usage across active contracts (Session AAW): remaining inclusive parts + call-outs
    // per contract-year, with an over-limit flag so the office bills the excess by hand.
    allowances: () => request<ContractAllowanceRow[]>("/contracts/allowances"),
    renewals: () => request<RenewalListResponse>("/contracts/renewals"),
    // D7 (Session AIC): the propose-then-confirm renewal ledger.
    renewalProposals: () => request<RenewalProposal[]>("/contracts/renewals/proposals"),
    proposeRenewals: () => request<{ proposed: number }>("/contracts/renewals/propose", { method: "POST" }),
    confirmRenewal: (id: string) =>
      request<{ ok: true; proposedAnnual: number }>(`/contracts/renewals/proposals/${encodeURIComponent(id)}/confirm`, { method: "POST" }),
    skipRenewalProposal: (id: string) =>
      request<{ ok: true }>(`/contracts/renewals/proposals/${encodeURIComponent(id)}/skip`, { method: "POST" }),
    restoreRenewalProposal: (id: string) =>
      request<{ ok: true }>(`/contracts/renewals/proposals/${encodeURIComponent(id)}/restore`, { method: "POST" }),
    // RULE 1: the hard delete needs a reachable surface, not just an endpoint. It is 409-blocked once
    // the price has been applied, so it only ever removes a proposal that changed nothing.
    deleteRenewalProposal: (id: string) =>
      request<{ ok: true }>(`/contracts/renewals/proposals/${encodeURIComponent(id)}`, { method: "DELETE" }),
    renewalLetter: (id: string) =>
      request<RenewalDraft>(`/contracts/${encodeURIComponent(id)}/renewal-letter`, { method: "POST" }),
    sendRenewal: (id: string, body: { subject: string; body: string }) =>
      request<{ drafted: boolean }>(`/contracts/${encodeURIComponent(id)}/send-renewal`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // Generate + download the branded renewal-letter PDF from the (edited) subject + body.
    renewalLetterPdf: (id: string, payload: { subject: string; body: string }) => downloadRenewalLetterPdf(id, payload),
    // Delete a contract. The server returns 409 (ApiError with a plain-English message) when the
    // contract is active or has invoices / jobs / LOLER reports; only an unused draft can be removed.
    remove: (id: string) => request<void>(`/contracts/${encodeURIComponent(id)}`, { method: "DELETE" }),
    // Recurring-invoicing enrolment (Session AEC). Enabling stamps autoInvoiceFrom = the first
    // period boundary strictly after today; 409 with a plain message when the contract is not
    // active or has no start/commencement date.
    setAutoInvoice: (id: string, on: boolean) =>
      request<{ xeroAutoInvoice: boolean; autoInvoiceFrom: string | null }>(
        `/contracts/${encodeURIComponent(id)}/auto-invoice`,
        { method: "POST", body: JSON.stringify({ on }) },
      ),
    // ---- Lift coverage ("Manage lifts", Session ACF) ----
    // The snapshot: covered lifts, uncontracted lifts available at the site, and the amendment history.
    coverage: (id: string) => request<ContractCoverage>(`/contracts/${encodeURIComponent(id)}/coverage`),
    // Preview the old vs new price for a proposed covered-asset set (admin). No write.
    coveragePreview: (id: string, assetIds: string[], effectiveDate?: string) =>
      request<ContractCoveragePreview>(`/contracts/${encodeURIComponent(id)}/coverage/preview`, {
        method: "POST",
        body: JSON.stringify({ assetIds, ...(effectiveDate ? { effectiveDate } : {}) }),
      }),
    // Apply a coverage change (admin): moves activeContractId, reprices the contract, writes an
    // amendment. effectiveDate ("YYYY-MM-DD") must be today or later; returns the fresh snapshot.
    coverageApply: (id: string, body: { assetIds: string[]; effectiveDate: string; reason?: string }) =>
      request<ContractCoverage>(`/contracts/${encodeURIComponent(id)}/coverage`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // ---- "Link coverage only" (Session AJJ) ----
    // A separate pair of endpoints, not a mode on the two above: assetIds here are the lifts to LINK
    // (add-only, so a removal cannot be expressed), there is no effective date because a pointer
    // correction states what was already true, and no pricing runs on either call.
    coverageLinkPreview: (id: string, assetIds: string[]) =>
      request<ContractCoverageLinkPreview>(`/contracts/${encodeURIComponent(id)}/coverage/link/preview`, {
        method: "POST",
        body: JSON.stringify({ assetIds }),
      }),
    coverageLinkApply: (id: string, body: { assetIds: string[]; reason?: string }) =>
      request<ContractCoverage>(`/contracts/${encodeURIComponent(id)}/coverage/link`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // Revert the MOST RECENT amendment (admin): restores the prior coverage + price. 409 if a newer
    // amendment exists (revert those first). A linkOnly amendment reverts its coverage only, never
    // the money (its stored figure is a link-time snapshot, not a price the link displaced).
    coverageRevert: (id: string, amendmentId: string) =>
      request<void>(`/contracts/${encodeURIComponent(id)}/amendments/${encodeURIComponent(amendmentId)}`, { method: "DELETE" }),
    // ---- "Start a service now" (Session AMC) ----
    startServiceNowReadiness: (id: string) => request<StartServiceNowSnapshot>(`/contracts/${encodeURIComponent(id)}/start-service-now`),
    startServiceNow: (
      id: string,
      body: { engineerId?: string; billing: StartServiceNowBilling; agreedPricePence?: number; agreedPriceReason?: string },
    ) =>
      request<StartServiceNowResult>(`/contracts/${encodeURIComponent(id)}/start-service-now`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // ---- Register confirmation gate (Session AIG, "Renewal A") ----
    // The read view + the confirm stamp. Correcting a flagged field is NOT here: it goes through the
    // existing `assets` resource (src/lib/resources.ts), the same PATCH the Lifts register uses.
    registerReview: (id: string) => request<ContractRegisterReview>(`/contracts/${encodeURIComponent(id)}/register-review`),
    confirmRegisterReview: (id: string) =>
      request<ContractRegisterReview>(`/contracts/${encodeURIComponent(id)}/register-review/confirm`, { method: "POST" }),
    // ---- The customer renewal link (Session AII, "Renewal B") ----
    // The token is a bearer credential for an unauthenticated page, so it crosses to a signed-in
    // office session and nowhere else. Issuing never rotates a token already with a customer.
    renewalLink: (id: string) => request<ContractRenewalLink>(`/contracts/${encodeURIComponent(id)}/renewal-link`),
    // Session ANC: `send` asks the platform to EMAIL the customer their invitation, not just mint the
    // link. It is opt-in and omitted by default, so a caller that does not ask gets a Gmail DRAFT,
    // never a send: on a fresh mint, and since Session APA on a re-press when no invitation reached
    // the customer this cycle and none is already waiting in Drafts (a renewal letter does not count:
    // it is not the invitation). Sending on a link that already exists is the RESEND path.
    //
    // The body is only serialised when a flag is actually passed: this route accepted no body at all
    // before ANC, and apiRequest sets the JSON content type only when there is a body to go with it.
    issueRenewalLink: (id: string, opts?: { send?: boolean }) =>
      request<ContractRenewalLink>(`/contracts/${encodeURIComponent(id)}/renewal-link`, {
        method: "POST",
        ...(opts ? { body: JSON.stringify(opts) } : {}),
      }),
    // ---- Start a spent renewal again (Session AQG, Director decision 22 September 2026) ----
    // The customer used their link and the office then voided the paperwork it raised. This reopens
    // THAT link, keeping the record that they used it; the reason is stored on the contract and
    // audited. Refused (409) while the paperwork is live, once a renewal is signed, and on a link that
    // was never used.
    // Session BAK: the office's own record that the customer was told the new charge (the ledger reads it as disclosed).
    disclosureRecords: (id: string) =>
      request<{ records: RenewalDisclosureRecordDto[]; ledger: RenewalDisclosureLedger | null }>(`/contracts/${encodeURIComponent(id)}/renewal-disclosure-records`, { noCache: true }),
    addDisclosureRecord: (id: string, body: { disclosedOn: string; method: DisclosureMethod; emailLink?: string; note?: string }) =>
      request<{ record: RenewalDisclosureRecordDto; ledger: RenewalDisclosureLedger | null }>(`/contracts/${encodeURIComponent(id)}/renewal-disclosure-records`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    removeDisclosureRecord: (id: string, recordId: string) =>
      request<{ ok: true; ledger: RenewalDisclosureLedger | null }>(`/contracts/${encodeURIComponent(id)}/renewal-disclosure-records/${encodeURIComponent(recordId)}`, { method: "DELETE" }),
    restartRenewal: (id: string, body: { reason: string }) =>
      request<{ id: string; restartedAt: string; actionedAt: string | null; spent: boolean }>(
        `/contracts/${encodeURIComponent(id)}/renewal-restart`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    // RULE 1's way back from the restart above: clears it, so the link is spent again exactly as it
    // was. Refused (409) once the customer has used the reopened link.
    undoRenewalRestart: (id: string) =>
      request<{ id: string; spent: boolean; restartedAt: null }>(`/contracts/${encodeURIComponent(id)}/renewal-restart`, {
        method: "DELETE",
      }),
    // ---- The renewal visit-frequency sign-off (Session AIK, Director decision 3) ----
    // A cadence change asked for at renewal does not take effect on signature; the office decides it
    // here. `id` is the contract that was RENEWED, and the decision is applied to its successor.
    renewalFrequency: (id: string) => request<RenewalFrequencyReview>(`/contracts/${encodeURIComponent(id)}/renewal-frequency`),
    confirmRenewalFrequency: (id: string, body: { frequency: string; annualValue?: number }) =>
      request<{ ok: true; applied: boolean; frequency: string; annualValue: number; successorContractId: string; review: RenewalFrequencyReview | null }>(
        `/contracts/${encodeURIComponent(id)}/renewal-frequency/confirm`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    // ---- The renewal price override (Session AKO, Director decision) ----
    // A manual figure that replaces the CPI + escalator calculation for this contract's next renewal
    // cycle. Clearable back to "no override" (DELETE), so a mistaken figure can always be undone.
    renewalPriceOverride: (id: string) =>
      request<RenewalPriceOverrideView>(`/contracts/${encodeURIComponent(id)}/renewal-price-override`),
    setRenewalPriceOverride: (id: string, body: { annualValue: number; reason?: string }) =>
      request<RenewalPriceOverrideView>(`/contracts/${encodeURIComponent(id)}/renewal-price-override`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    clearRenewalPriceOverride: (id: string) =>
      request<RenewalPriceOverrideView>(`/contracts/${encodeURIComponent(id)}/renewal-price-override`, { method: "DELETE" }),
    // Session AER (admin): record that maintenance cover has stopped. The SINGLE writer of the
    // lapse date, so it is what opens the 30-day call-out grace window. The end date cannot be in
    // the future (a scheduled termination is not built), and the reason is required.
    end: (id: string, body: { effectiveDate: string; reason: string }) =>
      request<{ id: string; status: string; lapsedAt: string | null; endReason: string | null }>(
        `/contracts/${encodeURIComponent(id)}/end`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    // Undo an End done by mistake. Clears the lapse stamp entirely, so no stale grace window is left.
    // Since Session AHN it also clears any cancellation-notice record.
    reopen: (id: string) =>
      request<{ id: string; status: string; lapsedAt: string | null; endReason: string | null }>(
        `/contracts/${encodeURIComponent(id)}/reopen`,
        { method: "POST" },
      ),
    // ---- Formal cancellation with written notice (Session AHN, admin) ----
    // The letter PREVIEW opens in a new tab (inline PDF, commits nothing); the reason key comes
    // from ContractDetail.cancellation.reasons.
    cancellationLetterHref: (id: string, reason: string) =>
      `/api/contracts/${encodeURIComponent(id)}/cancellation-letter?reason=${encodeURIComponent(reason)}`,
    // Commit: renders the letter, creates a Gmail DRAFT in info@ with the PDF attached (never
    // sent), then records the notice. A Clause 5.2 reason ends cover the same day; a Clause 5.1
    // reason leaves the contract active until the notice period expires (the daily sweep ends it).
    cancel: (id: string, body: { reason: string; notes?: string }) =>
      request<{ drafted: boolean; to: string; cc: string | null; immediate: boolean; terminationDate: string; status: string; filename: string }>(
        `/contracts/${encodeURIComponent(id)}/cancel`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    // Withdraw a pending Clause 5.1 notice before its effective date (the contract is still active).
    cancellationWithdraw: (id: string) =>
      request<{ withdrawn: boolean; message: string }>(`/contracts/${encodeURIComponent(id)}/cancellation/withdraw`, { method: "POST" }),
    // ---- Xero billing (Session ARY, Item 4) ----
    // The verdict view: is Xero's own repeating invoice billing what the contract says. Reads the
    // platform's cache only; it never calls Xero itself (that is `xeroBillingTemplates`/`xeroBillingCheck` below).
    xeroBilling: (id: string) => request<XeroBillingView>(`/contracts/${encodeURIComponent(id)}/xero-billing`),
    // The picker's candidates, read LIVE from Xero (can fail 409 xero_not_connected/xero_refused or
    // 502 xero_unavailable; the caller shows the server's own `message`). noCache: a stale list of
    // Xero's own repeating invoices would offer a template already linked or deleted elsewhere.
    xeroBillingTemplates: (id: string) =>
      request<XeroTemplateCandidates>(`/contracts/${encodeURIComponent(id)}/xero-billing/templates`, { noCache: true }),
    xeroBillingLink: (id: string, xeroRepeatingInvoiceId: string) =>
      request<{ link: XeroBillingLink; view: XeroBillingView }>(`/contracts/${encodeURIComponent(id)}/xero-billing/links`, {
        method: "POST",
        body: JSON.stringify({ xeroRepeatingInvoiceId }),
      }),
    xeroBillingUnlink: (id: string, linkId: string) =>
      request<{ unlinked: true; view: XeroBillingView }>(
        `/contracts/${encodeURIComponent(id)}/xero-billing/links/${encodeURIComponent(linkId)}`,
        { method: "DELETE" },
      ),
    // The billing mode when there is no Xero template linked: "not_repeating" records HOW it is
    // billed instead (a plain-English reason, 3 to 500 chars); "unset" clears that record. Refused
    // 409 links_exist while a template is linked.
    xeroBillingSetMode: (id: string, body: { mode: "not_repeating"; reason: string } | { mode: "unset" }) =>
      request<{ view: XeroBillingView }>(`/contracts/${encodeURIComponent(id)}/xero-billing/mode`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    // ---- Replacement agreement (Session BBZ): preview unless preview is false; the commit saves a DRAFT quotation only ----
    replacementAgreement: (id: string, signatory: { name: string; email: string }, preview: boolean) =>
      request<ReplacementAgreementResult>(`/contracts/${encodeURIComponent(id)}/replacement-agreement`, {
        method: "POST",
        body: JSON.stringify({ signatory, preview }),
      }),
    // ---- Guvnor billing (Session BBZ) ----
    // Every write previews unless preview is false. noCache: the view feeds the switch and the date actions.
    guvnorBilling: (id: string) => request<GuvnorBillingView>(`/contracts/${encodeURIComponent(id)}/guvnor-billing`, { noCache: true }),
    guvnorBillingSwitchOn: (id: string, nextInvoiceDate: string, preview: boolean) =>
      request<GuvnorSwitchPreview>(`/contracts/${encodeURIComponent(id)}/guvnor-billing/switch-on`, {
        method: "POST",
        body: JSON.stringify({ nextInvoiceDate, preview }),
      }),
    guvnorBillingSwitchOff: (id: string, reason: string) =>
      request<{ modeAfter: string }>(`/contracts/${encodeURIComponent(id)}/guvnor-billing/switch-off`, {
        method: "POST",
        body: JSON.stringify({ reason, preview: false }),
      }),
    guvnorBillingRun: (id: string, preview: boolean) =>
      request<GuvnorRunResult>(`/contracts/${encodeURIComponent(id)}/guvnor-billing/run`, { method: "POST", body: JSON.stringify({ preview }) }),
    guvnorBillingDate: (id: string, rowId: string, action: "skip" | "restore" | "release", reason?: string) =>
      request<{ invoiceDate: string }>(`/contracts/${encodeURIComponent(id)}/guvnor-billing/dates/${encodeURIComponent(rowId)}/${action}`, {
        method: "POST",
        body: JSON.stringify(action === "skip" ? { reason, preview: false } : { preview: false }),
      }),
    // "Check now": re-reads every linked template from Xero and re-derives the verdict.
    xeroBillingCheck: (id: string) =>
      request<{ refresh: { checked: number; failed: number }; view: XeroBillingView }>(
        `/contracts/${encodeURIComponent(id)}/xero-billing/check`,
        { method: "POST" },
      ),
  },

  callouts: {
    // assetId (Session SC2B): one lift's call-outs only. Without it, every call-out, as before.
    // noCache (Clearance 3): call-out state (assigned, attended, the SLA clock, entrapment) is changed by engineers, the
    // sweep and webhooks while a page is open, so every call-out read on this object goes to the server, never to the
    // request cache. The Call-outs list and the hub are the screens somebody watches during an emergency.
    list: (opts?: { assetId?: string }) =>
      request<CalloutListItem[]>(opts?.assetId ? `/callouts?assetId=${encodeURIComponent(opts.assetId)}` : "/callouts", { noCache: true }),
    // Session SC2C: one PAGE of the list, filtered and sorted on the server, with the TRUE total of every matching call-out.
    listPage: (opts: CalloutListPageOptions) => {
      const params = new URLSearchParams();
      if (opts.status) params.set("status", opts.status);
      if (opts.siteId) params.set("siteId", opts.siteId);
      if (opts.from) params.set("from", opts.from);
      if (opts.to) params.set("to", opts.to);
      if (opts.q) params.set("q", opts.q);
      if (opts.sort) {
        params.set("sort", opts.sort);
        params.set("dir", opts.dir ?? "asc");
      }
      params.set("limit", String(opts.limit));
      params.set("offset", String(opts.offset));
      return request<{ items: CalloutListItem[]; total: number }>(`/callouts?${params.toString()}`, { noCache: true });
    },
    // Session AUT: the SLA holds on a call-out's response clock (place, release, correct, remove), each audited.
    slaHolds: (id: string) => request<SlaHoldView>(`/callouts/${encodeURIComponent(id)}/sla-holds`, { noCache: true }),
    placeSlaHold: (id: string, body: { reason: SlaHoldReason; reasonOther?: string | null; heldFrom?: string; releasedAt?: string | null }) =>
      request<SlaHoldView>(`/callouts/${encodeURIComponent(id)}/sla-holds`, { method: "POST", body: JSON.stringify(body) }),
    releaseSlaHold: (id: string, body?: { releasedAt?: string }) =>
      request<SlaHoldView>(`/callouts/${encodeURIComponent(id)}/sla-holds/release`, { method: "POST", body: JSON.stringify(body ?? {}) }),
    correctSlaHold: (id: string, holdId: string, body: { reason?: SlaHoldReason; reasonOther?: string | null; heldFrom?: string; releasedAt?: string | null }) =>
      request<SlaHoldView>(`/callouts/${encodeURIComponent(id)}/sla-holds/${encodeURIComponent(holdId)}`, { method: "PATCH", body: JSON.stringify(body) }),
    removeSlaHold: (id: string, holdId: string) =>
      request<SlaHoldView>(`/callouts/${encodeURIComponent(id)}/sla-holds/${encodeURIComponent(holdId)}`, { method: "DELETE" }),
    create: (body: CalloutCreateInput) =>
      request<CalloutCreateResult>("/callouts", { method: "POST", body: JSON.stringify(body) }),
    emergency: (body: EmergencyCalloutInput) =>
      request<EmergencyCalloutResult>("/callouts/emergency", { method: "POST", body: JSON.stringify(body) }),
    // Session ASW (D22): the caller check, shown on the confirm before anything is logged. The intake runs it again.
    emergencyCallerMatch: (body: { customerName: string; customerEmail: string; customerPhone?: string; siteAddress: string }) =>
      request<CallerMatchResult>("/callouts/emergency/caller-match", { method: "POST", body: JSON.stringify(body) }),
    // Log an emergency entrapment (person trapped in a lift; Session AK Item 10). Alerts the office +
    // active engineers immediately and starts the 60-minute SLA.
    entrapment: (body: { assetId: string; callerName?: string; description?: string; callLogId?: string }) =>
      request<CalloutListItem>("/callouts/entrapment", { method: "POST", body: JSON.stringify(body) }),
    // Session AQY (D-AQX-4): when an open dispatch job exists, the route also cancels IT atomically
    // and accepts the optional "attended, then called off" travel record; travelLogged/
    // travelSkippedReason are present only on that branch (the original call-out-only cancel, when
    // there is no dispatch job yet, returns the plain CalloutListItem unchanged).
    cancel: (id: string, body?: { attended?: boolean; travelMins?: number; arrivalTime?: string; notes?: string }) =>
      request<CalloutListItem & { travelLogged?: boolean; travelSkippedReason?: string | null; travelNotRechecked?: string[] }>(`/callouts/${encodeURIComponent(id)}/cancel`, {
        method: "PATCH",
        body: JSON.stringify(body ?? {}),
      }),
    // Mark complete (stamps completedAt). A completed emergency call-out triggers the
    // post-service NPS survey 24 hours later.
    complete: (id: string) =>
      request<CalloutListItem>(`/callouts/${encodeURIComponent(id)}/complete`, { method: "PATCH" }),
    // Best-effort drive-time estimate (minutes, rounded to 15) from the ELE base to a
    // site address, to pre-fill the emergency form's travel dropdown. null when Maps is
    // not set up or the lookup fails.
    travelEstimate: (address: string) =>
      request<{ travelMins: number | null }>(`/callouts/travel-estimate?address=${encodeURIComponent(address)}`),
    // The server's own answer to "would a call-out on this lift bill in contract?" (the 30-day lapse
    // grace included), for the Log call-out estimate (Session AVJ). Never re-derived on the client.
    billingBasis: (assetId: string) =>
      request<{ inContract: boolean; tier: string | null; basis: string; terminatedForCause?: TerminatedForCauseNote | null }>(`/callouts/billing-basis?assetId=${encodeURIComponent(assetId)}`),
    // Active engineers for the assign dropdown (admin only).
    engineers: () => request<AssignableEngineer[]>("/callouts/engineers"),
    // Assign an engineer (admin only): records the engineer, moves the call-out to
    // in_progress, and creates the linked engineer-PWA job.
    assign: (id: string, engineerId: string) =>
      request<CalloutListItem>(`/callouts/${encodeURIComponent(id)}/assign`, { method: "PATCH", body: JSON.stringify({ engineerId }) }),
    // Hard delete (admin only): removes the call-out, deletes its DRAFT Xero invoice
    // and deactivates its Stripe link. Returns what was scrubbed.
    delete: (id: string) => request<ScrubSummary>(`/callouts/${encodeURIComponent(id)}`, { method: "DELETE" }),
    // Single call-out detail incl. the computed charge (for the office charge panel
    // and the photo gallery).
    get: (id: string) => request<CalloutDetail>(`/callouts/${encodeURIComponent(id)}`, { noCache: true }),
    // Compute and store the office-confirmed Schedule of Fees charge. Returns the
    // detail with the fresh breakdown.
    charge: (id: string, body: CalloutChargeInput) =>
      request<CalloutDetail>(`/callouts/${encodeURIComponent(id)}/charge`, { method: "PUT", body: JSON.stringify(body) }),
    // Raise a Xero DRAFT invoice from the computed charge. Refuses (409) when an
    // invoice already exists for the call-out (the office reconciles in Xero). DRAFT only.
    createInvoice: (id: string) =>
      request<CalloutInvoiceResult>(`/callouts/${encodeURIComponent(id)}/invoice`, { method: "POST", body: JSON.stringify({}) }),
    // Manually link this call-out to an existing Xero invoice from the cache (Session AO Item 3),
    // for the historical backlog. Stores the invoice's Xero id + number on the call-out; never
    // creates or modifies a Xero invoice. unlink clears the reference (the invoice is untouched).
    linkInvoice: (id: string, invoiceId: string) =>
      request<{ ok: true; linkedInvoice: { invoiceCacheId: string; invoiceNumber: string | null; xeroInvoiceId: string } }>(`/callouts/${encodeURIComponent(id)}/link-invoice`, { method: "POST", body: JSON.stringify({ invoiceId }) }),
    unlinkInvoice: (id: string) =>
      request<{ ok: true }>(`/callouts/${encodeURIComponent(id)}/link-invoice`, { method: "DELETE" }),
    // Session AEY: the Call-Out Attendance Report. reportPreview returns the authoritative charge
    // for the live UI (no browser money math); generateReport creates/updates the CallOut, raises a
    // Xero DRAFT (reusing an existing one), renders the PDF and (when save) files it to Drive,
    // returning the PDF as base64 plus the reference and the Xero result.
    reportPreview: (body: CalloutReportPreviewInput) =>
      request<CalloutReportPreviewResult>("/callouts/report/preview", { method: "POST", body: JSON.stringify(body) }),
    generateReport: (body: CalloutReportInput, save: boolean) =>
      request<CalloutReportResult>(`/callouts/report?save=${save ? "true" : "false"}`, { method: "POST", body: JSON.stringify(body) }),
    // Session AHJ: correct the reference on an issued attendance report. It does NOT change the PDF
    // that was already rendered and filed (the reference is baked into those bytes); regenerating
    // is what produces a document that agrees.
    updateReportRef: (id: string, reportRef: string) =>
      request<{ ok: true; reportRef: string; changed: boolean }>(`/callouts/${encodeURIComponent(id)}/report-ref`, { method: "PATCH", body: JSON.stringify({ reportRef }) }),
    // Session AHJ: the filed report PDF, streamed from Drive through the authenticated proxy (the
    // raw Drive link never reaches the browser). An href, opened with window.open, matching the
    // platform's convention for a plain authenticated GET the browser can navigate to.
    reportPdfHref: (id: string) => `/api/callouts/${encodeURIComponent(id)}/report-pdf`,
    // Session AJW, rebuilt Session AQY (D-AQX-3): RELEASE the filed report PDF to the customer --
    // opens portal visibility, drafts (never sends) the completion email and, for an eligible
    // emergency call-out, drafts the NPS survey too, all in the one action. Was an immediate
    // sendGmailHtml with no hold state; now mirrors jobsheets.approve below, drafting to Gmail for
    // the office to review and send. 409 when nothing is filed yet, 422 when there is no customer
    // email on file.
    releaseReport: (id: string) =>
      request<{ releasedAt: string; releasedTo: string; alreadyReleased: boolean; draftNote?: string; npsNote?: string | null }>(
        `/callouts/${encodeURIComponent(id)}/release-report`,
        { method: "POST", body: JSON.stringify({}) },
      ),
  },

  defects: {
    // assetId (Session ADI): the lift detail page's Defects section fetches just that lift's defects.
    list: (status: DefectStatusFilter = "all", assetId?: string) =>
      request<DefectListItem[]>(`/defects?status=${encodeURIComponent(status)}${assetId ? `&assetId=${encodeURIComponent(assetId)}` : ""}`),
    // Session SC2C: one PAGE of the list, filtered, searched and sorted on the server, with the TRUE total and the sites whose out-of-service
    // client notice is drafted and waiting (the page's banner).
    listPage: (opts: DefectListPageOptions) => {
      const params = new URLSearchParams({ status: opts.status });
      if (opts.category) params.set("category", opts.category);
      if (opts.riddor) params.set("riddor", "open");
      if (opts.siteId) params.set("siteId", opts.siteId);
      if (opts.jobId) params.set("jobId", opts.jobId);
      if (opts.origin) params.set("origin", opts.origin);
      if (opts.state) params.set("state", opts.state);
      if (opts.q) params.set("q", opts.q);
      if (opts.sort) {
        params.set("sort", opts.sort);
        params.set("dir", opts.dir ?? "asc");
      }
      params.set("limit", String(opts.limit));
      params.set("offset", String(opts.offset));
      return request<{ items: DefectListItem[]; total: number; readySites: string[] }>(`/defects?${params.toString()}`);
    },
    get: (id: string) => request<DefectDetail>(`/defects/${encodeURIComponent(id)}`),
    resolve: (id: string, status: "open" | "resolved" = "resolved") =>
      request<DefectListItem>(`/defects/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ status }) }),
    // Regrade a defect's severity (Session ANW, office edit/override authority): open or
    // resolved, whatever the state of the job that raised it. A severity-only body never
    // touches status (never a side-effect resolve/reopen). Re-runs the Category A/B follow-up
    // server-side when severity actually changes.
    regradeSeverity: (id: string, severity: "advisory" | "immediate") =>
      request<DefectListItem>(`/defects/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ severity }) }),
    // Regenerate the advisory AI triage. regenerated is false when the AI is not configured
    // or generation failed (then aiTriage is the unchanged existing value).
    regenerateTriage: (id: string) =>
      request<{ aiTriage: string | null; regenerated: boolean }>(`/defects/${encodeURIComponent(id)}/regenerate-triage`, { method: "POST" }),
    // Delete a defect logged or auto-raised in error (admin only). 204 on success; 409 (with a
    // plain-English message) once it has been actioned (a remedial job, a client notification, or
    // a recorded RIDDOR report).
    remove: (id: string) => request<void>(`/defects/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },

  // Session BBF: an insurer's findings recorded against a lift, the never-charged check an engineer makes of them, and the office's
  // hand-offs (a Defect for a confirmed-present item, a link to the priced repair estimate). Office and admin only.
  insuranceItems: {
    list: (q: { assetId?: string; customerId?: string; siteId?: string; state?: InsuranceItemState; search?: string; includeArchived?: boolean; limit?: number; offset?: number } = {}) => {
      const qs = new URLSearchParams();
      if (q.assetId) qs.set("assetId", q.assetId);
      if (q.customerId) qs.set("customerId", q.customerId);
      if (q.siteId) qs.set("siteId", q.siteId);
      if (q.state) qs.set("state", q.state);
      if (q.search) qs.set("search", q.search);
      if (q.includeArchived) qs.set("includeArchived", "true");
      qs.set("limit", String(q.limit ?? 100));
      if (q.offset) qs.set("offset", String(q.offset));
      return request<InsuranceItemListResult>(`/insurance-items?${qs}`);
    },
    record: (body: RecordInsuranceItemsBody) => request<RecordInsuranceItemsResult>("/insurance-items", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: UpdateInsuranceItemBody) =>
      request<{ item: InsuranceItemDto }>(`/insurance-items/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    archive: (id: string) => request<InsuranceItemDto>(`/insurance-items/${encodeURIComponent(id)}/archive`, { method: "POST" }),
    restore: (id: string) => request<InsuranceItemDto>(`/insurance-items/${encodeURIComponent(id)}/restore`, { method: "POST" }),
    // 204 on success; 409 (with a plain-English message) once the item has an answer or a link: archive it instead.
    remove: (id: string) => request<void>(`/insurance-items/${encodeURIComponent(id)}`, { method: "DELETE" }),
    checkJob: (body: CheckJobBody) => request<CheckJobResult>("/insurance-items/check-job", { method: "POST", body: JSON.stringify(body) }),
    raiseDefect: (id: string, body: { severity?: "advisory" | "immediate"; preview?: boolean } = {}) =>
      request<RaiseDefectPlan>(`/insurance-items/${encodeURIComponent(id)}/raise-defect`, { method: "POST", body: JSON.stringify(body) }),
    linkEstimate: (id: string, repairEstimateId: string | null) =>
      request<InsuranceItemDto>(`/insurance-items/${encodeURIComponent(id)}/repair-estimate`, { method: "PUT", body: JSON.stringify({ repairEstimateId }) }),
  },

  // Repair estimates (Session ARL, gap W-9): office/admin only. Shown on the defect page, the
  // call-out page and the customer page. A send makes a Gmail DRAFT and nothing more (no Xero object
  // since Session ARS, D91). Recording an APPROVAL raises or attaches the repair job at the agreed
  // price when that is unambiguous (the response's jobRaise says what happened), and otherwise the
  // office decides through jobOptions / raiseJob.
  repairEstimates: {
    // `fresh`: the caller KNOWS its cached copy is stale (the server has just refused an action
    // because somebody else changed the estimate, which no mutation of ours cleared, or a read
    // started before our own create may have re-cached the old list). The whole request cache is
    // dropped first, exactly as a successful mutation drops it, so this read and the next visit to
    // any page both see the server's current answer rather than one up to five minutes old.
    list: async (params: RepairEstimateListQuery = {}, opts: { fresh?: boolean } = {}) => {
      if (opts.fresh) clearRequestCache();
      const qs = new URLSearchParams();
      if (params.defectId) qs.set("defectId", params.defectId);
      if (params.calloutId) qs.set("calloutId", params.calloutId);
      if (params.customerId) qs.set("customerId", params.customerId);
      const q = qs.toString();
      const res = await request<{ estimates: RepairEstimateDto[] }>(`/repair-estimates${q ? `?${q}` : ""}`);
      return res.estimates;
    },
    // Session SC7A (the Repairs hub's Repair estimates tab): one PAGE of every estimate, newest first, optionally one status
    // ("awaiting" = sent and not yet answered), with the true total.
    listPage: (opts: { status?: "draft" | "sent" | "approved" | "declined" | "awaiting"; sort?: "created" | "reference" | "amount" | "status"; dir?: "asc" | "desc"; limit: number; offset: number }, extra: { fresh?: boolean } = {}) => {
      if (extra.fresh) clearRequestCache();
      const qs = new URLSearchParams();
      if (opts.status) qs.set("status", opts.status);
      if (opts.sort) {
        qs.set("sort", opts.sort);
        qs.set("dir", opts.dir ?? "asc");
      }
      qs.set("limit", String(opts.limit));
      qs.set("offset", String(opts.offset));
      return request<{ items: RepairEstimateDto[]; total: number }>(`/repair-estimates?${qs.toString()}`);
    },
    get: (id: string) => request<RepairEstimateDto>(`/repair-estimates/${encodeURIComponent(id)}`),
    create: (body: RepairEstimateCreateInput) =>
      request<RepairEstimateDto>("/repair-estimates", { method: "POST", body: JSON.stringify(body) }),
    // A draft only; 409 not_draft once anything has been put in front of the customer.
    update: (id: string, body: RepairEstimateUpdateInput) =>
      request<RepairEstimateDto>(`/repair-estimates/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    send: (id: string, via: RepairEstimateDocumentVariant) =>
      request<RepairEstimateSendResult>(`/repair-estimates/${encodeURIComponent(id)}/send`, { method: "POST", body: JSON.stringify({ via }) }),
    // The customer's answer, however it reached the office. The note is sent only when there is one.
    // An approval's response carries jobRaise: what the automatic raise of the repair job did.
    recordResponse: (id: string, outcome: "approved" | "declined", note?: string) =>
      request<RepairEstimateResponseResult>(`/repair-estimates/${encodeURIComponent(id)}/response`, {
        method: "POST",
        body: JSON.stringify(note && note.trim() ? { outcome, note: note.trim() } : { outcome }),
      }),
    // 409 job_linked while a repair job is linked: detach the estimate or cancel the job first.
    clearResponse: (id: string) => request<RepairEstimateDto>(`/repair-estimates/${encodeURIComponent(id)}/response`, { method: "DELETE" }),
    // 409 (with a plain-English message) for an approved estimate (clear the answer first) and while
    // a repair job is linked (detach the estimate or cancel the job first).
    remove: (id: string) => request<RepairEstimateDeleteResult>(`/repair-estimates/${encodeURIComponent(id)}`, { method: "DELETE" }),
    // Session ARS: what raising the repair job for an approved estimate would do right now (read-only).
    // Never cached: it names a live job whose state a colleague may have changed a moment ago.
    jobOptions: (id: string) => request<RepairEstimateJobOptions>(`/repair-estimates/${encodeURIComponent(id)}/job-options`, { noCache: true }),
    // Session ARS: the office's own decision, attach to the job the options named or raise a new one.
    raiseJob: (id: string, body: RepairEstimateRaiseJobInput) =>
      request<RepairEstimateRaiseJobResult>(`/repair-estimates/${encodeURIComponent(id)}/job`, { method: "POST", body: JSON.stringify(body) }),
    // Session ARS review: take the estimate off its still-open repair job (a mistaken attach), clearing the
    // job's agreed price. 409, with a sentence, once the job has closed or its price was set some other way.
    detachJob: (id: string) => request<RepairEstimateDetachJobResult>(`/repair-estimates/${encodeURIComponent(id)}/job`, { method: "DELETE" }),
    // The PDF preview of either emailed document, served inline by the office-guarded route.
    documentUrl: (id: string, variant: RepairEstimateDocumentVariant) =>
      `/api/repair-estimates/${encodeURIComponent(id)}/document?variant=${encodeURIComponent(variant)}`,
  },

  riddor: {
    // RIDDOR advisory flags (office/admin). The flag is raised automatically by the matcher;
    // the office reviews and sets the status. The platform never marks a flag reported.
    flags: (status: RiddorFlagStatus | "all" = "open") =>
      request<RiddorFlagItem[]>(`/riddor/flags?status=${encodeURIComponent(status)}`),
    updateFlag: (id: string, body: { status: RiddorFlagStatus; note?: string | null }) =>
      request<RiddorFlagItem>(`/riddor/flags/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
  },

  modReExam: {
    // Modification LOLER re-examination advisory flags (office/admin). Raised when a project's
    // works classification is set to "modification". Advisory only; never blocks completion.
    flags: (status: ModReExamStatus | "all" = "open") =>
      request<ModReExamFlagItem[]>(`/modification-flags/flags?status=${encodeURIComponent(status)}`),
    // Set the status and/or record the completion-prompt response (completionPrompted).
    updateFlag: (id: string, body: { status?: ModReExamStatus; note?: string | null; completionPrompted?: boolean }) =>
      request<ModReExamFlagItem>(`/modification-flags/flags/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
  },

  // Session BAQ: the examiner's LOLER report items, worked to closure (Insurer items page).
  lolerItems: {
    list: (q: LolerItemsQuery = {}) => {
      const p = new URLSearchParams();
      if (q.assetId) p.set("assetId", q.assetId);
      if (q.siteId) p.set("siteId", q.siteId);
      if (q.reportId) p.set("reportId", q.reportId);
      if (q.state && q.state.length > 0) p.set("state", q.state.join(","));
      if (q.category) p.set("category", q.category);
      if (q.overdue !== undefined) p.set("overdue", String(q.overdue));
      if (q.q?.trim()) p.set("q", q.q.trim());
      if (q.sort) p.set("sort", q.sort);
      if (q.dir) p.set("dir", q.dir);
      if (q.page) p.set("page", String(q.page));
      if (q.pageSize) p.set("pageSize", String(q.pageSize));
      const qs = p.toString();
      return request<LolerItemsPage>(`/loler/items${qs ? `?${qs}` : ""}`);
    },
    markDone: (id: string, note?: string) =>
      request<LolerItemActionResult>(`/loler/items/${encodeURIComponent(id)}/done`, { method: "POST", body: JSON.stringify(note ? { note } : {}) }),
    verify: (id: string, note?: string) =>
      request<LolerItemActionResult>(`/loler/items/${encodeURIComponent(id)}/verify`, { method: "POST", body: JSON.stringify(note ? { note } : {}) }),
    reopen: (id: string, note?: string) =>
      request<LolerItemActionResult>(`/loler/items/${encodeURIComponent(id)}/reopen`, { method: "POST", body: JSON.stringify(note ? { note } : {}) }),
    // Admin only; 409 when the item has been actioned (the server's message says why).
    remove: (id: string) => request<void>(`/loler/defects/${encodeURIComponent(id)}`, { method: "DELETE" }),
    backfillPreview: (reportIds?: string[]) =>
      request<LolerItemsBackfillPreview>("/loler/items/backfill", { method: "POST", body: JSON.stringify({ preview: true, ...(reportIds ? { reportIds } : {}) }) }),
    backfillCommit: (reportIds?: string[]) =>
      request<LolerItemsBackfillResult>("/loler/items/backfill", { method: "POST", body: JSON.stringify({ preview: false, ...(reportIds ? { reportIds } : {}) }) }),
  },

  loler: {
    // Session SC7A: the Due dates list, one PAGE, with the lift count in each band.
    duePage: (opts: LolerDuePageOptions) => {
      const p = new URLSearchParams();
      if (opts.customerId) p.set("customerId", opts.customerId);
      if (opts.band) p.set("band", opts.band);
      if (opts.q) p.set("q", opts.q);
      if (opts.sort) {
        p.set("sort", opts.sort);
        p.set("dir", opts.dir ?? "asc");
      }
      p.set("limit", String(opts.limit));
      p.set("offset", String(opts.offset));
      return request<{ items: LolerDueRow[]; total: number; counts: Record<LolerDueBand, number> }>(`/loler/due?${p.toString()}`);
    },
    reports: (filter: LolerReportFilter = "open", siteId?: string) =>
      request<LolerReportListItem[]>(`/loler/reports?status=${encodeURIComponent(filter)}${siteId ? `&siteId=${encodeURIComponent(siteId)}` : ""}`),
    // Session SC2C: one PAGE of the list. The Open / Closed / All filter, the search and the sort run on the server, with the TRUE total.
    reportsPage: (opts: { filter: LolerReportFilter; siteId?: string; q?: string; sort?: string; dir?: "asc" | "desc"; limit: number; offset: number }) => {
      const params = new URLSearchParams({ status: opts.filter });
      if (opts.siteId) params.set("siteId", opts.siteId);
      if (opts.q) params.set("q", opts.q);
      if (opts.sort) {
        params.set("sort", opts.sort);
        params.set("dir", opts.dir ?? "asc");
      }
      params.set("limit", String(opts.limit));
      params.set("offset", String(opts.offset));
      return request<{ items: LolerReportListItem[]; total: number }>(`/loler/reports?${params.toString()}`);
    },
    // One lift's full LOLER report history, newest first (the Lifts-page panel).
    reportsForAsset: (assetId: string) => request<LolerReportListItem[]>(`/loler/reports?assetId=${encodeURIComponent(assetId)}`),
    report: (id: string) => request<LolerReportDetail>(`/loler/reports/${encodeURIComponent(id)}`),
    // Delete a report (admin only). 204 on success; 409 when it has actioned items (a
    // confirmed treatment, a remedial quote or a scheduled job) -- the guard never orphans a
    // Xero draft or job. The filed Drive PDF is left in place.
    deleteReport: (id: string) => request<void>(`/loler/reports/${encodeURIComponent(id)}`, { method: "DELETE" }),
    createReport: (body: LolerReportCreateInput) => request<LolerReportDetail>("/loler/reports", { method: "POST", body: JSON.stringify(body) }),
    updateReport: (id: string, body: Partial<LolerReportCreateInput>) =>
      request<LolerReportDetail>(`/loler/reports/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    addDefect: (id: string, body: LolerDefectInput) =>
      request<LolerDefectItem>(`/loler/reports/${encodeURIComponent(id)}/defects`, { method: "POST", body: JSON.stringify(body) }),
    // Delete a single defect item logged in error (admin only). 204 on success; 409 when the item
    // has been actioned (a confirmed treatment, a remedial quote or a scheduled job).
    deleteDefect: (defectId: string) => request<void>(`/loler/defects/${encodeURIComponent(defectId)}`, { method: "DELETE" }),
    // Extract defects (and header) for an existing report. Pass text to read pasted text;
    // omit text to read the report's uploaded PDF from Drive (the server's fallback).
    extract: (id: string, text?: string) =>
      request<ExtractedReport>(`/loler/reports/${encodeURIComponent(id)}/extract`, {
        method: "POST",
        body: JSON.stringify(text ? { text } : {}),
      }),
    // Front-of-flow: upload a report PDF (no report row yet) and get back the draft header
    // + defects to pre-fill the New report form. Nothing is persisted; never auto-converts.
    extractUpload: (file: File) => {
      const fd = new FormData();
      fd.append("report", file, file.name);
      return request<ExtractedReport>("/loler/extract", { method: "POST", body: fd });
    },
    updateDefect: (id: string, body: LolerDefectPatch) =>
      request<LolerDefectItem>(`/loler/defects/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    cover: (id: string) => request<LolerDefectItem>(`/loler/defects/${encodeURIComponent(id)}/cover`, { method: "POST" }),
    quote: (id: string, body: RemedialQuoteInput) => request<LolerDefectItem>(`/loler/defects/${encodeURIComponent(id)}/quote`, { method: "POST", body: JSON.stringify(body) }),
    absorb: (id: string, body: { note?: string; value?: number }) =>
      request<LolerDefectItem>(`/loler/defects/${encodeURIComponent(id)}/absorb`, { method: "POST", body: JSON.stringify(body) }),
    reportPdfHref: (id: string) => `/api/loler/reports/${encodeURIComponent(id)}/report-pdf`,
    uploadReportPdf: (id: string, file: File) => {
      const fd = new FormData();
      fd.append("report", file, file.name);
      return request<LolerReportDetail>(`/loler/reports/${encodeURIComponent(id)}/upload`, { method: "POST", body: fd });
    },
    // Session AGF: sweep the inbox for inspection-body LOLER report emails, AI-extract each PDF, and
    // return a review queue. Writes nothing; the office confirms each candidate (createReport then
    // fileGmailAttachment). 503 when Google is not connected or the AI is not configured.
    sweepGmail: () => request<LolerGmailSweepResult>("/loler/gmail-sweep", { method: "POST" }),
    // Read a swept email's PDF before confirming it, so the office attests a statutory record having
    // seen the document (the extraction carries no lift identifier). Sender-guarded server-side.
    // filename is the stable half of the handle: Gmail reissues attachment ids on every read, so the
    // id the sweep returned is already stale by the time the office presses View (Session AGQ).
    gmailAttachmentHref: (gmailMessageId: string, gmailAttachmentId: string, filename?: string) =>
      `/api/loler/gmail-attachment?messageId=${encodeURIComponent(gmailMessageId)}&attachmentId=${encodeURIComponent(gmailAttachmentId)}` +
      (filename ? `&filename=${encodeURIComponent(filename)}` : ""),
    // File a swept email's PDF against an already-created report (the Gmail twin of uploadReportPdf).
    // The browser never holds the blob: the server fetches the bytes from Gmail and files them.
    fileGmailAttachment: (id: string, body: { gmailMessageId: string; gmailAttachmentId: string; filename: string }) =>
      request<{ driveFileId: string; driveWebViewLink: string }>(`/loler/reports/${encodeURIComponent(id)}/gmail-attachment`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    scopeProfile: (contractId: string) => request<ScopeProfile>(`/loler/contracts/${encodeURIComponent(contractId)}/scope-profile`),
    // Session BAD: the review list.
    candidates: (state: "open" | "dismissed" | "recorded" | "all" = "open") => request<LolerCandidateList>(`/loler/candidates?state=${state}`),
    confirmCandidate: (id: string, body: { assetId: string; examinationDate?: string; allowDuplicate?: boolean; allowDuplicateReason?: string }) =>
      request<{ reportId: string; assetId: string; pdfFiled: boolean; pdfNote?: string; alreadyRecorded?: boolean }>(`/loler/candidates/${encodeURIComponent(id)}/confirm`, { method: "POST", body: JSON.stringify(body) }),
    dismissCandidate: (id: string, reason: string) => request<{ id: string }>(`/loler/candidates/${encodeURIComponent(id)}/dismiss`, { method: "POST", body: JSON.stringify({ reason }) }),
    restoreCandidate: (id: string) => request<{ id: string }>(`/loler/candidates/${encodeURIComponent(id)}/restore`, { method: "POST" }),
    deleteCandidate: (id: string) => request<void>(`/loler/candidates/${encodeURIComponent(id)}`, { method: "DELETE" }),
    // The "Add LOLER report" control's Gmail picker and the one-attachment read. Neither writes anything.
    gmailMessages: (q = "", days = 120) => request<{ messages: LolerGmailMessage[]; searched: number; capped: boolean }>(`/loler/gmail-messages?q=${encodeURIComponent(q)}&days=${days}`),
    gmailExtract: (messageId: string, filename: string) => request<LolerGmailExtract>("/loler/gmail-extract", { method: "POST", body: JSON.stringify({ messageId, filename }) }),
  },

  prospects: {
    list: (filters: ProspectListFilters = {}) => {
      const q = new URLSearchParams();
      if (filters.status) q.set("status", filters.status);
      if (filters.owner) q.set("owner", filters.owner);
      if (filters.source) q.set("source", filters.source);
      const qs = q.toString();
      return request<ProspectListItem[]>(`/prospects${qs ? `?${qs}` : ""}`);
    },
    owners: () => request<ProspectOwner[]>("/prospects/owners"),
    hot: () => request<HotProspect[]>("/prospects/hot"),
    // D291 (Session AUI): the five retired CRM pages and their api methods are deleted; only the two reads another page
    // still uses (the raise-work lead list, the complaint owner picker) and the dashboard's hot() remain.
  },

  // Complaints and corrective actions (ISO 9001 clause 10.2). Office/admin, tenantScoped.
  complaints: {
    list: (filters: ComplaintListFilters = {}) => {
      const q = new URLSearchParams();
      if (filters.status) q.set("status", filters.status);
      if (filters.source) q.set("source", filters.source);
      if (filters.severity) q.set("severity", filters.severity);
      if (filters.customerId) q.set("customerId", filters.customerId);
      if (filters.from) q.set("from", filters.from);
      if (filters.to) q.set("to", filters.to);
      const qs = q.toString();
      return request<ComplaintListItem[]>(`/complaints${qs ? `?${qs}` : ""}`);
    },
    get: (id: string) => request<ComplaintDetail>(`/complaints/${encodeURIComponent(id)}`),
    create: (body: ComplaintCreateInput) =>
      request<ComplaintListItem>("/complaints", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: ComplaintUpdateInput) =>
      request<ComplaintDetail>(`/complaints/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    remove: (id: string) =>
      request<{ ok: boolean; archived: boolean; message?: string }>(`/complaints/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },

  // Accidents and incidents (Session AOM). Office/admin only; the PWA quick-log is a separate,
  // write-only call under api.engineer.
  accidents: {
    list: (filters: AccidentListFilters = {}) => {
      const q = new URLSearchParams();
      if (filters.status) q.set("status", filters.status);
      if (filters.eventType) q.set("eventType", filters.eventType);
      if (filters.reportable) q.set("reportable", filters.reportable);
      if (filters.siteId) q.set("siteId", filters.siteId);
      if (filters.from) q.set("from", filters.from);
      if (filters.to) q.set("to", filters.to);
      if (filters.archived) q.set("archived", filters.archived);
      const qs = q.toString();
      return request<AccidentListItem[]>(`/accidents${qs ? `?${qs}` : ""}`);
    },
    get: (id: string) => request<AccidentDetail>(`/accidents/${encodeURIComponent(id)}`),
    create: (body: AccidentCreateInput) => request<AccidentListItem>("/accidents", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: AccidentUpdateInput) =>
      request<AccidentDetail>(`/accidents/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    restore: (id: string) => request<AccidentDetail>(`/accidents/${encodeURIComponent(id)}/restore`, { method: "POST" }),
    remove: (id: string) =>
      request<{ ok: boolean; archived: boolean; message?: string }>(`/accidents/${encodeURIComponent(id)}`, { method: "DELETE" }),
    riddorReference: () => request<RiddorReference>("/accidents/riddor-categories"),
    dashboardSummary: () => request<AccidentDashboardSummary>("/accidents/dashboard-summary"),
  },

  // HSE / Local Authority enforcement notices and prosecutions (Session AON), CHAS Q94's companion
  // register to the accidents register above. Office/admin.
  enforcementActions: {
    list: (filters: EnforcementActionListFilters = {}) => {
      const qs = filters.status ? `?status=${encodeURIComponent(filters.status)}` : "";
      return request<EnforcementActionListItem[]>(`/enforcement-actions${qs}`);
    },
    get: (id: string) => request<EnforcementActionDetail>(`/enforcement-actions/${encodeURIComponent(id)}`),
    create: (body: EnforcementActionCreateInput) =>
      request<EnforcementActionListItem>("/enforcement-actions", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: EnforcementActionUpdateInput) =>
      request<EnforcementActionDetail>(`/enforcement-actions/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    remove: (id: string) =>
      request<{ ok: boolean; archived: boolean; message?: string }>(`/enforcement-actions/${encodeURIComponent(id)}`, { method: "DELETE" }),
    // The notice/judgment document itself: filed to Drive, streamed back through the authenticated
    // proxy. Re-uploading replaces whatever was filed before.
    uploadDocument: (id: string, file: Blob, filename: string) => {
      const fd = new FormData();
      fd.append("file", file, filename);
      return request<EnforcementActionDetail>(`/enforcement-actions/${encodeURIComponent(id)}/document`, { method: "POST", body: fd });
    },
    // A same-origin, cookie-authenticated link the browser opens to view/download the document (the
    // server streams the Drive bytes; the raw Drive link is never exposed).
    documentDownloadHref: (id: string) => `/api/enforcement-actions/${encodeURIComponent(id)}/document`,
    dashboardSummary: () => request<EnforcementActionDashboardSummary>("/enforcement-actions/dashboard-summary"),
  },

  // Purchase orders (Session ASY): supplier orders, upstream of van stock. Office/admin.
  purchaseOrders: {
    list: (filters: PurchaseOrderListFilters = {}) => {
      const params = new URLSearchParams();
      if (filters.status) params.set("status", filters.status);
      if (filters.supplier) params.set("supplier", filters.supplier);
      const qs = params.toString();
      return request<{ purchaseOrders: PurchaseOrderListItem[] }>(`/purchase-orders${qs ? `?${qs}` : ""}`).then((r) => r.purchaseOrders);
    },
    get: (id: string) => request<PurchaseOrderListItem>(`/purchase-orders/${encodeURIComponent(id)}`),
    create: (body: PurchaseOrderCreateInput) =>
      request<PurchaseOrderListItem>("/purchase-orders", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: PurchaseOrderUpdateInput) =>
      request<PurchaseOrderListItem>(`/purchase-orders/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    markOrdered: (id: string) => request<PurchaseOrderListItem>(`/purchase-orders/${encodeURIComponent(id)}/order`, { method: "POST" }),
    cancel: (id: string) => request<PurchaseOrderListItem>(`/purchase-orders/${encodeURIComponent(id)}/cancel`, { method: "POST" }),
    receive: (id: string, lines: Array<{ itemId: string; receivedNow: number }>) =>
      request<PurchaseOrderListItem>(`/purchase-orders/${encodeURIComponent(id)}/receive`, { method: "POST", body: JSON.stringify({ lines }) }),
    remove: (id: string) => request<{ ok: boolean; deleted: boolean }>(`/purchase-orders/${encodeURIComponent(id)}`, { method: "DELETE" }),
    setDelivery: (id: string, body: { expectedAt?: string | null; supplierEmail?: string | null; supplierCopyEmail?: string | null; forJobId?: string | null }) =>
      request<PurchaseOrderListItem>(`/purchase-orders/${encodeURIComponent(id)}/delivery`, { method: "PATCH", body: JSON.stringify(body) }),
    // Session PJ1F: the order as the supplier receives it (a same-origin address a link opens), and its Gmail DRAFT (never a send).
    pdfHref: (id: string) => `/api/purchase-orders/${encodeURIComponent(id)}/pdf`,
    // Session PJ2F3: fetched first, so a refusal is shown on screen rather than as a JSON reply in a new tab.
    downloadPdf: (id: string, reference: string) => downloadPdf(`/api/purchase-orders/${encodeURIComponent(id)}/pdf`, `Purchase_order_${reference}.pdf`),
    supplierDraft: (id: string, body: { to?: string; preview: boolean }) =>
      request<PurchaseOrderSupplierDraftPreview | PurchaseOrderSupplierDraftDone>(`/purchase-orders/${encodeURIComponent(id)}/supplier-draft`, { method: "POST", body: JSON.stringify(body) }),
  },

  // Internal audits (ISO 9001 clause 9.2). Office/admin.
  internalAudits: {
    list: (status?: InternalAuditStatus | "all") => {
      const qs = status && status !== "all" ? `?status=${encodeURIComponent(status)}` : "";
      return request<InternalAuditListItem[]>(`/internal-audits${qs}`);
    },
    get: (id: string) => request<InternalAuditDetail>(`/internal-audits/${encodeURIComponent(id)}`),
    create: (body: InternalAuditCreateInput) =>
      request<InternalAuditListItem>("/internal-audits", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: InternalAuditUpdateInput) =>
      request<InternalAuditDetail>(`/internal-audits/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    remove: (id: string) =>
      request<{ ok: boolean; archived: boolean; message?: string }>(`/internal-audits/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },

  // Management reviews (ISO 9001 clause 9.3). Office/admin.
  managementReviews: {
    list: (status?: ManagementReviewStatus | "all") => {
      const qs = status && status !== "all" ? `?status=${encodeURIComponent(status)}` : "";
      return request<ManagementReviewListItem[]>(`/management-reviews${qs}`);
    },
    get: (id: string) => request<ManagementReviewDetail>(`/management-reviews/${encodeURIComponent(id)}`),
    create: (body: ManagementReviewCreateInput) =>
      request<ManagementReviewListItem>("/management-reviews", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: ManagementReviewUpdateInput) =>
      request<ManagementReviewDetail>(`/management-reviews/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    remove: (id: string) =>
      request<{ ok: boolean; archived: boolean; message?: string }>(`/management-reviews/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },

  // Personal compliance documents (Session AL): DBS / data-protection certs by person. Office/admin.
  personnelCompliance: {
    list: () => request<PersonalComplianceList>("/personnel-compliance"),
    create: (body: PersonalComplianceCreateInput) =>
      request<PersonalDocDto>("/personnel-compliance", { method: "POST", body: JSON.stringify(body) }),
    remove: (id: string) =>
      request<void>(`/personnel-compliance/${encodeURIComponent(id)}`, { method: "DELETE" }),
    // A same-origin, cookie-authenticated link the browser opens to download the document
    // (the raw Drive link never reaches the browser).
    downloadHref: (id: string) => `/api/personnel-compliance/${encodeURIComponent(id)}/download`,
  },

  // Environmental records (ISO 14001): waste / incidents / near misses / fuel. Office/admin.
  environmentalRecords: {
    list: (filters: EnvRecordListFilters = {}) => {
      const q = new URLSearchParams();
      if (filters.type) q.set("type", filters.type);
      if (filters.from) q.set("from", filters.from);
      if (filters.to) q.set("to", filters.to);
      if (filters.archived) q.set("archived", "1");
      const qs = q.toString();
      return request<EnvRecordItem[]>(`/environmental-records${qs ? `?${qs}` : ""}`);
    },
    get: (id: string) => request<EnvRecordItem>(`/environmental-records/${encodeURIComponent(id)}`),
    create: (body: EnvRecordCreateInput) =>
      request<EnvRecordItem>("/environmental-records", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: EnvRecordUpdateInput) =>
      request<EnvRecordItem>(`/environmental-records/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    remove: (id: string) =>
      request<{ ok: boolean; archived: boolean; message?: string }>(`/environmental-records/${encodeURIComponent(id)}`, { method: "DELETE" }),
    // Reactivate an archived record (clears archivedAt so it returns to the default list).
    reactivate: (id: string) =>
      request<EnvRecordItem>(`/environmental-records/${encodeURIComponent(id)}/reactivate`, { method: "POST" }),
    // Waste consignment notes (EPA 1990 Duty of Care evidence) filed against a record.
    wasteNotes: (id: string) => request<WasteNoteItem[]>(`/environmental-records/${encodeURIComponent(id)}/waste-notes`),
    uploadWasteNote: (id: string, file: Blob, filename: string) => {
      const fd = new FormData();
      fd.append("file", file, filename);
      return request<WasteNoteItem>(`/environmental-records/${encodeURIComponent(id)}/waste-notes`, { method: "POST", body: fd });
    },
    deleteWasteNote: (id: string, noteId: string) =>
      request<void>(`/environmental-records/${encodeURIComponent(id)}/waste-notes/${encodeURIComponent(noteId)}`, { method: "DELETE" }),
    // A same-origin, cookie-authenticated link the browser opens to view/download the note
    // (the server streams the Drive bytes; the raw Drive link is never exposed).
    wasteNoteDownloadHref: (id: string, noteId: string) =>
      `/api/environmental-records/${encodeURIComponent(id)}/waste-notes/${encodeURIComponent(noteId)}/download`,
  },

  // Environmental objectives (ISO 14001): targets tracked against a status. Office/admin.
  environmentalObjectives: {
    list: (status?: ObjectiveStatus | "all") => {
      const qs = status ? `?status=${encodeURIComponent(status)}` : "";
      return request<EnvObjectiveItem[]>(`/environmental-objectives${qs}`);
    },
    get: (id: string) => request<EnvObjectiveItem>(`/environmental-objectives/${encodeURIComponent(id)}`),
    create: (body: EnvObjectiveCreateInput) =>
      request<EnvObjectiveItem>("/environmental-objectives", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: EnvObjectiveUpdateInput) =>
      request<EnvObjectiveItem>(`/environmental-objectives/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    remove: (id: string) =>
      request<{ ok: boolean; archived: boolean; message?: string }>(`/environmental-objectives/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },

  communications: {
    list: (target: CommunicationTarget) => request<CommunicationItem[]>(`/communications?${communicationTargetQuery(target)}`),
    timeline: (target: CommunicationTarget) => request<TimelineItem[]>(`/communications/timeline?${communicationTargetQuery(target)}`),
    create: (body: CommunicationCreateInput) =>
      request<CommunicationItem>("/communications", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: CommunicationUpdateInput) =>
      request<CommunicationItem>(`/communications/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    remove: (id: string) => request<{ deleted: boolean }>(`/communications/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },

  projects: {
    // Session BDM: a project's works over several working days. ONE works job (raised the first time, reused after), never billed from the
    // job (the project is billed by its payment milestones), nothing sent. Every write takes preview: true first.
    workDays: {
      get: (id: string) => request<{ projectId: string; jobId: string | null; days: JobDaysView | null }>(`/projects/${encodeURIComponent(id)}/work-days`, { noCache: true }),
      schedule: (
        id: string,
        body: {
          days?: string[];
          startDate?: string;
          workingDays?: number;
          includeWeekends?: boolean;
          includeBankHolidays?: boolean;
          startTime?: string;
          engineerId?: string;
          crewEngineerIds?: string[];
          preview?: boolean;
        },
      ) => request<JobDaysResult>(`/projects/${encodeURIComponent(id)}/work-days`, { method: "POST", body: JSON.stringify(body) }),
    },
    list: (filter: ProjectFilter = "active") => request<ProjectListItem[]>(`/projects?status=${encodeURIComponent(filter)}`),
    activeCount: () => request<{ count: number }>("/projects/active-count"),
    get: (id: string) => request<ProjectDetail>(`/projects/${encodeURIComponent(id)}`),
    create: (body: ProjectCreateInput) => request<ProjectDetail>("/projects", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: ProjectUpdateInput) => request<ProjectDetail>(`/projects/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    // Session AAY: rename the project reference in place. Returns the refreshed detail plus a
    // renameWarning (non-null when the project already had a signed quotation or invoices tied to
    // the old reference). A clash surfaces as a 409 ApiError with a plain-English message.
    renameReference: (id: string, projectRef: string) =>
      request<ProjectDetail & { renameWarning: string | null }>(`/projects/${encodeURIComponent(id)}/reference`, { method: "PATCH", body: JSON.stringify({ projectRef }) }),
    // Delete a project. The server archives (soft delete) a project with retained-tier
    // history (issued invoices / a sent or signed agreement / quotation) and hard-deletes an
    // early-stage one; the response says which happened.
    remove: (id: string) =>
      request<{ ok: boolean; archived: boolean; message?: string }>(`/projects/${encodeURIComponent(id)}`, { method: "DELETE" }),
    reactivate: (id: string) => request<{ ok: boolean }>(`/projects/${encodeURIComponent(id)}/reactivate`, { method: "POST" }),
    // Session ABN: the admin "Reopen for editing" back door. Reopens the project to "quoted"
    // from ANY status with a required reason (audited server-side); returns the refreshed detail.
    reopen: (id: string, reason: string) =>
      request<ProjectDetail>(`/projects/${encodeURIComponent(id)}/reopen`, { method: "POST", body: JSON.stringify({ reason }) }),
    // Session ABS: send the post-works LOLER examination offer email (admin only). Allowed only
    // while the project is in_progress or complete; returns the send time so the card can show
    // "Offer sent [date]" without a full reload.
    sendLolerOffer: (id: string) =>
      request<{ ok: boolean; sentAt: string }>(`/projects/${encodeURIComponent(id)}/loler-offer`, { method: "POST" }),
    addSubcontractor: (projectId: string, body: ProjectSubcontractorInput) =>
      request<ProjectSubcontractor>(`/projects/${encodeURIComponent(projectId)}/subcontractors`, { method: "POST", body: JSON.stringify(body) }),
    updateSubcontractor: (projectId: string, sid: string, body: Partial<ProjectSubcontractorInput>) =>
      request<ProjectSubcontractor>(`/projects/${encodeURIComponent(projectId)}/subcontractors/${encodeURIComponent(sid)}`, { method: "PATCH", body: JSON.stringify(body) }),
    removeSubcontractor: (projectId: string, sid: string) =>
      request<void>(`/projects/${encodeURIComponent(projectId)}/subcontractors/${encodeURIComponent(sid)}`, { method: "DELETE" }),
    sendSubcontractorAgreement: (projectId: string, sid: string) =>
      request<{ ok: boolean; result: string; coveringDraftNote?: string | null }>(`/projects/${encodeURIComponent(projectId)}/subcontractors/${encodeURIComponent(sid)}/agreement/send`, { method: "POST" }),
    // Standalone directory subcontractor links (Session J), distinct from the nested
    // project-scoped subcontractors above.
    subcontractorLinks: (projectId: string) =>
      request<ProjectLinkedSubcontractor[]>(`/projects/${encodeURIComponent(projectId)}/subcontractor-links`),
    linkSubcontractor: (projectId: string, body: { subcontractorId: string; role?: string }) =>
      request<{ ok: boolean; linkId: string; alreadyLinked?: boolean }>(`/projects/${encodeURIComponent(projectId)}/subcontractor-links`, { method: "POST", body: JSON.stringify(body) }),
    unlinkSubcontractor: (projectId: string, linkId: string) =>
      request<void>(`/projects/${encodeURIComponent(projectId)}/subcontractor-links/${encodeURIComponent(linkId)}`, { method: "DELETE" }),
    documents: (projectId: string) => request<ProjectDocument[]>(`/projects/${encodeURIComponent(projectId)}/documents`),
    createDocument: (projectId: string, body: ProjectDocumentInput) =>
      request<ProjectDocument>(`/projects/${encodeURIComponent(projectId)}/documents`, { method: "POST", body: JSON.stringify(body) }),
    generateDocument: (projectId: string, body: { docType: ProjectDocType; subcontractorId?: string }) =>
      request<ProjectDocument>(`/projects/${encodeURIComponent(projectId)}/documents/generate`, { method: "POST", body: JSON.stringify(body) }),
    updateDocument: (projectId: string, docId: string, body: { title?: string; content?: string | null; status?: DocStatus }) =>
      request<ProjectDocument>(`/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(docId)}`, { method: "PATCH", body: JSON.stringify(body) }),
    // Session AW, Item 2: inline AI refinement of a draft. Applies only the described change and
    // returns the updated document (reset to draft). The office saves the current content first, so
    // this refines exactly what is on screen (manual edits included).
    refineDocument: (projectId: string, docId: string, instruction: string) =>
      request<ProjectDocument>(`/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(docId)}/refine`, { method: "POST", body: JSON.stringify({ instruction }) }),
    removeDocument: (projectId: string, docId: string) =>
      request<void>(`/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(docId)}`, { method: "DELETE" }),
    // Session AS, Item 4: upload a supplier's package quotation PDF as a project document (multipart;
    // no Content-Type header, the browser sets the boundary). The AI scope generation reads these.
    uploadSupplierQuote: (projectId: string, args: { file: File; supplier: string }) => {
      const fd = new FormData();
      fd.append("supplier", args.supplier);
      fd.append("file", args.file, args.file.name);
      return request<ProjectDocument>(`/projects/${encodeURIComponent(projectId)}/documents/supplier-quote`, { method: "POST", body: fd });
    },
    // Session AU, Item 2: preview then send a document to the client or the subcontractor.
    documentSendPreview: (projectId: string, docId: string) =>
      request<DocumentSendPreview>(`/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(docId)}/send-preview`),
    sendDocument: (projectId: string, docId: string) =>
      // Session RC2: drafted is true for a works instruction to a subcontractor, which is left as a Gmail draft, not sent.
      request<{ ok: boolean; to: string; drafted?: boolean }>(`/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(docId)}/send`, { method: "POST" }),
    // Recording a works instruction as sent from Gmail (follows RC2): the person's record, sends nothing. The route
    // previews by default, so the commit passes preview: false.
    markDocumentSent: (projectId: string, docId: string, sentOn: string) =>
      request<{ ok: boolean; to: string; sentOn: string; sentOnLabel: string; sentAt: string }>(
        `/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(docId)}/mark-sent`,
        { method: "POST", body: JSON.stringify({ sentOn, preview: false }) },
      ),
    clearDocumentSent: (projectId: string, docId: string) =>
      request<{ ok: boolean; status: string }>(
        `/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(docId)}/mark-sent?preview=false`,
        { method: "DELETE" },
      ),
    invoices: (projectId: string) => request<ProjectInvoice[]>(`/projects/${encodeURIComponent(projectId)}/invoices`),
    raiseInvoice: (projectId: string, body: MilestoneInvoiceInput) =>
      request<{ ok: boolean; invoiceId: string; amount: number; cached: boolean }>(`/projects/${encodeURIComponent(projectId)}/invoices`, { method: "POST", body: JSON.stringify(body) }),
    // A direct, cookie-authenticated link to the quotation PDF the browser opens.
    quotationPdfHref: (projectId: string) => `/api/projects/${encodeURIComponent(projectId)}/quotation.pdf`,
    // A direct, cookie-authenticated link to the standard project works T&Cs PDF download
    // (Session ABO). No project data: the same standard document every time.
    tcPdfHref: (projectId: string) => `/api/projects/${encodeURIComponent(projectId)}/tc-pdf`,
    // Save the Costs tab (internal cost breakdown + project value). The server derives the
    // third-party payment-schedule inputs and (unless a quotation has line items) sets contractValue.
    saveCosts: (projectId: string, body: ProjectCostsInput) =>
      request<ProjectDetail>(`/projects/${encodeURIComponent(projectId)}/costs`, { method: "PUT", body: JSON.stringify(body) }),
    saveQuotation: (projectId: string, detail: QuotationDetail) =>
      request<ProjectDetail>(`/projects/${encodeURIComponent(projectId)}/quotation`, { method: "PUT", body: JSON.stringify(detail) }),
    deleteQuotation: (projectId: string) =>
      // quotationEnvelopeVoidFailed is present (true) only when a live Signable envelope
      // could not be cancelled during the delete, so the office can warn the client that
      // the old signing link may still work (Session AED).
      request<ProjectDetail & { quotationEnvelopeVoidFailed?: boolean }>(`/projects/${encodeURIComponent(projectId)}/quotation`, { method: "DELETE" }),
    sendQuotation: (projectId: string, via: "email" | "signable") =>
      // `to` = who received it (result "sent"); `sentAt` = when the envelope originally went
      // (result "already_sent"), so the office sees a real confirmation (Session AAC, Item 3).
      request<{ ok: boolean; result: string; to?: string; sentAt?: string }>(`/projects/${encodeURIComponent(projectId)}/quotation/send`, { method: "POST", body: JSON.stringify({ via }) }),
    // Session AEI (D1): send the customer the branded acceptance link (they read the quotation +
    // the full Conditions of Contract, then Sign and Accept). An optional second signatory may be
    // set first: an object saves it, `null` clears it, `undefined` leaves it unchanged.
    sendAcceptanceLink: (projectId: string, secondSignatory?: { name: string; email: string; role: string } | null) =>
      request<{ ok: boolean; result: string; to?: string }>(`/projects/${encodeURIComponent(projectId)}/quotation/send-acceptance-link`, {
        method: "POST",
        body: JSON.stringify(secondSignatory === undefined ? {} : { secondSignatory }),
      }),
    // Session ATI (Q-8): the quotation version/option ledger. quotationVersions lists every
    // version newest-first (a legacy un-versioned project's live columns synthesise a single
    // entry); startQuotationVersion supersedes the current one and opens a fresh draft the
    // office builds and sends through the ordinary Quote tab, exactly as for the first quotation.
    quotationVersions: (projectId: string) =>
      request<{ versions: ProjectQuotationVersion[] }>(`/projects/${encodeURIComponent(projectId)}/quotations`),
    startQuotationVersion: (projectId: string, label?: string | null) =>
      request<{ ok: boolean; versionId: string; supersededId: string | null }>(`/projects/${encodeURIComponent(projectId)}/quotations`, {
        method: "POST",
        body: JSON.stringify({ label: label ?? undefined }),
      }),
    // RULE 1: delete a superseded, unsigned quotation version/option. The server refuses (409)
    // the current version or a signed one.
    deleteQuotationVersion: (projectId: string, versionId: string) =>
      request<void>(`/projects/${encodeURIComponent(projectId)}/quotations/${encodeURIComponent(versionId)}`, { method: "DELETE" }),
    // Void the outstanding Signable envelope and send a fresh one (Session AAC, Item 5).
    // oldLinkCancelled reports whether the void actually succeeded (a failed void means the
    // original link may still work; the UI says so rather than claiming it was cancelled).
    resendQuotation: (projectId: string) =>
      request<{ ok: boolean; result: string; to?: string; oldLinkCancelled?: boolean }>(`/projects/${encodeURIComponent(projectId)}/quotation/resend`, { method: "POST" }),
    // Supporting photos (Session AP), distinct from the quotation form and PDF. The office
    // renders thumbnails via the proxy (by index); the raw Drive URLs never reach the browser.
    photoCount: (projectId: string) => request<{ count: number }>(`/projects/${encodeURIComponent(projectId)}/photos`),
    photoUrl: (projectId: string, index: number) => `/api/projects/${encodeURIComponent(projectId)}/photos/${index}`,
    uploadPhotos: (projectId: string, files: File[]) => {
      const fd = new FormData();
      for (const f of files) fd.append("photos", f, f.name);
      return request<{ count: number }>(`/projects/${encodeURIComponent(projectId)}/photos`, { method: "POST", body: fd });
    },
    deletePhoto: (projectId: string, index: number) =>
      request<{ count: number }>(`/projects/${encodeURIComponent(projectId)}/photos/${index}`, { method: "DELETE" }),
  },

  // Identify a lift component from a photo already stored in V2 (Session AP). One reusable
  // endpoint for both photo surfaces (job sheets and projects); read-only, informational.
  partIdentification: {
    identify: (body: { source: "jobsheet" | "project"; id: string; index: number }) =>
      request<PartIdentificationResult>("/part-identification/identify", { method: "POST", body: JSON.stringify(body) }),
  },

  ai: {
    query: (question: string) =>
      request<AiQueryResult>("/ai/query", { method: "POST", body: JSON.stringify({ question }) }),
  },

  documents: {
    scheduleOfFeesHref: () => SCHEDULE_OF_FEES_HREF,
    // Session DOC1: the company standard subcontractor rate card PDF. ADMIN ONLY on the server (an office login gets a 403).
    subcontractorRateCardHref: () => SUBCONTRACTOR_RATE_CARD_HREF,
    // Session RCS: private links to the standard rate card, one per named person (ADMIN ONLY on the server). create
    // returns the link ONCE; the platform keeps only its hash.
    rateCardLinks: {
      list: () => request<{ links: RateCardShareLink[]; days: number }>("/documents/rate-card-links"),
      create: (recipientName: string) =>
        request<{ preview: false; link: RateCardShareLink; url: string; versionLabel: string }>("/documents/rate-card-links", {
          method: "POST",
          body: JSON.stringify({ recipientName }),
        }),
      revoke: (id: string) =>
        request<{ preview: false; link: RateCardShareLink; switchedOff: boolean }>(`/documents/rate-card-links/${encodeURIComponent(id)}/revoke`, {
          method: "POST",
          body: JSON.stringify({}),
        }),
      remove: (id: string) => request<{ deleted: true }>(`/documents/rate-card-links/${encodeURIComponent(id)}`, { method: "DELETE" }),
    },
  },

  complianceReports: {
    // The generation audit trail (what was generated, by whom, when). No PDF content.
    list: () => request<ComplianceReportHistoryItem[]>("/compliance-reports"),
    // Generate + download a report PDF. Resolves when the browser download is
    // triggered; rejects (ApiError) so the caller can surface the failure.
    // opts.dryRun (Session AOY): render and download without writing an audit-trail entry.
    download: (reportType: string, parameters?: Record<string, unknown>, opts?: { dryRun?: boolean }) =>
      downloadComplianceReport(reportType, parameters, opts),
    // Session AOY: classify a past history row as an internal verification / testing render, or
    // clear that classification. Admin/office only (an auditor is refused 403 by the route). The
    // row is never deleted and never leaves the office's own view of the history.
    markInternal: (id: string, internal: boolean, note?: string) =>
      request<{ id: string; internal: boolean; markedInternalAt: string | null; markedInternalNote: string | null }>(
        `/compliance-reports/${encodeURIComponent(id)}/internal`,
        { method: "PATCH", body: JSON.stringify({ internal, ...(note ? { note } : {}) }) },
      ),
  },

  // IMS document library (Session AR Item 17): the ISO 9001/14001/45001 suite under docs/compliance/.
  complianceDocs: {
    list: () => request<ComplianceDocsCatalogue>("/compliance-docs"),
    // A cookie-authenticated link the browser can open (View) or download.
    href: (category: string, filename: string) =>
      `/api/compliance-docs/${encodeURIComponent(category)}/${encodeURIComponent(filename)}`,
  },

  reports: {
    // Monthly portfolio report figures + AI narrative (admin only). month is YYYY-MM;
    // omit for the current month.
    monthly: (month?: string) =>
      request<MonthlyReport>(`/reports/monthly${month ? `?month=${encodeURIComponent(month)}` : ""}`),
    // A direct, cookie-authenticated link to the branded report PDF (browser navigates to it).
    pdfHref: (month?: string) => `/api/reports/monthly.pdf${month ? `?month=${encodeURIComponent(month)}` : ""}`,
  },

  admin: {
    // A direct, cookie-authenticated download link the browser navigates to. Streams
    // a fresh gzipped pg_dump of the whole database (admin only); nothing is stored.
    backupDownloadHref: () => "/api/admin/backup/download",
    // Session AJI: listAuditors / createAuditor / revokeAuditor are gone with their routes. Auditor
    // access is now the magic link (api.auditorTokens), which composes no email and hands the URL
    // to the office to send by hand.
    // Office-user (Google staff) offboarding (admin only, Session AAQ). Deactivate is soft +
    // reversible (locks out at once); delete is a permanent removal. Both are blocked for your own
    // account and the last active administrator.
    listOfficeUsers: () => request<OfficeUser[]>("/admin/office-users"),
    // Add an office or admin account (Director decision, 26 September 2026, reconciled with Session ASG's
    // Google-invite-only sign-in, D120). Two shapes: give it a password directly for an immediate email +
    // password login, or omit both password fields to invite them to sign in with Google instead (their
    // first "Sign in with Google" then adopts the row by email; @eastlondonelevators.co.uk only).
    createOfficeUser: (data: {
      displayName: string;
      email: string;
      role: "office" | "admin";
      password?: string;
      confirmPassword?: string;
    }) => request<OfficeUser>("/admin/office-users", { method: "POST", body: JSON.stringify(data) }),
    // Set or reset the password on an existing office/admin account, including one that has only
    // ever signed in with Google.
    setOfficeUserPassword: (id: string, data: { password: string; confirmPassword: string }) =>
      request<{ ok: boolean }>(`/admin/office-users/${encodeURIComponent(id)}/set-password`, { method: "POST", body: JSON.stringify(data) }),
    deactivateOfficeUser: (id: string) =>
      request<OfficeUser>(`/admin/office-users/${encodeURIComponent(id)}/deactivate`, { method: "POST" }),
    reactivateOfficeUser: (id: string) =>
      request<OfficeUser>(`/admin/office-users/${encodeURIComponent(id)}/reactivate`, { method: "POST" }),
    deleteOfficeUser: (id: string) =>
      request<{ id: string; deleted: boolean }>(`/admin/office-users/${encodeURIComponent(id)}`, { method: "DELETE" }),
    // Sign one person out on every device (admin only, Session ASB): office, admin, engineer or auditor. Ends their
    // sessions without touching the account.
    revokeUserSessions: (id: string) =>
      request<{ ok: boolean; revoked: number }>(`/admin/users/${encodeURIComponent(id)}/revoke-sessions`, { method: "POST" }),
    // Publish the H&S/RAMS/insurance bundle to the client portals (Session AQS, M-3): previously
    // reachable only through Guvnor's trigger_compliance_upload MCP tool.
    compliancePublish: () =>
      request<{
        documents: { filename: string; published: boolean; status: string; error?: string }[];
        publishedCount: number;
        failedCount: number;
      }>("/admin/compliance-publish", { method: "POST" }),
  },

  assets: {
    // One lift with its computed compliance + next-service status (the lift detail page). The
    // server enriches the single-asset record the same way the list does (complianceStatus,
    // nextScheduledServiceAt, nextServiceTone, hasContract), plus the site address.
    get: (id: string) => request<ResourceAsset>(`/assets/${encodeURIComponent(id)}`),
    // This lift's recent service visits, newest first, paginated. Reuses the site service-history
    // shape (engineer first name only; defect detail rides each row).
    serviceHistory: (id: string, params: { page?: number; limit?: number } = {}) => {
      const q = new URLSearchParams();
      if (params.page) q.set("page", String(params.page));
      if (params.limit) q.set("limit", String(params.limit));
      const qs = q.toString();
      return request<ServiceHistoryResponse>(`/assets/${encodeURIComponent(id)}/service-history${qs ? `?${qs}` : ""}`);
    },
    // Upload the examining body's LOLER certificate PDF, file it to the site's Drive
    // folder and record the cert URL + expiry + last examination date on the asset
    // (office/admin). ELE does not issue LOLER certificates.
    uploadLolerCertificate: (id: string, file: File, meta: UploadLolerInput) => {
      const fd = new FormData();
      fd.append("certificate", file, file.name);
      fd.append("outcome", meta.outcome);
      for (const k of ["inspectionDate", "nextDueDate", "inspectorName", "certificateRef", "notes"] as const) {
        const v = meta[k];
        if (v != null && v !== "") fd.append(k, v);
      }
      return request<UploadedLolerCertificate>(`/assets/${encodeURIComponent(id)}/loler-certificate/upload`, {
        method: "POST",
        body: fd,
      });
    },
    // A direct, cookie-authenticated download link the browser navigates to.
    lolerCertificateHref: (id: string) => `/api/assets/${encodeURIComponent(id)}/loler-certificate`,
    // Combined photo history across the lift's jobs (latest ~20, newest first). Each
    // item carries the jobId for the gallery; the raw Drive URLs are never returned.
    photoHistory: (id: string) => request<AssetPhotoHistoryItem[]>(`/assets/${encodeURIComponent(id)}/photos`),
    // Parts-used history across the lift's jobs (newest first), for the detail-page Parts History.
    parts: (id: string) => request<{ items: AssetPartItem[] }>(`/assets/${encodeURIComponent(id)}/parts`),
    // Log a part against the lift directly (office/admin) and delete a part record. A manual part
    // need not belong to a job; an optional jobId links it to an existing job on the lift. Deleting
    // a part on an APPROVED job sheet is blocked server-side (409, edit the job sheet instead).
    createPart: (id: string, body: LogPartInput) =>
      request<AssetPartItem>(`/assets/${encodeURIComponent(id)}/parts`, { method: "POST", body: JSON.stringify(body) }),
    deletePart: (id: string, partId: string) =>
      request<void>(`/assets/${encodeURIComponent(id)}/parts/${encodeURIComponent(partId)}`, { method: "DELETE" }),
    // Statutory test certificates (office/admin): list, generate, and a direct
    // cookie-authenticated download link.
    certificates: (id: string) => request<AssetCertificateItem[]>(`/assets/${encodeURIComponent(id)}/certificates`),
    generateCertificate: (id: string, body: GenerateCertificateInput) =>
      request<AssetCertificateItem>(`/assets/${encodeURIComponent(id)}/certificates/generate`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    certificateDownloadHref: (id: string, certId: string) =>
      `/api/assets/${encodeURIComponent(id)}/certificates/${encodeURIComponent(certId)}/download`,
    // SAFed supplementary testing on the lift record (office/admin). The interval is set
    // per record; nextDueDate is derived + stored server-side from lastTestDate + interval.
    safedTests: (id: string) => request<SafedTestItem[]>(`/assets/${encodeURIComponent(id)}/safed-tests`),
    createSafedTest: (id: string, body: SafedTestInput) =>
      request<SafedTestItem>(`/assets/${encodeURIComponent(id)}/safed-tests`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    updateSafedTest: (id: string, testId: string, body: SafedTestInput) =>
      request<SafedTestItem>(`/assets/${encodeURIComponent(id)}/safed-tests/${encodeURIComponent(testId)}`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    deleteSafedTest: (id: string, testId: string) =>
      request<void>(`/assets/${encodeURIComponent(id)}/safed-tests/${encodeURIComponent(testId)}`, { method: "DELETE" }),
    // RFID (NFC) tag URL management on the lift record. Generate mints a token for an untagged
    // lift (office/admin); regenerate rotates an existing one and remove clears it (both admin
    // only on the server). markNfcSent stamps the "sent to engineer" time. The public URL is
    // built client-side as `${origin}/nfc/${token}`.
    // Care equipment retirement (Session ACR): the engineers-pattern soft deactivation for
    // equipment with completed job history (kept, off the schedule, reversible). Equipment
    // only -- the server refuses a lift (its own locked delete flow stands).
    retire: (id: string) =>
      request<{ retiredAt: string | null; clearedForwardVisits?: number }>(`/assets/${encodeURIComponent(id)}/retire`, { method: "POST" }),
    reactivate: (id: string) =>
      request<{ retiredAt: null }>(`/assets/${encodeURIComponent(id)}/reactivate`, { method: "POST" }),
    generateNfc: (id: string) =>
      request<NfcTokenResult>(`/assets/${encodeURIComponent(id)}/nfc/generate`, { method: "POST" }),
    regenerateNfc: (id: string) =>
      request<NfcTokenResult>(`/assets/${encodeURIComponent(id)}/nfc/regenerate`, { method: "POST" }),
    markNfcSent: (id: string) =>
      request<{ rfidSentAt: string | null }>(`/assets/${encodeURIComponent(id)}/nfc/sent`, { method: "POST" }),
    removeNfc: (id: string) =>
      request<{ ok: boolean }>(`/assets/${encodeURIComponent(id)}/nfc`, { method: "DELETE" }),

    // The Guv POP Tag (Session AMZ as "Tapping For Tracking"): the lift's SECOND, INTERNAL tag, sited with the paper log
    // card in the motor room. Four routes mirroring the RFID four above (generate, rotate, mark
    // sent, remove) because the office does the identical job with both tags; the two token spaces
    // are kept apart in the DATA and the URL (/tap/<token>, its own unique column) rather than by
    // giving the office two differently-shaped sets of controls.
    //
    // removeTap is NOT a delete of the attendance history. It clears the TOKEN only, so any physical
    // sticker stops resolving; the recorded taps stay exactly where they are, because retiring a
    // sticker does not undo the fact that an engineer stood at that lift on that day. The only way a
    // person removes a tap is voidTap below.
    generateTap: (id: string) =>
      request<TapTokenResult>(`/assets/${encodeURIComponent(id)}/tap/generate`, { method: "POST" }),
    regenerateTap: (id: string) =>
      request<TapTokenResult>(`/assets/${encodeURIComponent(id)}/tap/regenerate`, { method: "POST" }),
    markTapSent: (id: string) =>
      request<{ attendanceTapSentAt: string | null }>(`/assets/${encodeURIComponent(id)}/tap/sent`, { method: "POST" }),
    removeTap: (id: string) =>
      request<{ ok: boolean }>(`/assets/${encodeURIComponent(id)}/tap`, { method: "DELETE" }),
    // The tap history for one lift, newest first, in fixed pages of 20 (the server's own window;
    // `page` is 1-based and is the only paging parameter the route reads).
    tapEvents: (id: string, params: { page?: number } = {}) => {
      const qs = params.page && params.page > 1 ? `?page=${params.page}` : "";
      return request<AttendanceTapEventsResponse>(`/assets/${encodeURIComponent(id)}/tap/events${qs}`);
    },
    // Void a tap (office/admin). The reason is REQUIRED by the server and is the whole audit trail
    // for saying a recorded visit was not one, so it is a required argument here too rather than an
    // optional one that produces a 400 at the far end.
    voidTap: (id: string, tapId: string, reason: string) =>
      request<{ voidedAt: string | null }>(
        `/assets/${encodeURIComponent(id)}/tap/events/${encodeURIComponent(tapId)}/void`,
        { method: "POST", body: JSON.stringify({ reason }) },
      ),
    // The reversal (admin only), so a void is never a one-way door.
    unvoidTap: (id: string, tapId: string) =>
      request<{ ok: boolean }>(
        `/assets/${encodeURIComponent(id)}/tap/events/${encodeURIComponent(tapId)}/unvoid`,
        { method: "POST" },
      ),

    // Standing PER-LIFT photos (Session AID): the lift's own visual log (servicing context, parts
    // condition, progression over time), a separate bucket from BOTH the per-visit job photos and
    // the site's general reference photos. Thumbnails render via the indexed proxy; the raw Drive
    // URLs never reach the browser. The array order is the display order (drag-reorder permutes it).
    //
    // NOTE the /lift-photos path. `photoHistory` above is a DIFFERENT endpoint (/assets/:id/photos,
    // Session W): the read-only view over this lift's JOB photos. The two are not interchangeable.
    liftPhotoCount: (id: string) => request<{ count: number }>(`/assets/${encodeURIComponent(id)}/lift-photos`),
    liftPhotoUrl: (id: string, index: number) => `/api/assets/${encodeURIComponent(id)}/lift-photos/${index}`,
    uploadLiftPhotos: (id: string, files: File[]) => {
      const fd = new FormData();
      for (const f of files) fd.append("photos", f, f.name);
      return request<{ count: number }>(`/assets/${encodeURIComponent(id)}/lift-photos`, { method: "POST", body: fd });
    },
    deleteLiftPhoto: (id: string, index: number) =>
      request<{ count: number }>(`/assets/${encodeURIComponent(id)}/lift-photos/${index}`, { method: "DELETE" }),
    reorderLiftPhotos: (id: string, order: number[]) =>
      request<{ count: number }>(`/assets/${encodeURIComponent(id)}/lift-photos/order`, { method: "PUT", body: JSON.stringify({ order }) }),
  },

  map: {
    // Every site with its cached coordinates, lift count and worst service status, for the Leaflet
    // portfolio map (office/admin). Un-geocoded sites are geocoded server-side in the background.
    sites: () => request<MapSite[]>("/map/sites"),
    // How many sites carry coordinates vs the total (for the Integrations card + map).
    geocodeStatus: () => request<GeocodeStatus>("/map/geocode-status"),
    // Geocode every un-located site in one pass (rate-limited; may take 15-30s). Returns a summary.
    geocodeAll: () => request<GeocodeSummary>("/map/geocode-all", { method: "POST" }),
  },

  sites: {
    // One site with its customer (office/admin) -- the site detail header + Details tab.
    get: (id: string) => request<SiteDetail>(`/sites/${encodeURIComponent(id)}`),
    // Paginated service-history feed across all the site's lifts (office/admin).
    serviceHistory: (
      id: string,
      params: { page?: number; limit?: number; assetId?: string; jobType?: string; dateFrom?: string; dateTo?: string } = {},
    ) => {
      const q = new URLSearchParams();
      if (params.page) q.set("page", String(params.page));
      if (params.limit) q.set("limit", String(params.limit));
      if (params.assetId) q.set("assetId", params.assetId);
      if (params.jobType) q.set("jobType", params.jobType);
      if (params.dateFrom) q.set("dateFrom", params.dateFrom);
      if (params.dateTo) q.set("dateTo", params.dateTo);
      const qs = q.toString();
      return request<ServiceHistoryResponse>(`/sites/${encodeURIComponent(id)}/service-history${qs ? `?${qs}` : ""}`);
    },
    // Standing SITE-level photos (Session ADE): the site's own reference images (access, plant
    // room, general condition), a separate bucket from per-visit job photos. Thumbnails render
    // via the indexed proxy; the raw Drive URLs never reach the browser. The array order is the
    // display order (drag-reorder permutes it).
    photoCount: (id: string) => request<{ count: number }>(`/sites/${encodeURIComponent(id)}/photos`),
    photoUrl: (id: string, index: number) => `/api/sites/${encodeURIComponent(id)}/photos/${index}`,
    uploadPhotos: (id: string, files: File[]) => {
      const fd = new FormData();
      for (const f of files) fd.append("photos", f, f.name);
      return request<{ count: number }>(`/sites/${encodeURIComponent(id)}/photos`, { method: "POST", body: fd });
    },
    deletePhoto: (id: string, index: number) =>
      request<{ count: number }>(`/sites/${encodeURIComponent(id)}/photos/${index}`, { method: "DELETE" }),
    reorderPhotos: (id: string, order: number[]) =>
      request<{ count: number }>(`/sites/${encodeURIComponent(id)}/photos/order`, { method: "PUT", body: JSON.stringify({ order }) }),
  },

  compliance: {
    // The operational compliance tiles + detail lists (office/admin).
    overview: () => request<ComplianceOverview>("/compliance/overview"),
    // The consolidated compliance dashboard: every statutory signal + a health traffic light,
    // in one efficient call (office/admin).
    dashboard: () => request<ComplianceDashboard>("/compliance/dashboard"),
    // Session ACR: the LOLER paperwork chase list -- every people-lifting care-equipment asset
    // with UNKNOWN examination status (no recorded exam; deliberately no fabricated due date),
    // with site + contract context so the office can chase the property manager.
    lolerChase: () => request<{ items: LolerChaseItem[] }>("/compliance/loler-chase"),
  },

  safed: {
    // Portfolio-level SAFed tests overdue or due within `days` (office/admin), most urgent first.
    due: (days = 90) => request<SafedDueItem[]>(`/safed/due?days=${encodeURIComponent(String(days))}`),
  },

  settings: {
    smsLog: () => request<SmsLogItem[]>("/settings/sms-log"),
    tenant: () => request<TenantSettings>("/settings/tenant"),
    // Partial patch: cpiRate is a fraction (0.032 for 3.2%); ppmRunwayDays is a whole number of
    // days [1, 365]. Send only the field(s) being changed (the server writes just those).
    // kbTrustedSenders replaces the whole list (it is not a merge). Sending [] resets to the
    // built-in default senders, which is the only reset path; it does NOT mean "ingest from nobody".
    //
    // Session AJG: `cpiRate: null` CLEARS the manual override so the live ONS reading is used again.
    // It is the only way to clear it. Do NOT send 0 for that purpose: 0 is a valid deliberate
    // override meaning genuine zero inflation, and would pin every renewal to escalator-only while
    // appearing cleared on screen.
    updateTenant: (patch: { cpiRate?: number | null; ppmRunwayDays?: number; quoteValidityDays?: number; labourCostPerHour?: number; callNpsDelayMinutes?: number; inboxChaseThresholdDays?: number; kbTrustedSenders?: string[] }) =>
      request<TenantSettings>("/settings/tenant", { method: "PATCH", body: JSON.stringify(patch) }),
    // Company bank details (admin or office). Seed default until the Director saves their own.
    company: () => request<{ bank: BankDetails }>("/settings/company"),
    updateBank: (bank: BankDetails) =>
      request<{ bank: BankDetails }>("/settings/bank", { method: "PATCH", body: JSON.stringify(bank) }),
    // Automation toggles (admin only): PPM auto-generation + automatic Xero DRAFT billing.
    // Both default on. Pass only the toggle(s) being changed (the server writes just those).
    automation: () => request<AutomationSettings>("/settings/automation"),
    updateAutomation: (patch: { ppmAutoGenerate?: boolean; autoInvoiceEnabled?: boolean }) =>
      request<AutomationSettings>("/settings/automation", { method: "PATCH", body: JSON.stringify(patch) }),
    // The copyable claude.ai connector instructions (admin only, Session AHG). The tool COUNT is
    // taken from the live MCP registry so it can never go stale; the tools themselves are not
    // listed out (Session AHM). Read only.
    mcpManifest: () => request<McpManifest>("/settings/mcp-manifest"),

    // Session ARZ: how a subcontractor's Xero payable contact is set up (read: office or admin; write: admin). All
    // three fields are sent on every save; null clears one, and a cleared field is left out of the Xero payload.
    subcontractorXero: () => request<SubcontractorXeroSettings>("/settings/subcontractor-xero"),
    updateSubcontractorXero: (body: SubcontractorXeroSettingsInput) =>
      request<SubcontractorXeroSettings>("/settings/subcontractor-xero", { method: "PUT", body: JSON.stringify(body) }),

    // Session ASQ (Stream G, Item 5): the privacy notice (read: office or admin; write: admin). The read feeds an
    // editable form, so it is never served from the request cache. put(null) or put("") CLEARS it, which unpublishes the
    // /privacy page and every link to it.
    // Session ATZ (Item 6): ELE's own email addresses, THE ONE LIST every part of the email system reads. null restores the
    // built-in list.
    eleAddresses: {
      get: () => request<EleAddressesSettings>("/settings/ele-addresses", { noCache: true }),
      put: (addresses: string[] | null) => request<EleAddressesSettings>("/settings/ele-addresses", { method: "PUT", body: JSON.stringify({ addresses }) }),
    },
    // Session AUA (DE-3): the one company signature every reply the platform drafts carries. The read feeds an editable form
    // (noCache). put previews unless preview is false. Since Session AUC it takes the three fields (CompanySignatureChange).
    companySignature: {
      get: () => request<CompanySignatureSettings>("/settings/company-signature", { noCache: true }),
      put: (body: CompanySignatureChange) =>
        request<CompanySignatureResponse>("/settings/company-signature", { method: "PUT", body: JSON.stringify(body) }),
    },
    // Session AUE (D-LATE-2): the late-reply opener wording. The read feeds an editable form (noCache); put previews unless
    // preview is false.
    // Session AUF (DL-2): the recommended examining body. The read feeds an editable form (noCache); put previews unless
    // preview is false.
    recommendedExaminer: {
      get: () => request<RecommendedExaminerSettings>("/settings/recommended-examiner", { noCache: true }),
      put: (body: { name: string; email: string; preview: boolean } | { useDefault: true; preview: boolean }) =>
        request<RecommendedExaminerResponse>("/settings/recommended-examiner", { method: "PUT", body: JSON.stringify(body) }),
    },
    lateReplyWording: {
      get: () => request<LateReplyWordingSettings>("/settings/late-reply-wording", { noCache: true }),
      put: (body: LateReplyWordingChange) =>
        request<LateReplyWordingResponse>("/settings/late-reply-wording", { method: "PUT", body: JSON.stringify(body) }),
    },
    // Session AUY: the subcontractor welcome pack's CIS notice block and the V2 agreement wording confirmation.
    subcontractorDocuments: {
      get: () => request<SubcontractorDocumentsSettings>("/settings/subcontractor-documents", { noCache: true }),
      putCisNotice: (body: { text?: string; useDefault?: true; preview: boolean }) =>
        request<CisNoticeResponse>("/settings/subcontractor-documents/cis-notice", { method: "PUT", body: JSON.stringify(body) }),
      putAgreementWording: (body: { confirmed: boolean; preview: boolean }) =>
        request<AgreementWordingResponse>("/settings/subcontractor-documents/agreement-wording", { method: "PUT", body: JSON.stringify(body) }),
    },
    // Session AUD (Item 5): the email deliverability check, read live from DNS on every press (noCache: it is the point).
    emailDeliverability: () => request<EmailDeliverabilityReport>("/settings/email-deliverability", { noCache: true }),
    privacyNotice: {
      get: () => request<PrivacyNoticeSettings>("/settings/privacy-notice", { noCache: true }),
      put: (text: string | null) =>
        request<PrivacyNoticeSettings>("/settings/privacy-notice", { method: "PUT", body: JSON.stringify({ text }) }),
    },
  },

  // Inbox triage (Session AMR): awaiting_reply / needs_chase / action_owed Gmail threads.
  inboxTriage: {
    threads: () => request<InboxTriageThreadsResponse>("/inbox-triage/threads"),
    // Runs the sweep now instead of waiting for the hourly automatic pass. Can take longer on a
    // tenant's very first run (a one-off 12-month backlog catch-up).
    sweepNow: () => request<InboxTriageSweepResult>("/inbox-triage/sweep", { method: "POST" }),
    dismiss: (id: string, reason?: string) =>
      request<{ id: string; dismissed: boolean }>(`/inbox-triage/threads/${encodeURIComponent(id)}/dismiss`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
    restore: (id: string) =>
      request<{ id: string; dismissed: boolean }>(`/inbox-triage/threads/${encodeURIComponent(id)}/restore`, { method: "POST" }),
  },

  // Enquiry review queue (Session ARE, D82): the drafting-capable front end onto actionable
  // InboxTriage threads. See the InboxTriage block above for the raw sweep/dismiss surface, which
  // this does not replace.
  enquiries: {
    // includeNoise and noiseSuppressed are Session ATM, Part A (backend, built in parallel). The
    // shape here matches the build spec (docs/specs/ELE-V2-SESSION-ATM-SPEC.md); reconcile if the
    // landed route differs.
    list: (params?: { status?: EnquiryStatus; search?: string; limit?: number; offset?: number; includeNoise?: boolean }) => {
      const q = new URLSearchParams();
      if (params?.status) q.set("status", params.status);
      if (params?.search) q.set("search", params.search);
      if (params?.limit != null) q.set("limit", String(params.limit));
      if (params?.offset != null) q.set("offset", String(params.offset));
      if (params?.includeNoise) q.set("includeNoise", "true");
      const qs = q.toString();
      return request<{ enquiries: EnquiryListRow[]; total: number; noiseSuppressed?: EnquiryNoiseSuppressed }>(
        `/enquiries${qs ? `?${qs}` : ""}`,
      );
    },
    get: (id: string) => request<{ enquiry: EnquiryDetail }>(`/enquiries/${encodeURIComponent(id)}`),
    match: (id: string, patch: { customerId: string | null; siteId?: string | null }) =>
      request<{ enquiry: EnquiryDetail }>(`/enquiries/${encodeURIComponent(id)}/match`, {
        method: "POST",
        body: JSON.stringify(patch),
      }),
    generateDraft: (id: string) =>
      request<{ enquiry: EnquiryDetail }>(`/enquiries/${encodeURIComponent(id)}/generate-draft`, { method: "POST" }),
    updateDraft: (id: string, patch: { subject?: string; bodyHtml?: string }) =>
      request<{ enquiry: EnquiryDetail }>(`/enquiries/${encodeURIComponent(id)}/draft`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    // Session ATW: what a send would do, read fresh; the confirm names this recipient and passes it back as expectedTo,
    // so a reply never goes to someone the person was not shown (the server refuses with 409 recipient_changed).
    sendPreview: (id: string) => request<{ preview: EnquirySendPreview }>(`/enquiries/${encodeURIComponent(id)}/send-preview`, { noCache: true }),
    approveAndSend: (id: string, expectedTo?: string) =>
      request<{ enquiry: EnquiryDetail; afterSend?: MailAfterSend }>(`/enquiries/${encodeURIComponent(id)}/approve-and-send`, {
        method: "POST",
        body: JSON.stringify(expectedTo ? { expectedTo } : {}),
      }),
    // Session ATM, Part C: clears the draft and returns the enquiry to "new". Refused (409) unless
    // status is "awaiting_review".
    rejectDraft: (id: string, reason?: string) =>
      request<{ enquiry: EnquiryDetail }>(`/enquiries/${encodeURIComponent(id)}/reject-draft`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
    remove: (id: string) => request<void>(`/enquiries/${encodeURIComponent(id)}`, { method: "DELETE" }),

    // Session ATZ (Item 4): mark old conversations "handled outside email" by a filter. The preview writes nothing and
    // gives the exact count and a fingerprint; the commit must quote both (409 changed_since_preview otherwise). Undo puts
    // a whole clearance back. Nothing is ever deleted.
    bulkHandled: {
      preview: (filter: EnquiryBulkFilter, page: { limit?: number; offset?: number } = {}) =>
        request<EnquiryBulkPreview>("/enquiries/bulk-handled/preview", { method: "POST", body: JSON.stringify({ filter, ...page }), noCache: true }),
      commit: (body: { filter: EnquiryBulkFilter; expectedCount: number; fingerprint: string; note?: string }) =>
        request<{ batchId: string; matched: number; marked: number; skipped: number; note: string }>("/enquiries/bulk-handled", {
          method: "POST",
          body: JSON.stringify(body),
        }),
      batches: () => request<{ batches: EnquiryBulkBatch[] }>("/enquiries/bulk-handled/batches", { noCache: true }),
      undo: (batchId: string) => request<{ batchId: string; restored: number }>(`/enquiries/bulk-handled/${encodeURIComponent(batchId)}/undo`, { method: "POST" }),
    },

    // Session ATM, Part A (backend, built in parallel): manual corrections to the noise
    // classification. No dedicated office UI in this session beyond what EnquiriesPage.tsx offers;
    // a standalone corrections manager is left for a follow-up session.
    senderOverrides: {
      list: () => request<{ overrides: EnquirySenderOverride[] }>("/enquiries/sender-overrides"),
      set: (body: { email: string; treatment: EnquirySenderTreatment; reason?: string }) =>
        request<{ override: EnquirySenderOverride }>("/enquiries/sender-overrides", {
          method: "POST",
          body: JSON.stringify(body),
        }),
      remove: (id: string) => request<void>(`/enquiries/sender-overrides/${encodeURIComponent(id)}`, { method: "DELETE" }),
    },

    // Session ATM, Part D: structured fact proposals derived from an enquiry's own text, one
    // customer-matched enquiry at a time.
    facts: {
      list: (enquiryId: string) => request<{ proposals: EnquiryFactProposal[] }>(`/enquiries/${encodeURIComponent(enquiryId)}/facts`),
      propose: (enquiryId: string) =>
        request<{ proposals: EnquiryFactProposal[] }>(`/enquiries/${encodeURIComponent(enquiryId)}/propose-facts`, { method: "POST" }),
      approve: (enquiryId: string, factId: string) =>
        request<{ proposal: EnquiryFactProposal }>(
          `/enquiries/${encodeURIComponent(enquiryId)}/facts/${encodeURIComponent(factId)}/approve`,
          { method: "POST" },
        ),
      dismiss: (enquiryId: string, factId: string) =>
        request<{ proposal: EnquiryFactProposal }>(
          `/enquiries/${encodeURIComponent(enquiryId)}/facts/${encodeURIComponent(factId)}/dismiss`,
          { method: "POST" },
        ),
      remove: (enquiryId: string, factId: string) =>
        request<void>(`/enquiries/${encodeURIComponent(enquiryId)}/facts/${encodeURIComponent(factId)}`, { method: "DELETE" }),
    },
  },

  // The email engine (Session ASO): every inbound message recorded with an outcome, its drafts, the reply library and
  // the draft quality report. The live reads are noCache: a queue and a health strip must never show a cached state, and
  // the message detail feeds the redraft form.
  emailEngine: {
    status: () => request<EmailEngineStatus>("/email-engine/status", { noCache: true }),
    poll: () => request<EmailEnginePollResult>("/email-engine/poll", { method: "POST" }),
    setDrafting: (draftingEnabled: boolean) =>
      request<{ draftingEnabled: boolean }>("/email-engine/settings", { method: "PUT", body: JSON.stringify({ draftingEnabled }) }),
    // Session BCR6 Phase 4: the investigating agent's settings. Changing the mode or a model is admin only (403 for the office).
    setAgentSettings: (body: { engineMode?: "classic" | "agent"; agentShadow?: boolean; agentDraftModel?: string | null; agentVerifierModel?: string | null }) =>
      request<{ engineMode: "classic" | "agent"; agentShadow: boolean; agentDraftModel: string | null; agentVerifierModel: string | null }>("/email-engine/settings", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    // Session BCR6 Phase 4: the shadow agent beside the classic engine over a window (no draft text in the list).
    agentComparison: (days?: number) => request<EmailAgentComparison>(`/email-engine/agent-comparison${days ? `?days=${days}` : ""}`, { noCache: true }),
    // Session BCR2 (D-BCR1-1): the sentence gate's switch, log only (false) or enforcing (true).
    setSentenceGate: (enforced: boolean) =>
      request<{ sentenceGateEnforced: boolean; sentenceGateMode: "log_only" | "enforce" }>("/email-engine/settings", { method: "PUT", body: JSON.stringify({ sentenceGateEnforced: enforced }) }),
    messages: (params?: {
      outcome?: InboundEmailOutcome | "overdue" | "reply_overdue";
      lane?: "file" | "work";
      draftKind?: "template" | "free" | "agent";
      templateKey?: string;
      search?: string;
      enquiryId?: string;
      days?: number;
      limit?: number;
      offset?: number;
    }) => {
      const q = new URLSearchParams();
      if (params?.outcome) q.set("outcome", params.outcome);
      if (params?.draftKind) q.set("draftKind", params.draftKind);
      if (params?.templateKey) q.set("templateKey", params.templateKey);
      if (params?.lane) q.set("lane", params.lane);
      if (params?.search) q.set("search", params.search);
      if (params?.enquiryId) q.set("enquiryId", params.enquiryId);
      if (params?.days != null) q.set("days", String(params.days));
      if (params?.limit != null) q.set("limit", String(params.limit));
      if (params?.offset != null) q.set("offset", String(params.offset));
      const qs = q.toString();
      return request<{ messages: EmailEngineMessageRow[]; total: number }>(`/email-engine/messages${qs ? `?${qs}` : ""}`, { noCache: true });
    },
    message: (id: string) => request<EmailEngineMessageDetail>(`/email-engine/messages/${encodeURIComponent(id)}`, { noCache: true }),
    // Session ATZ (Item 5): what adding a missed conversation would record (writes nothing), and the add itself.
    planAddConversation: (ids: { gmailThreadId?: string; gmailMessageId?: string }) =>
      request<{ plan: EmailConversationPlan }>(`/email-engine/add-conversation?${new URLSearchParams(ids as Record<string, string>).toString()}`, { noCache: true }),
    addConversation: (ids: { gmailThreadId?: string; gmailMessageId?: string }) =>
      request<{ result: EmailConversationAdded }>("/email-engine/add-conversation", { method: "POST", body: JSON.stringify(ids) }),
    removeMessage: (id: string) => request<{ ok: true }>(`/email-engine/messages/${encodeURIComponent(id)}`, { method: "DELETE" }),
    // Session BCR3: allowOldThread (with its reason) answers a very late fault or complaint in writing: the old-thread override.
    draft: (body: { inboundEmailId?: string; instruction?: string; preview?: boolean; candidateId?: string; allowOldThread?: boolean; allowOldThreadReason?: string }) =>
      request<EmailEngineDraftResponse>("/email-engine/draft", { method: "POST", body: JSON.stringify(body) }),
    redraft: (id: string, body: { instruction: string; preview?: boolean; candidateId?: string; allowOldThread?: boolean; allowOldThreadReason?: string }) =>
      request<EmailEngineDraftResponse>(`/email-engine/messages/${encodeURIComponent(id)}/redraft`, { method: "POST", body: JSON.stringify(body) }),
    library: (params?: { category?: string; search?: string; limit?: number; offset?: number }) => {
      const q = new URLSearchParams();
      if (params?.category) q.set("category", params.category);
      if (params?.search) q.set("search", params.search);
      if (params?.limit != null) q.set("limit", String(params.limit));
      if (params?.offset != null) q.set("offset", String(params.offset));
      const qs = q.toString();
      return request<ReplyLibraryResponse>(`/email-engine/library${qs ? `?${qs}` : ""}`, { noCache: true });
    },
    syncLibrary: () => request<ReplyLibrarySyncResult>("/email-engine/library/sync", { method: "POST" }),
    removeExample: (id: string) => request<{ ok: true }>(`/email-engine/library/${encodeURIComponent(id)}`, { method: "DELETE" }),
    // Through the one erasure path since Session ASQ, Stream G, Item 3: removed = deleted or kept with the person's details
    // removed; held only when something was kept under a legal hold.
    forgetAddress: (email: string) =>
      request<{ examplesRemoved: number; inboundRemoved: number; standardLinesRemoved: number; held?: number }>("/email-engine/library/forget", {
        method: "POST",
        body: JSON.stringify({ email }),
      }),
    quality: (days: number) => request<EmailQualityReport>(`/email-engine/quality?days=${days}`, { noCache: true }),
    // Session AUG (Phase 6, Item 2): the metrics view. from and to are London days, YYYY-MM-DD, "to" included.
    metrics: (range?: { from?: string; to?: string }) => {
      const q = new URLSearchParams();
      if (range?.from) q.set("from", range.from);
      if (range?.to) q.set("to", range.to);
      const qs = q.toString();
      return request<EmailMetrics>(`/email-engine/metrics${qs ? `?${qs}` : ""}`, { noCache: true });
    },
    standardLines: () => request<{ lines: EmailStandardLineRow[] }>("/email-engine/standard-lines", { noCache: true }),
    decideStandardLine: (id: string, decision: "accept" | "reject") =>
      request<{ id: string; status: string }>(`/email-engine/standard-lines/${encodeURIComponent(id)}/decision`, {
        method: "POST",
        body: JSON.stringify({ decision }),
      }),
    removeStandardLine: (id: string) => request<{ ok: true }>(`/email-engine/standard-lines/${encodeURIComponent(id)}`, { method: "DELETE" }),
    // Session ATX, Item 5: the all-time mailbox backfill. Start and stop preview unless preview is false.
    backfill: () => request<EmailBackfillStatus>("/email-engine/backfill", { noCache: true }),
    startBackfill: (body: { preview: boolean; from?: string; batchSize?: number; pauseSeconds?: number }) =>
      request<{ preview: boolean; plan: EmailBackfillPlan; status?: EmailBackfillStatus }>("/email-engine/backfill/start", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    pauseBackfill: () => request<{ status: EmailBackfillStatus }>("/email-engine/backfill/pause", { method: "POST", body: JSON.stringify({}) }),
    stopBackfill: (preview: boolean) =>
      request<{ preview: boolean; wouldSettle?: number; settled?: number; status: EmailBackfillStatus }>("/email-engine/backfill/stop", {
        method: "POST",
        body: JSON.stringify({ preview }),
      }),
    resetBackfill: () => request<{ status: EmailBackfillStatus }>("/email-engine/backfill", { method: "DELETE" }),
  },

  // Reply templates (Session AUA, email system Phase 3): the approved wording the email engine fills, the proposals queue,
  // mining, the working-session export and import, and the style guide. Every write previews unless preview is false.
  // Every read is noCache: the detail and the style guide feed editable forms, and a queue must never show a stale state.
  replyTemplates: {
    slots: () => request<{ slots: ReplyTemplateSlot[] }>("/reply-templates/slots", { noCache: true }),
    list: (params?: { category?: string; status?: "draft" | "approved" | "retired" | "any"; search?: string; flagged?: boolean; limit?: number; offset?: number }) => {
      const q = new URLSearchParams();
      if (params?.category) q.set("category", params.category);
      if (params?.status) q.set("status", params.status);
      if (params?.search) q.set("search", params.search);
      if (params?.flagged) q.set("flagged", "true");
      if (params?.limit != null) q.set("limit", String(params.limit));
      if (params?.offset != null) q.set("offset", String(params.offset));
      const qs = q.toString();
      return request<ReplyTemplateListResponse>(`/reply-templates${qs ? `?${qs}` : ""}`, { noCache: true });
    },
    get: (id: string) => request<ReplyTemplateDetail>(`/reply-templates/${encodeURIComponent(id)}`, { noCache: true }),
    create: (body: ReplyTemplateFields & { preview: boolean }) =>
      request<ReplyTemplateCreateResponse>("/reply-templates", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: Omit<ReplyTemplateFields, "key"> & { preview: boolean }) =>
      request<ReplyTemplateUpdateResponse>(`/reply-templates/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify(body) }),
    newVersion: (id: string, body: Omit<ReplyTemplateFields, "key"> & { preview: boolean }) =>
      request<ReplyTemplateNewVersionResponse>(`/reply-templates/${encodeURIComponent(id)}/new-version`, { method: "POST", body: JSON.stringify(body) }),
    approve: (id: string, body: { preview: boolean }) =>
      request<ReplyTemplateApproveResponse>(`/reply-templates/${encodeURIComponent(id)}/approve`, { method: "POST", body: JSON.stringify(body) }),
    retire: (id: string, body: { preview: boolean; reason?: string }) =>
      request<ReplyTemplateRetireResponse>(`/reply-templates/${encodeURIComponent(id)}/retire`, { method: "POST", body: JSON.stringify(body) }),
    remove: (id: string) => request<{ ok: true }>(`/reply-templates/${encodeURIComponent(id)}`, { method: "DELETE" }),
    proposals: (params?: { status?: ReplyTemplateProposalStatus | "all"; kind?: ReplyTemplateProposalKind; category?: string; limit?: number; offset?: number }) => {
      const q = new URLSearchParams();
      if (params?.status) q.set("status", params.status);
      if (params?.kind) q.set("kind", params.kind);
      if (params?.category) q.set("category", params.category);
      if (params?.limit != null) q.set("limit", String(params.limit));
      if (params?.offset != null) q.set("offset", String(params.offset));
      const qs = q.toString();
      return request<{ total: number; items: ReplyTemplateProposal[] }>(`/reply-templates/proposals${qs ? `?${qs}` : ""}`, { noCache: true });
    },
    proposal: (id: string) => request<{ proposal: ReplyTemplateProposal }>(`/reply-templates/proposals/${encodeURIComponent(id)}`, { noCache: true }),
    acceptProposal: (id: string, body: ReplyTemplateAcceptOverrides & { preview: boolean }) =>
      request<ReplyTemplateAcceptResponse>(`/reply-templates/proposals/${encodeURIComponent(id)}/accept`, { method: "POST", body: JSON.stringify(body) }),
    rejectProposal: (id: string, body: { preview: boolean; reason?: string }) =>
      request<ReplyTemplateRejectResponse>(`/reply-templates/proposals/${encodeURIComponent(id)}/reject`, { method: "POST", body: JSON.stringify(body) }),
    removeProposal: (id: string) => request<{ ok: true }>(`/reply-templates/proposals/${encodeURIComponent(id)}`, { method: "DELETE" }),
    mining: () => request<ReplyTemplateMiningStatus>("/reply-templates/mining", { noCache: true }),
    mine: (body: { preview: boolean; categories?: string[] }) =>
      request<ReplyTemplateMiningResponse>("/reply-templates/mining", { method: "POST", body: JSON.stringify(body) }),
    exportDocument: (include?: Array<"approved" | "drafts" | "proposals">) =>
      request<ReplyTemplateExport>(`/reply-templates/export${include && include.length ? `?include=${encodeURIComponent(include.join(","))}` : ""}`, { noCache: true }),
    importDocument: (body: { document: string; preview: boolean }) =>
      request<ReplyTemplateImportResponse>("/reply-templates/import", { method: "POST", body: JSON.stringify(body) }),
    styleGuide: () => request<ReplyStyleGuide>("/reply-templates/style-guide", { noCache: true }),
    setStyleGuide: (body: { text?: string | null; bannedPhrases?: string[]; preview: boolean }) =>
      request<ReplyStyleGuideResponse>("/reply-templates/style-guide", { method: "PUT", body: JSON.stringify(body) }),
  },

  integrations: {
    status: () => request<IntegrationStatus>("/integrations/status"),
    // OAuth providers have a server-side connect handshake the browser navigates to.
    connectHref: (provider: "google" | "xero") => `/api/integrations/${provider}/connect`,
    // White-label inbound-call webhook token (admin). Generate/rotate returns the URL to give the
    // phone provider; revoke clears it (the endpoint then rejects every delivery).
    generateInboundCallToken: () =>
      request<{ token: string; webhookUrl: string }>("/integrations/inbound-call/token", { method: "POST" }),
    revokeInboundCallToken: () =>
      request<{ ok: true }>("/integrations/inbound-call/token", { method: "DELETE" }),
    // Session ASB, Item 4: disconnect (admin). Removes the stored connection and asks the provider to revoke it.
    disconnect: (provider: "google" | "xero") =>
      request<{ ok: true; provider: "google" | "xero"; revokedAtProvider: boolean }>(`/integrations/${provider}`, { method: "DELETE" }),
  },

  backupStatus: {
    get: () => request<BackupStatus>("/backup/status", { noCache: true }),
  },

  // ---- Calendar sync (Session ATY): the four Google calendars Guvnor keeps on the info@ account ----
  // Both writes PREVIEW on the server unless `preview: false` is sent, so the office UI always sends it: the
  // confirm is the branded two-step button that precedes the call.
  calendarSync: {
    status: () => request<CalendarSyncStatus>("/calendar-sync/status", { noCache: true }),
    // Admin only. Turning a calendar off removes the events Guvnor put on it at the next pass.
    setCalendars: (body: Partial<Record<CalendarSyncKey, boolean>>) =>
      request<{ preview: boolean; changes: CalendarSyncSettingsChange[]; status: CalendarSyncStatus }>("/calendar-sync/settings", {
        method: "PATCH",
        body: JSON.stringify({ ...body, preview: false }),
      }),
    // Office or admin. 409 calendar_needs_reconnect / calendar_not_connected when Google lacks the permission;
    // 503 queue_unavailable. The server's sentence arrives as the ApiError code.
    resync: () =>
      request<{ preview: boolean; queued?: boolean; status: CalendarSyncStatus }>("/calendar-sync/resync", {
        method: "POST",
        body: JSON.stringify({ preview: false }),
      }),
  },

  engineers: {
    list: () => request<EngineerListItem[]>("/engineers"),
    // Session PN1: a generic test push to the engineer's phones, then one text if none was reached. The office presses it on the
    // Engineers page; nothing is sent unless preview is false.
    testNotification: (id: string, preview = false) =>
      request<{ preview: boolean; told?: boolean; channel?: "push" | "text" | "none"; devices?: number; message?: string; push?: string; phones?: number; textFallback?: boolean }>(
        `/engineers/${encodeURIComponent(id)}/test-notification`,
        { method: "POST", body: JSON.stringify({ preview }) },
      ),
    // Session BCE: 201 carries appDetails (the Gmail draft of the app details, left for the office to send). 400
    // invalid_request with details.email / details.mobile when either is missing or invalid; 409 email_taken.
    create: (body: EngineerCreateInput) =>
      request<EngineerCreateResult>("/engineers", { method: "POST", body: JSON.stringify(body) }),
    // Session ANK: reactivating can be refused 409 duplicate_active_engineer_name when another
    // ACTIVE engineer already carries this name (server/src/modules/engineers/
    // duplicateEngineerName.ts). acknowledgeDuplicateName is the deliberate override for two real
    // people of the same name; it is only ever sent after the office has read the refusal and
    // confirmed, never by default.
    setActive: (id: string, isActive: boolean, acknowledgeDuplicateName?: boolean) =>
      request<EngineerListItem>(`/engineers/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify({ isActive, ...(acknowledgeDuplicateName ? { acknowledgeDuplicateName: true } : {}) }),
      }),
    // Update the engineer's contact (email + mobile, used to send the RFID tag URL) and their
    // next-of-kin fallback contact (Session AOU, office-editable fallback: the engineer normally
    // sets this themselves in the PWA). "" clears a next-of-kin field to null, same as email/mobile.
    updateContact: (
      id: string,
      body: { email?: string; mobile?: string; nextOfKinName?: string; nextOfKinRelationship?: string; nextOfKinPhone?: string; homePostcode?: string },
    ) =>
      request<EngineerListItem>(`/engineers/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    // Set (or clear, with null) the engineer's internal cost rate per hour (Session AK Item 2).
    updateCostRate: (id: string, costRatePerHour: number | null) =>
      request<EngineerListItem>(`/engineers/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify({ costRatePerHour }),
      }),
    // Edit the engineer's details: name, login username, contact email + mobile (Session AFW), and
    // the optional subcontractor-register link (Session ALV: pass null, or "", to clear it; omit it
    // to leave it alone). Throws ApiError(409, "username_taken") when the username is already in
    // use, and ApiError(400) with details.subcontractorId when that subcontractor is not on this
    // account.
    // AUU fix-up 2: `affectsApprovedWeeks: true` is the office's confirmation, sent ONLY after previewSubcontractorLink
    // below has shown them the already approved weeks the link change would reprice; the server refuses such a change
    // without it (409 approved_weeks_affected).
    // Session RC1: isTechnician and payType (office and admin); the three PAYE figures are ADMIN ONLY (403 otherwise).
    update: (
      id: string,
      body: {
        displayName?: string;
        email?: string;
        mobile?: string;
        subcontractorId?: string | null;
        affectsApprovedWeeks?: true;
        isTechnician?: boolean;
        payType?: EngineerPayType | null;
        payeSalaryPence?: number | null;
        payeContractedWeeklyMins?: number | null;
        payeStartDate?: string | null;
      },
    ) =>
      request<EngineerListItem>(`/engineers/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    // AUU fix-up 2: the route's OWN preview of a subcontractor link change (link, move between firms, or unlink, with
    // null): the already approved weeks whose pay check figure would move, and a sentence saying what the change does to
    // them. Writes nothing. The Edit engineer modal asks it before saving a changed link and confirms with the office
    // when any week is named; the Guvnor tool link_engineer_subcontractor asks the same preview.
    previewSubcontractorLink: (id: string, subcontractorId: string | null) =>
      request<EngineerSubcontractorLinkPreview>(`/engineers/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify({ subcontractorId, preview: true }),
      }),
    // Hard delete (Session AFW). Works even with completed job history: the engineer's open jobs
    // return to the unassigned pool, completed jobs are kept (rendered "Unassigned"), and the login
    // stops working immediately. Session AGR: an unassigned job is on NO engineer's phone, so those
    // open jobs need reassigning before anyone can attend them. Session AGA: timesheet (payroll) records no longer block it either --
    // the engineer's weeks are retained (engineerId SET NULL) rather than the delete being refused.
    remove: (id: string) =>
      request<{ id: string; deleted: boolean; openJobsUnassigned?: number }>(`/engineers/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),

    // Session BCE: leave the engineer the app details (what the app is, where to install it, that they sign in with
    // their email and an emailed code) as a Gmail DRAFT the office sends. Office and admin. previewAppDetails answers
    // the address and writes nothing (the two-step confirm reads it). Engineers have no password, so there is no
    // reset and no password link any more. Throws ApiError for a refusal (no email, inactive, Gmail not connected).
    previewAppDetails: (id: string) =>
      request<EngineerAppDetailsPreview>(`/engineers/${encodeURIComponent(id)}/app-details`, {
        method: "POST",
        body: JSON.stringify({ preview: true }),
        noCache: true,
      }),
    sendAppDetails: (id: string) =>
      request<EngineerAppDetailsResult>(`/engineers/${encodeURIComponent(id)}/app-details`, {
        method: "POST",
        body: JSON.stringify({ preview: false }),
      }),

    // ---- Van stock (office: per-engineer; engineer: own van) ----
    stock: (id: string) => request<VanStockItem[]>(`/engineers/${encodeURIComponent(id)}/stock`),
    addStock: (id: string, body: VanStockCreateInput) =>
      request<VanStockItem>(`/engineers/${encodeURIComponent(id)}/stock`, { method: "POST", body: JSON.stringify(body) }),
    updateStock: (id: string, partCode: string, body: VanStockUpdateInput) =>
      request<VanStockItem>(`/engineers/${encodeURIComponent(id)}/stock/${encodeURIComponent(partCode)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    removeStock: (id: string, partCode: string) =>
      request<void>(`/engineers/${encodeURIComponent(id)}/stock/${encodeURIComponent(partCode)}`, { method: "DELETE" }),
    reorderNeeded: () => request<VanStockReorderItem[]>("/engineers/stock/reorder-needed"),
    // All van stock across every engineer (the consolidated Van stock page, Session AK Item 3).
    allStock: () => request<VanStockAdminItem[]>("/engineers/stock/all"),
    // Engineer's own van (engineer role).
    myStock: () => request<VanStockItem[]>("/engineers/me/stock"),
    updateMyStock: (partCode: string, body: { delta?: number; quantity?: number }) =>
      request<VanStockItem>(`/engineers/me/stock/${encodeURIComponent(partCode)}`, { method: "PATCH", body: JSON.stringify(body) }),

    // ---- Next of kin (Session AOU): the engineer's own fallback contact, self-service ----
    myNextOfKin: () => request<EngineerNextOfKin>("/engineers/me/next-of-kin"),
    // "" clears a field to null; omitting a field leaves it unchanged.
    updateMyNextOfKin: (body: { nextOfKinName?: string; nextOfKinRelationship?: string; nextOfKinPhone?: string }) =>
      request<EngineerNextOfKin>("/engineers/me/next-of-kin", { method: "PATCH", body: JSON.stringify(body) }),

    // ---- Calendar feed (Session ATY): the engineer's own jobs in their own calendar app ----
    // 404 no_engineer_record for a login with no engineer row. Never returns the link.
    myCalendarFeed: () => request<{ feed: CalendarFeedSummary | null }>("/engineers/me/calendar-feed", { noCache: true }),
    // Makes a link, or a NEW link (the old one stops at once). The ONLY reply that ever carries the link: keep it in
    // component state, never in storage. 409 feed_changed on a race.
    createMyCalendarFeed: () =>
      request<{ feed: CalendarFeedSummary; replaced: boolean; links: CalendarFeedLinks }>("/engineers/me/calendar-feed", { method: "POST" }),
    revokeMyCalendarFeed: () => request<{ revoked: number }>("/engineers/me/calendar-feed", { method: "DELETE" }),
    // Office or admin: every engineer who has ever had a feed.
    calendarFeeds: () => request<{ engineers: EngineerCalendarFeedRow[] }>("/engineers/calendar-feeds", { noCache: true }),
    // The server previews unless preview is false; the office's confirm is the two-step button before this call.
    revokeCalendarFeed: (engineerId: string) =>
      request<{ preview: boolean; engineerName: string; revoked: number }>(`/engineers/${encodeURIComponent(engineerId)}/calendar-feed/revoke`, {
        method: "POST",
        body: JSON.stringify({ preview: false }),
      }),
    // Removes a SWITCHED-OFF feed's record; 409 feed_live while it is still on.
    deleteCalendarFeed: (feedId: string) =>
      request<{ deleted: true }>(`/engineers/calendar-feeds/${encodeURIComponent(feedId)}`, { method: "DELETE" }),

    // ---- Certifications (office: per-engineer; portfolio expiring-soon) ----
    certifications: (id: string) => request<CertificationItem[]>(`/engineers/${encodeURIComponent(id)}/certifications`),
    addCertification: (id: string, body: CertificationCreateInput) =>
      request<CertificationItem>(`/engineers/${encodeURIComponent(id)}/certifications`, { method: "POST", body: JSON.stringify(body) }),
    updateCertification: (id: string, certId: string, body: CertificationUpdateInput) =>
      request<CertificationItem>(`/engineers/${encodeURIComponent(id)}/certifications/${encodeURIComponent(certId)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    removeCertification: (id: string, certId: string) =>
      request<void>(`/engineers/${encodeURIComponent(id)}/certifications/${encodeURIComponent(certId)}`, { method: "DELETE" }),
    uploadCertDocument: (id: string, certId: string, file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      return request<CertificationItem>(`/engineers/${encodeURIComponent(id)}/certifications/${encodeURIComponent(certId)}/document`, {
        method: "POST",
        body: fd,
      });
    },
    // The document is a direct browser download (same-origin authenticated proxy URL), not a fetch.
    certDocumentUrl: (id: string, certId: string) =>
      `/api/engineers/${encodeURIComponent(id)}/certifications/${encodeURIComponent(certId)}/document`,
    certificationsExpiring: (days?: number) =>
      request<ExpiringCertificationItem[]>(`/engineers/certifications/expiring${days != null ? `?days=${days}` : ""}`),

    // ---- Competence (office: per-engineer records + the engineers x areas matrix) ----
    competence: (id: string) => request<CompetenceRecordItem[]>(`/engineers/${encodeURIComponent(id)}/competence`),
    addCompetence: (id: string, body: CompetenceCreateInput) =>
      request<CompetenceRecordItem>(`/engineers/${encodeURIComponent(id)}/competence`, { method: "POST", body: JSON.stringify(body) }),
    updateCompetence: (id: string, recId: string, body: CompetenceUpdateInput) =>
      request<CompetenceRecordItem>(`/engineers/${encodeURIComponent(id)}/competence/${encodeURIComponent(recId)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    removeCompetence: (id: string, recId: string) =>
      request<void>(`/engineers/${encodeURIComponent(id)}/competence/${encodeURIComponent(recId)}`, { method: "DELETE" }),
    competenceMatrix: () => request<CompetenceMatrix>("/engineers/competence-matrix"),
    competenceReviewsDue: () => request<CompetenceReviewDueItem[]>("/engineers/competence-reviews-due"),
  },

  reporting: {
    // The management BI overview. from/to (yyyy-mm-dd) window the operations panel.
    overview: (from?: string, to?: string) => {
      const q = new URLSearchParams();
      if (from) q.set("from", from);
      if (to) q.set("to", to);
      const qs = q.toString();
      return request<ReportingOverview>(`/reporting/overview${qs ? `?${qs}` : ""}`);
    },
    // The deterministic, ranked daily action list ("what needs doing").
    // noCache (Clearance 3): the list is what needs doing now (entrapments, call-outs, drafts awaiting authorisation), so a
    // copy up to five minutes old can hide an entrapment that was logged since. Both reads go to the server every time.
    morningActions: () => request<MorningActionItem[]>("/reporting/morning-actions", { noCache: true }),
    // Session SC2B: the top `limit` items with the TRUE total and the count per category (the page shows the top of a list that can run
    // to a thousand and prints "Showing the top N of total"). Without a limit the route answers a bare array, as before.
    morningActionsTop: (limit: number) => request<MorningActionsPage>(`/reporting/morning-actions?limit=${limit}`, { noCache: true }),
    // Session SC2B: figures only. The Billing hub reads `invoices.tracked` here instead of loading every invoice to count them.
    counts: () => request<PlatformCountsLite>("/reporting/counts"),
    // Session SPD1: the same route read fresh, for the Release and send tabs' counts (send state: a cached copy would show a tab count
    // that a colleague's release or send has already changed).
    releaseCounts: () => request<ReleaseCountsLite>("/reporting/counts", { noCache: true }),
    // The post-service NPS panel (rolling average, response rate, distribution, recent).
    nps: () => request<NpsSummary>("/reporting/nps"),
    // Split panels (Session V): each independently refreshable, with its own "last updated".
    portfolioHealth: () => request<PortfolioHealthPanel>("/reporting/portfolio-health"),
    commercial: () => request<CommercialPanel>("/reporting/commercial"),
    operations: (from?: string, to?: string) => {
      const q = new URLSearchParams();
      if (from) q.set("from", from);
      if (to) q.set("to", to);
      const qs = q.toString();
      return request<OperationsPanel>(`/reporting/operations${qs ? `?${qs}` : ""}`);
    },
    complianceSummary: () => request<ComplianceSummaryPanel>("/reporting/compliance-summary"),
    // Job-cost profitability (Session AK Item 2): avg margin %, total labour + parts cost, the
    // highest/lowest-margin job. from/to (yyyy-mm-dd) default to the current month.
    profitability: (from?: string, to?: string) => {
      const q = new URLSearchParams();
      if (from) q.set("from", from);
      if (to) q.set("to", to);
      const qs = q.toString();
      return request<ProfitabilityPanel>(`/reporting/profitability${qs ? `?${qs}` : ""}`);
    },
    // Per-lift cost and breakdown analytics (Session ANO): cost/charge/margin per lift, breakdown
    // count and repeat-visit count. from/to (yyyy-mm-dd) default to the current month.
    liftPerformance: (from?: string, to?: string) => {
      const q = new URLSearchParams();
      if (from) q.set("from", from);
      if (to) q.set("to", to);
      const qs = q.toString();
      return request<LiftPerformancePanel>(`/reporting/lift-performance${qs ? `?${qs}` : ""}`);
    },
    // Tap reconciliation (Session AVS): where the POP tag record and the job record disagree, filtered, sorted and paged on the server.
    tapReconciliation: (q: TapReconciliationQuery) => {
      const p = new URLSearchParams();
      for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== "") p.set(k, String(v));
      const qs = p.toString();
      return request<TapReconciliationPanel>(`/reporting/tag-reconciliation${qs ? `?${qs}` : ""}`);
    },
    // Engineer utilisation (Session AK Item 5): per-engineer jobs, hours, type mix, NPS. from/to
    // (yyyy-mm-dd) default to the current month.
    utilisation: (from?: string, to?: string) => {
      const q = new URLSearchParams();
      if (from) q.set("from", from);
      if (to) q.set("to", to);
      const qs = q.toString();
      return request<UtilisationPanel>(`/reporting/utilisation${qs ? `?${qs}` : ""}`);
    },
    // PPM compliance over a period (on time / late / missed / upcoming). from/to (yyyy-mm-dd)
    // default to the current calendar year; siteId scopes to one site.
    ppmCompliance: (opts: { from?: string; to?: string; siteId?: string } = {}) => {
      const q = new URLSearchParams();
      if (opts.from) q.set("from", opts.from);
      if (opts.to) q.set("to", opts.to);
      if (opts.siteId) q.set("siteId", opts.siteId);
      const qs = q.toString();
      return request<ReportingPpmComplianceResult>(`/reporting/ppm-compliance${qs ? `?${qs}` : ""}`);
    },
  },

  // Weekly engineer timesheet (Session ABU Part C): per-engineer jobs attended + total time on
  // site for a week, from the captured arrival/departure times. Admin only. week = any day in the
  // target week (yyyy-mm-dd); omit for the current week.
  timesheets: {
    week: (week?: string) => {
      const qs = week ? `?week=${encodeURIComponent(week)}` : "";
      return request<TimesheetWeekResult>(`/timesheets${qs}`);
    },

    // ---- The editable weekly timesheet (Session AEL, D41 and D31) ----
    // engineerId may be omitted by an ENGINEER, which resolves to their own sheet; the office
    // must name one. A week with no timesheet yet returns 200 with seven empty days and a null
    // timesheetId, never a 404, and creates nothing.
    sheet: (params?: { engineerId?: string; weekStarting?: string }) => {
      const qs = new URLSearchParams();
      if (params?.engineerId) qs.set("engineerId", params.engineerId);
      if (params?.weekStarting) qs.set("weekStarting", params.weekStarting);
      const q = qs.toString();
      return request<TimesheetSheet>(`/timesheets/sheet${q ? `?${q}` : ""}`);
    },
    engineers: () => request<TimesheetEngineersResult>("/timesheets/engineers"),
    // Find-or-create the week's timesheet. The GET deliberately never creates, so call this
    // before adding the first manual entry to a week with no completed jobs.
    ensure: (engineerId: string, weekStarting: string) =>
      request<{ timesheetId: string }>("/timesheets/ensure", {
        method: "POST",
        body: JSON.stringify({ engineerId, weekStarting }),
      }),
    addEntry: (timesheetId: string, body: TimesheetEntryInput) =>
      request<TimesheetEntry>(`/timesheets/${encodeURIComponent(timesheetId)}/entries`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    updateEntry: (timesheetId: string, entryId: string, body: TimesheetEntryPatch) =>
      request<TimesheetEntry>(
        `/timesheets/${encodeURIComponent(timesheetId)}/entries/${encodeURIComponent(entryId)}`,
        { method: "PATCH", body: JSON.stringify(body) },
      ),
    // Two-step. Called with no token it RETURNS a token and a summary and deletes nothing;
    // called again with that token it deletes. The token lapses after 60 seconds, after which
    // the server answers 409 and the office simply starts again.
    deleteEntry: (timesheetId: string, entryId: string, confirmationToken?: string) =>
      request<{ confirmationToken: string; summary: string } | void>(
        `/timesheets/${encodeURIComponent(timesheetId)}/entries/${encodeURIComponent(entryId)}`,
        { method: "DELETE", body: JSON.stringify(confirmationToken ? { confirmationToken } : {}) },
      ),
    // logTravel / deleteTravel (Session ANF) were removed in Session ANT with the routes behind
    // them. The engineer records travel on the JOB SHEET now, so it rides the completion payload
    // through the offline outbox rather than needing a live request of its own.
    submit: (timesheetId: string) =>
      request<{ status: TimesheetStatus }>(`/timesheets/${encodeURIComponent(timesheetId)}/submit`, { method: "POST" }),
    // `acknowledgePendingJobs` is the office's DELIBERATE override of the pending-visits guard
    // (Session ANF), and is only ever sent by a second press against a refusal that named the count.
    // Never default it and never infer it: a week approved with visits still outstanding cannot have
    // their hours added afterwards without reopening it, which is the whole point of the refusal.
    approve: (timesheetId: string, body?: { acknowledgePendingJobs: true }) =>
      request<{ status: TimesheetStatus; acknowledgedPendingJobs: number | null }>(
        `/timesheets/${encodeURIComponent(timesheetId)}/approve`,
        { method: "POST", ...(body ? { body: JSON.stringify(body) } : {}) },
      ),
    reopen: (timesheetId: string) =>
      request<{ status: TimesheetStatus }>(`/timesheets/${encodeURIComponent(timesheetId)}/reopen`, { method: "POST" }),
    // ---- Session ANH: the rate card check figure, the standby flag and the Xero journal ----
    // ADMIN ONLY on the server (a 403 for the office role): keyed on engineer + week like sheet(),
    // and answers for a week with no timesheet row yet.
    pay: (params: { engineerId: string; weekStarting?: string }) => {
      const qs = new URLSearchParams();
      qs.set("engineerId", params.engineerId);
      if (params.weekStarting) qs.set("weekStarting", params.weekStarting);
      return request<TimesheetPayReport>(`/timesheets/pay?${qs.toString()}`);
    },
    // The office's standby-week decision. Refused on an APPROVED week; clearing is the same call
    // with false. Needs a timesheet row: call ensure() first for a week with nothing on it.
    setStandby: (timesheetId: string, standbyWeek: boolean) =>
      request<{ standbyWeek: boolean }>(`/timesheets/${encodeURIComponent(timesheetId)}/standby`, {
        method: "PATCH",
        body: JSON.stringify({ standbyWeek }),
      }),
    // Draft the zero-net journal in Xero for an APPROVED week. Admin only. `acknowledgeExceptions`
    // is the deliberate override of the review-needed refusal, sent only by a second press against a
    // refusal that named the count, exactly as approve()'s acknowledgePendingJobs is.
    draftXeroJournal: (timesheetId: string, body?: { acknowledgeExceptions: true }) =>
      request<{ journalId: string; amountPence: number; acknowledgedReviewCount: number | null }>(
        `/timesheets/${encodeURIComponent(timesheetId)}/xero-journal`,
        { method: "POST", ...(body ? { body: JSON.stringify(body) } : {}) },
      ),
    // Release the journal record so a corrected week can be drafted again. Admin only. A drafted
    // journal is released only once Xero says it is voided or deleted (checked live); a stuck draft
    // attempt is released once it is stale. Touches nothing in Xero.
    clearXeroJournal: (timesheetId: string) =>
      request<{ cleared: "stale_claim" | "journal_gone"; journalId: string | null; xeroStatus: string | null }>(
        `/timesheets/${encodeURIComponent(timesheetId)}/xero-journal`,
        { method: "DELETE" },
      ),
    // Session AVA: the weekly statement of hours (hours only, never money). Generated from an APPROVED week; released by a
    // person to the Subcontractor Portal or as a Gmail DRAFT (never sent); withdrawn from the portal.
    statement: (timesheetId: string) => request<TimesheetStatementView>(`/timesheets/${encodeURIComponent(timesheetId)}/statement`, { noCache: true }),
    downloadStatement: (timesheetId: string, filename: string) => downloadPdf(`/api/timesheets/${encodeURIComponent(timesheetId)}/statement/pdf`, filename),
    previewStatementRelease: (timesheetId: string, via: "portal" | "email_draft") =>
      request<TimesheetStatementReleasePreview>(`/timesheets/${encodeURIComponent(timesheetId)}/statement/release`, { method: "POST", body: JSON.stringify({ via }) }),
    releaseStatement: (timesheetId: string, via: "portal" | "email_draft") =>
      request<{ released?: boolean; drafted?: boolean; via: string; company?: string; to?: string }>(`/timesheets/${encodeURIComponent(timesheetId)}/statement/release`, {
        method: "POST",
        body: JSON.stringify({ via, preview: false }),
      }),
    withdrawStatement: (timesheetId: string) => request<{ withdrawn: true }>(`/timesheets/${encodeURIComponent(timesheetId)}/statement/release`, { method: "DELETE" }),
  },

  // ---- Scheduled report delivery (Session BBN). A schedule only ever leaves a Gmail draft; run makes one now.
  reportSchedules: {
    list: () => request<ReportScheduleList>("/report-schedules", { noCache: true }),
    create: (body: { reportType: string; cadence: string; recipient: string; enabled?: boolean }) =>
      request<ReportScheduleItem>("/report-schedules", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: { reportType?: string; cadence?: string; recipient?: string; enabled?: boolean }) =>
      request<ReportScheduleItem>(`/report-schedules/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
    run: (id: string) => request<ReportScheduleRunResult>(`/report-schedules/${encodeURIComponent(id)}/run`, { method: "POST", body: JSON.stringify({}) }),
    remove: (id: string) => request<{ deleted: true }>(`/report-schedules/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },

  // ---- Work orders (Session ATZ, email system Phase 2, Item 3): requests a property platform (Dwellant) assigned to ELE,
  // proposed by the email engine. Raising the work goes through api.callouts.create / api.schedule.plannedRepair with the
  // proposal's id, never through here.
  // Session AUB (email system Phase 4): the Mail workspace. Lists and the review card read live; every write is a person's
  // decision on the conversation's one enquiry record. The SEND is not here: prepareSend makes the reply the enquiry's
  // reviewed draft, then enquiries.sendPreview and enquiries.approveAndSend send it (the one person-pressed path).
  // Session AUH (email system Phase 7): chasing. Every write previews by default (preview: true) and acts on preview: false.
  chases: {
    list: (params: { status?: "open" | "ended" | "all"; view?: "due" | "calls" | "referral" | "all"; ladder?: string; search?: string; limit?: number; offset?: number } = {}) => {
      const q = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
      return request<ChaseListResponse>(`/chases${q.size ? `?${q.toString()}` : ""}`, { noCache: true });
    },
    get: (id: string) => request<{ chase: Chase }>(`/chases/${encodeURIComponent(id)}`, { noCache: true }),
    metrics: (from?: string, to?: string) => {
      const q = new URLSearchParams();
      if (from) q.set("from", from);
      if (to) q.set("to", to);
      return request<ChaseMetrics>(`/chases/metrics${q.size ? `?${q.toString()}` : ""}`, { noCache: true });
    },
    settings: () => request<ChaseSettings>("/chases/settings", { noCache: true }),
    setLadders: (ladders: Record<string, number[] | null>, preview = false) =>
      request<{ preview: boolean; ladders: Array<{ key: string; steps: number[] }> }>("/chases/settings/ladder", { method: "PUT", body: JSON.stringify({ ladders, preview }) }),
    setSuppliers: (suppliers: ChaseSupplierFlag[], preview = false) =>
      request<{ preview: boolean; suppliers: ChaseSupplierFlag[] }>("/chases/settings/suppliers", { method: "PUT", body: JSON.stringify({ suppliers, preview }) }),
    setLateInterestRates: (referenceRates: ChaseReferenceRate[], preview = false) =>
      request<{ preview: boolean; referenceRates: ChaseReferenceRate[] }>("/chases/settings/late-interest", { method: "PUT", body: JSON.stringify({ referenceRates, preview }) }),
    setRecovery: (name: string | null, email: string | null, preview = false) =>
      request<{ preview: boolean; recovery: { name: string | null; email: string | null } }>("/chases/settings/recovery", { method: "PUT", body: JSON.stringify({ name, email, preview }) }),
    create: (source: ChaseCreateSource, opts: { dueAt?: string; forJobId?: string; preview?: boolean } = {}) =>
      request<{ preview: boolean; created?: boolean; message?: string; chase?: Chase | null; wouldJoin?: boolean }>("/chases", {
        method: "POST",
        body: JSON.stringify({ source, ...(opts.dueAt ? { dueAt: opts.dueAt } : {}), ...(opts.forJobId ? { forJobId: opts.forJobId } : {}), preview: opts.preview ?? false }),
      }),
    run: () => request<{ preview: false; [k: string]: unknown }>("/chases/run", { method: "POST", body: JSON.stringify({ preview: false }) }),
    draft: (id: string, instruction?: string) =>
      request<{ preview: false; ended: boolean; chase: Chase | null; message: string }>(`/chases/${encodeURIComponent(id)}/draft`, {
        method: "POST",
        body: JSON.stringify({ ...(instruction ? { instruction } : {}), preview: false }),
      }),
    editDraft: (id: string, body: string, preview = false) =>
      request<{ preview: boolean; warnings: ChaseFailure[]; chase?: Chase }>(`/chases/${encodeURIComponent(id)}/draft`, { method: "PATCH", body: JSON.stringify({ body, preview }) }),
    previewSend: (items: Array<{ id: string; fingerprint: string }>) =>
      request<{ preview: true; items: ChaseSendPreviewItem[]; wouldSend: number }>("/chases/send", { method: "POST", body: JSON.stringify({ items, preview: true }) }),
    send: (items: Array<{ id: string; fingerprint: string }>) =>
      request<{ preview: false; results: ChaseSendResult[]; sent: number; failed: number; refused: number }>("/chases/send", { method: "POST", body: JSON.stringify({ items, preview: false }) }),
    refer: (id: string, opts: { preview: boolean; fingerprint?: string }) =>
      request<{
        preview: boolean;
        prepared?: boolean;
        wouldSend?: boolean;
        message?: string;
        reason?: string;
        pack?: { to: string; subject?: string };
        notice?: { to: string; cc?: string | null; subject?: string };
        fingerprint?: string | null;
        packTo?: string;
        noticeTo?: string;
        note?: string;
        chase?: Chase | null;
      }>(`/chases/${encodeURIComponent(id)}/refer`, { method: "POST", body: JSON.stringify({ preview: opts.preview, ...(opts.fingerprint ? { fingerprint: opts.fingerprint } : {}) }) }),
    snooze: (id: string, until: string | null) =>
      request<{ preview: false; chase: Chase }>(`/chases/${encodeURIComponent(id)}/snooze`, { method: "POST", body: JSON.stringify({ until, preview: false }) }),
    stop: (id: string, reason: string) =>
      request<{ preview: false; chase: Chase; draftsRemoved: number; draftNotRemoved?: string | null }>(`/chases/${encodeURIComponent(id)}/stop`, { method: "POST", body: JSON.stringify({ reason, preview: false }) }),
    override: (id: string, change: { tone?: ChaseTone | null; dueAt?: string | null }) =>
      request<{ preview: false; chase: Chase | null }>(`/chases/${encodeURIComponent(id)}/override`, { method: "POST", body: JSON.stringify({ ...change, preview: false }) }),
    call: (id: string, action: "convert" | "record", callLogId?: string) =>
      request<{ preview: false; chase: Chase }>(`/chases/${encodeURIComponent(id)}/call`, { method: "POST", body: JSON.stringify({ action, ...(callLogId ? { callLogId } : {}), preview: false }) }),
    remove: (id: string) => request<{ deleted: true }>(`/chases/${encodeURIComponent(id)}`, { method: "DELETE" }),
    // Session BAB: the customer's "we've got this" hold.
    getHold: (customerId: string) =>
      request<ChaseCustomerHold>(`/chases/customer-hold/${encodeURIComponent(customerId)}`),
    setHold: (customerId: string, until: string | null, reason: string | null, preview: boolean) =>
      request<{ preview: boolean; customer: string; until: string | null; reason: string | null; openChasesAffected: number }>(`/chases/customer-hold`, {
        method: "PUT",
        body: JSON.stringify({ customerId, until, reason, preview }),
      }),
  },
  mail: {
    list: (params: MailListParams) => {
      const q = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v === undefined || v === null || v === "") continue;
        q.set(k, String(v));
      }
      return request<{ lane: MailMessageLane; messages: MailListRow[]; total: number; limit: number; offset: number }>(`/mail/messages?${q.toString()}`, { noCache: true });
    },
    counts: () => request<MailCounts>("/mail/counts", { noCache: true }),
    // Session AUD (Item 2): the Outbox.
    outbox: {
      list: () =>
        request<{ items: OutboxNotice[]; counts: { total: number; sendable: number; failed: number; blocked: number } }>("/mail/outbox", { noCache: true }),
      previewSend: (items: Array<{ key: string; fingerprint: string }>) =>
        request<{ preview: true; items: OutboxPreviewItem[]; wouldSend: number }>("/mail/outbox/send", { method: "POST", body: JSON.stringify({ items, preview: true }) }),
      send: (items: Array<{ key: string; fingerprint: string }>) =>
        request<{ preview: false; results: OutboxSendResult[]; sent: number; failed: number; refused: number }>("/mail/outbox/send", {
          method: "POST",
          body: JSON.stringify({ items, preview: false }),
        }),
      dismiss: (key: string) => request<{ dismissed: true; noticeId: string }>("/mail/outbox/dismiss", { method: "POST", body: JSON.stringify({ key }) }),
      dismissed: () => request<{ dismissed: OutboxDismissal[] }>("/mail/outbox/dismissed", { noCache: true }),
      restore: (decisionId: string) => request<{ removed: true }>(`/mail/outbox/decisions/${encodeURIComponent(decisionId)}`, { method: "DELETE" }),
    },
    assignees: () => request<{ assignees: MailAssignee[] }>("/mail/assignees"),
    get: (messageId: string) => request<{ message: MailMessageDetail }>(`/mail/messages/${encodeURIComponent(messageId)}`, { noCache: true }),
    getEnquiry: (enquiryId: string) =>
      request<{ message?: MailMessageDetail; redirectMessageId?: string }>(`/mail/enquiries/${encodeURIComponent(enquiryId)}`, { noCache: true }),
    prepareSend: (messageId: string, bodyText?: string) =>
      request<{ prepared: MailPrepared }>(`/mail/messages/${encodeURIComponent(messageId)}/prepare-send`, {
        method: "POST",
        body: JSON.stringify(bodyText === undefined ? {} : { bodyText }),
      }),
    // The conversation actions take a MailListRow's own target: its message, or an older enquiry's id.
    assign: (target: MailTarget, userId: string | null) =>
      request<{ enquiryId: string; assignedToUserId: string | null; assignedName: string | null }>(`${mailTarget(target)}/assign`, {
        method: "POST",
        body: JSON.stringify({ userId }),
      }),
    snooze: (target: MailTarget, until: string | null) =>
      request<{ enquiryId: string; snoozedUntil: string | null }>(`${mailTarget(target)}/snooze`, { method: "POST", body: JSON.stringify({ until }) }),
    dismiss: (target: MailTarget, reason: MailDismissReason, note?: string) =>
      request<{ enquiryId: string; dismissed: true }>(`${mailTarget(target)}/dismiss`, {
        method: "POST",
        body: JSON.stringify(note ? { reason, note } : { reason }),
      }),
    restore: (target: MailTarget) => request<{ enquiryId: string; dismissed: false }>(`${mailTarget(target)}/restore`, { method: "POST" }),
    // Session SC4: a message the engine settled as needing no reply (or filed) goes back in front of a person.
    bringBack: (messageId: string) =>
      request<{ messageId: string; brought: boolean; wasFiled: boolean; wasDismissed?: boolean; wasSnoozed?: boolean; previousReason: string | null; fileLabelRemoved?: boolean | null }>(`/mail/messages/${encodeURIComponent(messageId)}/bring-back`, {
        method: "POST",
        body: JSON.stringify({}),
      }),
    match: (target: MailTarget, customerId: string | null, siteId?: string | null) =>
      request<{ enquiryId: string; customerId: string | null; siteId: string | null }>(`${mailTarget(target)}/match`, {
        method: "POST",
        body: JSON.stringify(siteId === undefined ? { customerId } : { customerId, siteId }),
      }),
    engineerEmail: (userId: string) =>
      request<EngineerRecentEmail>(`/mail/engineers/${encodeURIComponent(userId)}/email`, { noCache: true }),
    customerEmail: (customerId: string, params?: { direction?: "inbound" | "sent"; limit?: number; offset?: number; before?: string }) => {
      const q = new URLSearchParams();
      if (params?.direction) q.set("direction", params.direction);
      if (params?.limit != null) q.set("limit", String(params.limit));
      if (params?.offset != null) q.set("offset", String(params.offset));
      if (params?.before) q.set("before", params.before);
      const qs = q.toString();
      return request<CustomerEmailPage>(
        `/mail/customers/${encodeURIComponent(customerId)}/email${qs ? `?${qs}` : ""}`,
        { noCache: true },
      );
    },
  },
  workOrders: {
    list: (params: { status?: WorkOrderStatusFilter; search?: string; limit?: number; offset?: number } = {}) => {
      const q = new URLSearchParams();
      if (params.status) q.set("status", params.status);
      if (params.search) q.set("search", params.search);
      if (params.limit) q.set("limit", String(params.limit));
      if (params.offset) q.set("offset", String(params.offset));
      const qs = q.toString();
      return request<{ workOrders: WorkOrder[]; total: number; counts: { pending: number; accepted: number; dismissed: number } }>(
        `/work-orders${qs ? `?${qs}` : ""}`,
        { noCache: true },
      );
    },
    get: (id: string) => request<WorkOrderDetail>(`/work-orders/${encodeURIComponent(id)}`, { noCache: true }),
    setSite: (id: string, siteId: string | null, rememberName = false) =>
      request<{ workOrder: WorkOrder; rememberedAlias: string | null }>(`/work-orders/${encodeURIComponent(id)}/site`, {
        method: "POST",
        body: JSON.stringify(siteId ? { siteId, rememberName } : { siteId: null }),
      }),
    dismiss: (id: string, reason?: string) =>
      request<{ workOrder: WorkOrder }>(`/work-orders/${encodeURIComponent(id)}/dismiss`, { method: "POST", body: JSON.stringify(reason ? { reason } : {}) }),
    restore: (id: string) => request<{ workOrder: WorkOrder }>(`/work-orders/${encodeURIComponent(id)}/restore`, { method: "POST" }),
    remove: (id: string) => request<{ ok: true }>(`/work-orders/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },

  // ---- Office notices (Session ATZ, email system Phase 2, Item 1): the platform's notices to the office, on the dashboard,
  // in place of the emails it used to send to its own inbox.
  officeNotices: {
    list: (params: { unread?: boolean; limit?: number; offset?: number } = {}) => {
      const q = new URLSearchParams();
      if (params.unread) q.set("unread", "true");
      if (params.limit) q.set("limit", String(params.limit));
      if (params.offset) q.set("offset", String(params.offset));
      const qs = q.toString();
      return request<{ notices: OfficeNotice[]; total: number; unreadCount: number }>(`/office-notices${qs ? `?${qs}` : ""}`, { noCache: true });
    },
    markRead: (id: string) => request<{ ok: true }>(`/office-notices/${encodeURIComponent(id)}/read`, { method: "POST" }),
    markUnread: (id: string) => request<{ ok: true }>(`/office-notices/${encodeURIComponent(id)}/unread`, { method: "POST" }),
    markAllRead: () => request<{ marked: number }>("/office-notices/read-all", { method: "POST" }),
    remove: (id: string) => request<{ ok: true }>(`/office-notices/${encodeURIComponent(id)}`, { method: "DELETE" }),
    removeRead: () => request<{ deleted: number }>("/office-notices/read", { method: "DELETE" }),
  },

  // ---- The standby rota (Session ANY) ----
  // Office AND admin, unlike the timesheet reads above: who is on call tonight is an operations
  // fact. Nothing here returns a rate or a figure.
  standby: {
    // The rota plus the resolved weeks forward. `weeks` defaults to 12 on the server.
    overview: (params?: { fromWeek?: string; weeks?: number }) => {
      const qs = new URLSearchParams();
      if (params?.fromWeek) qs.set("fromWeek", params.fromWeek);
      if (params?.weeks != null) qs.set("weeks", String(params.weeks));
      const suffix = qs.toString();
      return request<StandbyOverview>(`/standby${suffix ? `?${suffix}` : ""}`);
    },
    // A full REPLACE of the rotation: the engineers not sent are no longer in it. Refused if an
    // engineer appears twice or is no longer active.
    setRota: (body: { startWeek: string; weeksPerTurn: number; engineerIds: string[]; notes?: string | null }) =>
      request<Omit<StandbyRota, "notes">>("/standby/rota", { method: "PUT", body: JSON.stringify(body) }),
    // Remove the rota entirely (RULE 1). Per-week overrides survive; every other week falls back to
    // nobody until a new rota is set.
    deleteRota: () => request<{ deleted: true }>("/standby/rota", { method: "DELETE" }),
    // Set ONE week, overriding the pattern. engineerId null is a deliberate "nobody on standby".
    setWeek: (weekStarting: string, body: { engineerId: string | null; reason?: string | null }) =>
      request<StandbyWeek>(`/standby/weeks/${encodeURIComponent(weekStarting)}`, { method: "PUT", body: JSON.stringify(body) }),
    // Clear that override so the week falls back to the pattern. NOT the same as setting it to
    // nobody, which is a decision; this removes the decision.
    clearWeek: (weekStarting: string) =>
      request<{ cleared: true; week: StandbyWeek }>(`/standby/weeks/${encodeURIComponent(weekStarting)}`, { method: "DELETE" }),
    // Session ATU. The preview is never cached: it names who would receive an email right now.
    noticePreview: (weeks: StandbyNoticeWeeks) => request<StandbyNoticePreview>(`/standby/notice?weeks=${weeks}`, { noCache: true }),
    // The rota PDF itself, opened in a new tab before sending (a same-origin GET with the session cookie).
    noticePdfUrl: (weeks: StandbyNoticeWeeks) => `/api/standby/notice/pdf?weeks=${weeks}`,
    // Sends one email per engineer. One engineer's failure never stops the others; sending again sends again.
    sendNotice: (weeks: StandbyNoticeWeeks) => request<StandbyNoticeResult>("/standby/notice", { method: "POST", body: JSON.stringify({ weeks }) }),
  },

  // ---- Lone worker safety (Session AOB) ----
  // The office side. There is deliberately no check-in call here: a check-in is an explicit tap by
  // the person on site, and an office control that could make one would defeat the whole mechanism.
  loneWorker: {
    // noCache (Session AQT): this is the 60-second safety poll LoneWorkerPage's own comment names
    // as "the one office screen somebody might sit and watch while ringing an engineer who is not
    // answering" -- a cached minute-old status here is exactly the harm the cache's own opt-out
    // exists to prevent, never a genuine performance case.
    // `background` (Session ASB review, F4): the page's 60-second poll passes it, so a screen left open does not keep
    // the office session alive; the first load and a person's refresh do not.
    sessions: (opts: { background?: boolean } = {}) =>
      request<LoneWorkerSessions>("/lone-worker/sessions", { noCache: true, ...(opts.background ? { background: true } : {}) }),
    // The office half of the stop condition: the escalation repeats "until the engineer checks in or
    // the office marks it resolved". Closing ends the session and stops the alerts.
    close: (id: string, note?: string) =>
      request<{ closed: true }>(`/lone-worker/sessions/${encodeURIComponent(id)}/close`, {
        method: "POST",
        body: JSON.stringify(note?.trim() ? { note: note.trim() } : {}),
      }),
    // RULE 1. Admin only, and refused 409 once the session has raised an alert: at that point the row
    // is the record of what the office was told and how it was answered.
    remove: (id: string) => request<{ deleted: true }>(`/lone-worker/sessions/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },

  // Global record search (nav search is client-side; this is the full-text record search). Returns
  // matches grouped by entity type, each capped to `limit` (default 3). Tenant-scoped server-side.
  search: (q: string, limit = 3) => {
    const params = new URLSearchParams({ q, limit: String(limit) });
    return request<SearchResults>(`/search?${params.toString()}`);
  },

  // UK postcode -> address autocomplete (Session AV, Item 3). Proxied server-side (Google Places),
  // so the API key stays on the server. `configured` is false when no key is set (the form then stays
  // plain manual entry). `details` resolves a chosen prediction to a full address + postcode.
  addressLookup: {
    search: (query: string) => request<AddressLookupResult>(`/address-lookup?query=${encodeURIComponent(query)}`),
    details: (placeId: string) => request<AddressDetails>(`/address-lookup/details?placeId=${encodeURIComponent(placeId)}`),
  },

  // Free address lookup for the office quote calculator (Session AAP): postcodes.io postcode
  // validation + Nominatim free-text autocomplete. No paid key; every call degrades to manual entry.
  address: {
    // Validate a postcode and resolve town/district/region + lat/lng (served from the tenant cache
    // when warm). ok=false means "not resolved" (the form keeps manual entry).
    postcode: (postcode: string) => request<PostcodeLookupResponse>(`/address/postcode?postcode=${encodeURIComponent(postcode)}`),
    // Autocomplete a free-text address into suggestions ([] for a short query or any failure).
    search: (query: string) => request<AddressSearchResponse>(`/address/search?query=${encodeURIComponent(query)}`),
    // Clear the postcode lookup cache (Settings; DELETE RULE). Whole cache, or one postcode.
    clearCache: () => request<{ deleted: number }>("/address/postcode-cache", { method: "DELETE" }),
    clearCacheEntry: (postcode: string) =>
      request<{ deleted: number }>(`/address/postcode-cache/${encodeURIComponent(postcode)}`, { method: "DELETE" }),
  },

  // Companies House company lookup for the quote form's client block (Session AAP). Server-proxied
  // (the free key stays server-side). `configured:false` -> the form stays manual entry.
  companies: {
    search: (query: string) => request<CompanySearchResponse>(`/companies/search?query=${encodeURIComponent(query)}`),
    get: (companyNumber: string) => request<CompanyProfile>(`/companies/${encodeURIComponent(companyNumber)}`),
  },

  // Pre-fill a quote from existing records (Session AAP, Loop 3): autocomplete existing customers /
  // sites so raising a quote against one fills every known field and links it.
  quotePrefill: {
    customers: (query: string) => request<CustomerPrefill[]>(`/quote-prefill/customers?query=${encodeURIComponent(query)}`),
    sites: (query: string) => request<SitePrefill[]>(`/quote-prefill/sites?query=${encodeURIComponent(query)}`),
  },

  // Office NPS view (read-only): the responded surveys (optional category filter) + the summary panel.
  nps: {
    list: (category?: NpsCategory) => request<NpsListItem[]>(`/nps${category ? `?category=${encodeURIComponent(category)}` : ""}`),
    summary: () => request<NpsOfficeSummary>("/nps/summary"),
    // Manual/ad-hoc survey send to a chosen customer (Session AR Item 18).
    send: (body: { customerId: string; email?: string; note?: string }) =>
      request<{ ok: true }>("/nps/send", { method: "POST", body: JSON.stringify(body) }),
  },

  preferences: {
    // The office sidebar state: the structured nav `layout` ([{label, items:[to,...]}], Session AD,
    // Item 4) OR the legacy flat `order` (a list of nav `to` paths, for a row saved before Item 4),
    // plus the user's expanded nav groups (section labels). Exactly one of layout/order is non-null;
    // all are null/empty for the default state.
    getNavOrder: () =>
      request<{ order: string[] | null; layout?: NavLayoutSection[] | null; expandedGroups?: string[] | null }>("/me/nav-order"),
    // Save the full structured layout (a cross-group drag / group move); an empty layout resets to
    // the default. Returns the stored state.
    setNavLayout: (layout: NavLayoutSection[]) =>
      request<{ order: string[] | null; layout?: NavLayoutSection[] | null; expandedGroups?: string[] | null }>("/me/nav-order", {
        method: "PUT",
        body: JSON.stringify({ layout }),
      }),
    // Save the expanded nav groups (collapse/expand state); an empty list resets to default.
    setExpandedGroups: (expandedGroups: string[]) =>
      request<{ order: string[] | null; layout?: NavLayoutSection[] | null; expandedGroups?: string[] | null }>("/me/nav-order", {
        method: "PUT",
        body: JSON.stringify({ expandedGroups }),
      }),
  },

  jobsheets: {
    // archived: "archived" lists only archived sheets (for reactivation); omit for the
    // default (active) list. kind splits the list into the Service Records (service/loler/
    // repair) and Call-outs (callout) views; omit for every sheet.
    // Scale target S1: queue narrows the list to one stage of the release gate (awaiting_release: closed by the engineer,
    // not yet released; ready_to_send: released, not yet sent or recorded as sent outside Guvnor).
    // Session SPD1: sent_today is the third tab (reports whose send date is today); view "queue" asks for the queue row only (a reference,
    // a site, the warning pills' facts, the summary and the send state) instead of the whole record, about a sixth of the bytes.
    list: (opts?: { archived?: "archived"; kind?: "service" | "callout"; queue?: "awaiting_release" | "ready_to_send" | "sent_today"; view?: "queue" }) => {
      const params = new URLSearchParams();
      if (opts?.archived) params.set("archived", opts.archived);
      if (opts?.kind) params.set("kind", opts.kind);
      if (opts?.queue) params.set("queue", opts.queue);
      if (opts?.view) params.set("view", opts.view);
      const qs = params.toString();
      // noCache for a queue read (Clearance 3): the release and send queue is send state, and its page re-reads the list
      // before every recipients check and every send precisely so a visit that left the queue is skipped. A cached copy
      // would hand that re-read the old list. The plain list (no queue) keeps the cache.
      return request<JobSheetListItem[]>(`/jobsheets${qs ? `?${qs}` : ""}`, opts?.queue ? { noCache: true } : undefined);
    },
    // Session SC2B: one PAGE of the list, slim, sorted on the server, with the true total of all matching sheets.
    listPage: (opts: { archived?: "archived"; kind?: "service" | "callout"; limit: number; offset: number; sort?: JobSheetListSortKey; dir?: "asc" | "desc" }) => {
      const params = new URLSearchParams();
      if (opts.archived) params.set("archived", opts.archived);
      if (opts.kind) params.set("kind", opts.kind);
      params.set("limit", String(opts.limit));
      params.set("offset", String(opts.offset));
      if (opts.sort) {
        params.set("sort", opts.sort);
        params.set("dir", opts.dir ?? "asc");
      }
      params.set("view", "list");
      return request<{ items: JobSheetSlimItem[]; total: number }>(`/jobsheets?${params.toString()}`);
    },
    // One record by job id (Session ADE): powers the inline service-record panel on the lift
    // detail page. Same allow-list DTO as the list; 404 when the job has no sheet.
    get: (jobId: string) => request<JobSheetListItem>(`/jobsheets/${encodeURIComponent(jobId)}`),
    // Manually create a backdated completed service/call-out record (Session AR Item 11). Does not
    // email, invoice or generate a PDF; it just writes the history record.
    createManual: (body: {
      assetId: string;
      visitDate: string;
      visitType: "service" | "callout";
      engineerName?: string;
      outcome: "pass" | "fail" | "observations";
      notes?: string;
      defectNotes?: string;
    }) => request<{ ok: true; jobId: string; visitType: string }>("/jobsheets/manual", { method: "POST", body: JSON.stringify(body) }),
    // The Service Record PDF is a direct browser download (same-origin URL), not a fetch.
    // Since Session ADJ this is the SHORT branded customer summary; fullPdfUrl is the
    // office-only full itemised checklist record (never sent to a customer automatically).
    pdfUrl: (jobId: string) => `/api/jobsheets/${encodeURIComponent(jobId)}/pdf`,
    fullPdfUrl: (jobId: string) => `/api/jobsheets/${encodeURIComponent(jobId)}/pdf/full`,
    // Email the Service Record PDF to the customer (BCC office) + attach it to the job's
    // DRAFT Xero invoice. Returns when the send was stamped. emailSentTo is the resolved
    // recipient on a fresh send; omitted (undefined) on an already-sent/claim-lost reply, where
    // the caller should fall back to whatever recipient it already has on file.
    // Session BBH: To and Cc chosen by the person; the server PREVIEWS unless preview is false (the confirm names every
    // address first), and sendAgain sends a record already sent and unchanged (to someone else, say).
    sendToClient: (jobId: string, body: ReportSendBody = {}) =>
      request<{ sentToClientAt: string; alreadySent: boolean; emailSentTo?: string | null; to?: string[]; cc?: string[] }>(
        `/jobsheets/${encodeURIComponent(jobId)}/send-to-client`,
        { method: "POST", body: JSON.stringify({ ...body, preview: false }) },
      ),
    sendToClientPreview: (jobId: string, body: Omit<ReportSendBody, "sendAgain"> = {}) =>
      request<ReportSendPreview>(`/jobsheets/${encodeURIComponent(jobId)}/send-to-client`, { method: "POST", body: JSON.stringify({ ...body, preview: true }) }),
    // Approve a submitted job: generate the service/call-out PDF, file it to Drive, put it in front
    // of the customer, then complete the job. The error carries the failed step + message.
    //
    // Since Session AGX the two job types differ here and the UI must not conflate them. A CALL-OUT
    // is emailed inline and comes back with emailSentTo set. A SERVICE visit is DRAFTED and sends
    // nothing, so emailSentTo is null BY DESIGN and draftNote says what is waiting in Gmail (or why
    // nothing is). Reading emailSentTo alone would have the office told a customer received a
    // certificate nobody has sent. invoiceNote reports the visit's billing decision either way.
    // timesheetNote (Session AEL, surfaced client-side Session AKY): set only when this visit's
    // on-site hours were NOT added to the engineer's timesheet (no engineer assigned, or that week
    // is already approved) -- the two cases generateTimesheetForApprovedJob's own header calls out
    // as a real loss that must never be a silent log line. Absent/null on the ordinary path, where
    // the entry was written (or correctly skipped as an existing hand-typed one).
    approve: (jobId: string) =>
      request<{
        ok: boolean;
        status: string;
        alreadyApproved: boolean;
        driveFileUrl: string | null;
        emailSentTo: string | null;
        draftNote?: string | null;
        draftedTo?: string | null;
        invoiceNote?: string | null;
        timesheetNote?: string | null;
        // Session ARK (W-10): the OPEN defects this job was raised to fix (Defect.remedialJobId),
        // offered to the office to resolve. Empty for a job no defect points at; absent on a repeat
        // approval (alreadyApproved), since the first approval made the offer.
        defectsToReview?: RemedialDefectOffer[];
      }>(`/jobsheets/${encodeURIComponent(jobId)}/approve`, { method: "POST" }),
    // Resend the approval email using the already-filed Drive PDF (does not regenerate it).
    resend: (jobId: string, body: Omit<ReportSendBody, "sendAgain"> = {}) =>
      request<{ ok: boolean; emailSentTo: string | null; to?: string[]; cc?: string[] }>(`/jobsheets/${encodeURIComponent(jobId)}/resend`, {
        method: "POST",
        body: JSON.stringify({ ...body, preview: false }),
      }),
    resendPreview: (jobId: string, body: Omit<ReportSendBody, "sendAgain"> = {}) =>
      request<ReportSendPreview>(`/jobsheets/${encodeURIComponent(jobId)}/resend`, { method: "POST", body: JSON.stringify({ ...body, preview: true }) }),
    // Session BBH: record a report the office sent from Gmail by hand (sends nothing), and take that record away again.
    markSentOutside: (jobId: string, body: { sentAt: string; to: string[]; cc?: string[]; note?: string }) =>
      request<{ ok: boolean; sentAt: string; to: string[]; cc: string[]; note: string | null; becameLatestSend: boolean }>(
        `/jobsheets/${encodeURIComponent(jobId)}/sent-outside`,
        { method: "POST", body: JSON.stringify({ ...body, preview: false }) },
      ),
    // Session SPD1: record several reports as sent outside Guvnor in one request (sends nothing). One date and note, each report's own
    // addresses; one result per report, and a refusal on one stops nothing else. The preview writes nothing.
    markSentOutsideBatch: (body: { sentAt: string; note?: string; items: Array<{ jobId: string; to: string[]; cc?: string[] }> }, preview = false) =>
      request<SentOutsideBatchResult>(`/jobsheets/sent-outside-batch`, { method: "POST", body: JSON.stringify({ ...body, preview }) }),
    clearSentOutside: (jobId: string) =>
      request<{ ok: boolean; cleared: boolean; restoredEarlierSend: boolean }>(`/jobsheets/${encodeURIComponent(jobId)}/sent-outside/clear`, {
        method: "POST",
        body: JSON.stringify({ preview: false }),
      }),
    // Session BDG2: reopen an APPROVED job (admin only). The preview names what goes and what is kept; a refusal is a 409
    // carrying every reason (ApiError body.refusals). The commit needs a reason of ten characters or more.
    reopenApprovedPreview: (jobId: string) =>
      request<ReopenApprovalPlan>(`/jobsheets/${encodeURIComponent(jobId)}/reopen-approved`, { method: "POST", body: JSON.stringify({ preview: true }) }),
    reopenApproved: (jobId: string, reason: string) =>
      request<ReopenApprovalPlan & { reopened: true; driveNote: string | null }>(`/jobsheets/${encodeURIComponent(jobId)}/reopen-approved`, {
        method: "POST",
        body: JSON.stringify({ preview: false, reason }),
      }),
    // Return a submitted job to the engineer for review (status -> in_progress, with a reason).
    returnToEngineer: (jobId: string, reason: string) =>
      request<{ ok: boolean }>(`/jobsheets/${encodeURIComponent(jobId)}/return`, { method: "POST", body: JSON.stringify({ reason }) }),
    // Delete a job sheet. The server archives a completed/invoiced/sent sheet (a service
    // record) and hard-deletes a pre-completion stub; the response says which happened.
    remove: (jobId: string) =>
      request<{ ok: boolean; archived: boolean; message?: string }>(`/jobsheets/${encodeURIComponent(jobId)}`, { method: "DELETE" }),
    reactivate: (jobId: string) => request<{ ok: boolean }>(`/jobsheets/${encodeURIComponent(jobId)}/reactivate`, { method: "POST" }),
    // Session ASW (D10): the office adds, corrects or removes a risk assessment on a completed record.
    recordRiskAssessment: (jobId: string, body: RiskAssessmentInput) =>
      request<{ ok: boolean; added: boolean }>(`/jobsheets/${encodeURIComponent(jobId)}/risk-assessment`, { method: "PUT", body: JSON.stringify(body) }),
    removeRiskAssessment: (jobId: string) =>
      request<{ ok: boolean }>(`/jobsheets/${encodeURIComponent(jobId)}/risk-assessment`, { method: "DELETE" }),
    // Amend a submitted/completed sheet (Session ABD Loop 1): correct a checklist item, add or
    // remove a part, add a defect, edit a note. Only the sent fields change; every amendment is
    // appended to the on-sheet audit trail. amendedSinceSent is true when the sheet had already
    // been delivered (re-send prompt); invoiceReviewNeeded flags a DRAFT invoice to review by hand.
    // timesheetNote (Session AKY): set when a corrected arrival/departure could not reach the
    // TimesheetEntry approval already wrote (see approve() above for the two real-loss reasons).
    amend: (
      jobId: string,
      body: {
        checklist?: JobSheetChecklistEntry[];
        defects?: string[];
        partsUsed?: JobSheetPartUsed[];
        advisoryNotes?: string | null;
        engineerNotes?: string | null;
        faultFound?: string | null;
        workCarriedOut?: string | null;
        liftStatusOnLeaving?: string | null;
        // Visit times + date (Session ADJ): ISO datetimes; null clears a time recorded in
        // error. visitDate corrects the job's completion date. labourMinutes is recomputed
        // server-side from the resulting pair.
        arrivalTime?: string | null;
        departureTime?: string | null;
        visitDate?: string;
        reason?: string;
      },
    ) =>
      request<{
        ok: true;
        amendedSinceSent: boolean;
        invoiceReviewNeeded: boolean;
        amendment: { at: string; summary: string; byName: string | null };
        timesheetNote?: string | null;
      }>(`/jobsheets/${encodeURIComponent(jobId)}`, { method: "PATCH", body: JSON.stringify(body) }),
    // The standard checklist template for a job (office-assisted completion, Session ADJ): the
    // same resolved per-lift-type checklist the engineer PWA would show, so the office can record
    // the engineer's phone-confirmed results onto a sheet that has none.
    checklistTemplate: (jobId: string) =>
      request<{ name: string; items: { id: string; item: string; category?: string }[] }>(
        `/jobsheets/${encodeURIComponent(jobId)}/checklist-template`,
      ),
    // The same template keyed on the LIFT (Session AES), for recording a visit that was never on
    // the schedule and so has no job to key on. Resolved by the same server-side helper.
    assetChecklistTemplate: (assetId: string) =>
      request<{ name: string; items: { id: string; item: string; category?: string }[] }>(
        `/jobsheets/asset-checklist-template/${encodeURIComponent(assetId)}`,
      ),
    // Record a scheduled visit as completed on its REAL past date (Session AEB): full checklist
    // detail, honest no-signature provenance, then the ordinary approval gate. No signature
    // field exists on this payload by design - the server rejects one outright.
    recordBackdated: (jobId: string, body: BackdatedEntryBody) =>
      request<{ ok: boolean; status: string; jobId: string }>(`/jobsheets/${encodeURIComponent(jobId)}/backdated`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // Record a service visit that was NEVER on the schedule (Session AES): the sibling of
    // recordBackdated for work the platform never knew about. Creates the visit on its real past
    // date AND its record in one step, landing in pending_approval so the same approval gate
    // produces the certificate and files it to Drive. Same body, plus the lift.
    recordRetrospectiveVisit: (body: BackdatedEntryBody & { assetId: string }) =>
      request<{ ok: boolean; status: string; jobId: string }>(`/jobsheets/retrospective-visit`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // Undo a backdated entry made in error - only before approval, never after the customer
    // has the document. A visit that WAS on the schedule returns to scheduled; a retrospective
    // visit (which was never scheduled) is removed entirely, since it only ever existed to
    // carry the record being undone.
    revertBackdated: (jobId: string) =>
      request<{ ok: boolean; status: string }>(`/jobsheets/${encodeURIComponent(jobId)}/backdated/revert`, { method: "POST" }),
    // Manually link a job to an existing Xero invoice from the cache (Session AO Item 3), for the
    // historical backlog. Stamps the back-reference on the job + sets the reverse link; never
    // creates or modifies a Xero invoice. unlink clears both sides (the invoice is untouched).
    linkInvoice: (jobId: string, invoiceId: string) =>
      request<{ ok: true; linkedInvoice: { invoiceCacheId: string; invoiceNumber: string | null } }>(`/jobsheets/${encodeURIComponent(jobId)}/link-invoice`, { method: "POST", body: JSON.stringify({ invoiceId }) }),
    unlinkInvoice: (jobId: string) =>
      request<{ ok: true }>(`/jobsheets/${encodeURIComponent(jobId)}/link-invoice`, { method: "DELETE" }),
  },

  // ---- Audit timeline (Session AQW): "what happened to this record" ----
  // A generic read over AuditLog, keyed on the entity+entityId pair every writer already stamps
  // (link_invoice, charge_computed, assign, approve, and dozens more). admin/office only.
  auditLog: {
    feed: (q: { userId?: string; entity?: string; from?: string; to?: string; page?: number; pageSize?: number }) => {
      const p = new URLSearchParams();
      for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== "") p.set(k, String(v));
      return request<ActivityLogPage>(`/audit-log/feed?${p.toString()}`, { noCache: true });
    },
    feedFilters: () => request<ActivityLogFilters>("/audit-log/feed/filters"),
    list: (entity: string, entityId: string, limit?: number) =>
      request<{ entries: AuditTimelineEntry[] }>(
        `/audit-log?entity=${encodeURIComponent(entity)}&entityId=${encodeURIComponent(entityId)}${limit ? `&limit=${limit}` : ""}`,
      ),
  },

  // ---- Data retention (Session ASQ, Stream G, Item 4; admin only, office UI only) ----
  // The overview is never cached (it is re-checked live on every read); every write clears the request cache.
  dataRetention: {
    overview: () => request<RetentionOverview>("/gdpr/retention", { noCache: true }),
    runSweep: () =>
      request<{ totals: RetentionSweepTotals; ranAt: string; failedModels: string[]; failedRules: string[] }>("/gdpr/retention/sweep", { method: "POST" }),
    confirm: (ruleId: string, planHash: string) =>
      request<RetentionConfirmResult>(`/gdpr/retention/${encodeURIComponent(ruleId)}/confirm`, { method: "POST", body: JSON.stringify({ planHash, confirm: true }) }),
    dismiss: (proposalId: string, reason: string) =>
      request<{ ok: true }>(`/gdpr/retention/proposals/${encodeURIComponent(proposalId)}/dismiss`, { method: "POST", body: JSON.stringify({ reason }) }),
    restore: (proposalId: string) => request<{ ok: true }>(`/gdpr/retention/proposals/${encodeURIComponent(proposalId)}/restore`, { method: "POST" }),
    remove: (proposalId: string) => request<void>(`/gdpr/retention/proposals/${encodeURIComponent(proposalId)}`, { method: "DELETE" }),
  },

  // ---- Personal data requests (Session ASQ, Stream G; admin only, office UI only) ----
  // The search is a POST (the identifiers never go in a URL) and is never cached; the export downloads a ZIP.
  gdpr: {
    search: (identifiers: Partial<GdprIdentifiers>) =>
      request<GdprSearchResult>("/gdpr/search", { method: "POST", body: JSON.stringify(identifiers), noCache: true }),
    downloadExport: (identifiers: GdprIdentifiers, records: GdprRecordRef[]) => downloadSubjectAccessExport(identifiers, records),
    // Erasure (Session ASQ, Stream G, Item 3). Never cached: every preview reads the records as they are now.
    erasure: {
      preview: (identifiers: GdprIdentifiers, records: GdprErasureRef[]) =>
        request<GdprErasurePreview>("/gdpr/erasure/preview", { method: "POST", body: JSON.stringify({ identifiers, records }), noCache: true }),
      // plannedAt is the preview's own, sent back unchanged: it is how the server knows a repeat of this commit (the
      // "Try the Drive deletes again" / "Save the erasure record again" retry) from a new erasure of the same person.
      commit: (identifiers: GdprIdentifiers, records: GdprErasureRef[], planHash: string, plannedAt: string) =>
        request<GdprErasureResult>("/gdpr/erasure/commit", {
          method: "POST",
          body: JSON.stringify({ identifiers, records, planHash, plannedAt, confirm: true }),
          noCache: true,
        }),
    },
    // Lift an erasure record: the person is then recorded and imported without a warning.
    tombstones: {
      // One email address or one phone number (Session ASQ, Item 3's review: a phone is kept as an erasure record too).
      lift: (identifier: { email: string } | { phone: string }) =>
        request<GdprTombstoneLiftResult>("/gdpr/tombstones/lift", { method: "POST", body: JSON.stringify(identifier), noCache: true }),
    },
  },

  // ---- Knowledge Base (Session AFA; office/admin, tenant-scoped) ----
  // Documents enter from three sources (manual upload, Drive pull, Gmail attachment pull), are
  // ingested in the background (so the create returns immediately), and can be queried in plain
  // English. The delete is two-step: soft (deletedAt) then a hard delete that removes the
  // chunks/embeddings and any Drive copy we made.
  knowledge: {
    documents: {
      list: (query: KnowledgeDocumentListQuery = {}) => {
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(query)) {
          if (value !== undefined && value !== null && `${value}` !== "") params.set(key, `${value}`);
        }
        const qs = params.toString();
        return request<KnowledgeDocumentListResult>(`/knowledge/documents${qs ? `?${qs}` : ""}`);
      },
      get: (id: string) => request<{ document: KnowledgeDocumentDetail }>(`/knowledge/documents/${encodeURIComponent(id)}`),
      // Manual upload: the file plus optional site/lift/manufacturer/model tags. A blank tag is
      // simply omitted. apiRequest sends FormData without a JSON content type (the browser sets the
      // multipart boundary), so the same-origin session cookie still rides along.
      uploadFile: (file: File, tags: KnowledgeTagInput = {}) => {
        const fd = new FormData();
        fd.append("file", file, file.name);
        if (tags.siteId) fd.append("siteId", tags.siteId);
        if (tags.assetId) fd.append("assetId", tags.assetId);
        if (tags.manufacturer) fd.append("manufacturer", tags.manufacturer);
        if (tags.liftModel) fd.append("liftModel", tags.liftModel);
        return request<KnowledgeUploadResult>("/knowledge/documents/upload", { method: "POST", body: fd });
      },
      fromDrive: (body: KnowledgeDrivePullInput) =>
        request<KnowledgePullResult>("/knowledge/documents/from-drive", { method: "POST", body: JSON.stringify(body) }),
      fromGmail: (body: KnowledgeGmailPullInput) =>
        request<KnowledgePullResult>("/knowledge/documents/from-gmail", { method: "POST", body: JSON.stringify(body) }),
      patchTags: (id: string, body: KnowledgeTagsPatchInput) =>
        request<{ document: KnowledgeDocument }>(`/knowledge/documents/${encodeURIComponent(id)}/tags`, {
          method: "PATCH",
          body: JSON.stringify(body),
        }),
      retry: (id: string) => request<{ ok: boolean }>(`/knowledge/documents/${encodeURIComponent(id)}/retry`, { method: "POST" }),
      // Two-step delete: soft first (reversible until the confirm), then hard.
      softDelete: (id: string) =>
        request<{ ok: boolean; deletedAt: string | null }>(`/knowledge/documents/${encodeURIComponent(id)}`, { method: "DELETE" }),
      hardDelete: (id: string) =>
        request<{ ok: boolean }>(`/knowledge/documents/${encodeURIComponent(id)}/confirm`, { method: "DELETE" }),
      // Office bulk import from a Drive folder (Session AFC): preview the PDF count + cost first,
      // then commit. Both office/admin only.
      bulkImportPreview: (body: KnowledgeBulkImportInput) =>
        request<KnowledgeBulkImportPreview>("/knowledge/documents/bulk-import/preview", { method: "POST", body: JSON.stringify(body) }),
      bulkImport: (body: KnowledgeBulkImportInput) =>
        request<KnowledgeBulkImportResult>("/knowledge/documents/bulk-import", { method: "POST", body: JSON.stringify(body) }),
    },
    queueStatus: () => request<KnowledgeQueueStatus>("/knowledge/queue-status"),
    // Ask a plain-language question. On a 503 the server returns a plain-English message (e.g. an
    // integration is not connected); apiRequest surfaces it as ApiError.code.
    query: (body: KnowledgeQueryInput) =>
      request<KnowledgeQueryResponse>("/knowledge/query", { method: "POST", body: JSON.stringify(body) }),
    // Engineer Fault Assistant (Session AFC): the SAME engine as query(), engineer-authenticated,
    // with similarSitesCount added. The engineer PWA uses this; the office uses query().
    queryEngineer: (body: KnowledgeQueryInput) =>
      request<KnowledgeEngineerQueryResponse>("/knowledge/query/engineer", { method: "POST", body: JSON.stringify(body) }),
    // Lift context to pre-fill the Fault Assistant (engineer). By lift id, or by job id (the call-out
    // entry point). context is null for a job with no lift.
    liftContext: (assetId: string) =>
      request<{ context: KnowledgeLiftContext | null }>(`/knowledge/lift-context/${encodeURIComponent(assetId)}`),
    liftContextByJob: (jobId: string) =>
      request<{ context: KnowledgeLiftContext | null }>(`/knowledge/lift-context/by-job/${encodeURIComponent(jobId)}`),
    // "Find the page" (Session BAV): the pages that answer a fault code or symptom, no AI answer. The engineer route
    // applies the engineer's read scope.
    searchPages: (body: { question: string; context?: KnowledgeQueryInput["context"]; widen?: boolean; pickedMaker?: string }) =>
      request<KnowledgePageSearchResponse>("/knowledge/search", { method: "POST", body: JSON.stringify(body) }),
    searchPagesEngineer: (body: { question: string; context?: KnowledgeQueryInput["context"]; widen?: boolean; pickedMaker?: string }) =>
      request<KnowledgePageSearchResponse>("/knowledge/search/engineer", { method: "POST", body: JSON.stringify(body) }),
    // Session BBX3: suggestions as you type, from the equipment vocabulary only (an engineer never sees site wording).
    suggestEngineer: (q: string, opts: { maker?: string; assetId?: string } = {}) => {
      const p = new URLSearchParams({ q });
      if (opts.maker) p.set("maker", opts.maker);
      if (opts.assetId) p.set("assetId", opts.assetId);
      return request<{ scope: string | null; suggestions: KnowledgeSuggestion[] }>(`/knowledge/suggest/engineer?${p.toString()}`);
    },
    // One page of a document for the page viewer; its picture is knowledgePageImageUrl(documentId, pageNumber).
    page: (documentId: string, pageNumber: number) =>
      request<{ page: KnowledgePageView }>(`/knowledge/documents/${encodeURIComponent(documentId)}/pages/${pageNumber}`),
    // The Drive manuals library (Session BAV; office and admin, settings admin only).
    drive: {
      // Both are read live (noCache): they report work in progress. `background` (the Session ASB idle rule) is passed by
      // the Manuals library tab's 15-second refresh, so a screen left open while files process does not keep an office
      // session alive.
      status: (opts: { background?: boolean } = {}) =>
        request<KnowledgeDriveStatusResponse>("/knowledge/drive/status", { noCache: true, ...(opts.background ? { background: true } : {}) }),
      files: (query: KnowledgeDriveFileListQuery = {}, opts: { background?: boolean } = {}) => {
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(query)) {
          if (value !== undefined && value !== null && `${value}` !== "") params.set(key, `${value}`);
        }
        const qs = params.toString();
        return request<KnowledgeDriveFileListResult>(`/knowledge/drive/files${qs ? `?${qs}` : ""}`, {
          noCache: true,
          ...(opts.background ? { background: true } : {}),
        });
      },
      sweep: () => request<KnowledgeDriveActionResult>("/knowledge/drive/sweep", { method: "POST", body: JSON.stringify({ preview: false }) }),
      retryFailed: () =>
        request<KnowledgeDriveActionResult>("/knowledge/drive/retry-failed", { method: "POST", body: JSON.stringify({ preview: false }) }),
      // The restore rebuild (Session BDX routes, admin only; the office control is Session SCR1). The same routes the Guvnor tool
      // rebuild_kb_library dispatches: a preview first, and a start must quote the preview's own number.
      rebuild: {
        status: (opts: { background?: boolean } = {}) =>
          request<{ rebuild: KnowledgeRebuildStatus }>("/knowledge/drive/rebuild", { noCache: true, ...(opts.background ? { background: true } : {}) }),
        preview: (mode: KnowledgeRebuildMode) =>
          request<KnowledgeRebuildPreview>("/knowledge/drive/rebuild", { method: "POST", body: JSON.stringify({ mode, preview: true }) }),
        start: (mode: KnowledgeRebuildMode, confirm: number) =>
          request<KnowledgeRebuildStartResult>("/knowledge/drive/rebuild", { method: "POST", body: JSON.stringify({ mode, preview: false, confirm }) }),
        stop: () =>
          request<{ ok: boolean; preview: false; message: string }>("/knowledge/drive/rebuild/stop", { method: "POST", body: JSON.stringify({ preview: false }) }),
      },
      retry: (id: string, mode: "retry" | "reingest") =>
        request<KnowledgeDriveActionResult>(`/knowledge/drive/files/${encodeURIComponent(id)}/retry`, {
          method: "POST",
          body: JSON.stringify({ mode, preview: false }),
        }),
      remove: (id: string) =>
        request<KnowledgeDriveActionResult>(`/knowledge/drive/files/${encodeURIComponent(id)}/remove`, {
          method: "POST",
          body: JSON.stringify({ preview: false }),
        }),
      forget: (id: string) =>
        request<KnowledgeDriveActionResult>(`/knowledge/drive/files/${encodeURIComponent(id)}?preview=false`, { method: "DELETE" }),
      manufacturers: () => request<{ names: KnowledgeLibraryName[] }>("/knowledge/drive/manufacturers"),
      previewManufacturerName: (body: KnowledgeLibraryNameInput) =>
        request<{ ok: boolean; preview: true; message: string; filesMoved: number }>("/knowledge/drive/manufacturers", {
          method: "PUT",
          body: JSON.stringify({ ...body, preview: true }),
        }),
      saveManufacturerName: (body: KnowledgeLibraryNameInput) =>
        request<{ ok: boolean; message: string; names: KnowledgeLibraryName[] }>("/knowledge/drive/manufacturers", {
          method: "PUT",
          body: JSON.stringify({ ...body, preview: false }),
        }),
      clearManufacturerName: (key: string) =>
        request<{ ok: boolean; message: string; names: KnowledgeLibraryName[] }>(`/knowledge/drive/manufacturers/${encodeURIComponent(key)}?preview=false`, { method: "DELETE" }),
      // Session BBX2: who made each library document, derived from its own text, and the review list.
      makerIdentity: () => request<KnowledgeMakerIdentitySummary>("/knowledge/maker-identity"),
      makerReview: (limit = 50, offset = 0, includeDecided = false) =>
        request<{ total: number; items: KnowledgeMakerReviewItem[] }>(
          `/knowledge/maker-identity/review?limit=${limit}&offset=${offset}&includeDecided=${includeDecided ? "true" : "false"}`,
        ),
      makerBackfill: (preview: boolean) =>
        request<{ ok: boolean; preview: boolean; message: string; documents: number; byConfidence: Record<string, number>; review: { needs: number }; firstRelease: Array<{ display: string; before: number; after: number; gained: number; lost: number }> }>(
          "/knowledge/maker-identity/backfill",
          { method: "POST", body: JSON.stringify({ preview }) },
        ),
      makerDecide: (documentId: string, body: { action: "approve" | "not_manual" | "reset" } | { action: "change"; maker: string }, preview: boolean) =>
        request<{ ok: boolean; preview: boolean; message: string }>(`/knowledge/maker-identity/review/${encodeURIComponent(documentId)}`, {
          method: "POST",
          body: JSON.stringify({ ...body, preview }),
        }),
      // Session BBX3: the search vocabulary and the printed fault code and troubleshooting rows.
      vocabularyStatus: () => request<KnowledgeVocabularyStatus>("/knowledge/vocabulary"),
      vocabularyRebuild: (body: { preview: boolean; confirmDocuments?: number }) =>
        request<{ ok: boolean; preview: boolean; running?: boolean; message: string; documents?: number; pagesWithText?: number; terms?: number }>("/knowledge/vocabulary/rebuild", {
          method: "POST",
          body: JSON.stringify(body),
        }),
      vocabularyClear: (preview: boolean) =>
        request<{ ok: boolean; preview: boolean; message: string; terms?: number; removed?: number }>(`/knowledge/vocabulary?preview=${preview ? "true" : "false"}`, { method: "DELETE" }),
      faultRowsBackfill: (body: { preview: boolean; confirmRows?: number; refresh?: boolean }) =>
        request<KnowledgeFaultRowsBackfill>("/knowledge/fault-rows/backfill", { method: "POST", body: JSON.stringify(body) }),
      faultRows: (opts: { kind?: "code" | "symptom"; makerKey?: string; documentId?: string; limit?: number; offset?: number }) => {
        const p = new URLSearchParams({ limit: String(opts.limit ?? 20), offset: String(opts.offset ?? 0) });
        if (opts.kind) p.set("kind", opts.kind);
        if (opts.makerKey) p.set("makerKey", opts.makerKey);
        if (opts.documentId) p.set("documentId", opts.documentId);
        return request<{ total: number; rows: KnowledgeFaultRowRecord[] }>(`/knowledge/fault-rows?${p.toString()}`);
      },
      faultRowDelete: (id: string, preview: boolean) =>
        request<{ ok: boolean; preview: boolean; message: string }>(`/knowledge/fault-rows/${encodeURIComponent(id)}?preview=${preview ? "true" : "false"}`, { method: "DELETE" }),
      faultRowsDelete: (scope: { documentId?: string; kind?: "code" | "symptom"; all?: boolean }, preview: boolean) => {
        const p = new URLSearchParams({ preview: preview ? "true" : "false" });
        if (scope.documentId) p.set("documentId", scope.documentId);
        if (scope.kind) p.set("kind", scope.kind);
        if (scope.all) p.set("all", "true");
        return request<{ ok: boolean; preview: boolean; message: string; rows?: number; removed?: number }>(`/knowledge/fault-rows?${p.toString()}`, { method: "DELETE" });
      },
      settings: () => request<{ settings: KnowledgeDriveSettings }>("/knowledge/drive/settings"),
      saveSettings: (body: KnowledgeDriveSettingsInput) =>
        request<{ settings: KnowledgeDriveSettings }>("/knowledge/drive/settings", {
          method: "PUT",
          body: JSON.stringify({ ...body, preview: false }),
        }),
    },
    queries: {
      list: (page = 1, pageSize = 10) =>
        request<KnowledgeQueryListResult>(`/knowledge/queries?page=${page}&pageSize=${pageSize}`),
      get: (id: string) => request<{ query: KnowledgeQueryDetail }>(`/knowledge/queries/${encodeURIComponent(id)}`),
    },
  },

  // ---- Portal document library, office side (Session AFE; office/admin) ----
  // Company-wide contractor documents (H&S policy, insurance, RAMS, COSHH, SAFed
  // annexes) + per-site documents, shown to portal customers. Upload is PDF-only;
  // category and file are immutable after upload (delete and re-upload to change);
  // delete is SOFT (the Drive file is never deleted). Downloads are same-origin hrefs
  // to the authenticated proxy (the raw Drive URL never reaches the browser).
  companyDocuments: {
    list: () => request<{ documents: CompanyDocumentItem[] }>("/company-documents"),
    upload: (file: File, fields: { category: CompanyDocCategoryKey; label: string; validUntil?: string }) => {
      const fd = new FormData();
      fd.append("category", fields.category);
      fd.append("label", fields.label);
      if (fields.validUntil) fd.append("validUntil", fields.validUntil);
      fd.append("file", file, file.name);
      return request<{ document: CompanyDocumentItem }>("/company-documents", { method: "POST", body: fd });
    },
    patch: (id: string, body: { label?: string; validUntil?: string | null }) =>
      request<{ document: CompanyDocumentItem }>(`/company-documents/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    remove: (id: string) => request<{ ok: boolean }>(`/company-documents/${encodeURIComponent(id)}`, { method: "DELETE" }),
    downloadHref: (id: string) => `/api/company-documents/${encodeURIComponent(id)}/download`,
  },
  siteDocuments: {
    list: (siteId: string) => request<{ documents: SiteDocumentItem[] }>(`/sites/${encodeURIComponent(siteId)}/documents`),
    upload: (siteId: string, file: File, fields: { category: SiteDocCategoryKey; label: string; validUntil?: string }) => {
      const fd = new FormData();
      fd.append("category", fields.category);
      fd.append("label", fields.label);
      if (fields.validUntil) fd.append("validUntil", fields.validUntil);
      fd.append("file", file, file.name);
      return request<{ document: SiteDocumentItem }>(`/sites/${encodeURIComponent(siteId)}/documents`, { method: "POST", body: fd });
    },
    patch: (siteId: string, docId: string, body: { label?: string; validUntil?: string | null }) =>
      request<{ document: SiteDocumentItem }>(`/sites/${encodeURIComponent(siteId)}/documents/${encodeURIComponent(docId)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    remove: (siteId: string, docId: string) =>
      request<{ ok: boolean }>(`/sites/${encodeURIComponent(siteId)}/documents/${encodeURIComponent(docId)}`, { method: "DELETE" }),
    downloadHref: (siteId: string, docId: string) =>
      `/api/sites/${encodeURIComponent(siteId)}/documents/${encodeURIComponent(docId)}/download`,
  },

  // Training reference documents (Session AFP): the office library behind the training types.
  trainingDocuments: {
    list: (opts: { includeSuperseded?: boolean; trainingType?: TrainingType } = {}) => {
      const params = new URLSearchParams();
      if (opts.includeSuperseded) params.set("includeSuperseded", "true");
      if (opts.trainingType) params.set("trainingType", opts.trainingType);
      const qs = params.toString();
      return request<{ documents: TrainingReferenceDocumentDto[] }>(`/training-documents${qs ? `?${qs}` : ""}`);
    },
    coverage: () => request<{ coverage: TrainingCoverage }>("/training-documents/coverage"),
    upload: (file: File, fields: { trainingType: TrainingType; title: string; version?: string; customerFacing: boolean }) => {
      const fd = new FormData();
      fd.append("trainingType", fields.trainingType);
      fd.append("title", fields.title);
      if (fields.version) fd.append("version", fields.version);
      fd.append("customerFacing", fields.customerFacing ? "true" : "false");
      fd.append("file", file, file.name);
      return request<{ document: TrainingReferenceDocumentDto }>("/training-documents", { method: "POST", body: fd });
    },
    patch: (id: string, body: { title?: string; version?: string | null }) =>
      request<{ document: TrainingReferenceDocumentDto }>(`/training-documents/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    remove: (id: string) => request<{ ok: boolean }>(`/training-documents/${encodeURIComponent(id)}`, { method: "DELETE" }),
    downloadHref: (id: string) => `/api/training-documents/${encodeURIComponent(id)}/download`,
    // Publish the two built-in safety notices with one click (Session AQS, M-3): previously
    // reachable only through Guvnor's publish_safety_notices MCP tool. Supersedes any existing
    // current document of the same training type and audience, exactly like an ordinary re-upload.
    publishSafetyNotices: () =>
      request<{ done: boolean; results: { reference: string; status: "published" | "failed"; detail?: string }[] }>(
        "/training-documents/publish-safety-notices",
        { method: "POST" },
      ),
    // Session AFP: email a customer-facing reference document to a customer (PDF attachment).
    send: (id: string, body: { to: string; recipientName?: string | null; siteId?: string }) =>
      request<{ ok: boolean; sentTo: string }>(`/training-documents/${encodeURIComponent(id)}/send`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // Resolve a site's customer contact so the send flow can pre-fill and display the recipient.
    siteContact: (siteId: string) =>
      request<{ email: string | null; name: string | null }>(`/training-documents/site-contact?siteId=${encodeURIComponent(siteId)}`),
  },
};

// ---- Portal document library types (Session AFE) ----
export type CompanyDocCategoryKey =
  | "HS_POLICY"
  | "PUBLIC_LIABILITY"
  | "EMPLOYERS_LIABILITY"
  | "RAMS"
  | "COSHH"
  | "SAFED_ANNEX"
  | "OTHER";
export type SiteDocCategoryKey = "SITE_RAMS" | "SITE_RISK_ASSESSMENT" | "OTHER";

export interface CompanyDocumentItem {
  id: string;
  category: CompanyDocCategoryKey;
  categoryLabel: string;
  label: string;
  mimeType: string;
  validUntil: string | null; // ISO date-only, for the edit form
  validUntilDisplay: string | null; // pre-formatted server-side ("31 March 2027")
  uploadedAtDisplay: string;
}

export interface SiteDocumentItem extends Omit<CompanyDocumentItem, "category"> {
  siteId: string;
  category: SiteDocCategoryKey;
}

// ---- Session BDA: SAFed test job views ------------------------------------------------------------------------------------
export interface SafedPanelRowView {
  testCode: string;
  name: string;
  cls: "STANDARD" | "STRESS";
  lastTestDate: string | null;
  nextDueDate: string | null;
  status: "not_assessed" | "scheduled" | "due_soon" | "overdue";
  evidence: "yes" | "no" | "unknown";
  /** The LG1 recommended periodicity and the condition it applies where. Guidance, never a mandate. */
  intervalNote: string;
  /** What a due date for this test is called: "LG1 recommended periodicity", with its condition. Never a bare "Due". */
  dueBasis?: string;
  /** The usual test for the lift's drive. ELE's suggestion: any test can still be raised. */
  suggested?: boolean;
}

export type SafedCheckAnswerView = "yes" | "no" | "na" | null;
export interface SafedTestAnswerView {
  status: "pending" | "passed" | "failed" | "stopped" | "not_done";
  checks: SafedCheckAnswerView[];
  checkNotes: string[];
  values: string[];
  comment: string;
  /** The closing item printed on every Annex A form. */
  remedial?: "yes" | "no" | null;
  recommendation?: string;
  testedBy: string;
  testedOn: string;
  stoppedAt?: string | null;
  failureFacts?: string;
  failureDefectId?: string | null;
  annexRev?: number;
  legacy?: { checks: SafedCheckAnswerView[]; checkNotes: string[]; values: string[] };
}
export interface SafedRecordView {
  gate: {
    ticks: boolean[];
    route: "reduced" | "standard" | null;
    /** LR1: does this test need car top or pit access? */
    accessNeeded?: "yes" | "no" | null;
    /** LR1 items marked "not needed for this test", keyed by gate item id, with the reason. */
    notNeeded?: Record<string, string>;
    preLr1?: boolean;
    gateRev?: number;
    evidenceFound: string;
    oemDocument: string;
    quoteRef: string;
    engineer1: string;
    engineer2: string;
    weightsTotal: string;
    weightsSupplier: string;
    buildingContact: string;
  };
  tests: Record<string, SafedTestAnswerView>;
  restoration: { ticks: boolean[]; parts: string };
  /** The testing engineers named in addition to engineer 1 and 2 (printed as "Tested by"). */
  witnesses: string[];
  /** The Competent Person who witnessed the tests (LG1 1.2, 2.4.1), when there was one. Not one of ELE's engineers. */
  competentWitness?: { name: string; organisation: string; date: string };
}
/** One printed item of an Annex A form, in printed order. checks and values of the answer line up with its yes/no items and its field items. */
export interface SafedAnnexItemView {
  kind: "yes_no" | "yes_na_no" | "number" | "text" | "choice" | "date" | "heading" | "note" | "explain";
  text: string;
  unit?: string;
  options?: string[];
  yesIsSatisfactory?: boolean;
  sheet: number;
  conditional?: boolean;
  footnote?: string;
}
export interface SafedTestView {
  id: string;
  /** Sentence case, for screens. */
  name: string;
  /** The printed Annex A title. */
  printedTitle?: string;
  /** The printed "Reference clause" line. */
  clause: string;
  /** ELE risk label: not an LG1 or SAFed term. */
  cls: "STANDARD" | "STRESS";
  restoration: boolean;
  /** A pointer, the numbers LG1 states and the recommended periodicity. No LG1 prose. */
  reference: { pointer: string; periodicity: string; appliesWhere: string | null; parameters: string[]; oemFirst: boolean };
  dueBasis?: string;
  form?: { sheets: { heading: string | null; page: number }[]; items: SafedAnnexItemView[] };
  checks: string[];
  values: { label: string; unit: string }[];
  photoPrompts?: string[];
}
export interface SafedJobView {
  jobId: string;
  status: string;
  assetRef: string | null;
  siteName: string | null;
  driveType: "traction" | "hydraulic" | "both";
  callForKind: "examiner_report" | "customer_request";
  callForRef: string | null;
  quoteRef: string | null;
  stress: boolean;
  tests: SafedTestView[];
  gateItems: string[];
  gateStressOnly: number[];
  /** The named gate list. Positions line up with gateItems and record.gate.ticks. Group "ELE" items are ELE rules or LG1 steps; "LR1" items point to LR1 Issue 04 by section number. */
  gateDefs?: { id: string; text: string; group: "ELE" | "LR1"; kind: "tick" | "access" | "lr1"; stressOnly: boolean }[];
  /** The closing wording printed on every Annex A form. */
  annex?: { remedialQuestion: string; recommendationPrompt: string; explainNo: string; explainYes: string; carryingOutLine: string };
  restorationItems: string[];
  restorationOwed: boolean;
  failureSteps: string[];
  record: SafedRecordView;
  authorisation: { signatoryName: string; signatoryRole: string; signedAt: string; hasDocument: boolean; receivedVia?: string | null } | null;
  engineerCount: number;
  quoteAccepted?: boolean | null;
  startRefusals: string[];
  submitRefusals: string[];
  certificateRef: string | null;
}
export interface SafedAssetJobView {
  jobId: string;
  status: string;
  scheduledFor: string | null;
  tests: { id: string; name: string; cls: "STANDARD" | "STRESS" }[];
  callForKind: "examiner_report" | "customer_request";
  callForRef: string | null;
  quoteRef: string | null;
  certificateRef: string | null;
  authorisation: { signatoryName: string; signatoryRole: string; signedAt: string; receivedVia?: string | null; hasDocument?: boolean } | null;
  // Session BDB
  quoteId?: string | null;
  quoteAccepted?: boolean | null;
  authDraftedAt?: string | null;
  resultsDraftedAt?: string | null;
  invoiceNumber?: string | null;
  engineerCount?: number;
  hasEngineer?: boolean;
  photoCount?: number;
}
